import * as THREE from 'three';
import * as BG from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Postprocessing } from './Postprocessing';
import {
  Sim, SeedType, BuildingType, ToolId, PlotSystem, MillSystem,
  ConstructionSystem,
  PLOT_W, PLOT_H, ROAD_ROW,
  TRUCK_TRAVEL_TIME, BUILDING_SIZE,
  PLOT_SUB_DIM,
  Season, SEASONS, MONTH_SEASON, MONTH_DAYS, MONTHS,
} from '../simulation';
import type { Settings } from '../../ui/Settings';

// ── Palette ─────────────────────────────────────────────────────────
const COL = {
  sky:          0x87bbe8,
  grass:        0x7ec050,
  grassDark:    0x4a803a,
  fieldDry:     0xb88458,
  fieldWet:     0x6a4830,
  stone:        0x9b9ba8,
  road:         0x4a4a52,
  roadStripe:   0xe8d870,
  path:         0xc4a978,
  earth:        0x7a4d28,
  earthDark:    0x4a2f1a,
  trunk:        0x6a4828,
  trunkDk:      0x4a3018,
  leaf:         0x5aa040,
  leafDk:       0x3a7028,
  leafLt:       0x80c060,
  bushDk:       0x3a7028,
  bushLt:       0x6ab040,
  tallGrass:    0x88c050,
  wheatGreen:   0x6aaa44,
  wheatGreenLt: 0x9bcf60,
  wheatGold:    0xe8c040,
  wheatGoldLt:  0xf8e078,
  wheatGoldDk:  0xb88030,
  cornStalk:    0x5a9438,
  cornStalkLt:  0x84c050,
  cornCob:      0xf4d258,
  cornCobLt:    0xfae89a,
  cornHusk:     0x9cb24a,
  carrotLeaf:   0x4a9438,
  carrotLeafLt: 0x74c450,
  carrotRoot:   0xe87830,
  carrotRootLt: 0xff9c4a,
  boulder:      0x888888,
  boulderLt:    0xb0b0b0,
  boulderDk:    0x585858,
  waterSheen:   0x5a9ad0,
  granaryWall:  0xb8503a,
  granaryWallD: 0x7e2e20,
  granaryRoof:  0x583020,
  granaryRoofL: 0x7c4530,
  wood:         0x9c6c44,
  woodDk:       0x6a4830,
  woodLt:       0xc89058,
  millStone:    0xa8a0a0,
  millStoneL:   0xc8c0b8,
  millStoneD:   0x787078,
  millSail:     0xe8d4a0,
  millSailD:    0x9c8a60,
  mailboxBody:  0xc4503a,
  mailboxFlag:  0xe8c040,
  truckCab:     0x4a8ac8,
  truckCabDk:   0x2a5288,
  truckBed:     0x9c6038,
  truckBedDk:   0x6a4020,
  truckTire:    0x202020,
  truckTireHi:  0x404040,
  truckGlass:   0xa8d4f0,
  // Lot-side voxel-variant accents
  wormPink:     0xc46c80,
  wormDk:       0x80384a,
  fossilLt:     0xe8e0c0,
  fossilDk:     0xb0a888,
  rootBr:       0x4a3018,
  rootBrLt:     0x6a4828,
  skeletonLt:   0xf0e8d0,
  skeletonDk:   0x90886c,
  earthLt:      0x8a5a30,
  earthVDk:     0x2a1810,
  hoverMark:    0xfff070,
  validSlot:    0x70ff60,
  invalidSlot:  0xff7060,
  flowerY:      0xffe080,
  flowerP:      0xff80a8,
  smokePuff:    0xeae8e0,
};

// ── Shared geometry + material cache ────────────────────────────────
const BOX = new THREE.BoxGeometry(1, 1, 1);
const MAT = new Map<number, THREE.MeshLambertMaterial>();
/** Geometries / materials that must NEVER be disposed because they're
 *  reused across many meshes. `disposeTree` checks against these sets
 *  before calling .dispose() on a mesh's resources. */
const SHARED_GEOMETRIES = new Set<THREE.BufferGeometry>([BOX]);
const SHARED_MATERIALS  = new Set<THREE.Material>();
function mat(col: number) {
  let m = MAT.get(col);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color: col });
    MAT.set(col, m);
    SHARED_MATERIALS.add(m);
  }
  return m;
}

/** Walk a Three.js subtree and dispose every Mesh / InstancedMesh's
 *  geometry + materials, skipping anything in the SHARED_GEOMETRIES /
 *  SHARED_MATERIALS sets. Without this, `scene.remove(obj)` leaks the
 *  underlying WebGL buffers — Three.js doesn't auto-free GPU resources
 *  when an object is detached from the scene graph, only when an
 *  explicit .dispose() call is made. The leaky-rebuild bug had the
 *  game's tab climb to many GB of RAM after a typical play session.
 *
 *  Safe to call on any Object3D — non-Mesh nodes (Group, Object3D, etc.)
 *  are walked through without trying to dispose anything. */
function disposeTree(obj: THREE.Object3D | null | undefined) {
  if (!obj) return;
  obj.traverse(o => {
    const mesh = o as THREE.Mesh & { isInstancedMesh?: boolean };
    if (!(mesh as any).isMesh && !mesh.isInstancedMesh) return;
    const g = mesh.geometry;
    if (g && !SHARED_GEOMETRIES.has(g)) g.dispose();
    const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(m)) {
      for (const mat of m) if (mat && !SHARED_MATERIALS.has(mat)) mat.dispose();
    } else if (m && !SHARED_MATERIALS.has(m)) {
      m.dispose();
    }
    // InstancedMesh has its own dispose() too — frees the per-instance
    // matrix buffer.
    if (mesh.isInstancedMesh) (mesh as any).dispose?.();
  });
}

/** Single shared material for all merged voxel objects. Uses vertex colors.
 *
 *  Patched at module load with onBeforeCompile to add a SEASONAL FOLIAGE
 *  TINT uniform. The shader detects "green-dominant" pixels (where the
 *  green channel is the highest of the three AND the color is not too
 *  dark) and multiplies them by `uFoliageTint`. Trunks (brown), stone
 *  (gray), road (dark), dirt (warm brown), wheat (gold), buildings, the
 *  truck, etc. all have non-green-dominant colors so they're unaffected.
 *
 *  The uniform itself lives on `VX_MAT.userData.seasonTintUniform` and
 *  is updated each frame by `animateSeasonalFoliage()` based on the
 *  current calendar season (and blended across season transitions).
 */
const VX_MAT = new THREE.MeshLambertMaterial({ vertexColors: true });
SHARED_MATERIALS.add(VX_MAT);
{
  const seasonTintUniform = { value: new THREE.Vector3(1, 1, 1) };
  (VX_MAT as any).userData = { seasonTintUniform };
  VX_MAT.onBeforeCompile = (shader) => {
    shader.uniforms.uFoliageTint = seasonTintUniform;
    // Inject the uniform declaration at file scope (uniforms can't be
    // declared inside main()), then the tint logic inside main().
    shader.fragmentShader =
      'uniform vec3 uFoliageTint;\n' + shader.fragmentShader.replace(
        '#include <color_fragment>',
        `
        #include <color_fragment>
        // Seasonal foliage tint: detect green-dominant pixels and
        // multiply them by uFoliageTint. The detector is a soft mask
        // so the tint fades to 0 on borderline colors (tall grass,
        // moss) instead of snapping.
        {
          vec3 c = diffuseColor.rgb;
          float maxC = max(max(c.r, c.g), c.b);
          // greenLead: how much the green channel exceeds the next-
          // highest. 0 if not green-dominant; up to ~0.5 for pure green.
          float greenLead = c.g - max(c.r, c.b);
          // brightness floor — avoid tinting near-black colors.
          float bright = step(0.08, maxC);
          float mask = clamp(greenLead * 4.0, 0.0, 1.0) * bright;
          // Blend toward tint-multiplied color by mask.
          vec3 tinted = c * uFoliageTint;
          diffuseColor.rgb = mix(c, tinted, mask);
        }
        `,
      );
  };
}

/** Sway-variant of VX_MAT used by the per-tile merged seed/stalk meshes.
 *  Shares the seasonal foliage-tint patch with VX_MAT (same uniform so
 *  tinting stays consistent), plus a vertex-shader sway: vertices above
 *  ROOT_THRESHOLD are displaced horizontally by sin(time + perStalkPhase)
 *  × amp × (y - threshold). Roots/dirt at low Y stay anchored — that
 *  fixes the tile-bobbing problem the rotation-based sway had.
 *  Updated each frame via the userData.swayTimeUniform.value = elapsed. */
const VX_MAT_SWAY = new THREE.MeshLambertMaterial({ vertexColors: true });
SHARED_MATERIALS.add(VX_MAT_SWAY);
{
  // Reuse VX_MAT's foliage-tint uniform so all merged voxel meshes
  // recolor together each season — no double bookkeeping.
  const seasonTintUniform = (VX_MAT as any).userData.seasonTintUniform as { value: THREE.Vector3 };
  const swayTimeUniform = { value: 0 };
  (VX_MAT_SWAY as any).userData = { seasonTintUniform, swayTimeUniform };
  VX_MAT_SWAY.onBeforeCompile = (shader) => {
    shader.uniforms.uFoliageTint = seasonTintUniform;
    shader.uniforms.uSwayTime    = swayTimeUniform;
    // Vertex patch — wind sway proportional to height above the root
    // threshold. Per-stalk phase is hashed from the base XZ position so
    // neighbouring stalks don't lean perfectly in sync.
    shader.vertexShader =
      'uniform float uSwayTime;\n' + shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
        #include <begin_vertex>
        {
          float swayY = transformed.y - 0.04;
          if (swayY > 0.0) {
            float ph = position.x * 7.0 + position.z * 11.0;
            transformed.x += sin(uSwayTime * 1.8 + ph)       * 0.08 * swayY;
            transformed.z += sin(uSwayTime * 1.45 + ph * 0.7) * 0.05 * swayY;
          }
        }
        `,
      );
    // Fragment patch — same seasonal foliage tint as VX_MAT.
    shader.fragmentShader =
      'uniform vec3 uFoliageTint;\n' + shader.fragmentShader.replace(
        '#include <color_fragment>',
        `
        #include <color_fragment>
        {
          vec3 c = diffuseColor.rgb;
          float maxC = max(max(c.r, c.g), c.b);
          float greenLead = c.g - max(c.r, c.b);
          float bright = step(0.08, maxC);
          float mask = clamp(greenLead * 4.0, 0.0, 1.0) * bright;
          vec3 tinted = c * uFoliageTint;
          diffuseColor.rgb = mix(c, tinted, mask);
        }
        `,
      );
  };
}

/** Reusable Y-axis vector for quaternion rotations — saves per-call allocs
 *  in hot paths like buildPlotWeeds (3000+ weeds = 3000+ axis allocs).
 *  rev: 2026-05-17 — FPS optimization batch (weed InstancedMesh, palette
 *  Color reuse, hover candidate cache, wet/seed Map index, pixelRatio cap). */
const THREE_Y_AXIS = new THREE.Vector3(0, 1, 0);
/** Extra world-units past the lot edge that the camera target is allowed
 *  to pan into. Generous enough that the player can scroll just past the
 *  perimeter to inspect the lot's edges without escaping into empty
 *  voxel-void territory. */
const CAM_CLAMP_MARGIN = 6;

function cube(sx: number, sy: number, sz: number, x: number, y: number, z: number, col: number): THREE.Mesh {
  const m = new THREE.Mesh(BOX, mat(col));
  m.scale.set(sx, sy, sz);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/**
 * Builds a merged-geometry voxel object: many small cubes packed into ONE
 * BufferGeometry with per-vertex colors. Result = a single Mesh = a single
 * draw call, which lets us pack 100+ voxels per object affordably.
 */
// sRGB → linear for vertex colors. Three.js auto-converts material `color`
// values from sRGB, but raw vertex colors are treated as already-linear — so
// the same hex would otherwise look darker via VoxelBuilder than via cube().
const sRGBToLinear = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

class VoxelBuilder {
  private geos: THREE.BufferGeometry[] = [];
  /** Per-builder offset added to every subsequent add(). Lets us call
   *  the same model-building function N times into one builder, each
   *  call placed at a different sub-cell position — used by the
   *  per-tile merged seed renderer to fold 64 seed meshes into one
   *  draw call. setOffset back to 0 to reset. */
  private offsetX = 0;
  private offsetY = 0;
  private offsetZ = 0;
  setOffset(x: number, y: number, z: number) { this.offsetX = x; this.offsetY = y; this.offsetZ = z; }
  add(sx: number, sy: number, sz: number, x: number, y: number, z: number, hex: number) {
    const g = new THREE.BoxGeometry(sx, sy, sz);
    g.translate(x + this.offsetX, y + this.offsetY, z + this.offsetZ);
    const N = g.attributes.position.count;
    const col = new Float32Array(N * 3);
    const r = sRGBToLinear(((hex >> 16) & 0xff) / 255);
    const gg = sRGBToLinear(((hex >> 8) & 0xff) / 255);
    const bb = sRGBToLinear((hex & 0xff) / 255);
    for (let i = 0; i < N; i++) {
      col[i * 3 + 0] = r;
      col[i * 3 + 1] = gg;
      col[i * 3 + 2] = bb;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.geos.push(g);
  }
  build(): THREE.Mesh {
    // Defensive: if no voxels were added, return a mesh with an empty
    // BufferGeometry. mergeGeometries returns null on an empty array,
    // and the renderer crashes downstream trying to read `.index`.
    if (this.geos.length === 0) {
      return new THREE.Mesh(new THREE.BufferGeometry(), VX_MAT);
    }
    const merged = BG.mergeGeometries(this.geos, false)!;
    // Dispose the source boxes — merge has its own buffers now.
    for (const g of this.geos) g.dispose();
    const mesh = new THREE.Mesh(merged, VX_MAT);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }
  /** Discard accumulated geos without building (used when we want two output meshes). */
  reset() {
    for (const g of this.geos) g.dispose();
    this.geos = [];
  }
}

/** Tiny PRNG so a single call gives multiple deterministic-but-varied numbers. */
function rngFn(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Voxel builders ──────────────────────────────────────────────────
/**
 * Dense voxel oak: trunk of chunky cubes, canopy as a sphere of ~80–120 small
 * voxels in 3 shades of green. Trunk + canopy are merged into TWO meshes so
 * we can sway the canopy without moving the trunk.
 *
 * `seed` makes color distribution deterministic per tile so adjacent trees
 * don't all look identical but a given (c, r) re-renders the same way.
 */
function makeTree(seed = 1): THREE.Object3D {
  const root = new THREE.Group();
  const rng = rngFn(seed * 9277 + 137);

  // Trunk — stacked subdivided rings (each "ring" is 4 voxels) so the bark
  // shows seams + colour variation instead of a single fat cylinder.
  const trunkBuilder = new VoxelBuilder();
  const TRUNK_H = 5;
  const RING_STEP = 0.17;     // half of the original 0.34 height for finer rings
  const RINGS = TRUNK_H * 2;  // 10 rings = visible bark stratification
  const BARK = [COL.trunk, COL.trunkDk, 0x6a4a26, 0x7c5a32, 0x553818];
  for (let i = 0; i < RINGS; i++) {
    const ringY = 0.085 + i * RING_STEP;
    // 4 quadrant voxels per ring so the bark face has visible vertical seams
    const SHRINK = i === 0 ? 0.05 : 0.0;     // base ring slightly wider (root flare blends in)
    const W = 0.20 - SHRINK;
    const quads: [number, number][] = [[-0.10, -0.10], [0.10, -0.10], [-0.10, 0.10], [0.10, 0.10]];
    for (const [qx, qz] of quads) {
      const col = BARK[Math.floor(rng() * BARK.length)];
      trunkBuilder.add(W, RING_STEP, W, qx, ringY, qz, col);
    }
    // Occasional knot — darker speck on one face
    if (rng() < 0.18) {
      const side = Math.floor(rng() * 4);
      const knotX = side === 0 ? 0.21 : side === 1 ? -0.21 : 0;
      const knotZ = side === 2 ? 0.21 : side === 3 ? -0.21 : 0;
      trunkBuilder.add(0.06, 0.06, 0.06, knotX, ringY, knotZ, 0x3a2010);
    }
  }
  // Root flare — chunky buttress bumps around the base, asymmetric for realism
  const flares: [number, number, number, number][] = [
    [ 0.28, 0,    0.20, 0.18],
    [-0.28, 0,    0.18, 0.16],
    [ 0,    0.28, 0.20, 0.18],
    [ 0,   -0.28, 0.18, 0.16],
    [ 0.22, 0.22, 0.14, 0.14],
    [-0.22,-0.22, 0.14, 0.14],
  ];
  for (const [fx, fz, fw, fh] of flares) {
    trunkBuilder.add(fw, fh, fw, fx, fh / 2 + 0.01, fz, COL.trunkDk);
  }
  // Two small protruding branch nubs at varied heights — gives the trunk
  // silhouette breaks from a perfect cylinder.
  for (let i = 0; i < 2; i++) {
    const branchY = 0.5 + rng() * (TRUNK_H * 0.34 - 0.6);
    const ang = rng() * Math.PI * 2;
    const bx = Math.cos(ang) * 0.16;
    const bz = Math.sin(ang) * 0.16;
    trunkBuilder.add(0.10, 0.10, 0.10, bx, branchY, bz, COL.trunkDk);
  }
  root.add(trunkBuilder.build());

  // Canopy — denser voxel sphere with smaller STEP. Outer shell + occasional
  // protruding clump for irregular silhouette.
  const swayGroup = new THREE.Group();
  swayGroup.position.y = TRUNK_H * 0.34;
  swayGroup.userData = { swayFreq: 1.0, swayAmp: 0.05, swayAxis: 'z' };

  const canopyBuilder = new VoxelBuilder();
  const STEP = 0.12;                    // finer than 0.16 for crisper edges
  const R = 1.35;
  const cy = R - 0.1;
  const bias = rng();
  for (let dx = -R; dx <= R + 1e-3; dx += STEP) {
    for (let dy = -R; dy <= R + 1e-3; dy += STEP) {
      for (let dz = -R; dz <= R + 1e-3; dz += STEP) {
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d > R) continue;
        if (d < R - STEP * 2.0) continue;
        const top = (dy + R) / (R * 2);
        const front = (-dz + R) / (R * 2);
        const lightness = top * 0.6 + front * 0.4 + bias * 0.3;
        const roll = rng();
        let c: number;
        if (lightness < 0.35 || roll < 0.18) c = COL.leafDk;
        else if (lightness < 0.65)            c = COL.leaf;
        else                                   c = COL.leafLt;
        // Drop ~5% of voxels for craggy outline (finer STEP means more
        // voxels — bump drop a little so the silhouette doesn't read solid).
        if (rng() < 0.05) continue;
        canopyBuilder.add(STEP * 1.02, STEP * 1.02, STEP * 1.02, dx, cy + dy, dz, c);
      }
    }
  }
  // 3–5 protruding leaf clumps for irregular outline — small leaf bumps on
  // the outside of the main canopy sphere.
  const lumps = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < lumps; i++) {
    const ang = rng() * Math.PI * 2;
    const ele = -0.3 + rng() * 1.4;
    const r0 = R + 0.10;
    const lcx = Math.cos(ang) * r0;
    const lcz = Math.sin(ang) * r0;
    const lcy = cy + ele;
    const lr = 0.18 + rng() * 0.12;
    for (let dx = -lr; dx <= lr + 1e-3; dx += STEP) {
      for (let dy = -lr; dy <= lr + 1e-3; dy += STEP) {
        for (let dz = -lr; dz <= lr + 1e-3; dz += STEP) {
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d > lr) continue;
          if (d < lr - STEP * 1.5) continue;
          if (rng() < 0.10) continue;
          const cc = rng() < 0.40 ? COL.leafLt : COL.leaf;
          canopyBuilder.add(STEP * 1.02, STEP * 1.02, STEP * 1.02, lcx + dx, lcy + dy, lcz + dz, cc);
        }
      }
    }
  }
  swayGroup.add(canopyBuilder.build());
  root.add(swayGroup);

  // Base tufts — small grass ring around the trunk so it integrates with
  // the lawn instead of floating on bare ground. 8–14 tiny voxel blades
  // distributed in a noisy ring; each leans outward like makeTallGrass
  // for a wild "growing around the trunk" silhouette.
  const tuftBuilder = new VoxelBuilder();
  const TUFT_GREENS = [0x6fb045, 0x7ad440, 0x5a9038, 0x80a050, 0x4a7a32, 0x9bb854];
  const tuftCount = 8 + Math.floor(rng() * 7);
  for (let i = 0; i < tuftCount; i++) {
    const ang = rng() * Math.PI * 2;
    const dist = 0.30 + rng() * 0.16;
    const bx = Math.cos(ang) * dist;
    const bz = Math.sin(ang) * dist;
    const h = 0.08 + rng() * 0.10;
    const lean = 0.05 + rng() * 0.06;
    const col = TUFT_GREENS[Math.floor(rng() * TUFT_GREENS.length)];
    // 2 stacked voxels — second one offset outward so the blade arches.
    tuftBuilder.add(0.06, h * 0.55, 0.06, bx, h * 0.28 + 0.02, bz, col);
    tuftBuilder.add(0.05, h * 0.55, 0.05,
      bx + Math.cos(ang) * lean, h * 0.83 + 0.02, bz + Math.sin(ang) * lean,
      rng() < 0.4 ? TUFT_GREENS[Math.floor(rng() * 3) + 3] : col);
    // 25% chance of a tiny wildflower next to the tuft
    if (rng() < 0.18) {
      const fc = rng() < 0.5 ? 0xfff080 : (rng() < 0.6 ? 0xffffff : 0xff80a8);
      tuftBuilder.add(0.04, 0.05, 0.04, bx, 0.12, bz, fc);
    }
  }
  root.add(tuftBuilder.build());
  return root;
}

function makeStump(): THREE.Object3D {
  const root = new THREE.Group();
  const b = new VoxelBuilder();
  const rng = rngFn(6151 + 29);
  // Bark palette — multi-shade so the perimeter doesn't read as a single
  // brown block. Inner-ring palette is a lighter sapwood gradient.
  const BARK = [COL.trunk, COL.trunkDk, 0x6a4a26, 0x7c5a32, 0x553818];
  const SAPWOOD_OUT = 0xb88a52;
  const SAPWOOD_MID = 0xc8986a;
  const SAPWOOD_IN  = 0xd8ac80;
  const HEARTWOOD   = 0x9c6c44;
  const MOSS_LT     = 0x86b048;
  const MOSS_DK     = 0x4a7028;

  const STUMP_H = 0.40;
  const RING_STEP = 0.10;     // 4 stacked bark rings
  const RINGS = Math.round(STUMP_H / RING_STEP);
  // Bark perimeter — 12 voxels per ring forming an octagonal-ish outline so
  // the silhouette breaks from a flat box. Outer ring is slightly wider at the
  // base (root flare blends in).
  const ringOffsets: [number, number][] = [
    [-0.27, -0.09], [-0.27, 0.09], [ 0.27, -0.09], [ 0.27, 0.09],
    [-0.09, -0.27], [ 0.09, -0.27], [-0.09, 0.27], [ 0.09, 0.27],
    [-0.21, -0.21], [ 0.21, -0.21], [-0.21, 0.21], [ 0.21, 0.21],
  ];
  for (let i = 0; i < RINGS; i++) {
    const ringY = RING_STEP * 0.5 + i * RING_STEP;
    const flare = i === 0 ? 0.04 : 0.0;   // bottom ring slightly larger
    for (const [ox, oz] of ringOffsets) {
      const col = BARK[Math.floor(rng() * BARK.length)];
      b.add(0.16 + flare, RING_STEP * 0.98, 0.16 + flare, ox, ringY, oz, col);
    }
  }
  // Concentric ring detail on the top cut surface — outer bark rim, then
  // light-to-lighter inner growth rings around a heartwood centre.
  const TOP_Y = STUMP_H + 0.04;
  // Outer bark rim (thin dark band)
  const rimOffsets: [number, number][] = [
    [-0.30, 0], [0.30, 0], [0, -0.30], [0, 0.30],
    [-0.24, -0.24], [0.24, -0.24], [-0.24, 0.24], [0.24, 0.24],
    [-0.18, -0.30], [0.18, -0.30], [-0.18, 0.30], [0.18, 0.30],
    [-0.30, -0.18], [-0.30, 0.18], [0.30, -0.18], [0.30, 0.18],
  ];
  for (const [ox, oz] of rimOffsets) {
    b.add(0.10, 0.04, 0.10, ox, TOP_Y, oz, COL.trunkDk);
  }
  // Ring 2 — outer sapwood
  const ring2: [number, number][] = [
    [-0.20, 0], [0.20, 0], [0, -0.20], [0, 0.20],
    [-0.14, -0.14], [0.14, -0.14], [-0.14, 0.14], [0.14, 0.14],
    [-0.20, -0.10], [0.20, -0.10], [-0.20, 0.10], [0.20, 0.10],
    [-0.10, -0.20], [0.10, -0.20], [-0.10, 0.20], [0.10, 0.20],
  ];
  for (const [ox, oz] of ring2) {
    b.add(0.10, 0.04, 0.10, ox, TOP_Y, oz, SAPWOOD_OUT);
  }
  // Ring 3 — mid sapwood
  const ring3: [number, number][] = [
    [-0.10, 0], [0.10, 0], [0, -0.10], [0, 0.10],
    [-0.08, -0.08], [0.08, -0.08], [-0.08, 0.08], [0.08, 0.08],
  ];
  for (const [ox, oz] of ring3) {
    b.add(0.10, 0.04, 0.10, ox, TOP_Y, oz, SAPWOOD_MID);
  }
  // Ring 4 — inner sapwood
  b.add(0.10, 0.04, 0.10, 0, TOP_Y, 0, SAPWOOD_IN);
  // Tiny pith dot at the very centre
  b.add(0.04, 0.045, 0.04, 0, TOP_Y + 0.005, 0, HEARTWOOD);
  // A couple of radial crack voxels — thin dark lines from rim toward centre
  const crackCount = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < crackCount; i++) {
    const ang = rng() * Math.PI * 2;
    const cx = Math.cos(ang) * 0.16;
    const cz = Math.sin(ang) * 0.16;
    b.add(0.05, 0.045, 0.05, cx, TOP_Y + 0.002, cz, 0x3a2814);
  }
  // Root flare — 3 chunky buttress bumps around the base (one dropped for asymmetry)
  const flares: [number, number, number, number][] = [
    [ 0.32, 0,    0.18, 0.14],
    [-0.30, 0,    0.16, 0.12],
    [ 0,    0.30, 0.18, 0.14],
    [ 0,   -0.28, 0.16, 0.12],
  ];
  const skipIdx = Math.floor(rng() * flares.length);
  for (let i = 0; i < flares.length; i++) {
    if (i === skipIdx) continue;
    const [fx, fz, fw, fh] = flares[i];
    b.add(fw, fh, fw, fx, fh / 2 + 0.005, fz, COL.trunkDk);
  }
  // 2-3 small moss patches clinging to the bark on the shady side
  const mossCount = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < mossCount; i++) {
    const ang = Math.PI * 0.5 + rng() * Math.PI;     // bias to one side
    const mx = Math.cos(ang) * 0.30;
    const mz = Math.sin(ang) * 0.30;
    const my = 0.08 + rng() * (STUMP_H - 0.10);
    const mc = rng() < 0.5 ? MOSS_LT : MOSS_DK;
    b.add(0.10, 0.06, 0.10, mx, my, mz, mc);
    if (rng() < 0.5) b.add(0.06, 0.04, 0.06, mx, my + 0.05, mz, MOSS_LT);
  }
  // A tiny moss patch on the cut top, near the rim
  {
    const ang = rng() * Math.PI * 2;
    const mx = Math.cos(ang) * 0.22;
    const mz = Math.sin(ang) * 0.22;
    b.add(0.08, 0.035, 0.08, mx, TOP_Y + 0.025, mz, MOSS_LT);
  }
  root.add(b.build());

  // Base tufts — grass ring around the stump, like the parent oak had.
  // Slightly denser than under a live tree (decay/reclaim feel).
  const tuftB = new VoxelBuilder();
  const TUFT_GREENS = [0x6fb045, 0x7ad440, 0x5a9038, 0x80a050, 0x4a7a32, 0x9bb854];
  const tc = 9 + Math.floor(rng() * 6);
  for (let i = 0; i < tc; i++) {
    const ang = rng() * Math.PI * 2;
    const dist = 0.32 + rng() * 0.14;
    const bx = Math.cos(ang) * dist;
    const bz = Math.sin(ang) * dist;
    const h = 0.08 + rng() * 0.10;
    const lean = 0.05 + rng() * 0.06;
    const col = TUFT_GREENS[Math.floor(rng() * TUFT_GREENS.length)];
    tuftB.add(0.06, h * 0.55, 0.06, bx, h * 0.28 + 0.02, bz, col);
    tuftB.add(0.05, h * 0.55, 0.05,
      bx + Math.cos(ang) * lean, h * 0.83 + 0.02, bz + Math.sin(ang) * lean, col);
  }
  root.add(tuftB.build());
  return root;
}

/**
 * Bush voxel model.
 * `berryStage` 0 = bare, 1 = unripe (small green berries), 2 = ripe (red).
 * `scale` < 1 produces a smaller bush (used by `bush-sapling`).
 */
function makeBush(seed = 1, berryStage = 0, scale = 1): THREE.Object3D {
  const root = new THREE.Group();
  root.userData = { swayFreq: 2.0, swayAmp: 0.05, swayAxis: 'z' };
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 4421 + 53);
  const STEP = 0.10;
  const clumps: { cx: number; cy: number; cz: number; r: number; bias: number }[] = [
    { cx:  0.00, cy: 0.10, cz:  0.00, r: 0.55, bias: 0.0 },
    { cx:  0.18, cy: 0.06, cz: -0.16, r: 0.36, bias: 0.1 },
    { cx: -0.20, cy: 0.04, cz:  0.14, r: 0.32, bias: -0.1 },
  ];
  const BUSH_GREENS = [COL.bushDk, COL.bushLt, 0x4a7a32, 0x6a9a40, 0x86b048];
  for (const clump of clumps) {
    for (let dx = -clump.r; dx <= clump.r + 1e-3; dx += STEP) {
      for (let dy = 0; dy <= clump.r + 1e-3; dy += STEP) {
        for (let dz = -clump.r; dz <= clump.r + 1e-3; dz += STEP) {
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d > clump.r) continue;
          if (d < clump.r - STEP * 1.8) continue;
          if (rng() < 0.06) continue;
          const lightBias = (dy / clump.r) * 0.4 + (-dz / clump.r) * 0.3 + clump.bias;
          let col: number;
          if (lightBias > 0.45)      col = BUSH_GREENS[2 + Math.floor(rng() * 3)];
          else if (lightBias > 0.10) col = BUSH_GREENS[1 + Math.floor(rng() * 2)];
          else                        col = BUSH_GREENS[0 + Math.floor(rng() * 2)];
          b.add(STEP * 1.08, STEP * 1.08, STEP * 1.08, clump.cx + dx, clump.cy + dy, clump.cz + dz, col);
        }
      }
    }
  }
  // Berries: only present at stage 1+. Stage 1 = tiny pale-green dots,
  // stage 2 = bright ripe clusters. Stage 0 = none (recently harvested).
  if (berryStage >= 1) {
    const berryCount = berryStage === 2
      ? 4 + Math.floor(rng() * 5)
      : 2 + Math.floor(rng() * 3);
    // Stage 2: warm red/orange/blue palette. Stage 1: small green/pale.
    const ripeColors    = [0xc44048, 0xd86040, 0xa01828, 0x4060a0, 0xff8030];
    const unripeColors  = [0x86a838, 0xa8c050, 0x6a8030];
    const palette = berryStage === 2 ? ripeColors : unripeColors;
    const berryCol = palette[Math.floor(rng() * palette.length)];
    const berrySize = berryStage === 2 ? 0.09 : 0.05;
    for (let i = 0; i < berryCount; i++) {
      const ang = rng() * Math.PI * 2;
      const dist = 0.32 + rng() * 0.18;
      const bx = Math.cos(ang) * dist;
      const bz = Math.sin(ang) * dist - 0.10;
      const by = 0.20 + rng() * 0.30;
      b.add(berrySize, berrySize, berrySize, bx, by, bz, berryCol);
      if (berryStage === 2 && rng() < 0.4) {
        b.add(0.03, 0.03, 0.03, bx, by + 0.05, bz, 0x3a4818);
      }
    }
  }
  // Twigs at the base.
  const twigs = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < twigs; i++) {
    const ang = rng() * Math.PI * 2;
    const tx = Math.cos(ang) * 0.18;
    const tz = Math.sin(ang) * 0.18;
    b.add(0.04, 0.18, 0.04, tx, 0.09, tz, 0x6a4220);
  }
  // Base tufts — grass ring around the bush so it nestles into the lawn.
  // A pinch fewer than around trees (the bush already has dense low foliage).
  const TUFT_GREENS_B = [0x6fb045, 0x7ad440, 0x5a9038, 0x80a050, 0x4a7a32];
  const bc = 6 + Math.floor(rng() * 4);
  for (let i = 0; i < bc; i++) {
    const ang = rng() * Math.PI * 2;
    const dist = 0.30 + rng() * 0.12;
    const bx = Math.cos(ang) * dist;
    const bz = Math.sin(ang) * dist;
    const h = 0.07 + rng() * 0.09;
    const lean = 0.04 + rng() * 0.05;
    const col = TUFT_GREENS_B[Math.floor(rng() * TUFT_GREENS_B.length)];
    b.add(0.05, h * 0.55, 0.05, bx, h * 0.28 + 0.02, bz, col);
    b.add(0.04, h * 0.55, 0.04,
      bx + Math.cos(ang) * lean, h * 0.83 + 0.02, bz + Math.sin(ang) * lean, col);
  }
  // Fallen berries — 0-2 tiny berry-colour specks on the ground if the
  // bush is at ripe stage, hinting recent foraging activity.
  if (berryStage === 2 && rng() < 0.7) {
    const fb = 1 + Math.floor(rng() * 2);
    const fallenCol = [0xc44048, 0xa01828, 0xd86040][Math.floor(rng() * 3)];
    for (let i = 0; i < fb; i++) {
      const ang = rng() * Math.PI * 2;
      const dist = 0.28 + rng() * 0.14;
      b.add(0.05, 0.04, 0.05, Math.cos(ang) * dist, 0.03, Math.sin(ang) * dist, fallenCol);
    }
  }
  const mesh = b.build();
  if (scale !== 1) mesh.scale.set(scale, scale, scale);
  root.add(mesh);
  return root;
}

/** Tiny half-height bush used while a planted berry is still maturing. */
function makeBushSapling(seed = 1): THREE.Object3D {
  return makeBush(seed, 0, 0.55);
}

/** Short voxel oak sapling — a thin trunk + small leaf canopy. */
function makeTreeSapling(seed = 1): THREE.Object3D {
  const g = new THREE.Group();
  g.userData = { swayFreq: 2.4, swayAmp: 0.04, swayAxis: 'z' };
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 5557 + 19);
  // Trunk: thin 3-segment column.
  const trunkH = 0.42;
  const segs = 3;
  const segH = trunkH / segs;
  for (let i = 0; i < segs; i++) {
    const sy = segH * (i + 0.5) + 0.01;
    const c = i % 2 === 0 ? COL.trunk : COL.trunkDk;
    b.add(0.10, segH * 0.98, 0.10, 0, sy, 0, c);
  }
  // Small canopy: a few leaf voxels above the trunk.
  const LEAVES = [COL.leaf, COL.leafDk, COL.leafLt, 0x6ac038];
  const STEP = 0.10;
  const cy = trunkH + 0.16;
  for (let dx = -0.18; dx <= 0.18 + 1e-3; dx += STEP) {
    for (let dy = -0.08; dy <= 0.16 + 1e-3; dy += STEP) {
      for (let dz = -0.18; dz <= 0.18 + 1e-3; dz += STEP) {
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d > 0.20) continue;
        if (rng() < 0.18) continue;
        b.add(STEP * 1.05, STEP * 1.05, STEP * 1.05, dx, cy + dy, dz, LEAVES[Math.floor(rng() * LEAVES.length)]);
      }
    }
  }
  // A tiny green tip poking out.
  b.add(0.06, 0.08, 0.06, 0, cy + 0.20, 0, COL.leafLt);
  g.add(b.build());
  return g;
}

/**
 * Cluster of mushrooms growing on a stump / log.
 * Stage 1 = a couple of small white-stem buttons sprouting.
 * Stage 2 = a fuller cluster with red-spotted caps + a few smaller browns.
 * `seed` keeps placement deterministic per-tile so re-renders don't shimmer.
 * `onLog` slightly raises the cluster onto the felled trunk surface.
 */
function makeMushrooms(stage: number, seed = 1, onLog = false): THREE.Object3D {
  const g = new THREE.Group();
  if (stage <= 0) return g;
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 11317 + 71);
  const STEM = 0xf0e8c8;
  const STEM_DK = 0xc8c0a0;
  const CAP_RED = 0xc83838;
  const CAP_RED_DK = 0x8a2020;
  const CAP_BRN = 0x9a6a3a;
  const CAP_BRN_DK = 0x6a4420;
  const SPOT = 0xfaf4d8;
  const count = stage >= 2 ? 4 + Math.floor(rng() * 3) : 2 + Math.floor(rng() * 2);
  const baseY = onLog ? 0.22 : 0.42;       // log surface sits lower than stump-top
  // Cluster radius — keep them tucked against the prop's silhouette.
  const radius = onLog ? 0.28 : 0.22;
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + rng() * 0.4;
    const r0 = rng() * radius;
    const mx = Math.cos(ang) * r0;
    const mz = Math.sin(ang) * r0;
    // Stem: 2-voxel column
    const stemH = stage >= 2 ? 0.08 + rng() * 0.04 : 0.05 + rng() * 0.03;
    const stemW = 0.05;
    b.add(stemW, stemH, stemW, mx, baseY + stemH * 0.5, mz, rng() < 0.4 ? STEM_DK : STEM);
    // Cap
    const ripe = stage >= 2;
    const isRed = ripe && rng() < 0.65;
    const capCol = isRed
      ? (rng() < 0.4 ? CAP_RED_DK : CAP_RED)
      : (rng() < 0.4 ? CAP_BRN_DK : CAP_BRN);
    const capW = ripe ? 0.12 : 0.08;
    const capH = ripe ? 0.06 : 0.04;
    const capY = baseY + stemH + capH * 0.5;
    b.add(capW, capH, capW, mx, capY, mz, capCol);
    // Tiny highlight + a couple of pale spots on red caps
    if (ripe) {
      b.add(capW * 0.55, capH * 0.55, capW * 0.55, mx, capY + capH * 0.5, mz, isRed ? 0xff7060 : 0xc4a070);
      if (isRed) {
        for (let s = 0; s < 2; s++) {
          const sang = rng() * Math.PI * 2;
          const sr = capW * 0.35;
          b.add(0.025, 0.02, 0.025, mx + Math.cos(sang) * sr, capY + capH * 0.5 + 0.005, mz + Math.sin(sang) * sr, SPOT);
        }
      }
    }
  }
  g.add(b.build());
  return g;
}

/**
 * Felled trunk lying on its side across a tile. Sits beside the stump.
 * Includes bark texture, a few moss patches, and (if mature) clusters of
 * mushrooms growing along its length.
 */
function makeFallenLog(seed = 1, mushStage = 0): THREE.Object3D {
  const g = new THREE.Group();
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 9277 + 17);
  // Lay the log along +X, offset to one side of the stump so both read clearly.
  const LEN = 0.95;
  const RAD = 0.16;
  const BARK = [COL.trunk, COL.trunkDk, 0x6a4a26, 0x7c5a32, 0x553818];
  const SAPWOOD = [0xb88a52, 0xc8986a, 0xd8ac80];
  // Offset the log so it lies "south-east" of the stump.
  const offsetZ = 0.18;
  // Build the cylinder as voxels along the X axis.
  const STEP = 0.10;
  for (let dx = -LEN / 2; dx <= LEN / 2 + 1e-3; dx += STEP) {
    for (let dy = -RAD; dy <= RAD + 1e-3; dy += STEP) {
      for (let dz = -RAD; dz <= RAD + 1e-3; dz += STEP) {
        const d = Math.sqrt(dy * dy + dz * dz);
        if (d > RAD) continue;
        if (d < RAD - STEP * 1.5) continue;  // shell only
        const col = BARK[Math.floor(rng() * BARK.length)];
        b.add(STEP * 1.08, STEP * 1.08, STEP * 1.08, dx, RAD + dy + 0.04, offsetZ + dz, col);
      }
    }
  }
  // End-cap rings on either side (visible cut surfaces).
  for (const endX of [-LEN / 2 - 0.005, LEN / 2 + 0.005]) {
    for (let dy = -RAD; dy <= RAD + 1e-3; dy += STEP) {
      for (let dz = -RAD; dz <= RAD + 1e-3; dz += STEP) {
        const d = Math.sqrt(dy * dy + dz * dz);
        if (d > RAD - STEP * 0.3) continue;
        const col = SAPWOOD[Math.floor(rng() * SAPWOOD.length)];
        b.add(STEP * 0.4, STEP * 1.08, STEP * 1.08, endX, RAD + dy + 0.04, offsetZ + dz, col);
      }
    }
  }
  // Moss patches on top
  const moss = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < moss; i++) {
    const mx = (rng() - 0.5) * LEN * 0.85;
    const mc = rng() < 0.5 ? 0x86b048 : 0x6a9a40;
    b.add(0.10, 0.04, 0.10, mx, RAD * 2 + 0.05, offsetZ + (rng() - 0.5) * RAD * 0.5, mc);
  }
  g.add(b.build());
  // Mushrooms growing along the log's spine — only when colony is mature
  // enough for visible caps.
  if (mushStage >= 1) {
    const ms = makeMushrooms(mushStage, seed * 3 + 7, true);
    // Walk a couple of clusters along the length so it doesn't look stacked.
    ms.position.set(LEN * 0.18, RAD * 2 - 0.04, offsetZ);
    g.add(ms);
    if (mushStage >= 2) {
      const ms2 = makeMushrooms(mushStage, seed * 5 + 13, true);
      ms2.position.set(-LEN * 0.25, RAD * 2 - 0.04, offsetZ - 0.04);
      g.add(ms2);
    }
  }
  return g;
}

function makeBoulder(seed = 1): THREE.Object3D {
  const root = new THREE.Group();
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 7919 + 11);
  // Bigger ellipsoid + denser voxels (STEP halved) for a chunky reading rock.
  const RX = 0.46, RY = 0.36, RZ = 0.46;
  const STEP = 0.10;
  const MOSS_TOP = 0.45;            // moss favors the top surface
  for (let dx = -RX; dx <= RX + 1e-3; dx += STEP) {
    for (let dy = 0; dy <= RY * 2 + 1e-3; dy += STEP) {
      for (let dz = -RZ; dz <= RZ + 1e-3; dz += STEP) {
        const ny = dy - RY;
        const d2 = (dx / RX) ** 2 + (ny / RY) ** 2 + (dz / RZ) ** 2;
        if (d2 > 1) continue;
        // Drop ~8% for a craggy outline (less than before since STEP is finer).
        if (rng() < 0.08) continue;
        // Surface-shell test — only voxels NEAR the outer hull get drawn so
        // we don't waste polys on the interior.
        if (d2 < 0.55) continue;
        // Palette: dark base, mid-grey hull, light highlights, occasional
        // mineral-vein white speck, occasional moss patch on top half.
        const roll = rng();
        let col: number;
        if (dy > MOSS_TOP * RY * 2 && roll < 0.18)        col = 0x6a9438;   // moss
        else if (roll < 0.05)                              col = 0xd8d8c8;  // mineral vein
        else if (roll < 0.12)                              col = 0x2a2828;  // dark crack
        else if (roll < 0.40)                              col = COL.boulderDk;
        else if (roll < 0.85)                              col = COL.boulder;
        else                                                col = COL.boulderLt;
        b.add(STEP * 1.08, STEP * 1.08, STEP * 1.08, dx, dy + 0.10, dz, col);
      }
    }
  }
  // 2–3 lichen tufts dotted on top — small green clumps for life
  const lichenCount = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < lichenCount; i++) {
    const lx = (rng() - 0.5) * RX * 1.4;
    const lz = (rng() - 0.5) * RZ * 1.4;
    const ly = RY * 2 + 0.06;
    const lcol = rng() < 0.5 ? 0x86a838 : 0x6a8830;
    b.add(0.08, 0.04, 0.08, lx, ly, lz, lcol);
    if (rng() < 0.5) b.add(0.05, 0.04, 0.05, lx + 0.04, ly + 0.02, lz, lcol);
  }
  // Base tufts — grass ring around the boulder so it sinks into the lawn
  // rather than perching on it. Skewed to one side for asymmetric realism.
  const TUFT_GREENS = [0x6fb045, 0x7ad440, 0x5a9038, 0x80a050, 0x4a7a32, 0x9bb854];
  const bc = 7 + Math.floor(rng() * 5);
  const skewAng = rng() * Math.PI * 2;
  for (let i = 0; i < bc; i++) {
    // Bias spawn toward one side of the boulder (the "shady" side).
    const ang = skewAng + (rng() - 0.5) * Math.PI * 1.4;
    const dist = Math.max(RX, RZ) + 0.04 + rng() * 0.12;
    const bx = Math.cos(ang) * dist;
    const bz = Math.sin(ang) * dist;
    const h = 0.08 + rng() * 0.10;
    const lean = 0.05 + rng() * 0.06;
    const col = TUFT_GREENS[Math.floor(rng() * TUFT_GREENS.length)];
    b.add(0.06, h * 0.55, 0.06, bx, h * 0.28 + 0.02, bz, col);
    b.add(0.05, h * 0.55, 0.05,
      bx + Math.cos(ang) * lean, h * 0.83 + 0.02, bz + Math.sin(ang) * lean, col);
  }
  root.add(b.build());
  return root;
}

/**
 * Voxel cloud sculpt. Builds a multi-puff blob from many small cubes:
 *   - 3–6 overlapping ellipsoid "puffs" clustered horizontally
 *   - Wider than tall (puffs flatten the Y radius)
 *   - Per-voxel height shading: top white, mid cream, underside soft blue-gray
 *   - 6–10% random voxel drop for a craggy edge silhouette
 *   - 1–2 tiny detached "wisp" cubes trailing off one side
 *
 * `scale` controls overall size — small clouds (~0.6) are wisps, big ones
 * (~1.4) are full anvils. Material is MeshBasic (unlit) so the cloud
 * keeps a flat painterly look regardless of sun direction.
 */
function makeCloud(seed = 1, scale = 1.0): THREE.Object3D {
  const root = new THREE.Group();
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 27361 + 13);
  const STEP = 0.30;
  // Palette: pure top → cream → soft underside (cool tint reads as
  // self-shadow without needing an actual light source).
  const TOP    = 0xffffff;
  const MID_LT = 0xf6f0e4;
  const MID    = 0xe8e0d4;
  const UND_LT = 0xc8d2e2;
  const UND    = 0xa8b4c8;

  // 3–6 puffs clustered around origin. Puff #0 is centred and largest.
  const puffCount = 3 + Math.floor(rng() * 4);
  const puffs: { cx: number; cy: number; cz: number; rx: number; ry: number; rz: number }[] = [];
  for (let i = 0; i < puffCount; i++) {
    if (i === 0) {
      puffs.push({ cx: 0, cy: 0, cz: 0,
        rx: 1.1 + rng() * 0.6, ry: 0.55 + rng() * 0.3, rz: 1.0 + rng() * 0.5 });
    } else {
      const ang = rng() * Math.PI * 2;
      const dist = 0.55 + rng() * 0.7;
      puffs.push({
        cx: Math.cos(ang) * dist,
        cy: (rng() - 0.5) * 0.30,
        cz: Math.sin(ang) * dist * 0.6,    // squash z so cloud is wider than deep
        rx: 0.55 + rng() * 0.55,
        ry: 0.35 + rng() * 0.22,
        rz: 0.55 + rng() * 0.45,
      });
    }
  }

  // Y bounds across all puffs — used to pick color per voxel height.
  let yMin = Infinity, yMax = -Infinity;
  for (const p of puffs) { yMin = Math.min(yMin, p.cy - p.ry); yMax = Math.max(yMax, p.cy + p.ry); }
  const ySpan = Math.max(0.01, yMax - yMin);

  // Voxelize each puff. Use a Set keyed by integer grid coords to dedupe
  // overlap between puffs so we only emit one cube per cell.
  const placed = new Set<string>();
  const dropRate = 0.06 + rng() * 0.04;
  for (const p of puffs) {
    for (let dx = -p.rx; dx <= p.rx + 1e-3; dx += STEP) {
      for (let dy = -p.ry; dy <= p.ry + 1e-3; dy += STEP) {
        for (let dz = -p.rz; dz <= p.rz + 1e-3; dz += STEP) {
          const d2 = (dx / p.rx) ** 2 + (dy / p.ry) ** 2 + (dz / p.rz) ** 2;
          if (d2 > 1) continue;
          if (rng() < dropRate) continue;
          const x = p.cx + dx, y = p.cy + dy, z = p.cz + dz;
          const k = `${Math.round(x / STEP)}:${Math.round(y / STEP)}:${Math.round(z / STEP)}`;
          if (placed.has(k)) continue;
          placed.add(k);
          // Color by relative height — top of the cloud is brightest.
          const yFrac = (y - yMin) / ySpan;
          let col: number;
          if (yFrac > 0.78)      col = TOP;
          else if (yFrac > 0.55) col = MID_LT;
          else if (yFrac > 0.35) col = MID;
          else if (yFrac > 0.18) col = UND_LT;
          else                    col = UND;
          b.add(STEP * 1.01, STEP * 1.01, STEP * 1.01, x, y, z, col);
        }
      }
    }
  }

  // 1–2 small detached wisp cubes trailing off one side — reads as
  // "cloud edge dispersing" rather than a solid blob.
  const wisps = 1 + Math.floor(rng() * 2);
  for (let i = 0; i < wisps; i++) {
    const ang = rng() * Math.PI * 2;
    const dist = 1.6 + rng() * 0.6;
    const wx = Math.cos(ang) * dist;
    const wz = Math.sin(ang) * dist * 0.6;
    const wy = (rng() - 0.5) * 0.20;
    b.add(STEP * 1.4, STEP * 1.0, STEP * 1.4, wx, wy, wz, MID_LT);
    if (rng() < 0.6) {
      b.add(STEP * 0.9, STEP * 0.8, STEP * 0.9,
        wx + Math.cos(ang) * 0.5, wy + 0.10, wz + Math.sin(ang) * 0.30, TOP);
    }
  }

  const mesh = b.build();
  // Unlit white material with vertex colors — clouds shouldn't darken
  // on the sun's shadow side or pick up the warm grade like ground does.
  mesh.material = new THREE.MeshBasicMaterial({ vertexColors: true, fog: true });
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  if (scale !== 1) mesh.scale.setScalar(scale);
  root.add(mesh);
  return root;
}

/**
 * Per-cargo-kind voxel crate. The crate itself is a small wooden box
 * (~0.16 × 0.14 × 0.16) with metal banding at top + bottom; what changes
 * by kind is the CONTENT poking out the top — golden wheat ears, husked
 * corn cobs, carrots with green tops, flour sacks, mushroom caps, etc.
 * — so the player can identify what's stacked on the truck at a glance
 * instead of just reading colored cubes.
 *
 * Built once per kind (templates cached on Diorama) and cloned per
 * instance so a fully-loaded truck/platform doesn't pay the build cost
 * 30+ times.
 */
type CargoCrateKind =
  | 'wheat' | 'corn' | 'carrot' | 'flour' | 'cornFlour' | 'wood'
  | 'mushroom' | 'berry' | 'acorn' | 'stone';
function makeCrate(kind: CargoCrateKind, seed = 1): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 271 + 41);
  const WOOD     = 0x8a5e2c;
  const WOOD_DK  = 0x5a3818;
  const WOOD_LT  = 0xa07440;
  const METAL    = 0x707070;
  const METAL_DK = 0x484848;
  const W = 0.16, H = 0.14, D = 0.16;

  // ── Crate body — open-top wooden box with planks + metal banding.
  // Bottom slab
  b.add(W, 0.025, D, 0, 0.012, 0, WOOD_DK);
  // 4 side walls (slight palette variance between facing pairs reads as
  // sun/shadow without needing real lighting tricks)
  b.add(W, H, 0.022, 0, 0.012 + H / 2, -D / 2 + 0.011, WOOD);
  b.add(W, H, 0.022, 0, 0.012 + H / 2, +D / 2 - 0.011, WOOD_DK);
  b.add(0.022, H, D, -W / 2 + 0.011, 0.012 + H / 2, 0, WOOD);
  b.add(0.022, H, D, +W / 2 - 0.011, 0.012 + H / 2, 0, WOOD_DK);
  // Horizontal plank seams — small dark stripes around the perimeter at
  // ~1/3 and ~2/3 height. Gives the crate visible "boards" instead of
  // a single uniform slab.
  for (let p = 1; p <= 2; p++) {
    const py = 0.012 + (p / 3) * H;
    b.add(W + 0.005, 0.005, 0.024, 0, py, -D / 2 + 0.005, WOOD_DK);
    b.add(W + 0.005, 0.005, 0.024, 0, py, +D / 2 - 0.005, WOOD_DK);
    b.add(0.024, 0.005, D + 0.005, -W / 2 + 0.005, py, 0, WOOD_DK);
    b.add(0.024, 0.005, D + 0.005, +W / 2 - 0.005, py, 0, WOOD_DK);
  }
  // Metal banding — top + bottom rims of the box for that "shipping
  // crate" silhouette.
  const bandT = 0.012 + H + 0.006;
  const bandB = 0.022;
  b.add(W + 0.014, 0.012, 0.028, 0, bandT, -D / 2, METAL);
  b.add(W + 0.014, 0.012, 0.028, 0, bandT, +D / 2, METAL);
  b.add(0.028, 0.012, D + 0.014, -W / 2, bandT, 0, METAL);
  b.add(0.028, 0.012, D + 0.014, +W / 2, bandT, 0, METAL);
  b.add(W + 0.014, 0.012, 0.028, 0, bandB, -D / 2, METAL_DK);
  b.add(W + 0.014, 0.012, 0.028, 0, bandB, +D / 2, METAL_DK);
  b.add(0.028, 0.012, D + 0.014, -W / 2, bandB, 0, METAL_DK);
  b.add(0.028, 0.012, D + 0.014, +W / 2, bandB, 0, METAL_DK);
  // Corner nail dots — tiny darker specks at the 4 vertical corners
  for (const cx of [-W / 2, W / 2]) for (const cz of [-D / 2, D / 2]) {
    b.add(0.018, 0.012, 0.018, cx, bandT, cz, 0x202020);
    b.add(0.018, 0.012, 0.018, cx, bandB, cz, 0x202020);
  }

  // ── Per-kind content sticking out the top of the crate ──
  const topY = 0.012 + H + 0.010;
  switch (kind) {
    case 'wheat': {
      // Bundle of golden wheat ears tied at the base
      for (let i = 0; i < 6; i++) {
        const ang = rng() * Math.PI * 2;
        const r = rng() * 0.045;
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        const sh = 0.05 + rng() * 0.04;
        const lean = (rng() - 0.5) * 0.02;
        // Stem
        b.add(0.018, sh, 0.018, x, topY + sh / 2 + 0.006, z, 0xc8a440);
        // Grain head
        b.add(0.032, 0.026, 0.032, x + lean, topY + sh + 0.020, z, 0xfae89a);
        b.add(0.022, 0.018, 0.022, x + lean, topY + sh + 0.034, z, 0xfff080);
      }
      // Twine wrap at the base of the bundle
      b.add(0.10, 0.012, 0.10, 0, topY + 0.012, 0, 0x6a4828);
      break;
    }
    case 'corn': {
      // 2 husked corn cobs lying across the crate
      for (let i = 0; i < 2; i++) {
        const x = (i - 0.5) * 0.06;
        // Cob — yellow kernels
        b.add(0.05, 0.045, 0.13, x, topY + 0.025, 0, 0xf4d258);
        b.add(0.05, 0.014, 0.13, x, topY + 0.046, 0, 0xfae89a);    // sun-side highlight
        // Kernel pattern — small darker speckles
        for (let k = 0; k < 4; k++) {
          const kz = (k - 1.5) * 0.030;
          b.add(0.010, 0.012, 0.010, x + 0.020, topY + 0.030, kz, 0xc89020);
        }
        // Husk peeking out the back
        b.add(0.05, 0.014, 0.06, x, topY + 0.025, +0.085, 0x80a050);
        b.add(0.04, 0.010, 0.04, x, topY + 0.030, +0.10, 0xa8c060);
      }
      break;
    }
    case 'carrot': {
      // 3 carrots poking up with green tops
      for (let i = 0; i < 3; i++) {
        const ang = i * Math.PI * 2 / 3 + rng() * 0.3;
        const x = Math.cos(ang) * 0.04;
        const z = Math.sin(ang) * 0.04;
        // Taper — fat near top, thin at root
        b.add(0.032, 0.022, 0.032, x, topY + 0.012, z, 0xff9040);
        b.add(0.026, 0.022, 0.026, x, topY + 0.034, z, 0xe87830);
        b.add(0.020, 0.014, 0.020, x, topY + 0.052, z, 0xc85820);
        // Green tops
        b.add(0.035, 0.025, 0.035, x, topY + 0.075, z, 0x60a040);
        b.add(0.020, 0.020, 0.020, x + 0.012, topY + 0.090, z, 0x80c050);
        b.add(0.020, 0.020, 0.020, x - 0.012, topY + 0.090, z, 0x4a8030);
      }
      break;
    }
    case 'flour': {
      // White paper sack tied at the top with brown twine — fills the crate
      const SAK = 0xf4ecd8;
      const SAK_DK = 0xc8b890;
      b.add(W * 0.95, 0.10, D * 0.95, 0, topY + 0.050, 0, SAK);
      b.add(W * 0.96, 0.020, D * 0.96, 0, topY + 0.012, 0, SAK_DK); // base shadow
      // Pinched top + twine knot
      b.add(0.06, 0.030, 0.06, 0, topY + 0.108, 0, SAK);
      b.add(0.04, 0.014, 0.04, 0, topY + 0.124, 0, 0x6a4828);          // twine
      // Stamped 'F' shape (just two small dark voxels suggesting a label)
      b.add(0.020, 0.014, 0.005, -0.020, topY + 0.060, -D / 2 + 0.014, 0x6a4828);
      b.add(0.014, 0.005, 0.005,  0.000, topY + 0.060, -D / 2 + 0.014, 0x6a4828);
      break;
    }
    case 'cornFlour': {
      // Same sack shape, yellow-tinted to differentiate from wheat flour
      const SAK = 0xf8d878;
      const SAK_DK = 0xc8a440;
      b.add(W * 0.95, 0.10, D * 0.95, 0, topY + 0.050, 0, SAK);
      b.add(W * 0.96, 0.020, D * 0.96, 0, topY + 0.012, 0, SAK_DK);
      b.add(0.06, 0.030, 0.06, 0, topY + 0.108, 0, SAK);
      b.add(0.04, 0.014, 0.04, 0, topY + 0.124, 0, 0x6a4828);
      break;
    }
    case 'wood': {
      // 4 logs stacked log-pile style (cut ends facing out) — no produce
      // content, the logs ARE the cargo.
      const LOG = 0x9c6c44;
      const LOG_DK = 0x6a4828;
      const LOG_END = 0xc89058;
      // Bottom row: 2 logs
      for (let i = 0; i < 2; i++) {
        const x = (i - 0.5) * 0.06;
        b.add(0.05, 0.05, D, x, topY + 0.026, 0, LOG);
        b.add(0.05, 0.014, D, x, topY + 0.045, 0, LOG_DK);
        // End-grain rings
        b.add(0.052, 0.052, 0.012, x, topY + 0.026, -D / 2 + 0.005, LOG_END);
        b.add(0.052, 0.052, 0.012, x, topY + 0.026, +D / 2 - 0.005, LOG_END);
        b.add(0.018, 0.018, 0.012, x, topY + 0.026, -D / 2 + 0.012, LOG_DK);
        b.add(0.018, 0.018, 0.012, x, topY + 0.026, +D / 2 - 0.012, LOG_DK);
      }
      // Top row: 1 log nestled between them
      b.add(0.05, 0.05, D, 0, topY + 0.072, 0, LOG);
      b.add(0.05, 0.014, D, 0, topY + 0.091, 0, LOG_DK);
      b.add(0.052, 0.052, 0.012, 0, topY + 0.072, -D / 2 + 0.005, LOG_END);
      b.add(0.052, 0.052, 0.012, 0, topY + 0.072, +D / 2 - 0.005, LOG_END);
      b.add(0.018, 0.018, 0.012, 0, topY + 0.072, -D / 2 + 0.012, LOG_DK);
      b.add(0.018, 0.018, 0.012, 0, topY + 0.072, +D / 2 - 0.012, LOG_DK);
      break;
    }
    case 'stone': {
      // Rough gray rubble — irregular chunks sticking out
      const STONE = [0x808080, 0x707070, 0x909090, 0x606060];
      for (let i = 0; i < 6; i++) {
        const ang = rng() * Math.PI * 2;
        const r = rng() * 0.05;
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        const sz = 0.030 + rng() * 0.022;
        const sh = 0.025 + rng() * 0.018;
        b.add(sz, sh, sz, x, topY + sh / 2 + 0.008, z, STONE[Math.floor(rng() * STONE.length)]);
      }
      break;
    }
    case 'mushroom': {
      // Cluster of red-cap mushrooms with white spots
      for (let i = 0; i < 3; i++) {
        const ang = i * Math.PI * 2 / 3 + rng() * 0.4;
        const r = 0.035 + rng() * 0.012;
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        // Stem
        b.add(0.022, 0.028, 0.022, x, topY + 0.020, z, 0xf4ecd0);
        // Cap
        b.add(0.052, 0.024, 0.052, x, topY + 0.046, z, 0xc83838);
        b.add(0.038, 0.012, 0.038, x, topY + 0.060, z, 0xe04848);
        // White spots
        b.add(0.012, 0.010, 0.012, x + 0.014, topY + 0.054, z, 0xffffff);
        b.add(0.010, 0.008, 0.010, x - 0.012, topY + 0.054, z, 0xffffff);
      }
      break;
    }
    case 'berry': {
      // Cluster of dark-blue/red berries piled in the crate
      const BERRIES = [0x4060a0, 0x506fb0, 0x6a4078, 0x804080];
      for (let i = 0; i < 8; i++) {
        const ang = rng() * Math.PI * 2;
        const r = rng() * 0.055;
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        const sy = 0.012 + rng() * 0.020;
        b.add(0.028, 0.026, 0.028, x, topY + sy, z, BERRIES[Math.floor(rng() * BERRIES.length)]);
        // Highlight dot
        if (rng() < 0.5) b.add(0.008, 0.006, 0.008, x + 0.008, topY + sy + 0.012, z, 0xa898ff);
      }
      // Green leaf accent peeking out
      b.add(0.030, 0.012, 0.018, 0.04, topY + 0.035, -0.04, 0x60a040);
      break;
    }
    case 'acorn': {
      // 4 acorns nestled together
      for (let i = 0; i < 5; i++) {
        const ang = i * Math.PI * 2 / 5 + rng() * 0.3;
        const r = rng() * 0.04;
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        // Nut body
        b.add(0.030, 0.032, 0.030, x, topY + 0.020, z, 0xd8a070);
        b.add(0.024, 0.012, 0.024, x, topY + 0.030, z, 0xc89055);
        // Cap
        b.add(0.034, 0.014, 0.034, x, topY + 0.044, z, 0x6a4220);
        b.add(0.020, 0.006, 0.020, x, topY + 0.054, z, 0x4a2810);
        // Tiny stem
        if (rng() < 0.5) b.add(0.008, 0.014, 0.008, x, topY + 0.064, z, 0x4a2810);
      }
      break;
    }
  }
  const mesh = b.build();
  mesh.castShadow = true;
  return mesh;
}

function makeTallGrass(): THREE.Object3D {
  const g = new THREE.Group();
  g.userData = { swayFreq: 2.6, swayAmp: 0.07, swayAxis: 'z' };
  const b = new VoxelBuilder();
  const rng = rngFn(4099 + 17);
  // Mixed green palette so the clump isn't a single flat tone.
  const GREENS = [COL.tallGrass, COL.bushLt, 0x6aa840, 0x86c054, 0xa8d068, 0x4e8a30];
  // 5-6 thin blades fanning outward in a small cluster, each leaning so it
  // arcs out from the base. We model the lean as a thin column whose top
  // voxel is offset slightly more than the bottom.
  const bladeCount = 5 + Math.floor(rng() * 2);
  for (let i = 0; i < bladeCount; i++) {
    const ang = (i / bladeCount) * Math.PI * 2 + rng() * 0.4;
    const baseR = 0.04 + rng() * 0.06;
    const bx = Math.cos(ang) * baseR;
    const bz = Math.sin(ang) * baseR;
    const lean = 0.08 + rng() * 0.10;
    const h = 0.36 + rng() * 0.26;
    // Color picks: most pieces share a colour, with a darker base voxel
    const baseCol = GREENS[Math.floor(rng() * GREENS.length)];
    const tipCol  = rng() < 0.4 ? GREENS[Math.floor(rng() * 2) + 3] : baseCol;
    // 3 stacked sub-voxels per blade so it arches outward
    const segH = h / 3;
    for (let s = 0; s < 3; s++) {
      const t = s / 2;     // 0 at root, 1 at tip
      const sx = bx + Math.cos(ang) * lean * t * t;
      const sz = bz + Math.sin(ang) * lean * t * t;
      const sy = segH * (s + 0.5);
      const c = s === 2 ? tipCol : (s === 0 ? COL.bushDk : baseCol);
      b.add(0.05, segH * 0.98, 0.05, sx, sy, sz, c);
    }
  }
  // 2-3 short bottom tufts at the base — broader voxels for visual weight
  const tufts = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < tufts; i++) {
    const ang = rng() * Math.PI * 2;
    const r0 = rng() * 0.10;
    const tx = Math.cos(ang) * r0;
    const tz = Math.sin(ang) * r0;
    b.add(0.08, 0.08, 0.08, tx, 0.04, tz, COL.bushDk);
  }
  // A few tiny seed-head voxels at the tip of select blades
  for (let i = 0; i < 2; i++) {
    const ang = rng() * Math.PI * 2;
    const r0 = 0.10 + rng() * 0.10;
    const sx = Math.cos(ang) * r0;
    const sz = Math.sin(ang) * r0;
    const sy = 0.42 + rng() * 0.18;
    b.add(0.03, 0.04, 0.03, sx, sy, sz, 0xd8c870);
  }
  g.add(b.build());
  return g;
}

function makeFlower(kind: 'daisy' | 'poppy'): THREE.Object3D {
  const g = new THREE.Group();
  g.userData = { swayFreq: 2.2, swayAmp: 0.045, swayAxis: 'z' };
  const b = new VoxelBuilder();
  const rng = rngFn(kind === 'daisy' ? 8101 : 8237);
  const STEM_DK = 0x3a6020;
  const STEM_LT = COL.bushLt;
  const LEAF_LT = 0x86c050;
  const LEAF_DK = 0x4a8830;
  // Stem — segmented so it shows colour variation along its length
  const stemH = 0.26;
  const segs = 4;
  const segH = stemH / segs;
  for (let s = 0; s < segs; s++) {
    const sy = segH * (s + 0.5) + 0.01;
    const c = s % 2 === 0 ? STEM_DK : STEM_LT;
    // Slight side-to-side jitter so the stem isn't perfectly straight
    const sx = (s - segs / 2) * 0.004;
    b.add(0.04, segH * 0.98, 0.04, sx, sy, 0, c);
  }
  // 1-2 small leaves on the stem at mid-height
  const leafCount = 1 + Math.floor(rng() * 2);
  for (let i = 0; i < leafCount; i++) {
    const side = i % 2 === 0 ? 1 : -1;
    const ly = 0.08 + rng() * 0.08;
    const lc = rng() < 0.5 ? LEAF_LT : LEAF_DK;
    b.add(0.10, 0.04, 0.05, side * 0.06, ly, 0, lc);
    // Tiny veined dot for detail
    b.add(0.03, 0.03, 0.03, side * 0.09, ly + 0.02, 0, LEAF_DK);
  }
  // A few base-leaf voxels at ground level
  for (let i = 0; i < 3; i++) {
    const ang = (i / 3) * Math.PI * 2 + rng() * 0.5;
    const r0 = 0.07 + rng() * 0.04;
    const bx = Math.cos(ang) * r0;
    const bz = Math.sin(ang) * r0;
    b.add(0.06, 0.04, 0.06, bx, 0.02, bz, LEAF_DK);
  }

  const headY = stemH + 0.04;
  if (kind === 'daisy') {
    // Yellow centre disk — slightly raised
    b.add(0.08, 0.05, 0.08, 0, headY, 0, COL.flowerY);
    b.add(0.04, 0.03, 0.04, 0, headY + 0.03, 0, 0xfff0a0);     // bright highlight
    // White petals around the centre — 8 cardinal + diagonal pieces
    const petalCol = 0xf8f4e8;
    const petalShadow = 0xc8c4b0;
    const petals: [number, number][] = [
      [0.10, 0], [-0.10, 0], [0, 0.10], [0, -0.10],
      [0.08, 0.08], [-0.08, 0.08], [0.08, -0.08], [-0.08, -0.08],
    ];
    for (let i = 0; i < petals.length; i++) {
      const [px, pz] = petals[i];
      // Petals near the back read slightly darker (light from front)
      const c = pz > 0 ? petalShadow : petalCol;
      b.add(0.06, 0.03, 0.06, px, headY - 0.005, pz, c);
    }
    // Tiny stamen dots on the disk
    for (let i = 0; i < 3; i++) {
      const ang = rng() * Math.PI * 2;
      const dx = Math.cos(ang) * 0.025;
      const dz = Math.sin(ang) * 0.025;
      b.add(0.02, 0.02, 0.02, dx, headY + 0.035, dz, 0xe0a040);
    }
  } else {
    // Poppy — bright red petal cluster + dark centre
    const RED_LT = 0xff6060;
    const RED_MD = COL.flowerP;
    const RED_DK = 0xc01828;
    const BLACK_CENTRE = 0x1a1010;
    // Petal cluster — 4 outer petals around a central voxel, dome shape
    b.add(0.16, 0.06, 0.16, 0, headY, 0, RED_MD);
    // Slightly upturned petal rim
    const petals: [number, number, number][] = [
      [ 0.10, 0,    0.02],
      [-0.10, 0,    0.02],
      [ 0,    0.10, 0.02],
      [ 0,   -0.10, 0.02],
      [ 0.07, 0.07, 0.03],
      [-0.07, 0.07, 0.03],
      [ 0.07,-0.07, 0.03],
      [-0.07,-0.07, 0.03],
    ];
    for (const [px, pz, lift] of petals) {
      const c = (rng() < 0.4) ? RED_DK : (rng() < 0.5 ? RED_LT : RED_MD);
      b.add(0.06, 0.04, 0.06, px, headY + lift, pz, c);
    }
    // Top highlight
    b.add(0.08, 0.03, 0.08, 0, headY + 0.05, 0, RED_LT);
    // Dark centre poking through
    b.add(0.06, 0.05, 0.06, 0, headY + 0.06, 0, BLACK_CENTRE);
    // Tiny pale stamens on the dark centre
    for (let i = 0; i < 4; i++) {
      const ang = (i / 4) * Math.PI * 2;
      const dx = Math.cos(ang) * 0.025;
      const dz = Math.sin(ang) * 0.025;
      b.add(0.02, 0.02, 0.02, dx, headY + 0.08, dz, 0xe0d0a0);
    }
  }
  g.add(b.build());
  return g;
}

/**
 * Dense voxel wheat field — ~25-50 thin stalks per tile arranged in a 5×5
 * grid with random jitter. Heights and colors come from the growth stage.
 */
function makeWheat(stage: number, seed = 1): THREE.Object3D {
  const b = new VoxelBuilder();
  if (stage === 0) {
    const rng = rngFn(seed * 17 + 3);
    for (let i = 0; i < 8; i++) {
      const x = (rng() - 0.5) * 0.85;
      const z = (rng() - 0.5) * 0.85;
      b.add(0.10, 0.10, 0.10, x, 0.05, z, COL.wheatGreenLt);
    }
    return b.build();
  }
  const heights = [0, 0.30, 0.55, 0.85, 1.10];
  const baseH = heights[stage];
  const stalkCol = stage >= 3 ? COL.wheatGoldDk : COL.wheatGreen;
  const tipCol  = stage === 4 ? COL.wheatGoldLt : stage === 3 ? COL.wheatGold : COL.wheatGreenLt;
  const rng = rngFn(seed * 79 + stage * 13);
  // 5×5 grid of stalks, each with small jitter
  for (let gx = -2; gx <= 2; gx++) {
    for (let gz = -2; gz <= 2; gz++) {
      // Drop ~15% randomly so the field looks organic
      if (rng() < 0.15) continue;
      const px = gx * 0.18 + (rng() - 0.5) * 0.06;
      const pz = gz * 0.18 + (rng() - 0.5) * 0.06;
      const h = baseH * (0.85 + rng() * 0.30);   // ±15% height variation
      b.add(0.10, h, 0.10, px, h * 0.5, pz, stalkCol);
      if (stage >= 3) {
        const headH = stage === 4 ? 0.20 : 0.14;
        b.add(0.16, headH, 0.16, px, h + headH * 0.5, pz, tipCol);
        if (stage === 4 && rng() < 0.5) {
          // bright kernel
          b.add(0.08, 0.10, 0.08, px + (rng() - 0.5) * 0.08, h + headH + 0.04, pz, COL.wheatGoldLt);
        }
      } else if (stage === 2 && rng() < 0.4) {
        // tiny leaf flicker
        b.add(0.14, 0.06, 0.04, px + 0.08, h * 0.6, pz, COL.wheatGreenLt);
      }
    }
  }
  return b.build();
}

function makeCorn(stage: number, seed = 1): THREE.Object3D {
  // Biology-modeled 5-stage corn: germination/seedling/vegetative/silking/mature.
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 53 + stage * 11);
  const SOIL       = 0x7a5828;
  const KERNEL_BUR = 0xd8b070;
  const COLEO_TIP  = 0xc8e09a;
  const STALK_GR   = COL.cornStalk;
  const STALK_LT   = COL.cornStalkLt;
  const STALK_DK   = 0x3e6a24;
  const STALK_DRY  = 0xc8b860;
  const STALK_DRYDK= 0x9a8838;
  const NODE       = 0x4a6a18;
  const LEAF_LT    = 0x9bd060;
  const LEAF_MD    = 0x70b048;
  const LEAF_DK    = 0x4a8830;
  const LEAF_DRY   = 0xc0b860;
  const HUSK_GR    = COL.cornHusk;
  const HUSK_DRY   = 0xc8a050;
  const HUSK_DRYDK = 0x9a7838;
  const COB        = COL.cornCob;
  const COB_LT     = COL.cornCobLt;
  const KERNEL_RIPE= 0xfae068;
  const KERNEL_HI  = 0xfff0a8;
  const SILK_GR    = 0xe0d8a0;
  const SILK_DK    = 0x9a8848;
  const TASSEL_LT  = 0xe8d860;
  const TASSEL_MD  = 0xbcb050;

  if (stage === 0) {
    // Germination — 4 soil mounds with buried kernel + pale coleoptile tip
    const spots = 4;
    for (let i = 0; i < spots; i++) {
      const cx = (rng() - 0.5) * 0.75;
      const cz = (rng() - 0.5) * 0.75;
      b.add(0.20, 0.05, 0.18, cx, 0.025, cz, SOIL);
      b.add(0.10, 0.04, 0.10, cx + 0.04, 0.05, cz - 0.02, 0x6a4a20);
      b.add(0.07, 0.05, 0.07, cx, 0.05, cz, KERNEL_BUR);
      const tipH = 0.05 + rng() * 0.04;
      b.add(0.04, tipH, 0.04, cx + (rng() - 0.5) * 0.02, 0.06 + tipH / 2, cz, COLEO_TIP);
      b.add(0.03, 0.02, 0.03, cx, 0.06, cz + 0.02, STALK_GR);
    }
    return b.build();
  }
  // Sparse stalk slots — corn is tall, fewer per tile than wheat
  const slots: [number, number][] = [
    [-0.30, -0.28], [0.00, -0.30], [0.30, -0.26],
    [-0.28,  0.04], [0.04,  0.06], [0.30,  0.02],
    [-0.22,  0.30], [0.10,  0.32],
  ];
  const heights = [0, 0.45, 0.85, 1.15, 1.45];
  const baseH = heights[stage];

  for (let si = 0; si < slots.length; si++) {
    if (rng() < 0.12) continue;
    const [bx, bz] = slots[si];
    const px = bx + (rng() - 0.5) * 0.06;
    const pz = bz + (rng() - 0.5) * 0.06;
    const h = baseH * (0.88 + rng() * 0.22);

    if (stage === 1) {
      // V2 seedling — short central shoot + 2-3 long narrow arching blades
      const shootH = h;
      b.add(0.07, shootH, 0.07, px, shootH / 2 + 0.012, pz, STALK_LT);
      const blades = 2 + Math.floor(rng() * 2);
      for (let k = 0; k < blades; k++) {
        const ang = (k / blades) * Math.PI * 2 + rng() * 0.6;
        const arc = 0.10 + rng() * 0.06;
        const blH = shootH * (0.85 + rng() * 0.25);
        const segH = blH / 3;
        for (let s = 0; s < 3; s++) {
          const t = s / 2;
          const sx = px + Math.cos(ang) * arc * t * t;
          const sz = pz + Math.sin(ang) * arc * t * t;
          const sy = segH * (s + 0.5) + 0.02;
          const c = (k + s) % 2 === 0 ? LEAF_MD : LEAF_LT;
          b.add(0.05, segH * 0.98, 0.05, sx, sy, sz, c);
        }
      }
      b.add(0.05, 0.04, 0.05, px, shootH + 0.04, pz, LEAF_LT);
      continue;
    }

    // Stages 2-4 — tall stalk with node bands + alternating broad leaves + ear/tassel
    const isDry = stage === 4;
    const stalkCol   = isDry ? STALK_DRY   : STALK_GR;
    const stalkColDk = isDry ? STALK_DRYDK : STALK_DK;
    const husk       = isDry ? HUSK_DRY    : HUSK_GR;
    const huskDk     = isDry ? HUSK_DRYDK  : 0x7a8838;
    const leafA      = isDry ? LEAF_DRY    : LEAF_MD;
    const leafB      = isDry ? STALK_DRYDK : LEAF_DK;
    const leafC      = isDry ? LEAF_DRY    : LEAF_LT;

    // 6 internodes — slight droop at top for stage 4 (ripe corn leans)
    const internodes = 6;
    const segH = h / internodes;
    for (let j = 0; j < internodes; j++) {
      const droopX = isDry ? Math.sin(j / internodes * Math.PI) * 0.02 : 0;
      const segY = segH * (j + 0.5) + 0.01;
      const segCol = j % 2 === 0 ? stalkCol : stalkColDk;
      b.add(0.10, segH * 0.96, 0.10, px + droopX, segY, pz, segCol);
      // Node band — slightly darker, slightly fatter
      b.add(0.12, 0.025, 0.12, px + droopX, segH * (j + 1), pz, NODE);
    }

    // Alternating broad leaves wrapping out from each node
    const leafLevels = stage === 2 ? 5 : 6;
    for (let k = 0; k < leafLevels; k++) {
      const lvl = (k + 1) / (leafLevels + 1);
      const ly = h * lvl;
      const side = k % 2 === 0 ? 1 : -1;
      const ang  = (k * 1.7) % (Math.PI * 2);
      const dx = Math.cos(ang) * 0.04;
      const dz = Math.sin(ang) * 0.04;
      b.add(0.10, 0.05, 0.06, px + side * 0.06 + dx, ly, pz + dz, leafA);
      b.add(0.14, 0.04, 0.08, px + side * 0.14 + dx, ly - 0.01, pz + dz * 1.4, leafB);
      b.add(0.16, 0.04, 0.08, px + side * 0.22 + dx * 1.5, ly - 0.05, pz + dz * 1.8, leafC);
      if (rng() < 0.4) {
        b.add(0.06, 0.03, 0.04, px + side * 0.28, ly - 0.08, pz + dz * 2.0, leafC);
      }
    }

    if (stage === 2) {
      // V8 — upright pale-green whorl at the top of the stalk
      b.add(0.08, 0.10, 0.08, px, h + 0.05, pz, LEAF_LT);
      b.add(0.05, 0.06, 0.05, px + 0.02, h + 0.12, pz, LEAF_LT);
    } else {
      // Stage 3 or 4 — tassel fronds + visible ear
      const tasselBase = h;
      const tasselCol1 = isDry ? TASSEL_MD     : TASSEL_LT;
      const tasselCol2 = isDry ? STALK_DRYDK   : TASSEL_MD;
      b.add(0.04, 0.20, 0.04, px, tasselBase + 0.10, pz, tasselCol1);
      const fronds = 5;
      for (let f = 0; f < fronds; f++) {
        const fang = (f / fronds) * Math.PI * 2;
        const fx = Math.cos(fang) * 0.08;
        const fz = Math.sin(fang) * 0.08;
        const fh = 0.12 + rng() * 0.06;
        b.add(0.03, fh, 0.03, px + fx, tasselBase + fh / 2 + 0.04, pz + fz, f % 2 === 0 ? tasselCol1 : tasselCol2);
        if (rng() < 0.6) {
          b.add(0.02, 0.02, 0.02, px + fx * 1.3, tasselBase + fh - 0.02, pz + fz * 1.3, tasselCol1);
        }
      }

      // Ear poking out on the alternating side
      const earSide = si % 2 === 0 ? 1 : -1;
      const earX = px + earSide * 0.10;
      const earZ = pz + 0.10;
      const earY = h * (isDry ? 0.45 : 0.55);
      b.add(0.14, 0.26, 0.10, earX, earY, earZ, husk);
      b.add(0.12, 0.22, 0.10, earX, earY, earZ + 0.02, huskDk);
      // Vertical husk seams (papery lines)
      for (let s = 0; s < 3; s++) {
        const sx = earX - 0.05 + s * 0.05;
        b.add(0.015, 0.22, 0.02, sx, earY, earZ + 0.06, isDry ? STALK_DRYDK : LEAF_DK);
      }

      if (stage === 3) {
        // Silking — bright green-yellow silk hanging from the husk top
        b.add(0.10, 0.05, 0.06, earX, earY + 0.14, earZ + 0.02, SILK_GR);
        for (let s = 0; s < 4; s++) {
          const sx = earX - 0.05 + s * 0.035;
          const sh = 0.06 + rng() * 0.05;
          b.add(0.015, sh, 0.015, sx, earY + 0.14 + sh / 2, earZ + 0.05, s % 2 === 0 ? SILK_GR : SILK_DK);
        }
      } else {
        // Mature — kernels visible at the open top of the ear
        b.add(0.10, 0.05, 0.08, earX, earY + 0.13, earZ + 0.04, COB);
        for (let row = 0; row < 2; row++) {
          for (let col = 0; col < 3; col++) {
            const kx = earX - 0.045 + col * 0.045;
            const ky = earY + 0.10 - row * 0.04;
            const kCol = (col + row) % 2 === 0 ? KERNEL_RIPE : COB_LT;
            b.add(0.04, 0.03, 0.04, kx, ky, earZ + 0.07, kCol);
          }
        }
        b.add(0.03, 0.025, 0.03, earX, earY + 0.06, earZ + 0.08, KERNEL_HI);
        // Brown drying silk wisps still poking out the top
        for (let s = 0; s < 3; s++) {
          const sx = earX - 0.04 + s * 0.04;
          b.add(0.015, 0.05, 0.015, sx, earY + 0.18, earZ + 0.06, SILK_DK);
        }
      }
    }
  }
  return b.build();
}

/**
 * Detailed voxel carrot at growth stages 0-4. Modeled after the biology:
 *   0 = germination — soil mound, buried seed, tiny green sprout poking
 *   1 = cotyledon — 2-3 small feathery leaves
 *   2 = leaf development — lush bushy ferny foliage spreading out
 *   3 = root development — taller fern foliage + hint of orange at soil line
 *   4 = mature — ferny foliage + clearly visible orange crown, ridged + paler outer leaves
 */
function makeCarrot(stage: number, seed = 1): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(seed * 41 + stage * 7);
  // Palette — bright sap-green ferns + earthy + carrot-orange ridges
  const SOIL       = 0x7a5828;
  const SOIL_DK    = 0x4a3010;
  const SEED       = 0x8a6028;
  const SPROUT     = 0xc8e08a;
  const LEAF_DK    = 0x3a7028;
  const LEAF_MD    = COL.carrotLeaf;
  const LEAF_LT    = COL.carrotLeafLt;
  const LEAF_BLEACH= 0xa8c468;       // sun-bleached outer leaf
  const STEM       = 0x4a8028;
  const ROOT       = COL.carrotRoot;
  const ROOT_LT    = COL.carrotRootLt;
  const ROOT_DK    = 0xb04818;
  const ROOT_HI    = 0xff9c4a;
  const ROOT_RIDGE = 0xc85018;

  if (stage === 0) {
    // Germination — 4 soil mounds with buried seed + tiny sprout
    for (let i = 0; i < 4; i++) {
      const cx = (rng() - 0.5) * 0.7;
      const cz = (rng() - 0.5) * 0.7;
      // Soil mound
      b.add(0.18, 0.05, 0.18, cx, 0.025, cz, SOIL);
      b.add(0.10, 0.03, 0.10, cx + 0.04, 0.04, cz - 0.02, SOIL_DK);
      // Buried seed — small dark dot
      b.add(0.05, 0.04, 0.05, cx, 0.05, cz, SEED);
      // Tiny green sprout poking up
      const sH = 0.04 + rng() * 0.03;
      b.add(0.03, sH, 0.03, cx, 0.05 + sH / 2, cz, SPROUT);
      if (rng() < 0.5) b.add(0.02, 0.02, 0.02, cx + 0.02, 0.07, cz, LEAF_LT);
    }
    return b.build();
  }

  // Carrot tufts in 2×2 grid — denser foliage at higher stages
  const positions: [number, number][] = [
    [-0.22, -0.22], [0.22, -0.22], [-0.22, 0.22], [0.22, 0.22],
  ];

  for (const [px, pz] of positions) {
    if (stage === 1) {
      // Cotyledon — 2-3 small simple paired leaves on a short stem
      const stemH = 0.06;
      b.add(0.025, stemH, 0.025, px, stemH / 2 + 0.005, pz, STEM);
      const pairs = 1 + (rng() < 0.5 ? 1 : 0);
      for (let i = 0; i < pairs; i++) {
        const yLevel = stemH * (0.5 + i * 0.3) + 0.015;
        // Pair of small leaves, opposite sides
        b.add(0.06, 0.025, 0.04, px - 0.04, yLevel, pz, LEAF_LT);
        b.add(0.06, 0.025, 0.04, px + 0.04, yLevel, pz, LEAF_LT);
        // Center vein
        b.add(0.02, 0.02, 0.06, px, yLevel, pz, LEAF_MD);
      }
      // Pale tip
      b.add(0.025, 0.025, 0.025, px, stemH + 0.03, pz, SPROUT);
      continue;
    }

    // Stages 2-4: ferny bushy foliage modeled as many tiny voxels in tufts of
    // 3 fronds, each frond has a thin central stem with leaflets.
    const heights = [0, 0, 0.22, 0.32, 0.36];
    const h = heights[stage] * (0.92 + rng() * 0.18);
    const fronds = stage === 2 ? 5 : stage === 3 ? 6 : 7;
    // Short central stalk base
    b.add(0.05, 0.06, 0.05, px, 0.03, pz, STEM);

    for (let f = 0; f < fronds; f++) {
      const fang = (f / fronds) * Math.PI * 2 + rng() * 0.4;
      const lean = 0.10 + rng() * 0.08;
      const frondH = h * (0.85 + rng() * 0.30);
      // Central frond stem — 3 stacked voxels arching outward
      const segCount = 4;
      const segH = frondH / segCount;
      const isOuter = f === 0 || f === fronds - 1;
      const segCol = (stage === 4 && isOuter) ? LEAF_BLEACH : (rng() < 0.4 ? LEAF_MD : LEAF_DK);

      for (let s = 0; s < segCount; s++) {
        const t = s / (segCount - 1);
        const sx = px + Math.cos(fang) * lean * t;
        const sz = pz + Math.sin(fang) * lean * t;
        const sy = segH * (s + 0.5) + 0.03;
        b.add(0.025, segH * 0.95, 0.025, sx, sy, sz, segCol);

        // Feathery leaflets — pairs of tiny voxels on either side of each segment
        if (s > 0) {
          const perp = fang + Math.PI / 2;
          const leafletR = 0.035 + rng() * 0.015;
          const lpx = sx + Math.cos(perp) * leafletR;
          const lpz = sz + Math.sin(perp) * leafletR;
          const lnx = sx - Math.cos(perp) * leafletR;
          const lnz = sz - Math.sin(perp) * leafletR;
          const llCol = (stage === 4 && isOuter && rng() < 0.4) ? LEAF_BLEACH
                       : (rng() < 0.35 ? LEAF_LT : LEAF_MD);
          b.add(0.020, segH * 0.6, 0.020, lpx, sy, lpz, llCol);
          b.add(0.020, segH * 0.6, 0.020, lnx, sy, lnz, llCol);
          // Tiny tertiary feathers between leaflets at upper segments
          if (s >= 2 && rng() < 0.7) {
            b.add(0.012, 0.020, 0.012, lpx + Math.cos(perp) * 0.012, sy + segH * 0.3, lpz + Math.sin(perp) * 0.012, LEAF_LT);
            b.add(0.012, 0.020, 0.012, lnx - Math.cos(perp) * 0.012, sy + segH * 0.3, lnz - Math.sin(perp) * 0.012, LEAF_LT);
          }
        }
      }
      // Bright tip cluster at the end of each frond
      const tipX = px + Math.cos(fang) * lean;
      const tipZ = pz + Math.sin(fang) * lean;
      b.add(0.025, 0.025, 0.025, tipX, frondH + 0.04, tipZ, LEAF_LT);
    }

    // Orange root crown — stages 3+
    if (stage >= 3) {
      if (stage === 3) {
        // Hint of orange just visible at soil line — small dark-orange patch
        b.add(0.12, 0.04, 0.12, px, 0.02, pz, ROOT_DK);
        b.add(0.06, 0.04, 0.06, px, 0.04, pz, ROOT);
        // A few faint ridge marks
        b.add(0.03, 0.02, 0.10, px - 0.04, 0.05, pz, ROOT_RIDGE);
        b.add(0.03, 0.02, 0.10, px + 0.04, 0.05, pz, ROOT_RIDGE);
      } else {
        // Stage 4 mature — clearly visible carrot crown with ridges + highlight
        // Wider base, narrowing toward soil line
        b.add(0.24, 0.06, 0.24, px, 0.03, pz, ROOT_DK);
        b.add(0.22, 0.08, 0.22, px, 0.07, pz, ROOT);
        b.add(0.18, 0.06, 0.18, px, 0.12, pz, ROOT_LT);
        b.add(0.10, 0.04, 0.10, px, 0.16, pz, ROOT_HI);
        // Vertical ridge bands around the crown — 4 sides
        const ridges: [number, number][] = [
          [-0.10, 0], [0.10, 0], [0, -0.10], [0, 0.10],
        ];
        for (const [rx, rz] of ridges) {
          b.add(0.03, 0.10, 0.03, px + rx, 0.08, pz + rz, ROOT_RIDGE);
        }
        // Dark soil-collar voxels at the very base where root meets earth
        b.add(0.14, 0.025, 0.14, px, 0.01, pz, SOIL_DK);
      }
    }
  }
  return b.build();
}

function makeCrop(type: SeedType, stage: number, seed = 1): THREE.Object3D {
  let obj: THREE.Object3D;
  if (type === 'corn') obj = makeCorn(stage, seed);
  else if (type === 'carrot') obj = makeCarrot(stage, seed);
  else obj = makeWheat(stage, seed);
  const amp = type === 'carrot' ? 0.025 : type === 'corn' ? 0.06 : 0.045;
  obj.userData = { ...(obj.userData ?? {}), swayFreq: 1.8, swayAmp: amp, swayAxis: 'z' };
  return obj;
}

/**
 * Voxel stone tile — base slab + a cluster of irregular grey rock voxels
 * scattered on top. Color and pattern jittered deterministically per tile.
 */
function makeStone(seed = 1): THREE.Object3D {
  const b = new VoxelBuilder();
  const V = 0.12;
  const rng = rngFn(seed * 313 + 11);
  // base slab
  b.add(1.0, 0.20, 1.0, 0, -0.1, 0, COL.stone);
  // jittered top-face voxels
  for (let gx = -4; gx < 4; gx++) {
    for (let gz = -4; gz < 4; gz++) {
      const r = rng();
      const px = gx * V + V / 2;
      const pz = gz * V + V / 2;
      const c = r < 0.55 ? COL.stone : (r < 0.85 ? COL.boulderLt : COL.boulderDk);
      b.add(V, V * 0.5, V, px, 0.025, pz, c);
    }
  }
  // 4–6 chunky rocks scattered on top
  const N = 4 + Math.floor(rng() * 3);
  for (let i = 0; i < N; i++) {
    const px = (rng() - 0.5) * 0.75;
    const pz = (rng() - 0.5) * 0.75;
    const h = 0.10 + rng() * 0.18;
    const c = rng() < 0.5 ? COL.boulder : (rng() < 0.5 ? COL.boulderLt : COL.boulderDk);
    b.add(V * 1.4, h, V * 1.4, px, 0.05 + h * 0.5, pz, c);
    // tiny chip on the side
    if (rng() < 0.4) {
      b.add(V * 0.7, V * 0.6, V * 0.7, px + (rng() - 0.5) * 0.18, 0.06, pz + (rng() - 0.5) * 0.18, COL.boulderDk);
    }
  }
  return b.build();
}

/**
 * Voxel field tile — parallel ridges + trenches running along Z, with random
 * per-voxel jitter, occasional clods, and (when watered) puddles of blue
 * water in the trench bottoms. Replaces the flat brown slab that previously
 * rendered for `surf === 'field'`.
 */
function makeField(watered: boolean, seed = 1): THREE.Object3D {
  const b = new VoxelBuilder();
  const V = 0.10;                                          // voxel size
  const rng = rngFn(seed * 257 + (watered ? 999 : 0));
  const ridgeBase   = watered ? 0x6a4220 : 0xa07440;
  const ridgeLight  = watered ? 0x8a5a30 : 0xc89058;
  const ridgeDark   = watered ? 0x4a2c14 : 0x7a5028;
  const trenchBase  = watered ? 0x442010 : 0x6a4424;
  const trenchDark  = watered ? 0x281008 : 0x4a3018;

  // 10×10 grid; furrows run along Z. Even gx = ridge (higher), odd = trench.
  for (let gx = -5; gx < 5; gx++) {
    const isRidge = (gx + 5) % 2 === 0;
    for (let gz = -5; gz < 5; gz++) {
      const r = rng();
      const ridgeCol = r < 0.6 ? ridgeBase : (r < 0.85 ? ridgeLight : ridgeDark);
      const trenchCol = r < 0.7 ? trenchBase : trenchDark;
      const px = gx * V + V / 2;
      const pz = gz * V + V / 2;
      if (isRidge) {
        // Ridge top at Y=0, height ~0.22
        const h = 0.20 + rng() * 0.05;
        b.add(V, h, V, px, -h / 2 + 0.005, pz, ridgeCol);
        // Occasional little clod on top
        if (rng() < 0.08) {
          b.add(V * 0.6, V * 0.5, V * 0.6, px + (rng() - 0.5) * V * 0.4, V * 0.25, pz + (rng() - 0.5) * V * 0.4, ridgeDark);
        }
      } else {
        // Trench shallower
        const h = 0.13;
        b.add(V, h, V, px, -h / 2 - 0.04, pz, trenchCol);
        // Puddle of water in trench
        if (watered && rng() < 0.55) {
          b.add(V * 0.95, V * 0.06, V * 0.95, px, -0.02, pz, 0x4a8ac8);
        }
      }
    }
  }
  // Sub-soil shelf (so the ridges don't look like floating bricks from the side)
  b.add(1.0, 0.10, 1.0, 0, -0.18, 0, watered ? 0x281008 : 0x4a3018);
  return b.build();
}

// ── Lot-side voxel variants ─────────────────────────────────────────
type SideVariant = 'plain' | 'worm' | 'fossil' | 'stones' | 'roots' | 'skeleton';

/** Deterministic per-(tile, side) variant pick. Tree tiles force `roots`. */
function pickSideVariant(c: number, r: number, axis: 'N' | 'S' | 'E' | 'W', thing: unknown): SideVariant {
  if (thing === 'tree-oak') return 'roots';
  const seed = ((c * 73856093) ^ (r * 19349663) ^ (axis.charCodeAt(0) * 83492791)) >>> 0;
  const rng = rngFn(seed);
  const roll = rng();
  if (roll < 0.62) return 'plain';
  if (roll < 0.74) return 'worm';
  if (roll < 0.84) return 'fossil';
  if (roll < 0.93) return 'stones';
  return 'skeleton';
}

/**
 * Apply a decorative variant on top of the base dirt strip for one perimeter
 * tile side. (cx, cz) is the tile center; sideTop is the Y of the grass-slab
 * bottom; vox is the voxel size used in the strip.
 */
function applySideVariant(
  b: VoxelBuilder,
  variant: SideVariant,
  side: { axis: 'N' | 'S' | 'E' | 'W'; nx: number; nz: number },
  cx: number, cz: number, sideTop: number, vox: number,
): void {
  // Coordinate helper — returns (x, z) for slot a (0..3) along the side strip.
  const at = (a: number) => {
    if (side.axis === 'N' || side.axis === 'S') {
      return { x: cx - 0.5 + (a + 0.5) * 0.25, z: cz + side.nz * 0.5 + side.nz * vox * 0.5 };
    }
    return { z: cz - 0.5 + (a + 0.5) * 0.25, x: cx + side.nx * 0.5 + side.nx * vox * 0.5 };
  };
  const seed = (((cx * 191) | 0) ^ ((cz * 313) | 0) ^ (side.axis.charCodeAt(0) * 17)) >>> 0;
  const rng = rngFn(seed);

  switch (variant) {
    case 'plain': return;

    case 'worm': {
      // 5-segment wriggly pink line from the top down
      const startA = 1 + Math.floor(rng() * 2);
      let yy = sideTop - 0.20;
      let pos = at(startA);
      for (let i = 0; i < 5; i++) {
        const col = i % 2 === 0 ? COL.wormPink : COL.wormDk;
        b.add(vox * 0.7, vox * 0.6, vox * 0.7, pos.x, yy, pos.z, col);
        const off = (i % 2 === 0 ? 1 : -1) * vox * 0.6;
        if (side.axis === 'N' || side.axis === 'S') pos = { x: pos.x + off, z: pos.z };
        else pos = { x: pos.x, z: pos.z + off };
        yy -= vox * 0.95;
      }
      const head = at(startA);
      b.add(vox * 0.5, vox * 0.5, vox * 0.5, head.x, sideTop - 0.05, head.z, COL.wormPink);
      return;
    }

    case 'fossil': {
      // Ribcage: 4 rib bones stacked, plus a spine
      const startA = 1 + Math.floor(rng() * 1.5);
      let yy = sideTop - 0.30;
      for (let i = 0; i < 4; i++) {
        const len = 3 - (i % 2);
        for (let j = 0; j < len; j++) {
          const a = startA + j - Math.floor(len / 2);
          if (a < 0 || a > 3) continue;
          const pos = at(a);
          const col = (i + j) % 2 === 0 ? COL.fossilLt : COL.fossilDk;
          b.add(vox * 0.8, vox * 0.5, vox * 0.5, pos.x, yy, pos.z, col);
        }
        yy -= vox * 1.0;
      }
      // spine
      for (let i = 0; i < 4; i++) {
        const pos = at(startA);
        b.add(vox * 0.3, vox * 0.3, vox * 0.3, pos.x, sideTop - 0.30 - i * vox, pos.z, COL.fossilLt);
      }
      return;
    }

    case 'stones': {
      // 5-7 chunky grey pebbles scattered in the lower half
      const n = 5 + Math.floor(rng() * 3);
      for (let i = 0; i < n; i++) {
        const a = Math.floor(rng() * 4);
        const pos = at(a);
        const yy = sideTop - 0.30 - rng() * 1.0;
        const c = rng() < 0.5 ? COL.boulder : (rng() < 0.5 ? COL.boulderLt : COL.boulderDk);
        const sz = vox * (0.6 + rng() * 0.35);
        b.add(sz, sz, sz, pos.x, yy, pos.z, c);
      }
      return;
    }

    case 'roots': {
      // 3 vertical tendrils + a few lateral knobs
      const cols = [0, 1, 3];
      for (const a of cols) {
        for (let i = 0; i < 6; i++) {
          const pos = at(a);
          const c = (i % 2 === 0) ? COL.rootBr : COL.rootBrLt;
          b.add(vox * 0.6, vox * 0.9, vox * 0.6, pos.x, sideTop - 0.10 - i * vox * 0.95, pos.z, c);
        }
      }
      for (let i = 0; i < 4; i++) {
        const pos = at(1 + i % 3);
        b.add(vox * 0.4, vox * 0.4, vox * 0.4, pos.x, sideTop - 0.30 - i * 0.18, pos.z, COL.rootBr);
      }
      return;
    }

    case 'skeleton': {
      // skull + spine + ribs
      const a = 1 + Math.floor(rng() * 2);
      const pos = at(a);
      b.add(vox * 1.3, vox * 1.0, vox * 0.6, pos.x, sideTop - 0.22, pos.z, COL.skeletonLt);
      b.add(vox * 0.25, vox * 0.25, vox * 0.2, pos.x - vox * 0.35, sideTop - 0.22, pos.z + vox * 0.25, 0x101010);
      b.add(vox * 0.25, vox * 0.25, vox * 0.2, pos.x + vox * 0.35, sideTop - 0.22, pos.z + vox * 0.25, 0x101010);
      for (let i = 1; i < 5; i++) {
        b.add(vox * 0.5, vox * 0.4, vox * 0.5, pos.x, sideTop - 0.32 - i * vox * 0.9, pos.z, COL.skeletonLt);
      }
      for (let i = 0; i < 3; i++) {
        b.add(vox * 1.2, vox * 0.3, vox * 0.4, pos.x, sideTop - 0.48 - i * vox * 0.9, pos.z, COL.skeletonDk);
      }
      return;
    }
  }
}

/**
 * Dense voxel barn — upgraded to the new high-detail standard. Stone foundation
 * with mortar veins, multi-shade plank walls with weathering streaks + knots,
 * peaked roof with shingle rows + ridge cap + cupola, glowing hayloft window
 * with shutters + flower box, door with iron strap hinges + handle plate, hay
 * bales with bound straw + whiskers, sacks with patches, leaning pitchfork,
 * grain chute with spilled grain, stone chimney, and a tiny weathervane.
 */
function makeGranary(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(20410317);
  const V = 0.16;                                          // voxel side
  const halfW = 5;                                         // 10v wide → 1.6 world units
  const halfD = 4;                                         // 8v deep
  const bodyH = 8;

  // ── Stone foundation slab (multi-shade with mortar lines) ──
  const FOUND_SHADES = [0x7a7068, 0x686058, 0x8a8078, 0x585048, 0x9a8e84];
  for (let fx = -halfW; fx < halfW; fx++) {
    for (let fz = -halfD; fz < halfD; fz++) {
      const c = FOUND_SHADES[Math.floor(rng() * FOUND_SHADES.length)];
      b.add(V, V * 0.9, V, fx * V + V / 2, V * 0.45, fz * V + V / 2, c);
      if (rng() < 0.25) {
        b.add(V * 0.4, V * 0.06, V * 0.4, fx * V + V / 2 + (rng() - 0.5) * V * 0.3, V * 0.92, fz * V + V / 2 + (rng() - 0.5) * V * 0.3, 0x4a4640);
      }
    }
  }
  // Foundation lip — thin overhang
  b.add((halfW * 2 + 0.4) * V, V * 0.18, (halfD * 2 + 0.4) * V, 0, V * 0.99, 0, 0x4a4640);

  // ── Plank walls — 5-shade red palette + vertical seams + weathering ──
  const WALL = [COL.granaryWall, COL.granaryWallD, 0xa84030, 0xc86048, 0x8a2818];
  for (let y = 0; y < bodyH; y++) {
    for (let x = -halfW; x < halfW; x++) {
      for (let z = -halfD; z < halfD; z++) {
        const isEdge = (x === -halfW || x === halfW - 1 || z === -halfD || z === halfD - 1);
        if (!isEdge) continue;
        if (z === halfD - 1 && x >= -2 && x <= 1 && y < 4) continue;
        if (z === halfD - 1 && x >= -2 && x <= 0 && y >= 5 && y <= 6) continue;
        const corner = (x === -halfW || x === halfW - 1) && (z === -halfD || z === halfD - 1);
        const plankRow = (x + y * 3) % 4 === 0;
        const roll = rng();
        let c: number;
        if (corner) c = WALL[1];
        else if (plankRow) c = WALL[1];
        else if (roll < 0.15) c = WALL[4];
        else if (roll < 0.30) c = WALL[3];
        else if (roll < 0.55) c = WALL[2];
        else c = WALL[0];
        b.add(V, V, V, x * V + V / 2, V + y * V + V / 2, z * V + V / 2, c);
        if (!corner && y % 2 === 0 && rng() < 0.45) {
          if (z === -halfD) {
            b.add(V * 0.06, V, V * 0.2, x * V + V / 2, V + y * V + V / 2, (-halfD) * V + V * 0.45, 0x3a1810);
          } else if (z === halfD - 1) {
            b.add(V * 0.06, V, V * 0.2, x * V + V / 2, V + y * V + V / 2, (halfD - 1) * V + V * 0.55, 0x3a1810);
          }
        }
        if (!corner && roll < 0.04) {
          const dzk = z === -halfD ? -V * 0.4 : z === halfD - 1 ? V * 0.4 : 0;
          const dxk = x === -halfW ? -V * 0.4 : x === halfW - 1 ? V * 0.4 : 0;
          b.add(V * 0.18, V * 0.18, V * 0.06, x * V + V / 2 + dxk * 0.5, V + y * V + V / 2, z * V + V / 2 + dzk * 0.5, 0x2a0c08);
        }
      }
    }
  }
  for (let i = 0; i < 5; i++) {
    const sx2 = (-halfW + 1 + Math.floor(rng() * (halfW * 2 - 2))) * V + V / 2;
    const sy2 = V + (1 + Math.floor(rng() * (bodyH - 2))) * V;
    b.add(V * 0.12, V * 1.8, V * 0.06, sx2, sy2, (halfD - 1) * V + V * 0.55, 0x6a1e10);
  }

  // ── Door — vertical planks with iron strap hinges + handle plate ──
  for (let y = 0; y < 4; y++) {
    for (let x = -2; x <= 1; x++) {
      const roll = rng();
      const c = roll < 0.15 ? 0x4a2810 : roll < 0.45 ? COL.woodDk : roll < 0.80 ? COL.wood : COL.woodLt;
      b.add(V, V, V * 0.6, x * V + V / 2, V + y * V + V / 2, (halfD - 1) * V + V * 0.7, c);
      if (x < 1) {
        b.add(V * 0.05, V, V * 0.1, (x + 1) * V, V + y * V + V / 2, (halfD - 1) * V + V * 0.95, 0x2a1408);
      }
    }
  }
  for (const hy of [0, 3]) {
    for (let hx = -2; hx <= 1; hx++) {
      b.add(V * 0.95, V * 0.25, V * 0.12, hx * V + V / 2, V + hy * V + V * 0.5, (halfD - 1) * V + V * 1.0, 0x2a2418);
    }
    b.add(V * 0.12, V * 0.12, V * 0.08, -1.8 * V + V / 2, V + hy * V + V * 0.5, (halfD - 1) * V + V * 1.05, 0x686058);
    b.add(V * 0.12, V * 0.12, V * 0.08, 0.8 * V + V / 2, V + hy * V + V * 0.5, (halfD - 1) * V + V * 1.05, 0x686058);
  }
  for (let i = 0; i < 4; i++) {
    b.add(V, V * 0.3, V * 0.2, (-2 + i) * V + V / 2, V + (3 - i) * V + V / 2 + V * 0.2, (halfD - 1) * V + V * 0.95, COL.woodDk);
    b.add(V, V * 0.3, V * 0.2, (-2 + i) * V + V / 2, V + i * V + V / 2 + V * 0.2,         (halfD - 1) * V + V * 0.95, COL.woodDk);
  }
  b.add(V * 0.45, V * 0.45, V * 0.15, 0.5 * V, V + 1.7 * V, (halfD - 1) * V + V * 1.05, 0x484038);
  b.add(V * 0.3, V * 0.3, V * 0.3, 0.5 * V, V + 1.7 * V, (halfD - 1) * V + V * 1.18, 0x202018);

  // ── Window with warm glow + shutters + flower box ──
  for (let y = 5; y <= 6; y++) {
    for (let x = -2; x <= 0; x++) {
      b.add(V, V, V * 0.3, x * V + V / 2, V + y * V + V / 2, (halfD - 1) * V + V * 0.3, COL.woodDk);
      b.add(V * 0.85, V * 0.85, V * 0.2, x * V + V / 2, V + y * V + V / 2, (halfD - 1) * V + V * 0.5, 0xffd078);
    }
  }
  b.add(V * 0.15, V * 2, V * 0.15, -V * 0.5, V + 5.5 * V, (halfD - 1) * V + V * 0.65, COL.woodDk);
  b.add(V * 3.0, V * 0.15, V * 0.15, -V * 0.5, V + 5.5 * V, (halfD - 1) * V + V * 0.65, COL.woodDk);
  for (let y = 5; y <= 6; y++) {
    b.add(V * 0.4, V, V * 0.2, -2.7 * V, V + y * V + V / 2, (halfD - 1) * V + V * 0.7, COL.woodDk);
    b.add(V * 0.4, V, V * 0.2, 0.7 * V, V + y * V + V / 2, (halfD - 1) * V + V * 0.7, COL.woodDk);
  }
  b.add(V * 3.4, V * 0.3, V * 0.5, -V * 0.5, V + 4.6 * V, (halfD - 1) * V + V * 0.9, COL.woodDk);
  for (let fi = 0; fi < 4; fi++) {
    const fx = (-2 + fi * 0.9) * V;
    b.add(V * 0.3, V * 0.3, V * 0.3, fx, V + 4.9 * V, (halfD - 1) * V + V * 0.95, fi % 2 === 0 ? 0xff80a8 : COL.flowerY);
  }

  // ── Roof — peaked gable with shingle rows + mossy patches ──
  const ROOF = [COL.granaryRoof, COL.granaryRoofL, 0x3a1e10, 0x6a3520, 0x462818];
  const roofBaseY = V + bodyH * V;
  for (let lay = 0; lay < 6; lay++) {
    const wHalf = halfW + 1 - lay;
    if (wHalf < 1) break;
    const yy = roofBaseY + lay * V + V / 2;
    for (let x = -wHalf; x < wHalf; x++) {
      for (let z = -halfD - 1; z <= halfD; z++) {
        const roll = rng();
        let c: number;
        if (lay === 0) c = ROOF[2];
        else if (roll < 0.18) c = ROOF[3];
        else if (roll < 0.55) c = (lay % 2 === 0) ? ROOF[0] : ROOF[1];
        else if (roll < 0.85) c = ROOF[1];
        else c = ROOF[4];
        b.add(V, V, V, x * V + V / 2, yy, z * V + V / 2, c);
        if (z % 2 === 0 && (x === -wHalf || x === wHalf - 1)) {
          b.add(V * 0.3, V * 0.18, V * 1.0, x * V + V / 2 + (x < 0 ? -V * 0.3 : V * 0.3), yy - V * 0.45, z * V + V / 2, ROOF[2]);
        }
      }
    }
  }
  for (let z = -halfD - 1; z <= halfD; z++) {
    b.add(V * 1.2, V * 0.4, V * 0.6, -halfW * V, roofBaseY, z * V + V / 2, COL.woodDk);
    b.add(V * 1.2, V * 0.4, V * 0.6,  halfW * V, roofBaseY, z * V + V / 2, COL.woodDk);
  }
  for (let z = -halfD - 1; z <= halfD; z++) {
    b.add(V * 1.2, V * 0.6, V, 0, roofBaseY + 6 * V, z * V + V / 2, 0x3a1e10);
    if (z % 2 === 0) b.add(V * 1.6, V * 0.15, V, 0, roofBaseY + 6.35 * V, z * V + V / 2, 0x2a1408);
  }

  // ── Cupola + weathervane ──
  const cupY = roofBaseY + 6.6 * V;
  b.add(V * 1.6, V * 0.8, V * 1.6, 0, cupY + V * 0.4, 0, COL.woodDk);
  b.add(V * 1.4, V * 0.6, V * 0.3, 0, cupY + V * 0.7, V * 0.7, 0xffd078);
  b.add(V * 0.6, V * 0.4, V * 0.6, 0, cupY + V, 0, 0x3a1e10);
  b.add(V * 0.12, V * 1.4, V * 0.12, 0, cupY + V * 1.9, 0, 0x202018);
  b.add(V * 1.2, V * 0.12, V * 0.12, 0, cupY + V * 2.7, 0, 0x202018);
  b.add(V * 0.3, V * 0.4, V * 0.3, V * 0.7, cupY + V * 2.7, 0, 0x202018);

  // ── Stone chimney with brick courses ──
  const cx = (halfW - 2) * V + V / 2;
  const CHIM = [0x484038, 0x585048, 0x383028, 0x686058];
  for (let y = 0; y < 4; y++) {
    for (let xx = -1; xx <= 1; xx++) {
      for (let zz = -1; zz <= 1; zz++) {
        const c = CHIM[Math.floor(rng() * CHIM.length)];
        b.add(V * 0.5, V, V * 0.5, cx + xx * V * 0.5, roofBaseY + 5 * V + y * V, -halfD * V * 0.4 + zz * V * 0.5, c);
      }
    }
    b.add(V * 1.6, V * 0.08, V * 1.6, cx, roofBaseY + 5 * V + y * V + V * 0.5, -halfD * V * 0.4, 0x2a2418);
  }
  b.add(V * 1.8, V * 0.4, V * 1.8, cx, roofBaseY + 9.2 * V, -halfD * V * 0.4, 0x202018);

  // ── Grain chute on the side ──
  const chuteZ = halfD * V + V * 0.5;
  const chuteX = (halfW - 1.5) * V;
  for (let i = 0; i < 4; i++) {
    b.add(V * 0.9, V * 0.5, V * 0.8, chuteX, V + (4 - i * 0.3) * V, chuteZ + i * V * 0.3, COL.woodDk);
  }
  b.add(V * 0.4, V * 0.4, V * 0.4, chuteX, V + 2.6 * V, chuteZ + V * 1.4, 0xd8a04c);
  b.add(V * 0.5, V * 0.3, V * 0.5, chuteX, V + 1.2 * V, chuteZ + V * 1.5, 0xd8a04c);

  // ── Hay bales — multi-shade straw + twine bands + whiskers ──
  const HAY = [0xe6c870, 0xc8a44a, 0xf4dc8c, 0xb89030, 0xd8b860];
  const hbx = -3 * V, hbz = (halfD + 0.6) * V;
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 3; j++) {
      for (let k = 0; k < 2; k++) {
        const c = HAY[Math.floor(rng() * HAY.length)];
        b.add(V, V, V * 0.5, hbx + (i - 1.5) * V, V + j * V + V / 2, hbz + (k - 0.5) * V * 0.5, c);
      }
      if (rng() < 0.4) b.add(V * 0.05, V * 0.3, V * 0.05, hbx + (i - 1.5) * V + (rng() - 0.5) * V * 0.5, V + j * V + V * 0.6, hbz - V * 0.4, 0xf4dc8c);
    }
  }
  for (let i = -1; i <= 2; i++) {
    b.add(V, V * 0.1, V * 0.4, hbx + (i - 0.5) * V, V + 1.5 * V, hbz, 0x603018);
  }
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 2; j++) {
      const c = HAY[Math.floor(rng() * HAY.length)];
      b.add(V, V, V, hbx + (i - 1) * V, V + (3 + j) * V + V / 2, hbz, c);
    }
  }
  b.add(V * 3.2, V * 0.1, V * 0.4, hbx, V + 3.5 * V, hbz, 0x603018);
  b.add(V * 0.15, V * 4, V * 0.15, hbx + 1.5 * V, V + 2 * V, hbz - V * 0.4, 0x6a4828);
  for (let p = -1; p <= 1; p++) {
    b.add(V * 0.15, V * 0.5, V * 0.15, hbx + 1.5 * V + p * V * 0.3, V + 4 * V + V * 0.25, hbz - V * 0.4, 0xa8a08c);
  }

  // ── Grain sacks — multi-shade burlap with patches ──
  const SACK = [0xe8dcc0, 0xd0c0a0, 0xf0e4c8, 0xb8a888, 0xc8b89a];
  const sx = 3 * V, sz = (halfD + 0.6) * V;
  const sackPositions: [number, number, number][] = [
    [0, 0, 0], [V * 1.0, 0, 0], [V * 0.5, V, 0],
  ];
  for (const [dx, dy, dz] of sackPositions) {
    for (let sy = 0; sy < 3; sy++) {
      const c = SACK[Math.floor(rng() * SACK.length)];
      b.add(V * 0.9, V * 0.4, V * 0.9, sx + dx, V + sy * V * 0.4 + V * 0.2 + dy, sz + dz, c);
    }
    if (rng() < 0.6) {
      b.add(V * 0.3, V * 0.3, V * 0.1, sx + dx + (rng() - 0.5) * V * 0.4, V + V * 0.5 + dy, sz + dz + V * 0.45, 0x8a6a40);
    }
    b.add(V * 0.5, V * 0.18, V * 0.5, sx + dx, V + 1.2 * V + dy, sz + dz, 0x803818);
    b.add(V * 0.7, V * 0.10, V * 0.7, sx + dx, V + 1.3 * V + dy, sz + dz, 0xd8a04c);
  }
  for (let i = 0; i < 5; i++) {
    const gx = sx + (rng() - 0.5) * V * 1.6;
    const gz = sz + V * 0.6 + rng() * V * 0.5;
    b.add(V * 0.25, V * 0.08, V * 0.25, gx, V + V * 0.06, gz, 0xd8a04c);
  }

  return b.build();
}

/**
 * Dense voxel windmill. Two merged meshes:
 *   1. base + tapered stone tower + cone roof + door/windows (static)
 *   2. 4 sails as ONE merged blade-cross that rotates as a single group
 */
function makeMill(): THREE.Object3D & { userData: { sails: THREE.Object3D } } {
  const root = new THREE.Group() as THREE.Group & { userData: { sails: THREE.Object3D } };
  const V = 0.16;
  const towerLayers = 12;

  // Static body
  const body = new VoxelBuilder();
  // Foundation
  body.add(11 * V, V, 11 * V, 0, V / 2, 0, COL.wood);
  // Tower — tapering circular outline approximated by an 8-sided ring
  for (let lay = 0; lay < towerLayers; lay++) {
    const r = 4 - lay * 0.18;
    const yy = V + lay * V + V / 2;
    // ring of voxels around the circumference + one in the middle (for joins)
    const N = 14;
    for (let i = 0; i < N; i++) {
      const ang = (i / N) * Math.PI * 2;
      const x = Math.cos(ang) * r * V;
      const z = Math.sin(ang) * r * V;
      const c = (lay % 3 === 0) ? COL.millStoneD : (lay % 3 === 1) ? COL.millStoneL : COL.millStone;
      body.add(V * 1.05, V, V * 1.05, x, yy, z, c);
    }
    // mortar stripe every 2 layers
    if (lay % 2 === 1) {
      for (let i = 0; i < N; i++) {
        const ang = (i / N) * Math.PI * 2;
        const x = Math.cos(ang) * r * V;
        const z = Math.sin(ang) * r * V;
        body.add(V * 1.1, V * 0.1, V * 1.1, x, yy + V * 0.5, z, 0x4a4640);
      }
    }
  }

  // Door at base (south face)
  for (let y = 0; y < 4; y++) {
    for (let x = -1; x <= 1; x++) {
      body.add(V, V, V * 0.6, x * V, V + y * V + V / 2, 4 * V, COL.woodDk);
    }
  }
  // Door arch top
  body.add(3 * V, V * 0.6, V * 0.4, 0, V + 4.3 * V, 4 * V, 0x302418);
  // Door handle
  body.add(V * 0.3, V * 0.3, V * 0.2, 0.5 * V, V + 1.5 * V, 4 * V + V * 0.3, 0x202018);

  // Side window + glow (south-east)
  body.add(V * 1.6, V * 1.6, V * 0.3, 2 * V, V + 6 * V, 3.5 * V, COL.woodDk);
  body.add(V * 1.2, V * 1.2, V * 0.15, 2 * V, V + 6 * V, 3.5 * V + V * 0.2, 0xffd078);
  // Window cross
  body.add(V * 0.15, V * 1.6, V * 0.1, 2 * V, V + 6 * V, 3.5 * V + V * 0.3, COL.woodDk);
  body.add(V * 1.6, V * 0.15, V * 0.1, 2 * V, V + 6 * V, 3.5 * V + V * 0.3, COL.woodDk);

  // Conical roof — stacked rings shrinking, dark roof color
  const roofBase = V + towerLayers * V;
  for (let lay = 0; lay < 5; lay++) {
    const r = 3.4 - lay * 0.6;
    if (r < 0.3) break;
    const yy = roofBase + lay * V + V / 2;
    const N = 12;
    for (let i = 0; i < N; i++) {
      const ang = (i / N) * Math.PI * 2;
      const x = Math.cos(ang) * r * V;
      const z = Math.sin(ang) * r * V;
      body.add(V, V, V, x, yy, z, lay % 2 === 0 ? COL.granaryRoof : COL.granaryRoofL);
    }
    // fill smaller layers with center voxel
    if (r < 1.5) body.add(V * (r * 2), V, V * (r * 2), 0, yy, 0, COL.granaryRoof);
  }
  // Spire
  body.add(V * 0.4, V * 2, V * 0.4, 0, roofBase + 6 * V, 0, COL.woodDk);
  body.add(V * 1.5, V * 0.2, V * 0.2, 0, roofBase + 7 * V, 0, COL.woodDk);

  // ── Environmental storytelling ──
  // A cluster of white flour sacks stacked outside the door (south side).
  const fbx = -3 * V, fbz = 5.5 * V;
  const flourStack: [number, number, number][] = [
    [0, 0, 0], [V * 1.1, 0, 0], [V * 2.2, 0, 0],
    [V * 0.55, V * 1.2, 0], [V * 1.65, V * 1.2, 0],
    [V * 1.1, V * 2.4, 0],
  ];
  for (const [dx, dy, dz] of flourStack) {
    body.add(V * 1.0, V * 1.1, V * 1.0, fbx + dx, V + V * 0.55 + dy, fbz + dz, 0xf4ecd8);
    body.add(V * 0.5, V * 0.18, V * 0.5, fbx + dx, V + 1.2 * V + dy, fbz + dz, 0x6a3a1a);
    // tiny dust of flour on top
    body.add(V * 0.7, V * 0.05, V * 0.7, fbx + dx, V + 1.3 * V + dy, fbz + dz, 0xfff8e8);
  }
  // A wooden barrel beside the door
  const bx = 3 * V, bz = 4.6 * V;
  for (let y = 0; y < 5; y++) {
    body.add(V * 1.3, V, V * 1.3, bx, V + y * V + V / 2, bz, y % 2 === 0 ? COL.wood : COL.woodDk);
  }
  // metal bands
  body.add(V * 1.5, V * 0.15, V * 1.5, bx, V + V + V * 0.1, bz, 0x484038);
  body.add(V * 1.5, V * 0.15, V * 1.5, bx, V + 3.5 * V, bz, 0x484038);
  // barrel top — grain visible inside
  body.add(V * 1.1, V * 0.15, V * 1.1, bx, V + 5 * V + V * 0.1, bz, 0xd8a04c);
  // A small cart with a single sack — between barrel and entrance
  const cx_cart = 0, cz_cart = 6.2 * V;
  body.add(V * 2.2, V * 0.3, V * 1.4, cx_cart, V * 0.6, cz_cart, COL.wood);
  body.add(V * 0.4, V * 0.4, V * 0.4, cx_cart + V, V * 0.5, cz_cart - V * 0.5, COL.truckTire);
  body.add(V * 0.4, V * 0.4, V * 0.4, cx_cart + V, V * 0.5, cz_cart + V * 0.5, COL.truckTire);
  body.add(V * 0.4, V * 0.4, V * 0.4, cx_cart - V, V * 0.5, cz_cart - V * 0.5, COL.truckTire);
  body.add(V * 0.4, V * 0.4, V * 0.4, cx_cart - V, V * 0.5, cz_cart + V * 0.5, COL.truckTire);
  // cart handle pointing forward
  body.add(V * 0.2, V * 0.2, V * 1.5, cx_cart, V * 0.7, cz_cart + V * 1.2, COL.woodDk);
  // a flour sack on the cart
  body.add(V * 0.9, V * 1.0, V * 0.9, cx_cart, V * 1.2, cz_cart, 0xf4ecd8);
  body.add(V * 0.5, V * 0.18, V * 0.5, cx_cart, V * 1.7, cz_cart, 0x6a3a1a);

  root.add(body.build());

  // Sails — single merged 4-blade cross, rotated on Z
  const sails = new THREE.Group();
  sails.position.set(0, V + 7 * V, 4.2 * V);
  const sailsB = new VoxelBuilder();
  for (let dir = 0; dir < 4; dir++) {
    const ang = (dir / 4) * Math.PI * 2;
    const ax = Math.cos(ang), ay = Math.sin(ang);
    // Spar (8 voxels along blade)
    for (let i = 1; i <= 9; i++) {
      sailsB.add(V * 0.7, V * 0.7, V * 0.7, ax * i * V, ay * i * V, 0, COL.woodDk);
    }
    // Cloth panels — 6×3 grid offset perpendicular to spar
    const px = -ay, py = ax;
    for (let i = 2; i <= 8; i++) {
      for (let w = 1; w <= 3; w++) {
        const x = ax * i * V + px * w * V * 0.8;
        const y = ay * i * V + py * w * V * 0.8;
        const c = w === 3 ? COL.millSailD : COL.millSail;
        sailsB.add(V * 0.8, V * 0.8, V * 0.15, x, y, 0, c);
      }
    }
  }
  // Hub
  sailsB.add(V * 1.5, V * 1.5, V * 1.0, 0, 0, 0, COL.woodDk);
  sails.add(sailsB.build());
  root.add(sails);
  root.userData = { sails };
  return root;
}

/** Dense voxel mailbox — post + box body + door + raised flag. ~30 voxels. */
function makeMailbox(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(70314441);
  const V = 0.10;

  // ── Ground patch — stones + grass tufts at base ──
  const STONES = [0x686058, 0x8a8078, 0x4a4036];
  for (let i = 0; i < 8; i++) {
    const ang = rng() * Math.PI * 2;
    const r = V * 1.2 + rng() * V * 0.8;
    const c = STONES[Math.floor(rng() * STONES.length)];
    b.add(V * 0.4, V * 0.2, V * 0.4, Math.cos(ang) * r, V * 0.1, Math.sin(ang) * r, c);
  }
  for (let i = 0; i < 5; i++) {
    const ang = rng() * Math.PI * 2;
    const r = V * 1.6;
    b.add(V * 0.1, V * 0.4, V * 0.1, Math.cos(ang) * r, V * 0.2, Math.sin(ang) * r, COL.bushLt);
  }

  // ── Post — 4-shade wood-grain quadrant voxels + knots ──
  const POST = [COL.wood, COL.woodDk, COL.woodLt, 0x6a4828, 0x8a5a30];
  for (let i = 0; i < 6; i++) {
    // 4 quadrant voxels per ring so vertical grain stripes show
    const quads: [number, number][] = [[-V * 0.3, -V * 0.3], [V * 0.3, -V * 0.3], [-V * 0.3, V * 0.3], [V * 0.3, V * 0.3]];
    for (const [qx, qz] of quads) {
      const c = POST[Math.floor(rng() * POST.length)];
      b.add(V * 0.55, V, V * 0.55, qx, V * (i + 0.5), qz, c);
    }
    // Occasional knot
    if (rng() < 0.3) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? V * 0.6 : side === 1 ? -V * 0.6 : 0;
      const kz = side === 2 ? V * 0.6 : side === 3 ? -V * 0.6 : 0;
      b.add(V * 0.18, V * 0.18, V * 0.06, kx, V * (i + 0.5), kz, 0x3a1e10);
    }
  }
  // Iron support bracket where the post meets the box
  b.add(V * 1.6, V * 0.18, V * 1.6, 0, V * 5.9, 0, 0x484038);

  // ── Box body — multi-shade red with rivet seams + weathering streaks ──
  const RED = [COL.mailboxBody, 0xa84030, 0xd4604a, 0x882810, 0xb44838];
  const boxY = 6 * V;
  for (let y = 0; y < 3; y++) {
    for (let x = -2; x <= 2; x++) {
      for (let z = -2; z <= 1; z++) {
        const isEdge = (Math.abs(x) === 2 || y === 0 || y === 2 || z === -2 || z === 1);
        if (!isEdge) continue;
        if (z === 1 && x >= -2 && x <= 1) continue;
        const roll = rng();
        let c: number;
        if (roll < 0.12) c = RED[3];
        else if (roll < 0.30) c = RED[1];
        else if (roll < 0.55) c = RED[2];
        else if (roll < 0.85) c = RED[0];
        else c = RED[4];
        b.add(V, V, V, x * V, boxY + y * V + V / 2, z * V, c);
        // rivet on the side panels
        if ((y === 0 || y === 2) && (x === -2 || x === 2) && rng() < 0.6) {
          const dx = x === -2 ? -V * 0.55 : V * 0.55;
          b.add(V * 0.12, V * 0.12, V * 0.12, x * V + dx, boxY + y * V + V / 2, z * V, 0x484038);
        }
      }
    }
  }
  // Vertical rust streak on side
  b.add(V * 0.08, V * 2, V * 0.06, -2 * V - V * 0.5, boxY + 1.5 * V, -V * 0.3, 0x6a3018);

  // ── Door — plank rows with hinges + lock plate + mail slot ──
  for (let y = 0; y < 3; y++) {
    for (let x = -2; x <= 1; x++) {
      const roll = rng();
      const c = roll < 0.2 ? RED[3] : roll < 0.5 ? RED[1] : roll < 0.85 ? RED[0] : RED[2];
      b.add(V, V, V * 0.4, x * V, boxY + y * V + V / 2, 1 * V + V * 0.3, c);
      // horizontal plank seam
      if (y < 2) b.add(V, V * 0.05, V * 0.5, x * V, boxY + (y + 1) * V, 1 * V + V * 0.32, 0x4a1810);
    }
  }
  // Hinges on left edge
  for (const hy of [V * 0.3, V * 2.6]) {
    b.add(V * 0.3, V * 0.25, V * 0.18, -1.5 * V, boxY + hy, 1 * V + V * 0.5, 0x2a2418);
    b.add(V * 0.08, V * 0.08, V * 0.06, -1.5 * V, boxY + hy, 1 * V + V * 0.6, 0x686058);
  }
  // Lock plate + handle
  b.add(V * 0.45, V * 0.45, V * 0.12, 1 * V, boxY + 1.5 * V, 1 * V + V * 0.55, 0x484038);
  b.add(V * 0.25, V * 0.25, V * 0.18, 1 * V, boxY + 1.5 * V, 1 * V + V * 0.65, 0x202018);
  // Mail slot (dark)
  b.add(V * 1.6, V * 0.18, V * 0.06, -V * 0.5, boxY + 2.3 * V, 1 * V + V * 0.58, 0x101010);

  // ── Arched roof with shingle rows ──
  const ROOF = [0x803020, 0x602010, 0x9a4030, 0x4a1408];
  for (let x = -2; x <= 2; x++) {
    for (let zi = 0; zi < 4; zi++) {
      const zz = (zi - 1.5) * V + V * 0.5;
      const c = ROOF[Math.floor(rng() * ROOF.length)];
      b.add(V, V * 0.18, V, x * V, boxY + 3 * V + V * 0.09 + (zi % 2) * V * 0.05, zz, c);
    }
  }
  // ridge cap
  b.add(V * 5.2, V * 0.12, V * 0.4, 0, boxY + 3 * V + V * 0.3, -V * 0.5, 0x4a1408);

  // ── Flag — pole with iron bolt + bright flag + tilt ──
  for (let i = 0; i < 5; i++) {
    const c = i === 0 ? 0x484038 : COL.mailboxFlag;
    b.add(V * 0.3, V, V * 0.3, -2 * V - V * 0.4, boxY + i * V + V / 2, 0, c);
  }
  // bolt where flag meets the box
  b.add(V * 0.45, V * 0.25, V * 0.18, -2 * V - V * 0.4, boxY + V * 0.5, 0, 0x484038);
  b.add(V * 0.08, V * 0.08, V * 0.06, -2 * V - V * 0.4, boxY + V * 0.5, V * 0.1, 0x686058);
  // bright flag — staggered for waving effect
  for (let i = 0; i < 3; i++) {
    const y = boxY + (i + 2) * V + V / 2;
    const dx = -2 * V - V * 0.4 - V * 0.7 - i * V * 0.05;
    b.add(V * 0.4, V, V * 1.4, dx, y, V * 0.6, COL.mailboxFlag);
    // edge highlight
    b.add(V * 0.4, V * 0.1, V * 1.4, dx, y + V * 0.4, V * 0.6, 0xf8d860);
  }

  // ── Letters poking out + wax seal ──
  for (let i = 0; i < 3; i++) {
    const dx = (i - 1) * V * 0.6;
    const tilt = i * 0.04 - 0.04;
    b.add(V * 0.5, V * 0.5, V * 0.05, dx, boxY + 3 * V + V * 0.5, V * 0.7 + tilt, 0xfaf4e0);
    b.add(V * 0.12, V * 0.12, V * 0.04, dx + V * 0.15, boxY + 3 * V + V * 0.8, V * 0.71 + tilt, 0xc44048);
    // tiny address line
    b.add(V * 0.3, V * 0.04, V * 0.04, dx, boxY + 3 * V + V * 0.5 - V * 0.1, V * 0.72 + tilt, 0x4a3020);
  }
  // wax seal on top letter
  b.add(V * 0.15, V * 0.05, V * 0.05, V * 0.6, boxY + 3 * V + V * 0.5, V * 0.74, 0x801818);

  // ── Parcel — multi-shade kraft paper + string + address tag ──
  const KRAFT = [0xc89058, 0xa87038, 0xd4a070, 0x885828];
  for (let py = 0; py < 2; py++) {
    for (let pxx = -1; pxx <= 0; pxx++) {
      for (let pzz = -1; pzz <= 0; pzz++) {
        const c = KRAFT[Math.floor(rng() * KRAFT.length)];
        b.add(V * 0.7, V * 0.5, V * 0.6, V * 1.6 + pxx * V * 0.4, V * 0.25 + py * V * 0.5, V * 0.6 + pzz * V * 0.3, c);
      }
    }
  }
  // string ties
  b.add(V * 0.08, V * 1.0, V * 1.2, V * 1.6 - V * 0.3, V * 0.5, V * 0.6, 0x6a3a1a);
  b.add(V * 1.4, V * 1.0, V * 0.08, V * 1.6, V * 0.5, V * 0.6 - V * 0.3, 0x6a3a1a);
  // address label on top
  b.add(V * 0.8, V * 0.04, V * 0.6, V * 1.6, V * 1.01, V * 0.6, 0xfaf4e0);
  // tiny ink lines on label
  b.add(V * 0.5, V * 0.02, V * 0.04, V * 1.6, V * 1.04, V * 0.6 - V * 0.15, 0x202010);
  b.add(V * 0.5, V * 0.02, V * 0.04, V * 1.6, V * 1.04, V * 0.6 + V * 0.15, 0x202010);
  return b.build();
}

/** Dense voxel truck depot — wooden loading platform with sign post. */
function makeTruckDepot(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(80927155);
  const V = 0.16;

  // ── Stone foundation strip under the platform ──
  const STONE = [0x686058, 0x4a4036, 0x8a8068, 0x5a4e3c];
  for (let x = -3; x < 3; x++) {
    for (let z = -3; z < 3; z++) {
      const c = STONE[Math.floor(rng() * STONE.length)];
      b.add(V, V * 0.18, V, x * V + V / 2, V * 0.06, z * V + V / 2, c);
    }
  }

  // ── Plank platform — multi-shade with knots + tire tracks ──
  const PLANK = [COL.wood, COL.woodDk, COL.woodLt, 0x6a4828, 0x8a5a30];
  for (let x = -3; x < 3; x++) {
    for (let z = -3; z < 3; z++) {
      const roll = rng();
      let c: number;
      if (roll < 0.12) c = PLANK[3];
      else if (roll < 0.40) c = PLANK[1];
      else if (roll < 0.70) c = PLANK[0];
      else if (roll < 0.92) c = PLANK[4];
      else c = PLANK[2];
      b.add(V, V * 0.5, V, x * V + V / 2, V * 0.4, z * V + V / 2, c);
      // plank seam — thin dark gap on every-other plank's edge
      if (x % 2 === 0) b.add(V * 0.06, V * 0.5, V, x * V, V * 0.4, z * V + V / 2, 0x3a1e10);
      // knot
      if (rng() < 0.08) {
        b.add(V * 0.18, V * 0.08, V * 0.18, x * V + V / 2 + (rng() - 0.5) * V * 0.4, V * 0.66, z * V + V / 2 + (rng() - 0.5) * V * 0.4, 0x2a1408);
      }
      // nailhead
      if (rng() < 0.12) {
        b.add(V * 0.08, V * 0.06, V * 0.08, x * V + V / 2, V * 0.67, z * V + V * 0.2, 0x484038);
      }
    }
  }
  // Tire-track scuff in the dirt next to the platform
  for (let i = 0; i < 6; i++) {
    b.add(V * 0.5, V * 0.04, V * 0.4, -2.4 * V + i * V * 0.45, V * 0.21, 3.2 * V, 0x4a3018);
    b.add(V * 0.5, V * 0.04, V * 0.4, -2.4 * V + i * V * 0.45, V * 0.21, 3.2 * V + V * 0.5, 0x4a3018);
  }

  // ── Edge frame — visible joists ──
  for (let x = -3; x < 3; x++) {
    b.add(V, V * 0.6, V * 0.3, x * V + V / 2, V * 0.35, -3 * V, COL.woodDk);
    b.add(V, V * 0.6, V * 0.3, x * V + V / 2, V * 0.35,  3 * V - V * 0.7, COL.woodDk);
    if (x % 2 === 0) {
      b.add(V * 0.08, V * 0.6, V * 0.4, x * V + V * 0.3, V * 0.35, -3 * V, 0x686058);   // nailhead
      b.add(V * 0.08, V * 0.6, V * 0.4, x * V + V * 0.3, V * 0.35, 3 * V - V * 0.7, 0x686058);
    }
  }

  // ── Sign post — wood grain + iron bands + lantern ──
  for (let i = 0; i < 5; i++) {
    const quads: [number, number][] = [[-V * 0.15, -V * 0.15], [V * 0.15, -V * 0.15], [-V * 0.15, V * 0.15], [V * 0.15, V * 0.15]];
    for (const [qx, qz] of quads) {
      const c = PLANK[Math.floor(rng() * PLANK.length)];
      b.add(V * 0.22, V, V * 0.22, -2 * V + qx, V * 0.5 + i * V, -2 * V + qz, c);
    }
    if (i % 2 === 0) b.add(V * 0.5, V * 0.1, V * 0.5, -2 * V, V * 0.5 + i * V, -2 * V, 0x484038);
  }
  // Sign board with destination text
  for (let x = 0; x < 4; x++) {
    for (let y = 0; y < 2; y++) {
      const c = (x + y) % 2 === 0 ? COL.woodLt : COL.wood;
      b.add(V, V, V * 0.2, (-2 + x) * V + V / 2, V * 0.5 + (3 + y) * V, -2 * V + V * 0.3, c);
    }
  }
  // Tiny arrow + text lines on sign
  b.add(V * 0.6, V * 0.6, V * 0.1, V * 0.5, V * 0.5 + 3.5 * V, -2 * V + V * 0.5, 0x302010);
  b.add(V * 1.2, V * 0.05, V * 0.04, -1.4 * V, V * 0.5 + 4.2 * V, -2 * V + V * 0.45, 0x302010);
  b.add(V * 1.2, V * 0.05, V * 0.04, -1.4 * V, V * 0.5 + 3.8 * V, -2 * V + V * 0.45, 0x302010);
  // Sign frame
  for (const ey of [V * 0.5 + 2.9 * V, V * 0.5 + 5.1 * V]) {
    b.add(V * 4.2, V * 0.08, V * 0.3, -V * 0.5, ey, -2 * V + V * 0.3, 0x3a1e10);
  }
  // Small lantern hanging from the sign post
  b.add(V * 0.5, V * 0.55, V * 0.5, -2 * V, V * 0.5 + 4.6 * V, -2 * V + V * 0.7, 0x383028);
  b.add(V * 0.4, V * 0.4, V * 0.4, -2 * V, V * 0.5 + 4.65 * V, -2 * V + V * 0.7, 0xffd078);
  b.add(V * 0.18, V * 0.18, V * 0.18, -2 * V, V * 0.5 + 4.95 * V, -2 * V + V * 0.7, 0x202018);

  // ── Pallets stacked — multi-shade with rope ──
  const px = 2 * V, pz = 2 * V;
  for (const palY of [V * 0.45, V * 0.8]) {
    for (let i = 0; i < 3; i++) {
      const c = PLANK[Math.floor(rng() * PLANK.length)];
      b.add(V * 1.6, V * 0.2, V * 0.2, px, palY, pz + (i - 1) * V * 0.6, c);
    }
    for (let i = 0; i < 4; i++) {
      const c = PLANK[Math.floor(rng() * PLANK.length)];
      b.add(V * 0.2, V * 0.2, V * 1.4, px + (i - 1.5) * V * 0.5, palY, pz, c);
    }
  }
  // rope around pallet
  b.add(V * 1.8, V * 0.06, V * 0.06, px, V * 0.7, pz + V * 0.6, 0x603018);
  b.add(V * 0.06, V * 0.06, V * 1.5, px + V * 0.85, V * 0.7, pz, 0x603018);

  // ── Crate on pallets — multi-shade planks + metal corners + manifest ──
  const crateY = V * 1.05;
  for (let y = 0; y < 4; y++) {
    for (let x = -2; x <= 1; x++) {
      for (let z = -1; z <= 1; z++) {
        const isEdge = (x === -2 || x === 1 || y === 3 || z === -1 || z === 1);
        if (!isEdge) continue;
        const c = PLANK[Math.floor(rng() * PLANK.length)];
        b.add(V * 0.4, V * 0.4, V * 0.4, px + x * V * 0.4, crateY + y * V * 0.4, pz + z * V * 0.4, c);
      }
    }
  }
  // metal corner brackets
  for (const cx of [-2.2, 1.2]) {
    for (const cz of [-1.2, 1.2]) {
      b.add(V * 0.18, V * 1.7, V * 0.18, px + cx * V * 0.4, crateY + 0.7 * V, pz + cz * V * 0.4, 0x484038);
    }
  }
  // crate band + nailheads
  b.add(V * 1.7, V * 0.1, V * 1.0, px - V * 0.2, crateY + V * 0.8, pz, 0x483828);
  for (let bi = 0; bi < 3; bi++) {
    b.add(V * 0.08, V * 0.12, V * 0.06, px - V * 0.2 + (bi - 1) * V * 0.6, crateY + V * 0.8, pz + V * 0.5, 0x686058);
  }
  // manifest tag (paper) on top
  b.add(V * 0.4, V * 0.04, V * 0.3, px - V * 0.2, crateY + V * 1.7, pz, 0xfaf4e0);
  b.add(V * 0.3, V * 0.02, V * 0.04, px - V * 0.2, crateY + V * 1.72, pz, 0x202010);

  // ── Fuel drum — metal banded ──
  const dx2 = -2 * V, dz2 = 2 * V;
  for (let y = 0; y < 5; y++) {
    const c = y === 0 || y === 4 ? 0x3a4038 : (y % 2 === 0 ? 0x5a6258 : 0x484e48);
    b.add(V * 1.2, V * 0.3, V * 1.2, dx2, V * 0.5 + y * V * 0.3, dz2, c);
  }
  b.add(V * 1.4, V * 0.12, V * 1.4, dx2, V * 0.7, dz2, 0x484038);
  b.add(V * 1.4, V * 0.12, V * 1.4, dx2, V * 1.3, dz2, 0x484038);
  // hazard mark
  b.add(V * 0.3, V * 0.04, V * 0.3, dx2 + V * 0.6, V * 1.0, dz2, 0xc44048);
  // valve on top
  b.add(V * 0.2, V * 0.2, V * 0.2, dx2, V * 2.0, dz2, 0x686058);

  // ── Sandbags stacked at the corner ──
  const SAND = [0xb89870, 0x9a7848, 0xc8a880, 0xa08858];
  for (let i = 0; i < 3; i++) {
    const sx = 2.4 * V + (i % 2) * V * 0.5;
    const sy = V * 0.4 + Math.floor(i / 2) * V * 0.4;
    const sz = -2.2 * V;
    for (let sxi = 0; sxi < 2; sxi++) {
      const c = SAND[Math.floor(rng() * SAND.length)];
      b.add(V * 0.5, V * 0.4, V * 0.4, sx + sxi * V * 0.5, sy, sz, c);
    }
  }

  // ── Small hand-cart for loading crates ──
  const hcx = -1.5 * V, hcz = 1 * V;
  // bed
  for (let pi = -1; pi <= 1; pi++) {
    b.add(V * 0.3, V * 0.18, V, hcx + pi * V * 0.3, V * 0.55, hcz, pi % 2 === 0 ? COL.wood : COL.woodDk);
  }
  // wheels
  for (const wz of [hcz - V * 0.45, hcz + V * 0.45]) {
    b.add(V * 0.4, V * 0.4, V * 0.4, hcx + V * 0.1, V * 0.35, wz, COL.truckTire);
    b.add(V * 0.16, V * 0.16, V * 0.42, hcx + V * 0.1, V * 0.35, wz, 0x686058);
  }
  // handle
  b.add(V * 0.1, V * 0.1, V * 1.4, hcx - V * 0.5, V * 0.8, hcz, COL.woodDk);
  b.add(V * 0.1, V * 0.5, V * 0.1, hcx - V * 0.5, V * 1.0, hcz + V * 0.7, COL.woodDk);
  return b.build();
}

/** Bigger voxel truck — cab + bed + wheels, headlights + cargo slot returned
 *  as refs so the renderer can light them at night and stack cargo on the bed. */
/**
 * Farm pickup truck — 1950s pickup silhouette built from ~400 voxels.
 *
 * Anatomy (front-to-back along -Z → +Z): chrome bumper, vertical grille slats,
 * round headlights, sloped hood with center crease, rounded front wheel arches,
 * cab with tilted windshield + side windows + roof + mirrors + driver, running
 * boards stepping back into a planked cargo bed with side rails, rear wheel
 * arches, tailgate with hinges + chrome handle, and brake lights flanking it.
 * Four chunky tires with visible hubs and spokes sit under the body. The HOOD
 * protruding forward of the cab is what stops it from reading as "two blocks".
 *
 * Voxel-grid lives in local space; root is added to scene which positions it.
 * Z-axis is length, X is width, Y is up. -Z is the truck's forward direction.
 */
function makeTruck(): {
  obj: THREE.Group;
  headlights: { mesh: THREE.Mesh; light: THREE.PointLight }[];
  cargoSlot: THREE.Group;
  brakeLights: THREE.Mesh[];
  /** 4 wheel meshes — each centred at its hub. Spin around local X axis to
   *  simulate rolling. Order: FL, FR, RL, RR. */
  wheels: THREE.Mesh[];
  /** Local-space position of the exhaust pipe tip — for spawning smoke. */
  exhaustTip: THREE.Vector3;
  /** Local-space ground-contact positions of the 4 wheels (FL, FR, RL, RR).
   *  Used to spawn per-tire dust during drive. */
  wheelGround: THREE.Vector3[];
  /** Body subgroup (everything but the wheels). Pitch/roll the body for
   *  inertia + suspension without rotating the wheels with it. */
  body: THREE.Group;
} {
  const root = new THREE.Group();
  // BODY group — everything except wheels — pitches with inertia.
  const body = new THREE.Group();
  root.add(body);
  const b = new VoxelBuilder();
  const rng = rngFn(17427);

  // ── Palette ──
  const BODY     = COL.truckCab;       // 0x4a8ac8 — base blue
  const BODY_DK  = COL.truckCabDk;     // 0x2a5288 — shadow blue
  const BODY_LT  = 0x6aaae0;           // highlight blue (sun-side)
  const BED_WD   = COL.truckBed;       // 0x9c6038 — brown plank
  const BED_DK   = COL.truckBedDk;     // 0x6a4020 — plank shadow
  const BED_LT   = 0xb87648;           // plank highlight
  const CHROME   = 0xbcbcbc;
  const CHROME_D = 0x707070;
  const TIRE     = 0x1a1a1a;
  const TIRE_LT  = 0x383838;
  const HUB      = 0xa0a0a0;
  const GLASS    = 0x6890b0;
  const GLASS_D  = 0x3e5878;
  const RUST     = 0x6a3818;
  const SKIN     = 0xf0c898;
  const SHIRT    = 0x4a6a98;
  const HAT      = 0x1c1c1c;

  // ── 4 wheels — built as SEPARATE meshes so they can spin independently
  // of the body. Each wheel is centred at the origin in its own local space;
  // the parent root positions/orients them. Rolling = rotation around X.
  const wheelInfo: { x: number; z: number }[] = [
    { x: -0.46, z: -0.65 }, { x: +0.46, z: -0.65 },
    { x: -0.46, z: +0.55 }, { x: +0.46, z: +0.55 },
  ];
  const wheels: THREE.Mesh[] = [];
  for (const w of wheelInfo) {
    const wb = new VoxelBuilder();
    const RT = 0.18;
    const STEP = 0.040;
    // Outer / mid / inner face slices in local X — origin is the hub centre,
    // outside face faces ± from origin.
    const sign = w.x > 0 ? 1 : -1;
    const faces = [+0.06 * sign, +0.02 * sign, -0.02 * sign];
    for (const wx of faces) {
      for (let dy = -RT; dy <= RT + 1e-3; dy += STEP) {
        for (let dz = -RT; dz <= RT + 1e-3; dz += STEP) {
          const d = Math.hypot(dy, dz);
          if (d > RT) continue;
          if (d < RT * 0.55) continue;
          const col = d > RT * 0.82 ? TIRE : TIRE_LT;
          // Add a small radial-stripe pattern so the spin reads visually.
          const angle = Math.atan2(dy, dz);
          const tread = Math.abs(Math.sin(angle * 6)) > 0.7;
          const finalCol = (d > RT * 0.78 && tread) ? 0x080808 : col;
          wb.add(0.03, STEP * 1.05, STEP * 1.05, wx, dy, dz, finalCol);
        }
      }
    }
    // Hub cap on the outside face — silver disk + spokes.
    wb.add(0.04, 0.16, 0.16, +0.08 * sign, 0, 0, HUB);
    wb.add(0.04, 0.20, 0.04, +0.09 * sign, 0, 0, CHROME_D);
    wb.add(0.04, 0.04, 0.20, +0.09 * sign, 0, 0, CHROME_D);
    wb.add(0.05, 0.06, 0.06, +0.10 * sign, 0, 0, 0x303030);
    const wMesh = wb.build();
    wMesh.position.set(w.x, 0.18, w.z);
    root.add(wMesh);
    wheels.push(wMesh);
  }

  // ── Front fenders (rounded body curving over front wheels) ──
  // Two arches per side: outer + slight inner shadow band.
  for (const fx of [-0.46, +0.46]) {
    for (let dz = -0.22; dz <= 0.22 + 1e-3; dz += 0.04) {
      const archY = 0.40 + Math.sqrt(Math.max(0, 0.22 * 0.22 - dz * dz)) * 0.55;
      b.add(0.12, 0.06, 0.05, fx, archY, -0.65 + dz, BODY);
      b.add(0.12, 0.04, 0.05, fx, archY - 0.05, -0.65 + dz, BODY_DK);
      // Top sun highlight
      b.add(0.08, 0.03, 0.04, fx, archY + 0.025, -0.65 + dz, BODY_LT);
    }
  }

  // ── Rear fenders ──
  for (const fx of [-0.46, +0.46]) {
    for (let dz = -0.22; dz <= 0.22 + 1e-3; dz += 0.04) {
      const archY = 0.40 + Math.sqrt(Math.max(0, 0.22 * 0.22 - dz * dz)) * 0.50;
      b.add(0.12, 0.06, 0.05, fx, archY, 0.55 + dz, BED_WD);
      b.add(0.12, 0.04, 0.05, fx, archY - 0.05, 0.55 + dz, BED_DK);
    }
  }

  // ── Chassis (visible underbody frame between wheels) ──
  for (const fx of [-0.42, +0.42]) {
    b.add(0.05, 0.04, 1.60, fx, 0.27, -0.05, 0x303030);
  }
  // Cross members
  b.add(0.90, 0.04, 0.05, 0, 0.27, -0.30, 0x303030);
  b.add(0.90, 0.04, 0.05, 0, 0.27, +0.20, 0x303030);
  // Drive shaft running down the centre
  b.add(0.05, 0.05, 1.60, 0, 0.25, -0.05, 0x404040);

  // ── Underbody slab (where the cab + bed sit) ──
  b.add(0.94, 0.10, 1.90, 0, 0.40, 0, BODY_DK);

  // ── Hood (protrudes forward of cab — key silhouette feature) ──
  // z from -0.65 (cab front) to -1.18 (just behind grille)
  // y top from 0.58 at the cab to 0.52 at the grille — slight slope.
  for (let zi = 0; zi < 13; zi++) {
    const z = -0.65 - 0.040 * (zi + 1);            // -0.69..-1.17
    const yTop = 0.58 - zi * 0.005;
    for (let xi = -4; xi <= 4; xi++) {
      const x = xi * 0.10;
      // Per-voxel palette pick — checker-noisy so it doesn't look flat.
      const r = rng();
      const col = r < 0.10 ? BODY_DK : (r < 0.55 ? BODY : BODY_LT);
      b.add(0.10, 0.05, 0.045, x, yTop, z, col);
    }
    // Hood sides — slight inward step from cab width.
    b.add(0.05, 0.10, 0.045, -0.42, 0.50, z, BODY_DK);
    b.add(0.05, 0.10, 0.045, +0.42, 0.50, z, BODY_DK);
  }
  // Hood center crease — chrome trim down the middle
  for (let zi = 0; zi < 13; zi++) {
    const z = -0.65 - 0.040 * (zi + 1);
    b.add(0.04, 0.025, 0.045, 0, 0.61, z, CHROME);
  }

  // ── Front bumper + grille + headlights surround ──
  // Chrome bumper — wide bar at very front, low.
  b.add(1.10, 0.06, 0.06, 0, 0.34, -1.22, CHROME);
  b.add(1.10, 0.03, 0.04, 0, 0.38, -1.22, CHROME_D);
  // Grille frame
  b.add(0.85, 0.20, 0.04, 0, 0.50, -1.20, BODY_DK);
  // Vertical grille slats (chrome)
  for (let i = -3; i <= 3; i++) {
    b.add(0.025, 0.16, 0.04, i * 0.12, 0.50, -1.22, CHROME);
  }
  // Horizontal grille bar
  b.add(0.85, 0.03, 0.04, 0, 0.50, -1.23, CHROME_D);

  // ── Cab (taller box, behind hood) ──
  // Body walls — z = -0.65 (front) to -0.05 (back), x ±0.42, y from 0.45 to 0.78.
  for (let zi = 0; zi < 13; zi++) {
    const z = -0.62 + zi * 0.048;
    for (let xi = -4; xi <= 4; xi++) {
      const x = xi * 0.10;
      const isSide = (xi === -4 || xi === 4);
      // Lower body wall (below windows)
      b.add(0.10, 0.06, 0.050, x, 0.48, z, isSide ? BODY_DK : BODY);
      b.add(0.10, 0.06, 0.050, x, 0.54, z, isSide ? BODY : BODY_LT);
      // Upper body wall (window line — leave gaps for glass, only draw the
      // window FRAME on the sides + front pillar).
      if (zi === 0 || zi === 12) {
        // Front + back pillars
        b.add(0.10, 0.06, 0.050, x, 0.60, z, BODY);
        b.add(0.10, 0.06, 0.050, x, 0.66, z, BODY_DK);
      } else if (isSide) {
        // Side pillars only at edges where the door frame is
        if (xi === -4 || xi === 4) {
          // skip middle to leave window opening — we'll fill with glass below
        }
      }
    }
  }
  // Cab roof — flat top with slight overhang
  for (let zi = 0; zi < 13; zi++) {
    const z = -0.62 + zi * 0.048;
    for (let xi = -4; xi <= 4; xi++) {
      const x = xi * 0.10;
      b.add(0.10, 0.05, 0.050, x, 0.74, z, BODY_DK);
      b.add(0.08, 0.04, 0.045, x, 0.79, z, BODY_LT);
    }
  }
  // Roof "drip rail" trim along the edges
  for (let zi = 0; zi < 13; zi++) {
    const z = -0.62 + zi * 0.048;
    b.add(0.04, 0.03, 0.050, -0.44, 0.78, z, CHROME_D);
    b.add(0.04, 0.03, 0.050, +0.44, 0.78, z, CHROME_D);
  }

  // ── Windshield — large tilted glass between hood top and cab roof ──
  for (let xi = -3; xi <= 3; xi++) {
    const x = xi * 0.12;
    b.add(0.12, 0.05, 0.05, x, 0.62, -0.66, GLASS);
    b.add(0.12, 0.05, 0.05, x, 0.67, -0.69, GLASS);
    b.add(0.12, 0.05, 0.05, x, 0.72, -0.72, GLASS_D);
  }
  // Windshield wipers
  b.add(0.20, 0.015, 0.02, -0.10, 0.61, -0.65, 0x202020);
  b.add(0.20, 0.015, 0.02, +0.10, 0.61, -0.65, 0x202020);

  // ── Side windows (left + right) ──
  for (let zi = 1; zi < 12; zi++) {
    const z = -0.62 + zi * 0.048;
    b.add(0.030, 0.10, 0.05, -0.47, 0.65, z, (zi % 2 === 0 ? GLASS : GLASS_D));
    b.add(0.030, 0.10, 0.05, +0.47, 0.65, z, (zi % 2 === 0 ? GLASS : GLASS_D));
  }
  // Door panel divider (vertical chrome strip mid-cab)
  b.add(0.030, 0.15, 0.04, -0.47, 0.55, -0.25, CHROME_D);
  b.add(0.030, 0.15, 0.04, +0.47, 0.55, -0.25, CHROME_D);
  // Door handles
  b.add(0.040, 0.025, 0.06, -0.47, 0.56, -0.35, CHROME);
  b.add(0.040, 0.025, 0.06, +0.47, 0.56, -0.35, CHROME);

  // ── Side mirrors ──
  b.add(0.04, 0.05, 0.05, -0.52, 0.67, -0.55, BODY_DK);
  b.add(0.025, 0.02, 0.02, -0.49, 0.65, -0.55, CHROME_D);     // arm
  b.add(0.04, 0.05, 0.05, +0.52, 0.67, -0.55, BODY_DK);
  b.add(0.025, 0.02, 0.02, +0.49, 0.65, -0.55, CHROME_D);

  // ── Tiny driver visible through windshield ──
  b.add(0.06, 0.07, 0.06, -0.10, 0.66, -0.40, SKIN);            // head
  b.add(0.06, 0.025, 0.07, -0.10, 0.70, -0.42, HAT);             // cap
  b.add(0.10, 0.10, 0.08, -0.10, 0.57, -0.35, SHIRT);            // shirt
  // Steering wheel
  b.add(0.10, 0.012, 0.05, 0, 0.60, -0.50, 0x202020);
  b.add(0.012, 0.015, 0.05, 0, 0.56, -0.50, 0x202020);           // column

  // ── Running boards (step between front fender and bed) ──
  b.add(0.07, 0.025, 0.45, -0.50, 0.39, -0.10, BED_DK);
  b.add(0.07, 0.025, 0.45, +0.50, 0.39, -0.10, BED_DK);
  // Step plate texture
  for (let i = 0; i < 5; i++) {
    b.add(0.05, 0.02, 0.03, -0.50, 0.40, -0.30 + i * 0.10, BED_LT);
    b.add(0.05, 0.02, 0.03, +0.50, 0.40, -0.30 + i * 0.10, BED_LT);
  }

  // ── Cargo bed (planked) ──
  // Floor planks running lengthwise — alternating shades for plank seams.
  for (let zi = 0; zi < 14; zi++) {
    const z = -0.04 + zi * 0.085;
    const col = (zi % 3 === 0) ? BED_DK : (zi % 3 === 1 ? BED_WD : BED_LT);
    b.add(0.86, 0.03, 0.08, 0, 0.46, z, col);
    // Plank seam shadow
    b.add(0.86, 0.015, 0.01, 0, 0.47, z + 0.040, BED_DK);
  }
  // Side rails (with vertical plank pattern)
  for (let zi = 0; zi < 14; zi++) {
    const z = -0.04 + zi * 0.085;
    const col = (zi % 2 === 0) ? BED_WD : BED_DK;
    b.add(0.05, 0.20, 0.08, -0.43, 0.57, z, col);
    b.add(0.05, 0.20, 0.08, +0.43, 0.57, z, col);
    // Sun-side highlight on the outward face
    b.add(0.02, 0.18, 0.07, -0.45, 0.57, z, BED_DK);
    b.add(0.02, 0.18, 0.07, +0.45, 0.57, z, BED_LT);
  }
  // Side rail top caps (continuous chrome-trimmed wood)
  b.add(0.10, 0.04, 1.20, -0.43, 0.69, +0.55, BED_DK);
  b.add(0.10, 0.04, 1.20, +0.43, 0.69, +0.55, BED_DK);

  // ── Tailgate ──
  for (let yi = 0; yi < 6; yi++) {
    const y = 0.46 + yi * 0.04;
    for (let xi = -4; xi <= 4; xi++) {
      const x = xi * 0.10;
      const col = ((xi + yi) % 3 === 0) ? BED_DK : ((xi + yi) % 3 === 1 ? BED_WD : BED_LT);
      b.add(0.10, 0.04, 0.04, x, y, +1.20, col);
    }
  }
  // Tailgate hinges + handle
  b.add(0.05, 0.04, 0.06, -0.30, 0.46, +1.22, 0x303030);
  b.add(0.05, 0.04, 0.06, +0.30, 0.46, +1.22, 0x303030);
  b.add(0.24, 0.025, 0.03, 0, 0.66, +1.22, CHROME);
  b.add(0.04, 0.04, 0.03, 0, 0.66, +1.23, CHROME_D);     // handle bezel

  // ── Mud flaps ──
  b.add(0.10, 0.12, 0.025, -0.46, 0.30, +0.80, 0x1a1a1a);
  b.add(0.10, 0.12, 0.025, +0.46, 0.30, +0.80, 0x1a1a1a);

  // ── Exhaust pipe (under bed, exiting rear-right) ──
  b.add(0.05, 0.05, 0.80, +0.30, 0.25, +0.60, CHROME_D);
  b.add(0.06, 0.06, 0.06, +0.30, 0.25, +1.05, 0x202020);

  // ── Antenna ──
  b.add(0.015, 0.20, 0.015, +0.40, 0.84, -0.50, 0x202020);

  // ── Weathering — rust spots, paint chips ──
  for (let i = 0; i < 16; i++) {
    const rx = (rng() - 0.5) * 0.95;
    const ry = 0.40 + rng() * 0.35;
    const rz = (rng() - 0.5) * 2.40;
    b.add(0.025, 0.025, 0.025, rx, ry, rz, RUST);
  }

  body.add(b.build());

  // ── Headlights — separate THREE.Mesh + PointLight pairs in body group
  // so they pitch with the body. ──
  const headlights: { mesh: THREE.Mesh; light: THREE.PointLight }[] = [];
  const hlGeom = new THREE.BoxGeometry(0.13, 0.13, 0.04);
  for (const hx of [-0.32, +0.32]) {
    const hlMat = new THREE.MeshBasicMaterial({ color: 0xfff080 });
    const hlMesh = new THREE.Mesh(hlGeom, hlMat);
    hlMesh.position.set(hx, 0.50, -1.23);
    body.add(hlMesh);
    const ring = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.16, 0.03),
      new THREE.MeshBasicMaterial({ color: CHROME_D }),
    );
    ring.position.set(hx, 0.50, -1.215);
    body.add(ring);
    const light = new THREE.PointLight(0xfff0a0, 0, 8, 1.5);
    light.position.set(hx, 0.50, -1.32);
    body.add(light);
    headlights.push({ mesh: hlMesh, light });
  }

  // ── Brake lights ──
  const brakeLights: THREE.Mesh[] = [];
  const blGeom = new THREE.BoxGeometry(0.09, 0.06, 0.03);
  for (const bx of [-0.34, +0.34]) {
    const bmat = new THREE.MeshBasicMaterial({ color: 0x602010 });
    const bmesh = new THREE.Mesh(blGeom, bmat);
    bmesh.position.set(bx, 0.56, +1.23);
    body.add(bmesh);
    brakeLights.push(bmesh);
  }

  // ── Cargo slot — bed center, just above the planks ──
  const cargoSlot = new THREE.Group();
  cargoSlot.position.set(0, 0.50, 0.50);
  body.add(cargoSlot);

  // Local-space anchor points the animator uses for particle spawns.
  const exhaustTip = new THREE.Vector3(+0.30, 0.27, +1.06);
  const wheelGround = wheelInfo.map(w => new THREE.Vector3(w.x, 0.005, w.z));

  return { obj: root, headlights, cargoSlot, brakeLights, wheels, exhaustTip, wheelGround, body };
}

/**
 * Distant voxel town — a cluster of small houses, a church with a steeple,
 * and a few chimney smoke wisps. Built once at world creation, never rebuilt.
 */
function makeDistantTown(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(91011);
  // 7 houses in a loose cluster
  const houseSpots: [number, number, number][] = [
    [-7,  0,  0], [-3,  0,  1], [ 1,  0,  0], [ 5,  0,  2],
    [-5,  0, -3], [ 3,  0, -2], [ 8,  0,  0],
  ];
  for (const [hx, _hy, hz] of houseSpots) {
    const w = 1.2 + rng() * 0.5;
    const dpth = 1.2 + rng() * 0.5;
    const h = 1.0 + rng() * 0.4;
    const wallColor = rng() < 0.5 ? 0xb8826c : 0xa07458;
    // walls
    b.add(w, h, dpth, hx, h * 0.5, hz, wallColor);
    // sloped roof — 3 stacked layers
    for (let i = 0; i < 3; i++) {
      const rw = w * (1 - i * 0.3) + 0.1;
      const rd = dpth * (1 - i * 0.3) + 0.1;
      b.add(rw, 0.15, rd, hx, h + 0.075 + i * 0.13, hz, 0x6a3018);
    }
    // chimney
    const cx = hx + w * 0.3;
    const cz = hz + dpth * 0.3;
    b.add(0.18, 0.4, 0.18, cx, h + 0.4, cz, 0x4a3020);
    // tiny window
    b.add(0.22, 0.22, 0.04, hx, h * 0.55, hz + dpth * 0.5 + 0.02, 0xffd078);
  }
  // Church — at the center-back of the town
  const chX = 0, chZ = -5;
  b.add(2.0, 1.6, 2.4, chX, 0.8, chZ, 0xc8a880);
  // Steeple
  for (let i = 0; i < 4; i++) {
    const sw = 0.5 - i * 0.10;
    b.add(sw, 0.4, sw, chX, 1.6 + 0.20 + i * 0.4, chZ + 0.5, 0x484038);
  }
  // Cross on top
  b.add(0.08, 0.4, 0.08, chX, 1.6 + 1.85, chZ + 0.5, 0x202020);
  b.add(0.30, 0.08, 0.08, chX, 1.6 + 1.95, chZ + 0.5, 0x202020);
  // Church roof
  for (let i = 0; i < 3; i++) {
    b.add(2.2 - i * 0.6, 0.18, 2.6 - i * 0.6, chX, 1.6 + 0.09 + i * 0.16, chZ, 0x6a3018);
  }
  // Cobblestone plaza around church
  for (let z = -3; z < -7; z--) {
    for (let x = -2; x <= 2; x++) {
      b.add(1.0, 0.06, 1.0, x, 0.03, z, (x + z) % 2 === 0 ? 0x8c8c92 : 0x707078);
    }
  }
  // Couple of trees scattered between the houses
  const treeSpots: [number, number][] = [[-9, -1], [-1, -4], [7, -2], [9, 1]];
  for (const [tx, tz] of treeSpots) {
    b.add(0.25, 0.8, 0.25, tx, 0.4, tz, COL.trunk);
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = 0; dy <= 2; dy++)
        for (let dz = -1; dz <= 1; dz++) {
          if (dx * dx + (dy - 1) ** 2 + dz * dz > 2) continue;
          b.add(0.35, 0.35, 0.35, tx + dx * 0.3, 1.1 + dy * 0.3, tz + dz * 0.3,
                (dx + dy + dz) % 2 === 0 ? COL.leaf : COL.leafLt);
        }
  }
  return b.build();
}

/**
 * Distant low-poly hills along the horizon — voxel pyramids placed in a ring
 * around the lot to give the eye something to land on at the world edge.
 */
function makeHorizonHills(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(727);
  // Ring of hills at radius ~45 from origin
  const N = 28;
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2 + rng() * 0.1;
    const r = 44 + rng() * 8;
    const x = Math.cos(ang) * r;
    const z = Math.sin(ang) * r;
    const h = 2.5 + rng() * 4.0;
    const baseColor = rng() < 0.4 ? 0x4a6a3a : 0x5a7848;
    // 3-tier hill — base, mid, peak
    b.add(4.0 + rng() * 2.0, 0.6, 4.0 + rng() * 2.0, x, 0.3, z, baseColor);
    b.add(3.0 + rng() * 1.5, h * 0.4, 3.0 + rng() * 1.5, x, 0.6 + h * 0.2, z, baseColor);
    b.add(1.6 + rng() * 1.0, h * 0.4, 1.6 + rng() * 1.0, x, 0.6 + h * 0.6, z,
          rng() < 0.3 ? 0xa8a8b8 : baseColor);   // occasional snowy peak
  }
  return b.build();
}

/**
 * Ground + scenery beyond the lot — extended road southward, fields on both
 * sides of the road, scattered grass tufts, and the distant town.
 * One big merged mesh, built once.
 */
function makeWorldGround(plotW: number, plotH: number): THREE.Object3D {
  const b = new VoxelBuilder();
  const halfW = plotW / 2;
  const halfH = plotH / 2;
  // Big flat ground plane just below the lot's top surface (Y = -0.30)
  // Cover everything from -50 to +50 minus the lot footprint.
  const COVER = 50;
  const groundY = -0.30;
  const cellSize = 2.0;          // 2-tile chunks for the surrounding ground
  for (let z = -COVER; z < COVER; z += cellSize) {
    for (let x = -COVER; x < COVER; x += cellSize) {
      // Skip if this chunk is fully inside the lot bounds
      if (x + cellSize > -halfW && x < halfW && z + cellSize > -halfH && z < halfH) continue;
      const dist = Math.sqrt((x + cellSize / 2) ** 2 + (z + cellSize / 2) ** 2);
      const fade = Math.min(1, dist / (COVER * 0.7));
      // Slight color variation
      const c = ((x + z) | 0) % 3 === 0 ? COL.grassDark : COL.grass;
      const dark = fade > 0.8 ? 0x3a5828 : c;
      b.add(cellSize, 0.4, cellSize, x + cellSize / 2, groundY - 0.2, z + cellSize / 2, dark);
    }
  }
  // Extend the dirt road south from the lot edge → through to Z = +28
  // The plot road is at +halfH-2 to +halfH-1 inside the lot. Continue from
  // +halfH outward.
  for (let z = halfH; z < halfH + 28; z++) {
    for (let dx = -2; dx <= 1; dx++) {
      b.add(1.0, 0.12, 1.0, dx + 0.5, -0.06, z + 0.5, COL.road);
    }
    if (z % 2 === 0) {
      b.add(0.3, 0.04, 0.10, 0.0, 0.005, z + 0.5, COL.roadStripe);
    }
  }
  // A few scattered grass tufts in the countryside
  const rng = rngFn(13);
  for (let i = 0; i < 80; i++) {
    const x = (rng() - 0.5) * 90;
    const z = (rng() - 0.5) * 90;
    if (Math.abs(x) < halfW && Math.abs(z) < halfH) continue;  // skip inside lot
    if (Math.abs(x) < 2 && z > halfH) continue;                 // skip on the road
    b.add(0.15, 0.20, 0.15, x, -0.10, z, rng() < 0.5 ? COL.tallGrass : COL.leafLt);
  }
  return b.build();
}

/**
 * Voxel hoe — a wooden shaft with a dark metal blade. The mesh's origin is
 * at the blade tip so positioning it at the cursor's ground-projected point
 * makes the blade rest on the soil. The shaft leans backward toward the
 * camera so you see the head clearly.
 */
export function makeHoe(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(311);
  // Iron palette — shadow, base, highlight, edge, rust
  const IRON_DK = 0x2a2a2e;
  const IRON    = 0x3a3a42;
  const IRON_HI = 0x6a6a72;
  const EDGE    = 0x9aa0a8;
  const RUST    = 0x7a4020;
  const RUST_DK = 0x4a2410;
  // Wood palette — light/medium/dark/very-dark grain bands
  const WOOD_LT = 0xb88458;
  const WOOD    = 0x9c6c44;
  const WOOD_DK = 0x6a4828;
  const WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18;
  const LEATH_D = 0x3a2410;
  const DIRT    = 0x5a3818;
  // ── Curved blade — staircase profile with under-shadow + top highlight
  const bladeSteps: [number, number, number, number][] = [
    [-0.14, 0.018, 0.00, 1.0],
    [-0.07, 0.020, 0.00, 1.0],
    [ 0.00, 0.024, 0.01, 1.0],
    [ 0.07, 0.030, 0.02, 1.0],
    [ 0.14, 0.038, 0.04, 0.9],
  ];
  for (const [x, y, z, w] of bladeSteps) {
    b.add(0.08 * w, 0.018, 0.10, x, y, z, IRON_DK);
    b.add(0.08 * w, 0.024, 0.10, x, y + 0.024, z, IRON);
    b.add(0.08 * w, 0.012, 0.08, x, y + 0.046, z, IRON_HI);
    b.add(0.08 * w, 0.006, 0.04, x, y + 0.054, z + 0.04, EDGE);
  }
  // Rust + dirt patches on the blade
  b.add(0.04, 0.02, 0.04, -0.06, 0.038, -0.02, RUST);
  b.add(0.03, 0.014, 0.03, 0.04, 0.044, 0.02, RUST_DK);
  b.add(0.04, 0.012, 0.05, -0.10, 0.020, 0.02, DIRT);
  // Neck — iron strap binding the blade to the shaft, with rivets
  b.add(0.10, 0.10, 0.08, 0.00, 0.10, 0.06, IRON);
  b.add(0.10, 0.04, 0.08, 0.00, 0.15, 0.06, IRON_HI);
  b.add(0.018, 0.018, 0.018, -0.04, 0.10, 0.10, EDGE);
  b.add(0.018, 0.018, 0.018,  0.04, 0.10, 0.10, EDGE);
  b.add(0.018, 0.018, 0.018,  0.00, 0.14, 0.10, IRON_DK);
  // ── Shaft — wood-grain stripes (4 vertical sub-pillars per ring)
  const SHAFT_SEG = 9;
  const grainShades = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < SHAFT_SEG; i++) {
    const t = i / (SHAFT_SEG - 1);
    const yy = 0.20 + i * 0.085;
    const zz = 0.06 + t * 0.40;
    const quads: [number, number, number][] = [
      [-0.022, -0.022, grainShades[(i + 0) & 3]],
      [ 0.022, -0.022, grainShades[(i + 1) & 3]],
      [-0.022,  0.022, grainShades[(i + 2) & 3]],
      [ 0.022,  0.022, grainShades[(i + 3) & 3]],
    ];
    for (const [qx, qz, c] of quads) {
      b.add(0.026, 0.085, 0.026, qx, yy, zz + qz, c);
    }
    if (rng() < 0.20) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? 0.046 : side === 1 ? -0.046 : 0;
      const kz = side === 2 ? 0.046 : side === 3 ? -0.046 : 0;
      b.add(0.020, 0.025, 0.020, kx, yy, zz + kz, WOOD_VD);
    }
  }
  // Leather grip wrap near the top — two wraps with seam
  b.add(0.072, 0.06, 0.072, 0.00, 0.84, 0.40, LEATHER);
  b.add(0.075, 0.020, 0.075, 0.00, 0.87, 0.40, LEATH_D);
  b.add(0.072, 0.06, 0.072, 0.00, 0.92, 0.42, LEATHER);
  // Soil flecks at the blade base
  b.add(0.020, 0.012, 0.020, -0.08, 0.012, -0.04, DIRT);
  b.add(0.018, 0.010, 0.018,  0.10, 0.012,  0.02, DIRT);
  const mesh = b.build();
  mesh.name = 'hoe-prop';
  return mesh;
}

/** Voxel watering can — squat metal body, spout angled forward (out of the
 *  blade end of the shaft = +z direction), handle arch over the top. The
 *  mesh origin sits at the cursor projection so the spout tip hovers above
 *  the dirt and water particles fall onto the focused tile. */
export function makeWateringCan(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(523);
  // Galvanized metal palette
  const SHADOW = 0x3a4a58;
  const BODY   = 0x6c8aa0;
  const BODY_M = 0x809cb0;
  const RIM    = 0x4c6680;
  const ACC    = 0x8aa8c0;
  const HIGHL  = 0xa8c0d0;
  const RUST   = 0x9a5028;
  const RUST_D = 0x6a3010;
  const STAIN  = 0x4a3018;
  const RIVET  = 0x303840;
  // Body — 5 stacked rings, panel-shaded with seams + rivets + rust streaks
  const RINGS = 5;
  for (let i = 0; i < RINGS; i++) {
    const w  = 0.34 - i * 0.014;
    const y  = 0.10 + i * 0.06;
    const hRing = 0.062;
    const quads: [number, number, number][] = [
      [-w / 2 + 0.05, -w / 2 + 0.05, i % 2 === 0 ? BODY : BODY_M],
      [ w / 2 - 0.05, -w / 2 + 0.05, i % 2 === 0 ? BODY_M : BODY],
      [-w / 2 + 0.05,  w / 2 - 0.05, i % 2 === 0 ? BODY_M : SHADOW],
      [ w / 2 - 0.05,  w / 2 - 0.05, i % 2 === 0 ? SHADOW : BODY],
    ];
    for (const [qx, qz, c] of quads) {
      b.add(w / 2 + 0.02, hRing, w / 2 + 0.02, qx, y, qz, c);
    }
    if (i === RINGS - 1) {
      b.add(w + 0.02, 0.018, w + 0.02, 0, y + 0.030, 0, HIGHL);
    }
    if (i === 0) {
      b.add(w + 0.02, 0.014, w + 0.02, 0, y - 0.030, 0, SHADOW);
    }
    if (i % 2 === 0) {
      b.add(0.016, 0.016, 0.016,  w / 2 + 0.012, y, 0, RIVET);
      b.add(0.016, 0.016, 0.016, -w / 2 - 0.012, y, 0, RIVET);
    }
    if (rng() < 0.45) {
      const angle = rng() * Math.PI * 2;
      const rx = Math.cos(angle) * (w / 2 + 0.01);
      const rz = Math.sin(angle) * (w / 2 + 0.01);
      b.add(0.030, 0.022, 0.020, rx, y, rz, rng() < 0.5 ? RUST : RUST_D);
    }
    if (i < RINGS - 1 && rng() < 0.35) {
      const angle = rng() * Math.PI * 2;
      const sx = Math.cos(angle) * (w / 2 + 0.012);
      const sz = Math.sin(angle) * (w / 2 + 0.012);
      b.add(0.014, 0.06, 0.012, sx, y, sz, STAIN);
    }
  }
  // Spout — stepped pipe rising toward the tip
  const spoutSteps: [number, number, number, number][] = [
    [0.00, 0.16, 0.21, 0.060],
    [0.00, 0.18, 0.28, 0.058],
    [0.00, 0.20, 0.34, 0.054],
  ];
  for (const [sx, sy, sz, sw] of spoutSteps) {
    b.add(sw, sw, 0.07, sx, sy, sz, BODY);
    b.add(sw - 0.02, 0.012, 0.07, sx, sy + sw / 2 - 0.012, sz, HIGHL);
    b.add(sw - 0.02, 0.012, 0.07, sx, sy - sw / 2 + 0.012, sz, SHADOW);
  }
  // Spout collar (riveted band)
  b.add(0.10, 0.08, 0.05, 0.00, 0.16, 0.17, RIM);
  b.add(0.016, 0.016, 0.016, -0.040, 0.20, 0.18, RIVET);
  b.add(0.016, 0.016, 0.016,  0.040, 0.20, 0.18, RIVET);
  // Rose — flared disk with perforation dots (3x3 grid)
  b.add(0.14, 0.11, 0.04, 0.00, 0.21, 0.40, ACC);
  b.add(0.14, 0.018, 0.04, 0.00, 0.275, 0.40, HIGHL);
  b.add(0.14, 0.018, 0.04, 0.00, 0.155, 0.40, SHADOW);
  for (let gy = -1; gy <= 1; gy++) {
    for (let gx = -1; gx <= 1; gx++) {
      b.add(0.015, 0.015, 0.015, gx * 0.04, 0.21 + gy * 0.030, 0.422, RIM);
    }
  }
  // Handle — arch with riveted posts + cross bar + edge highlights
  b.add(0.05, 0.14, 0.05, -0.15, 0.42, 0.02, RIM);
  b.add(0.05, 0.14, 0.05,  0.15, 0.42, 0.02, RIM);
  b.add(0.018, 0.018, 0.018, -0.15, 0.36, 0.02, RIVET);
  b.add(0.018, 0.018, 0.018,  0.15, 0.36, 0.02, RIVET);
  b.add(0.36, 0.05, 0.05, 0.00, 0.50, 0.02, BODY);
  b.add(0.36, 0.014, 0.05, 0.00, 0.528, 0.02, HIGHL);
  b.add(0.36, 0.012, 0.05, 0.00, 0.472, 0.02, SHADOW);
  // Worn paint specks
  b.add(0.06, 0.012, 0.06, -0.10, 0.40, -0.12, RUST);
  b.add(0.04, 0.010, 0.04,  0.12, 0.40,  0.10, RUST_D);
  const mesh = b.build();
  mesh.name = 'water-prop';
  return mesh;
}

/** Voxel seed bag — burlap sack tied at the top. Player swings it lightly
 *  while held; clicking releases a seed onto the tilled tile below. */
export function makeSeedBag(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(719);
  // Jute / burlap palette — tan + brown specks for woven texture
  const TAN_LT = 0xd4a878, TAN = 0xc09058, TAN_DK = 0xa07840;
  const BROWN = 0x8a5828, BROWN_D = 0x6a3e18;
  const ROPE_LT = 0xc8a060, ROPE = 0x9a6830, TIE = 0x5a3f18;
  const LABEL = 0xe8d8b0, LABEL_D = 0xb8a878, INK = 0x382818;
  // 4 stacked rings, each rendered as a 3x3 weave grid
  const sackRings: [number, number, number][] = [
    [0.05, 0.18, 0.16], [0.13, 0.20, 0.17],
    [0.21, 0.18, 0.15], [0.28, 0.15, 0.13],
  ];
  for (let r = 0; r < sackRings.length; r++) {
    const [ry, hx, hz] = sackRings[r];
    for (let cx = -1; cx <= 1; cx++) {
      for (let cz = -1; cz <= 1; cz++) {
        const px = cx * hx * 0.66;
        const pz = cz * hz * 0.66;
        if (r === 3 && Math.abs(cx) === 1 && Math.abs(cz) === 1 && rng() < 0.6) continue;
        const weaveBase = (cx + cz + r) & 1 ? TAN : TAN_LT;
        const weave = rng() < 0.18 ? BROWN : rng() < 0.10 ? TAN_DK : weaveBase;
        b.add(hx * 0.55, 0.080, hz * 0.55, px, ry, pz, weave);
      }
    }
    b.add(hx * 1.05, 0.014, hz * 1.05, 0, ry - 0.043, 0, TAN_DK);
  }
  // Dark fibres / loose weave specks
  for (let i = 0; i < 8; i++) {
    const a = rng() * Math.PI * 2;
    const r0 = 0.10 + rng() * 0.06;
    b.add(0.022, 0.016, 0.022, Math.cos(a) * r0, 0.06 + rng() * 0.22, Math.sin(a) * r0,
          rng() < 0.5 ? BROWN : BROWN_D);
  }
  // Faded label patch on the +z face
  b.add(0.14, 0.10, 0.014, 0.00, 0.16, 0.18, LABEL);
  b.add(0.14, 0.014, 0.014, 0.00, 0.21, 0.182, LABEL_D);
  b.add(0.14, 0.014, 0.014, 0.00, 0.11, 0.182, LABEL_D);
  b.add(0.08, 0.014, 0.012, 0.00, 0.18, 0.190, INK);
  b.add(0.06, 0.014, 0.012, 0.00, 0.14, 0.190, INK);
  b.add(0.018, 0.018, 0.012, 0.05, 0.16, 0.190, INK);
  for (let i = -1; i <= 1; i++) {
    b.add(0.010, 0.010, 0.010, i * 0.06, 0.21, 0.188, INK);
    b.add(0.010, 0.010, 0.010, i * 0.06, 0.11, 0.188, INK);
  }
  // Gathered neck below the tie
  b.add(0.18, 0.05, 0.16, 0.00, 0.32, 0.00, TAN_DK);
  b.add(0.16, 0.05, 0.14, 0.00, 0.36, 0.00, BROWN);
  // Rope tie — three wraps + knot + trailing ends
  b.add(0.20, 0.020, 0.18, 0.00, 0.388, 0.00, ROPE);
  b.add(0.20, 0.020, 0.18, 0.00, 0.408, 0.00, ROPE_LT);
  b.add(0.20, 0.020, 0.18, 0.00, 0.428, 0.00, ROPE);
  b.add(0.05, 0.05, 0.04, 0.00, 0.41, 0.10, TIE);
  b.add(0.030, 0.030, 0.030, 0.020, 0.42, 0.115, ROPE_LT);
  b.add(0.016, 0.07, 0.016, -0.030, 0.37, 0.10, ROPE);
  b.add(0.016, 0.04, 0.016, -0.040, 0.33, 0.11, ROPE_LT);
  b.add(0.016, 0.06, 0.016,  0.040, 0.36, 0.10, ROPE_LT);
  // Loose fabric flap above the tie
  b.add(0.10, 0.06, 0.08, -0.05, 0.47, 0.04, TAN);
  b.add(0.08, 0.06, 0.08,  0.05, 0.46, -0.04, TAN_DK);
  b.add(0.06, 0.05, 0.06,  0.00, 0.50, 0.00, BROWN);
  // Stray seeds spilling at the base
  b.add(0.024, 0.014, 0.020, -0.18, 0.012, 0.10, 0xd8a050);
  b.add(0.020, 0.012, 0.020,  0.16, 0.010, -0.06, 0xc89040);
  b.add(0.018, 0.012, 0.018,  0.20, 0.010, 0.04, 0xd8a050);
  const mesh = b.build();
  mesh.name = 'seed-prop';
  return mesh;
}

/** Voxel scythe — long curved blade approximated by a staircase of voxels +
 *  a wooden shaft. The blade rests on the ground, parallel to the cursor,
 *  ready to sweep through mature crops. */
export function makeScythe(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(947);
  const STEEL_DK = 0x4a4e58, STEEL = 0x7a8088, STEEL_HI = 0x9aa0a8, EDGE = 0xd0d8e0;
  const RUST = 0x7a4020, RUST_DK = 0x4a2410;
  const WOOD_LT = 0xb88458, WOOD = 0x9c6c44, WOOD_DK = 0x6a4828, WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18, LEATH_D = 0x3a2410, RIVET = 0x303040;
  // Curved blade — 11-step sweep with under-shadow + top highlight + edge
  const steps: [number, number, number][] = [
    [-0.04, 0.020, -0.01], [ 0.04, 0.020,  0.00],
    [ 0.12, 0.022,  0.01], [ 0.20, 0.026,  0.02],
    [ 0.28, 0.032,  0.03], [ 0.34, 0.040,  0.05],
    [ 0.40, 0.052,  0.06], [ 0.45, 0.066,  0.07],
    [ 0.49, 0.084,  0.07], [ 0.52, 0.104,  0.08],
    [ 0.54, 0.126,  0.08],
  ];
  for (const [x, y, z] of steps) {
    b.add(0.085, 0.014, 0.06, x, y, z, STEEL_DK);
    b.add(0.085, 0.020, 0.06, x, y + 0.017, z, STEEL);
    b.add(0.085, 0.012, 0.05, x, y + 0.033, z, STEEL_HI);
    b.add(0.085, 0.006, 0.025, x, y + 0.042, z + 0.022, EDGE);
  }
  // Rust + dirt patches on the blade
  b.add(0.04, 0.014, 0.04, 0.10, 0.030, 0.00, RUST);
  b.add(0.030, 0.012, 0.030, 0.30, 0.038, 0.03, RUST_DK);
  b.add(0.025, 0.010, 0.025, 0.42, 0.058, 0.05, RUST);
  // Iron attachment collar with bolts (blade ↔ snath)
  b.add(0.10, 0.10, 0.10, 0.55, 0.180, 0.08, STEEL_DK);
  b.add(0.10, 0.05, 0.10, 0.55, 0.225, 0.08, STEEL_HI);
  b.add(0.020, 0.020, 0.020, 0.51, 0.180, 0.13, EDGE);
  b.add(0.020, 0.020, 0.020, 0.59, 0.180, 0.13, EDGE);
  b.add(0.020, 0.020, 0.020, 0.55, 0.155, 0.13, RIVET);
  // Snath — wood-grain stripes with subtle curvature + knots
  const SNATH_SEG = 11;
  const grain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < SNATH_SEG; i++) {
    const t = i / (SNATH_SEG - 1);
    const curveX = 0.54 - Math.sin(t * Math.PI) * 0.03;
    const yy = 0.24 + i * 0.080;
    const zz = 0.10 + t * 0.42;
    const quads: [number, number, number][] = [
      [-0.020, -0.020, grain[(i + 0) & 3]],
      [ 0.020, -0.020, grain[(i + 1) & 3]],
      [-0.020,  0.020, grain[(i + 2) & 3]],
      [ 0.020,  0.020, grain[(i + 3) & 3]],
    ];
    for (const [qx, qz, c] of quads) {
      b.add(0.024, 0.080, 0.024, curveX + qx, yy, zz + qz, c);
    }
    if (rng() < 0.22) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? 0.042 : side === 1 ? -0.042 : 0;
      const kz = side === 2 ? 0.042 : side === 3 ? -0.042 : 0;
      b.add(0.020, 0.024, 0.020, curveX + kx, yy, zz + kz, WOOD_VD);
    }
  }
  // Lower leather grip with seam
  b.add(0.072, 0.070, 0.072, 0.54, 0.44, 0.20, LEATHER);
  b.add(0.075, 0.020, 0.075, 0.54, 0.475, 0.20, LEATH_D);
  b.add(0.072, 0.025, 0.072, 0.54, 0.41, 0.20, LEATH_D);
  // Upper hand grip near the top end of the snath
  b.add(0.070, 0.060, 0.070, 0.54, 0.92, 0.50, LEATHER);
  b.add(0.072, 0.018, 0.072, 0.54, 0.95, 0.50, LEATH_D);
  // Perpendicular peen-grip nib handle
  b.add(0.18, 0.040, 0.040, 0.66, 0.86, 0.46, WOOD_DK);
  b.add(0.18, 0.014, 0.040, 0.66, 0.882, 0.46, WOOD_LT);
  b.add(0.020, 0.020, 0.020, 0.74, 0.86, 0.46, RIVET);
  const mesh = b.build();
  mesh.name = 'scythe-prop';
  return mesh;
}

// ── Tool stands ──────────────────────────────────────────────────
// Each stand returns `base` (always shown — the rack itself) and `tool`
// (the prop sitting on the rack, hidden while the player is holding it).
// Origin at the floor center so the stand can be positioned on a tile by
// setting its `.position` to the tile world coords.

function makeSeedBagStand(): { base: THREE.Object3D; tool: THREE.Object3D } {
  const baseB = new VoxelBuilder();
  const rng = rngFn(421);
  const W_LT = 0xb88458, W = COL.wood, W_DK = COL.woodDk, W_VD = 0x4a3018;
  const NAIL = 0x303040, KNOT = 0x3a2010;
  // Pallet — 3 weathered stacked planks each built from 3 sub-strips so the
  // wood grain seam shows along the plank length, plus end-grain darker tips.
  for (let i = 0; i < 3; i++) {
    const py = 0.03 + i * 0.06;
    const pz = -0.16 + i * 0.16;
    const base = i % 2 === 0 ? W : W_DK;
    // 3 grain strips along x
    baseB.add(0.60, 0.06, 0.06, 0, py, pz - 0.06, base);
    baseB.add(0.60, 0.06, 0.06, 0, py, pz,        i % 2 === 0 ? W_LT : W);
    baseB.add(0.60, 0.06, 0.06, 0, py, pz + 0.06, base);
    // End-grain darkening
    baseB.add(0.04, 0.06, 0.18, -0.28, py, pz, W_VD);
    baseB.add(0.04, 0.06, 0.18,  0.28, py, pz, W_VD);
    // Nails at each end
    baseB.add(0.020, 0.014, 0.020, -0.25, py + 0.03, pz, NAIL);
    baseB.add(0.020, 0.014, 0.020,  0.25, py + 0.03, pz, NAIL);
    // Random knot speck
    if (rng() < 0.7) {
      const kx = (rng() - 0.5) * 0.40;
      baseB.add(0.025, 0.018, 0.025, kx, py + 0.03, pz, KNOT);
    }
  }
  const toolB = new VoxelBuilder();
  // Three sacks stacked / leaning — with weave shading + rope ties
  const TAN_LT = 0xd4a878, TAN = 0xc09058, TAN_DK = 0xa07840;
  const BROWN = 0x8a5828, ROPE = 0x9a6830, TIE = 0x5a3f18;
  // Sack A (left): rounded mound
  toolB.add(0.26, 0.08, 0.22, -0.14, 0.22, 0,    TAN);
  toolB.add(0.22, 0.06, 0.20, -0.14, 0.27, -0.01, TAN_LT);
  toolB.add(0.20, 0.04, 0.18, -0.14, 0.30, -0.02, TAN_DK);
  toolB.add(0.10, 0.020, 0.10, -0.14, 0.33, -0.02, ROPE);
  toolB.add(0.05, 0.05, 0.04, -0.14, 0.345, 0.04, TIE);
  // Sack B (right): same but tilted
  toolB.add(0.24, 0.08, 0.20,  0.14, 0.22, -0.04, TAN);
  toolB.add(0.20, 0.05, 0.18,  0.14, 0.28, -0.05, TAN_DK);
  toolB.add(0.10, 0.020, 0.10, 0.14, 0.32, -0.05, ROPE);
  toolB.add(0.05, 0.05, 0.04,  0.14, 0.335, 0.01, TIE);
  // Sack C (top center): smaller capping sack
  toolB.add(0.20, 0.06, 0.16,  0.00, 0.34,  0.06, TAN);
  toolB.add(0.16, 0.04, 0.13,  0.00, 0.38,  0.06, TAN_LT);
  toolB.add(0.08, 0.020, 0.08, 0.00, 0.41,  0.06, ROPE);
  toolB.add(0.04, 0.04, 0.04,  0.00, 0.42,  0.12, TIE);
  // Loose weave / dirt specks
  toolB.add(0.022, 0.016, 0.022, -0.04, 0.25,  0.10, BROWN);
  toolB.add(0.022, 0.014, 0.022,  0.20, 0.26, -0.06, BROWN);
  toolB.add(0.020, 0.014, 0.020, -0.20, 0.24,  0.06, BROWN);
  return { base: baseB.build(), tool: toolB.build() };
}

function makeHoeStand(): { base: THREE.Object3D; tool: THREE.Object3D } {
  const baseB = new VoxelBuilder();
  const rng = rngFn(587);
  const W_LT = 0xb88458, W = COL.wood, W_DK = COL.woodDk, W_VD = 0x4a3018;
  const NAIL = 0x303040, KNOT = 0x3a2010;
  // Floor plank — split into 3 grain strips along x
  for (let s = -1; s <= 1; s++) {
    baseB.add(0.50, 0.04, 0.06, 0, 0.02, s * 0.07, s & 1 ? W_LT : W);
  }
  // End-grain darker tips
  baseB.add(0.04, 0.04, 0.20, -0.24, 0.02, 0, W_VD);
  baseB.add(0.04, 0.04, 0.20,  0.24, 0.02, 0, W_VD);
  // Knots on the plank
  baseB.add(0.024, 0.020, 0.024, -0.08, 0.035, 0.02, KNOT);
  baseB.add(0.020, 0.018, 0.020,  0.12, 0.035, -0.04, KNOT);
  // Nails at each end
  for (const nx of [-0.22, 0.22]) {
    baseB.add(0.018, 0.014, 0.018, nx, 0.045, -0.07, NAIL);
    baseB.add(0.018, 0.014, 0.018, nx, 0.045,  0.07, NAIL);
  }
  // Upright posts — built from grain stripes
  for (const px of [-0.20, 0.20]) {
    for (let i = 0; i < 5; i++) {
      const py = 0.06 + i * 0.10;
      baseB.add(0.024, 0.10, 0.024, px - 0.012, py, -0.062, W);
      baseB.add(0.024, 0.10, 0.024, px + 0.012, py, -0.062, W_DK);
      baseB.add(0.024, 0.10, 0.024, px - 0.012, py, -0.038, i & 1 ? W_LT : W);
      baseB.add(0.024, 0.10, 0.024, px + 0.012, py, -0.038, W_DK);
      if (rng() < 0.18) baseB.add(0.022, 0.020, 0.022, px, py, -0.062 + (rng() < 0.5 ? -0.012 : 0.012), KNOT);
    }
  }
  // Top rail — grain stripes
  baseB.add(0.50, 0.024, 0.05, 0, 0.510, -0.05, W);
  baseB.add(0.50, 0.024, 0.05, 0, 0.534, -0.05, W_LT);
  baseB.add(0.50, 0.012, 0.05, 0, 0.500, -0.05, W_VD);
  // Crossed joinery pegs
  baseB.add(0.020, 0.020, 0.020, -0.20, 0.520, -0.025, KNOT);
  baseB.add(0.020, 0.020, 0.020,  0.20, 0.520, -0.025, KNOT);
  // ── Hoe — detailed leaning hoe, head down
  const toolB = new VoxelBuilder();
  const IRON_DK = 0x2a2a2e, IRON = 0x3a3a42, IRON_HI = 0x6a6a72, EDGE = 0x9aa0a8;
  const WOOD_LT = 0xb88458, WOOD = 0x9c6c44, WOOD_DK = 0x6a4828, WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18;
  // Curved blade at the bottom
  toolB.add(0.22, 0.05, 0.08, -0.12, 0.07, 0.06, IRON);
  toolB.add(0.22, 0.025, 0.06, -0.12, 0.105, 0.06, IRON_HI);
  toolB.add(0.22, 0.012, 0.04, -0.12, 0.040, 0.06, IRON_DK);
  toolB.add(0.06, 0.014, 0.04, -0.12, 0.12, 0.10, EDGE);
  // Rust speck
  toolB.add(0.030, 0.014, 0.020, -0.05, 0.07, 0.06, 0x7a4020);
  // Iron collar with rivets
  toolB.add(0.10, 0.06, 0.06, -0.12, 0.14, 0.06, IRON_DK);
  toolB.add(0.018, 0.018, 0.018, -0.16, 0.14, 0.08, EDGE);
  toolB.add(0.018, 0.018, 0.018, -0.08, 0.14, 0.08, EDGE);
  // Leaning haft — wood-grain stripes
  const wgrain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < 7; i++) {
    const t = i / 6;
    const cx = -0.12 + t * 0.28;
    const cy = 0.18 + t * 0.34;
    const cz = 0.06 - t * 0.10;
    toolB.add(0.026, 0.08, 0.026, cx - 0.012, cy, cz - 0.012, wgrain[(i + 0) & 3]);
    toolB.add(0.026, 0.08, 0.026, cx + 0.012, cy, cz - 0.012, wgrain[(i + 1) & 3]);
    toolB.add(0.026, 0.08, 0.026, cx - 0.012, cy, cz + 0.012, wgrain[(i + 2) & 3]);
    toolB.add(0.026, 0.08, 0.026, cx + 0.012, cy, cz + 0.012, wgrain[(i + 3) & 3]);
  }
  // Leather end-cap grip
  toolB.add(0.07, 0.06, 0.07, 0.16, 0.52, -0.04, LEATHER);
  toolB.add(0.072, 0.018, 0.072, 0.16, 0.55, -0.04, 0x3a2410);
  return { base: baseB.build(), tool: toolB.build() };
}

function makeWaterCanStand(): { base: THREE.Object3D; tool: THREE.Object3D } {
  const baseB = new VoxelBuilder();
  const rng = rngFn(647);
  const W_LT = 0xb88458, W = COL.wood, W_DK = COL.woodDk, W_VD = 0x4a3018;
  const NAIL = 0x303040, KNOT = 0x3a2010;
  // Four turned legs with band shading + a brace plank between each pair
  for (const [px, pz] of [[-0.18, -0.18], [0.18, -0.18], [-0.18, 0.18], [0.18, 0.18]]) {
    for (let i = 0; i < 4; i++) {
      const py = 0.04 + i * 0.06;
      const c = i % 2 === 0 ? W : W_DK;
      baseB.add(0.054, 0.06, 0.054, px, py, pz, c);
      // Subtle ring/turning detail
      if (i === 1 || i === 3) baseB.add(0.064, 0.014, 0.064, px, py + 0.024, pz, W_VD);
    }
  }
  // Lower stretcher (front-back) between the legs
  baseB.add(0.40, 0.024, 0.034, 0, 0.10, -0.18, W_DK);
  baseB.add(0.40, 0.024, 0.034, 0, 0.10,  0.18, W_DK);
  baseB.add(0.034, 0.024, 0.40, -0.18, 0.10, 0, W_DK);
  baseB.add(0.034, 0.024, 0.40,  0.18, 0.10, 0, W_DK);
  // Tabletop — 3 plank strips with grain seams + nailed corners
  for (let s = -1; s <= 1; s++) {
    baseB.add(0.46, 0.05, 0.15, 0, 0.30, s * 0.155, s & 1 ? W_LT : W);
  }
  // End-grain darkening on the table edges
  baseB.add(0.04, 0.05, 0.46, -0.21, 0.30, 0, W_VD);
  baseB.add(0.04, 0.05, 0.46,  0.21, 0.30, 0, W_VD);
  baseB.add(0.46, 0.05, 0.04, 0, 0.30, -0.21, W_VD);
  baseB.add(0.46, 0.05, 0.04, 0, 0.30,  0.21, W_VD);
  // Nails at the 4 corners of the top
  for (const [nx, nz] of [[-0.18, -0.18], [0.18, -0.18], [-0.18, 0.18], [0.18, 0.18]]) {
    baseB.add(0.018, 0.014, 0.018, nx, 0.325, nz, NAIL);
  }
  // Knot specks on the tabletop
  for (let i = 0; i < 3; i++) {
    const kx = (rng() - 0.5) * 0.36;
    const kz = (rng() - 0.5) * 0.36;
    baseB.add(0.024, 0.018, 0.024, kx, 0.325, kz, KNOT);
  }
  // ── Watering can on the table (origin lifted by table-top height)
  const toolB = new VoxelBuilder();
  const SHADOW = 0x3a4a58, BODY = 0x6c8aa0, BODY_M = 0x809cb0;
  const RIM = 0x4c6680, HIGHL = 0xa8c0d0;
  const RUST = 0x9a5028, STAIN = 0x4a3018, RIVET = 0x303840;
  // Body — 4 stacked rings, with panel-shading + rivet seams
  for (let i = 0; i < 4; i++) {
    const w = 0.30 - i * 0.014;
    const y = 0.37 + i * 0.05;
    const quads: [number, number, number][] = [
      [-w / 2 + 0.04, -w / 2 + 0.04, i % 2 === 0 ? BODY : BODY_M],
      [ w / 2 - 0.04, -w / 2 + 0.04, BODY_M],
      [-w / 2 + 0.04,  w / 2 - 0.04, BODY_M],
      [ w / 2 - 0.04,  w / 2 - 0.04, i % 2 === 0 ? SHADOW : BODY],
    ];
    for (const [qx, qz, c] of quads) {
      toolB.add(w / 2 + 0.02, 0.05, w / 2 + 0.02, qx, y, qz, c);
    }
    // Rim shadows / highlights
    if (i === 0) toolB.add(w + 0.02, 0.014, w + 0.02, 0, y - 0.030, 0, SHADOW);
    if (i === 3) toolB.add(w + 0.02, 0.014, w + 0.02, 0, y + 0.030, 0, HIGHL);
    // Rivets along the seam
    if (i % 2 === 0) {
      toolB.add(0.014, 0.014, 0.014,  w / 2 + 0.010, y, 0, RIVET);
      toolB.add(0.014, 0.014, 0.014, -w / 2 - 0.010, y, 0, RIVET);
    }
  }
  // Spout — stepped pipe with collar + flared rose
  toolB.add(0.06, 0.06, 0.06, 0, 0.40, 0.16, RIM);
  toolB.add(0.05, 0.05, 0.06, 0, 0.41, 0.22, BODY);
  toolB.add(0.04, 0.04, 0.06, 0, 0.42, 0.28, BODY_M);
  toolB.add(0.12, 0.09, 0.04, 0, 0.42, 0.32, HIGHL);
  // Rose perforations (3x3)
  for (let gy = -1; gy <= 1; gy++) {
    for (let gx = -1; gx <= 1; gx++) {
      toolB.add(0.012, 0.012, 0.012, gx * 0.034, 0.42 + gy * 0.024, 0.340, RIM);
    }
  }
  // Handle — riveted arch
  toolB.add(0.04, 0.10, 0.04, -0.12, 0.55, 0, RIM);
  toolB.add(0.04, 0.10, 0.04,  0.12, 0.55, 0, RIM);
  toolB.add(0.014, 0.014, 0.014, -0.12, 0.50, 0, RIVET);
  toolB.add(0.014, 0.014, 0.014,  0.12, 0.50, 0, RIVET);
  toolB.add(0.28, 0.040, 0.04, 0, 0.61, 0, BODY);
  toolB.add(0.28, 0.014, 0.04, 0, 0.628, 0, HIGHL);
  // Rust streak + water stain on the body
  toolB.add(0.030, 0.024, 0.020, -0.14, 0.42, 0.06, RUST);
  toolB.add(0.014, 0.06, 0.014,  0.13, 0.42, -0.06, STAIN);
  return { base: baseB.build(), tool: toolB.build() };
}

function makeAxeStand(): { base: THREE.Object3D; tool: THREE.Object3D } {
  const baseB = new VoxelBuilder();
  const rng = rngFn(701);
  const BARK    = COL.trunk;
  const BARK_DK = COL.trunkDk;
  const BARK_LT = 0x7c5a32;
  const HEART   = 0xc4986a;
  const HEART_D = 0x9c7048;
  const RING    = 0x6a4828;
  const KNOT    = 0x3a2010;
  const MOSS    = 0x4a7028;
  // Chopping block — stacked bark rings, each as 4 sub-voxels for visible seams
  for (let i = 0; i < 4; i++) {
    const w = 0.46 - i * 0.02;
    const py = 0.04 + i * 0.08;
    const palette = [BARK, BARK_DK, BARK_LT, BARK_DK];
    const quads: [number, number][] = [[-w * 0.25, -w * 0.25], [w * 0.25, -w * 0.25], [-w * 0.25, w * 0.25], [w * 0.25, w * 0.25]];
    for (let q = 0; q < 4; q++) {
      baseB.add(w * 0.55, 0.08, w * 0.55, quads[q][0], py, quads[q][1], palette[(q + i) & 3]);
    }
    // Vertical bark seam (a knot)
    if (rng() < 0.6) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? w * 0.45 : side === 1 ? -w * 0.45 : 0;
      const kz = side === 2 ? w * 0.45 : side === 3 ? -w * 0.45 : 0;
      baseB.add(0.034, 0.07, 0.034, kx, py, kz, KNOT);
    }
  }
  // Concentric growth rings on the top — outer to inner
  baseB.add(0.40, 0.012, 0.40, 0, 0.36, 0, HEART_D);
  baseB.add(0.32, 0.018, 0.32, 0, 0.37, 0, HEART);
  baseB.add(0.24, 0.018, 0.24, 0, 0.378, 0, HEART_D);
  baseB.add(0.16, 0.018, 0.16, 0, 0.385, 0, HEART);
  baseB.add(0.08, 0.018, 0.08, 0, 0.392, 0, RING);
  // Central dark heart
  baseB.add(0.04, 0.014, 0.04, 0, 0.398, 0, KNOT);
  // Chip marks / hack notches on the top
  baseB.add(0.06, 0.012, 0.030, 0.10, 0.38, 0.12, KNOT);
  baseB.add(0.030, 0.010, 0.060, -0.14, 0.38, -0.10, KNOT);
  // Moss patch creeping up one side
  baseB.add(0.060, 0.06, 0.020, -0.21, 0.20, 0.10, MOSS);
  baseB.add(0.030, 0.030, 0.030, -0.21, 0.26, 0.12, MOSS);
  // ── Axe — buried in the stump, haft pointing up-back
  const toolB = new VoxelBuilder();
  const STEEL_DK = 0x4a4e58, STEEL = 0x6a6e76, STEEL_HI = 0x9aa0a8, EDGE = 0xd0d8e0;
  const RUST = 0x7a4020, RIVET = 0x303040;
  const WOOD_LT = 0xb88458, WOOD = 0x9c6c44, WOOD_DK = 0x6a4828, WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18;
  // Head — triangular with bevel: shadow, body, highlight, edge
  toolB.add(0.18, 0.10, 0.30, 0.00, 0.40, 0.00, STEEL_DK);
  toolB.add(0.16, 0.06, 0.30, 0.00, 0.44, 0.00, STEEL);
  toolB.add(0.14, 0.024, 0.30, 0.00, 0.48, 0.00, STEEL_HI);
  toolB.add(0.04, 0.08, 0.36, 0.10, 0.42, 0.00, EDGE);
  toolB.add(0.030, 0.014, 0.040, 0.13, 0.42, 0.16, EDGE);   // cutting tip
  // Worn edge dimples + rust
  toolB.add(0.030, 0.014, 0.024, 0.12, 0.42, -0.10, RUST);
  toolB.add(0.024, 0.012, 0.024, -0.04, 0.45, 0.10, RUST);
  // Eye socket where the haft passes through (dark, with metal wedge)
  toolB.add(0.06, 0.06, 0.06, -0.05, 0.42, 0.00, 0x101010);
  toolB.add(0.020, 0.020, 0.020, -0.05, 0.42, 0.08, EDGE);   // wedge
  toolB.add(0.020, 0.020, 0.020, -0.05, 0.42, -0.08, EDGE);
  // Haft — wood-grain stripes along the curved path
  const grain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < 8; i++) {
    const t = i / 7;
    const cx = -0.05 - t * 0.10;
    const cy = 0.44 + i * 0.075;
    const cz = 0.06 + t * 0.18;
    toolB.add(0.026, 0.075, 0.026, cx - 0.012, cy, cz - 0.012, grain[(i + 0) & 3]);
    toolB.add(0.026, 0.075, 0.026, cx + 0.012, cy, cz - 0.012, grain[(i + 1) & 3]);
    toolB.add(0.026, 0.075, 0.026, cx - 0.012, cy, cz + 0.012, grain[(i + 2) & 3]);
    toolB.add(0.026, 0.075, 0.026, cx + 0.012, cy, cz + 0.012, grain[(i + 3) & 3]);
    // Grip-wrapped section in the middle
    if (i >= 3 && i <= 4) {
      toolB.add(0.066, 0.075, 0.066, cx, cy, cz, LEATHER);
      toolB.add(0.070, 0.014, 0.070, cx, cy + 0.030, cz, 0x3a2410);
    }
  }
  // Leather end-cap with rivet
  toolB.add(0.072, 0.05, 0.072, -0.15, 0.985, 0.24, LEATHER);
  toolB.add(0.020, 0.020, 0.020, -0.15, 1.000, 0.24, RIVET);
  return { base: baseB.build(), tool: toolB.build() };
}

/** Consolidated tool rack — replaces the four individual hoe / water /
 *  scythe / axe stands with a single wall-mounted cabinet. Clicking the
 *  rack opens the ToolRackPanel so the player picks which tool to grab. */
function makeToolRack(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(2027);
  const W_LT = 0xb88458, WOOD = COL.wood, DARK = COL.woodDk, W_VD = 0x4a3018;
  const NAIL = 0x303040, KNOT = 0x3a2010;
  // Floor plank — 3 grain strips with end-grain caps + nailed joinery
  for (let s = -1; s <= 1; s++) {
    b.add(1.00, 0.06, 0.10, 0, 0.03, s * 0.106, s & 1 ? W_LT : WOOD);
  }
  b.add(0.04, 0.06, 0.32, -0.49, 0.03, 0, W_VD);
  b.add(0.04, 0.06, 0.32,  0.49, 0.03, 0, W_VD);
  for (let k = 0; k < 4; k++) {
    const kx = (rng() - 0.5) * 0.80;
    const kz = (rng() - 0.5) * 0.24;
    b.add(0.024, 0.020, 0.024, kx, 0.045, kz, KNOT);
  }
  for (const nx of [-0.42, -0.14, 0.14, 0.42]) {
    b.add(0.018, 0.014, 0.018, nx, 0.055, -0.12, NAIL);
    b.add(0.018, 0.014, 0.018, nx, 0.055,  0.12, NAIL);
  }
  // Side walls (vertical posts) — built from grain stripes with knots
  for (let i = 0; i < 7; i++) {
    const y = 0.08 + i * 0.10;
    for (const sign of [-1, 1] as const) {
      const px = sign * 0.48;
      b.add(0.04, 0.10, 0.04, px - 0.020, y, -0.13, i & 1 ? W_LT : WOOD);
      b.add(0.04, 0.10, 0.04, px + 0.020, y, -0.13, i & 1 ? WOOD : DARK);
      b.add(0.04, 0.10, 0.04, px - 0.020, y,  0.13, WOOD);
      b.add(0.04, 0.10, 0.04, px + 0.020, y,  0.13, DARK);
      b.add(0.04, 0.10, 0.04, px,         y,  0.00, DARK);
    }
    if (rng() < 0.15) {
      b.add(0.020, 0.020, 0.020, -0.48 + (rng() - 0.5) * 0.04, y, -0.10, KNOT);
    }
  }
  // Joinery nails at the post ↔ floor and post ↔ top junctions
  for (const px of [-0.48, 0.48]) {
    b.add(0.020, 0.016, 0.020, px, 0.075, -0.10, NAIL);
    b.add(0.020, 0.016, 0.020, px, 0.075,  0.10, NAIL);
    b.add(0.020, 0.016, 0.020, px, 0.745, -0.10, NAIL);
    b.add(0.020, 0.016, 0.020, px, 0.745,  0.10, NAIL);
  }
  // Back wall — slat planks with vertical grain stripes + knots
  for (let i = 0; i < 4; i++) {
    const x = -0.36 + i * 0.24;
    const baseCol = i % 2 === 0 ? 0xa07440 : 0x8b6423;
    b.add(0.066, 0.62, 0.04, x - 0.066, 0.40, -0.14, baseCol);
    b.add(0.066, 0.62, 0.04, x,         0.40, -0.14, i & 1 ? W_LT : baseCol);
    b.add(0.066, 0.62, 0.04, x + 0.066, 0.40, -0.14, W_VD);
    if (rng() < 0.55) {
      b.add(0.026, 0.026, 0.020, x + (rng() - 0.5) * 0.10, 0.30 + rng() * 0.30, -0.122, KNOT);
    }
  }
  // Slanted roof — stepped voxels with under-shadow + top highlight
  b.add(1.10, 0.05, 0.20, 0, 0.78,  0.02, 0xc8a55c);
  b.add(1.10, 0.014, 0.20, 0, 0.808,  0.02, 0xe0c078);
  b.add(1.10, 0.05, 0.20, 0, 0.84, -0.12, 0xa78240);
  b.add(1.10, 0.014, 0.20, 0, 0.868, -0.12, 0xc89c50);
  b.add(1.10, 0.012, 0.04, 0, 0.762, 0.10, W_VD);
  b.add(1.06, 0.05, 0.06, 0, 0.74,  0.10, DARK);
  for (let i = -4; i <= 4; i++) {
    b.add(0.06, 0.018, 0.020, i * 0.10, 0.756, 0.118, W_VD);
  }
  // Tool silhouettes hanging on the back wall — small voxel renditions
  // Hoe (far left): haft + iron head with shadow + edge highlight
  b.add(0.04, 0.40, 0.04, -0.36, 0.36, -0.10, 0x9c6c44);
  b.add(0.04, 0.40, 0.04, -0.345, 0.36, -0.10, 0xb88458);
  b.add(0.14, 0.06, 0.04, -0.36, 0.18, -0.10, 0x2a2a2e);
  b.add(0.14, 0.024, 0.04, -0.36, 0.21, -0.10, 0x6a6a72);
  b.add(0.024, 0.014, 0.020, -0.36, 0.21, -0.078, 0xd0d8e0);
  // Watering can (mid-left): body with rim shadow + spout + handle arch
  b.add(0.16, 0.12, 0.10, -0.10, 0.30, -0.10, 0x6c8aa0);
  b.add(0.16, 0.024, 0.10, -0.10, 0.36, -0.10, 0xa8c0d0);
  b.add(0.16, 0.014, 0.10, -0.10, 0.245, -0.10, 0x3a4a58);
  b.add(0.06, 0.06, 0.06, -0.10, 0.40, -0.10, 0x4c6680);
  b.add(0.04, 0.06, 0.10,  0.02, 0.30, -0.10, 0x6c8aa0);
  b.add(0.05, 0.05, 0.04,  0.06, 0.30, -0.10, 0xa8c0d0);
  // Scythe (mid-right): snath + curved blade + collar bolt
  b.add(0.04, 0.40, 0.04,  0.14, 0.36, -0.10, 0x9c6c44);
  b.add(0.04, 0.40, 0.04,  0.155, 0.36, -0.10, 0xb88458);
  b.add(0.06, 0.06, 0.05,  0.18, 0.18, -0.10, 0x4a4e58);
  b.add(0.06, 0.05, 0.05,  0.22, 0.20, -0.10, 0x7a8088);
  b.add(0.06, 0.04, 0.05,  0.26, 0.22, -0.10, 0x9aa0a8);
  b.add(0.10, 0.02, 0.04,  0.24, 0.24, -0.10, 0xd0d8e0);
  // Axe (far right): haft + head with edge highlight + worn tip
  b.add(0.04, 0.40, 0.04,  0.36, 0.36, -0.10, 0x9c6c44);
  b.add(0.04, 0.40, 0.04,  0.375, 0.36, -0.10, 0xb88458);
  b.add(0.12, 0.10, 0.04,  0.30, 0.22, -0.10, 0x4a4e58);
  b.add(0.10, 0.06, 0.04,  0.30, 0.24, -0.10, 0x6a6e76);
  b.add(0.06, 0.024, 0.04,  0.32, 0.27, -0.10, 0x9aa0a8);
  b.add(0.04, 0.07, 0.04,  0.26, 0.23, -0.10, 0xd0d8e0);
  b.add(0.024, 0.014, 0.020, 0.26, 0.20, -0.10, 0x7a4020);

  // Hanging tag-board labeling the rack
  b.add(0.06, 0.10, 0.02, -0.04, 0.66,  0.08, DARK);
  b.add(0.06, 0.014, 0.02, -0.04, 0.69,  0.08, W_LT);
  b.add(0.18, 0.10, 0.04,  0.05, 0.60,  0.08, WOOD);
  b.add(0.18, 0.014, 0.04,  0.05, 0.638, 0.08, W_LT);
  // Hooks where tools hang from the back wall
  for (const hx of [-0.36, -0.10, 0.18, 0.36]) {
    b.add(0.020, 0.024, 0.014, hx, 0.62, -0.118, 0x4a4e58);
    b.add(0.020, 0.014, 0.014, hx, 0.605, -0.106, 0x9aa0a8);
  }

  const mesh = b.build();
  mesh.name = 'toolrack';
  return mesh;
}

function makeScytheStand(): { base: THREE.Object3D; tool: THREE.Object3D } {
  const baseB = new VoxelBuilder();
  const rng = rngFn(811);
  const W_LT = 0xb88458, W = COL.wood, W_DK = COL.woodDk, W_VD = 0x4a3018;
  const NAIL = 0x303040, KNOT = 0x3a2010, HOOK = 0x4a4e58, HOOK_HI = 0x9aa0a8;
  // Floor plank — 3 grain strips along x
  for (let s = -1; s <= 1; s++) {
    baseB.add(0.40, 0.04, 0.05, 0, 0.02, s * 0.055, s & 1 ? W_LT : W);
  }
  baseB.add(0.04, 0.04, 0.16, -0.19, 0.02, 0, W_VD);
  baseB.add(0.04, 0.04, 0.16,  0.19, 0.02, 0, W_VD);
  baseB.add(0.020, 0.020, 0.020, -0.06, 0.035, 0.04, KNOT);
  baseB.add(0.020, 0.020, 0.020,  0.10, 0.035, -0.05, KNOT);
  baseB.add(0.018, 0.014, 0.018, -0.16, 0.045, 0.04, NAIL);
  baseB.add(0.018, 0.014, 0.018,  0.16, 0.045, -0.04, NAIL);
  // Tall post — 4 grain stripes the full length, knot bumps
  for (let i = 0; i < 16; i++) {
    const py = 0.08 + i * 0.05;
    const palette = [W_LT, W, W_DK, W_VD];
    baseB.add(0.022, 0.05, 0.022, -0.012, py, -0.012, palette[(i + 0) & 3]);
    baseB.add(0.022, 0.05, 0.022,  0.012, py, -0.012, palette[(i + 1) & 3]);
    baseB.add(0.022, 0.05, 0.022, -0.012, py,  0.012, palette[(i + 2) & 3]);
    baseB.add(0.022, 0.05, 0.022,  0.012, py,  0.012, palette[(i + 3) & 3]);
    if (rng() < 0.16) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? 0.030 : side === 1 ? -0.030 : 0;
      const kz = side === 2 ? 0.030 : side === 3 ? -0.030 : 0;
      baseB.add(0.018, 0.020, 0.018, kx, py, kz, KNOT);
    }
  }
  // Side hook arm — wooden then iron L-hook with rivet
  baseB.add(0.20, 0.040, 0.040, 0.10, 0.78, 0, W);
  baseB.add(0.20, 0.012, 0.040, 0.10, 0.802, 0, W_LT);
  baseB.add(0.04, 0.10, 0.04, 0.20, 0.74, 0, HOOK);
  baseB.add(0.04, 0.030, 0.04, 0.20, 0.69, 0.020, HOOK_HI);
  baseB.add(0.020, 0.020, 0.020, 0.20, 0.78, 0.020, NAIL);
  // Small tag plate
  baseB.add(0.08, 0.04, 0.012, 0.00, 0.10, 0.085, W_LT);
  baseB.add(0.060, 0.014, 0.014, 0.00, 0.11, 0.092, 0x382818);
  // ── Scythe hanging from the hook
  const toolB = new VoxelBuilder();
  const STEEL_DK = 0x4a4e58, STEEL = 0x7a8088, STEEL_HI = 0x9aa0a8, EDGE = 0xd0d8e0;
  const RUST = 0x7a4020;
  const WOOD_LT = 0xb88458, WOOD = 0x9c6c44, WOOD_DK = 0x6a4828, WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18;
  const grain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < 7; i++) {
    const t = i / 6;
    const cx = 0.20;
    const cy = 0.74 - i * 0.10;
    const cz = -0.04 + t * 0.10;
    toolB.add(0.024, 0.10, 0.024, cx - 0.012, cy, cz - 0.012, grain[(i + 0) & 3]);
    toolB.add(0.024, 0.10, 0.024, cx + 0.012, cy, cz - 0.012, grain[(i + 1) & 3]);
    toolB.add(0.024, 0.10, 0.024, cx - 0.012, cy, cz + 0.012, grain[(i + 2) & 3]);
    toolB.add(0.024, 0.10, 0.024, cx + 0.012, cy, cz + 0.012, grain[(i + 3) & 3]);
  }
  toolB.add(0.068, 0.08, 0.068, 0.20, 0.44, 0.02, LEATHER);
  toolB.add(0.070, 0.014, 0.070, 0.20, 0.48, 0.02, 0x3a2410);
  toolB.add(0.080, 0.06, 0.080, 0.20, 0.10, 0.06, STEEL_DK);
  toolB.add(0.020, 0.020, 0.020, 0.20, 0.10, 0.10, EDGE);
  toolB.add(0.020, 0.020, 0.020, 0.16, 0.10, 0.06, EDGE);
  const blade: [number, number, number][] = [
    [0.18, 0.08, 0.04], [0.12, 0.09, 0.05], [0.06, 0.12, 0.07],
    [0.00, 0.16, 0.08], [-0.06, 0.22, 0.07], [-0.10, 0.28, 0.06],
  ];
  for (const [x, y, z] of blade) {
    toolB.add(0.075, 0.020, 0.06, x, y, z, STEEL_DK);
    toolB.add(0.075, 0.026, 0.06, x, y + 0.020, z, STEEL);
    toolB.add(0.075, 0.014, 0.05, x, y + 0.040, z, STEEL_HI);
    toolB.add(0.075, 0.006, 0.025, x, y + 0.048, z + 0.020, EDGE);
  }
  toolB.add(0.024, 0.014, 0.024, 0.06, 0.13, 0.07, RUST);
  return { base: baseB.build(), tool: toolB.build() };
}

/** Small voxel weed tuft — used in plot mode for the pull-out minigame. */
/**
 * Detailed voxel wheat at growth stage 0-4. Modeled after the BBCH stages:
 *   0 = germination — soil mound, buried seed, pale coleoptile breaking through
 *   1 = leaf development — single thin stem with 3 narrow bright-green leaves
 *   2 = tillering — bushy clump of 4 stems with leaves fanning out at the base
 *   3 = heading — 2-3 tall stems, visible nodes, flag leaf, developing green spike
 *   4 = ripe — golden stems drooping under full grain heads with awns + grain bumps
 *
 * Geometry is sized for a plant silhouette (~0.06–0.12 wide) and heights grow
 * 0.04 → 0.32 across stages. With PLOT_SUB_DIM=16 the wheat naturally overlaps
 * neighbouring sub-cells, which reads as a denser packed field. `rng` is the
 * per-seed deterministic generator; `heightMul` scales overall size for
 * per-seed variance.
 */
function buildWheatModel(b: VoxelBuilder, stage: number, rng: () => number, heightMul: number): void {
  // Palette — biology-tuned. Young: bright sap-green. Mature: golden tan.
  const SEED      = 0xc89058;
  const SOIL      = 0x7a5828;
  const COLEO     = 0xb8d080;            // pale yellow-green sheath
  const LEAF_LT   = 0xa8d068;
  const LEAF_MD   = 0x90c050;
  const STEM_GR   = 0x80a040;
  const STEM_DK   = 0x6a8a30;
  const NODE      = 0x4a6a18;
  const HEAD_GR   = 0xb8c850;            // still-green head
  const STEM_DRY  = [0xc8a850, 0xb89438, 0xd0b860];
  const FLAG_DRY  = 0xc8b860;
  const HEAD_RIPE = 0xe8c850;
  const GRAIN_DK  = 0xb88838;
  const AWN       = 0xf0d878;

  switch (stage) {
    case 0: {
      // Germination — soil mound, buried seed, pale coleoptile tip
      b.add(0.060, 0.022, 0.050, 0, 0.011, 0, SOIL);
      const sx = (rng() - 0.5) * 0.015;
      const sz = (rng() - 0.5) * 0.015;
      b.add(0.026, 0.013, 0.026, sx, 0.007, sz, SEED);
      const tipH = (0.030 + rng() * 0.012) * heightMul;
      b.add(0.012, tipH, 0.012, 0, 0.023 + tipH / 2, 0, COLEO);
      return;
    }
    case 1: {
      // Leaf development — central thin stem + 3 narrow blade-leaves
      const H = 0.10 * heightMul;
      b.add(0.014, H, 0.014, 0, H / 2 + 0.012, 0, STEM_GR);
      const leafCount = 3;
      for (let i = 0; i < leafCount; i++) {
        const ang = (i / leafCount) * Math.PI * 2 + rng() * 0.6;
        const dx = Math.cos(ang) * 0.020;
        const dz = Math.sin(ang) * 0.020;
        const lh = (0.055 + rng() * 0.030) * heightMul;
        b.add(0.020, lh, 0.020, dx, 0.018 + lh / 2, dz, i % 2 ? LEAF_LT : LEAF_MD);
      }
      // A small lighter tip atop the central shoot
      b.add(0.012, 0.022 * heightMul, 0.012, 0, H + 0.005, 0, LEAF_LT);
      return;
    }
    case 2: {
      // Tillering — 4 stems forming a bushy clump + 5 leaves around the base
      const H = 0.16 * heightMul;
      const stems = 4;
      for (let i = 0; i < stems; i++) {
        const ang = (i / stems) * Math.PI * 2 + rng() * 0.35;
        const dx = Math.cos(ang) * 0.014;
        const dz = Math.sin(ang) * 0.014;
        const sh = H * (0.82 + rng() * 0.32);
        b.add(0.013, sh, 0.013, dx, sh / 2 + 0.012, dz, i < 2 ? STEM_GR : STEM_DK);
      }
      const leafCount = 5;
      for (let i = 0; i < leafCount; i++) {
        const ang = rng() * Math.PI * 2;
        const r0 = 0.012 + rng() * 0.014;
        const dx = Math.cos(ang) * r0;
        const dz = Math.sin(ang) * r0;
        const lh = (0.042 + rng() * 0.040) * heightMul;
        b.add(0.022, lh, 0.022, dx, 0.022 + lh / 2, dz, i % 2 ? LEAF_LT : LEAF_MD);
      }
      return;
    }
    case 3: {
      // Heading — 2-3 tall stems with node bands, flag leaf, developing spike
      const H = 0.24 * heightMul;
      const stems = 3 + (rng() < 0.5 ? 1 : 0);
      for (let i = 0; i < stems; i++) {
        const ang = (i / stems) * Math.PI * 2 + rng() * 0.3;
        const dx = Math.cos(ang) * 0.016;
        const dz = Math.sin(ang) * 0.016;
        const sh = H * (0.94 + rng() * 0.12);
        b.add(0.016, sh, 0.016, dx, sh / 2 + 0.012, dz, STEM_DK);
        b.add(0.020, 0.011, 0.020, dx, sh * 0.35, dz, NODE);
        b.add(0.020, 0.011, 0.020, dx, sh * 0.65, dz, NODE);
        // Developing seed head on each stem — green-yellow spike emerging
        const headBase = sh + 0.018;
        const headH = 0.054 * heightMul;
        b.add(0.026, headH, 0.026, dx, headBase + headH / 2, dz, HEAD_GR);
        // 3 short awns starting to extrude
        for (let a = 0; a < 3; a++) {
          const ax = (a - 1) * 0.010;
          b.add(0.005, 0.022 * heightMul, 0.005, dx + ax, headBase + headH + 0.012, dz, AWN);
        }
      }
      // Skirt of flag leaves — 4 wide blades partway up the stalks
      const flagH = 0.052 * heightMul;
      for (let i = 0; i < 4; i++) {
        const ang = (i / 4) * Math.PI * 2 + 0.4;
        const lx = Math.cos(ang) * 0.026;
        const lz = Math.sin(ang) * 0.026;
        b.add(0.032, flagH, 0.026, lx, H * 0.55 + flagH / 2, lz, i % 2 ? LEAF_LT : LEAF_MD);
      }
      // Low base leaves — fill the ground gap between neighbouring plants
      for (let i = 0; i < 4; i++) {
        const ang = ((i + 0.5) / 4) * Math.PI * 2;
        const lx = Math.cos(ang) * 0.040;
        const lz = Math.sin(ang) * 0.040;
        b.add(0.038, 0.028 * heightMul, 0.038, lx, 0.04, lz, i % 2 ? LEAF_LT : LEAF_MD);
      }
      return;
    }
    case 4: default: {
      // Ripe — tall golden stems + full grain heads with grain bumps + awns,
      // plus a generous skirt of base leaves so a fully-planted tile reads
      // as a dense field instead of separated stalks.
      const H = 0.28 * heightMul;
      const stems = 3 + (rng() < 0.5 ? 1 : 0);     // 3-4 stems per seed
      for (let i = 0; i < stems; i++) {
        const ang = (i / stems) * Math.PI * 2 + rng() * 0.3;
        const dx = Math.cos(ang) * 0.016;
        const dz = Math.sin(ang) * 0.016;
        const sh = H * (0.92 + rng() * 0.14);
        const stemCol = STEM_DRY[i % STEM_DRY.length];
        b.add(0.016, sh, 0.016, dx, sh / 2 + 0.012, dz, stemCol);
        // Darker nodes at 1/3 + 2/3 height for jointing detail
        b.add(0.020, 0.010, 0.020, dx, sh * 0.35, dz, NODE);
        b.add(0.020, 0.010, 0.020, dx, sh * 0.65, dz, NODE);
        // Full seed head — central golden spike + grain bumps + awns
        const headBase = sh + 0.014;
        const headH = 0.080 * heightMul;
        b.add(0.024, headH, 0.024, dx, headBase + headH / 2, dz, HEAD_RIPE);
        const grains = 4;
        for (let g = 0; g < grains; g++) {
          const gy = headBase + 0.010 + (g * (headH - 0.020)) / (grains - 1);
          const side = g % 4;
          const gx = side === 0 ? 0.013 : side === 1 ? -0.013 : 0;
          const gz = side === 2 ? 0.013 : side === 3 ? -0.013 : 0;
          b.add(0.010, 0.013, 0.010, dx + gx, gy, dz + gz, GRAIN_DK);
        }
        // 5 thin awns reaching up from the head — slight per-awn length jitter
        const awns = 5;
        for (let a = 0; a < awns; a++) {
          const ax = (a - 2) * 0.006;
          const ah = (0.040 + rng() * 0.022) * heightMul;
          b.add(0.004, ah, 0.004, dx + ax, headBase + headH + ah / 2 + 0.002, dz, AWN);
        }
      }
      // Yellow-tan flag leaves — a SKIRT of 5 broad blades radiating outward
      // from the centre, partly drooping. Fills the sub-cell visually so a
      // packed tile reads as wall-to-wall grain.
      const skirt = 5;
      for (let i = 0; i < skirt; i++) {
        const ang = (i / skirt) * Math.PI * 2 + rng() * 0.5;
        const lx = Math.cos(ang) * 0.030;
        const lz = Math.sin(ang) * 0.030;
        const lh = (0.050 + rng() * 0.025) * heightMul;
        b.add(0.034, lh, 0.034, lx, H * 0.50 + lh / 2, lz, i % 2 ? FLAG_DRY : LEAF_LT);
      }
      // Low base leaves — drooping fronds at ground level, broad + slightly
      // yellowed. These are what really fill the gaps between neighbouring
      // wheats in a packed tile.
      const base = 4;
      for (let i = 0; i < base; i++) {
        const ang = ((i + 0.5) / base) * Math.PI * 2;
        const lx = Math.cos(ang) * 0.042;
        const lz = Math.sin(ang) * 0.042;
        b.add(0.040, 0.025 * heightMul, 0.040, lx, 0.04, lz, i % 2 ? LEAF_MD : LEAF_LT);
      }
      return;
    }
  }
}

function makeWeed(): THREE.Object3D {
  // Wild weed tuft — chaotic stems with jaggy leaves and dead/yellow accents.
  const b = new VoxelBuilder();
  const rng = rngFn(3137 + 41);
  const GREENS = [0x3a5e20, 0x4a7a32, 0x5a8c38, 0x6a9a40, 0x80a050, 0x9bb854, 0xa0b860, 0x7c9a44];
  const DEAD = [0xc8b860, 0xc8c860, 0xa89438];
  const DARK = 0x2a4014;
  // Tangled root mat at the base
  for (let i = 0; i < 4; i++) {
    const ang = (i / 4) * Math.PI * 2 + rng() * 0.6;
    const r0 = rng() * 0.06;
    b.add(0.06, 0.04, 0.06, Math.cos(ang) * r0, 0.02, Math.sin(ang) * r0, DARK);
  }
  // 5-7 crooked stems splaying outward at random angles, each kinked
  const stems = 5 + Math.floor(rng() * 3);
  for (let i = 0; i < stems; i++) {
    const ang = rng() * Math.PI * 2;
    const lean = 0.08 + rng() * 0.12;
    const stemH = 0.16 + rng() * 0.14;
    const segH = stemH / 3;
    const bx = (rng() - 0.5) * 0.06;
    const bz = (rng() - 0.5) * 0.06;
    for (let s = 0; s < 3; s++) {
      const t = s / 2;
      const kink = (rng() - 0.5) * 0.04;
      const perp = ang + Math.PI / 2;
      const sx = bx + Math.cos(ang) * lean * t + Math.cos(perp) * kink;
      const sz = bz + Math.sin(ang) * lean * t + Math.sin(perp) * kink;
      const sy = segH * (s + 0.5) + 0.04;
      b.add(0.04, segH * 0.95, 0.04, sx, sy, sz, GREENS[Math.floor(rng() * GREENS.length)]);
    }
    // Jaggy leaves at 1-2 levels along the stem
    const leafLevels = rng() < 0.5 ? 2 : 1;
    for (let l = 0; l < leafLevels; l++) {
      const lt = 0.35 + l * 0.35 + rng() * 0.15;
      const lx = bx + Math.cos(ang) * lean * lt;
      const lz = bz + Math.sin(ang) * lean * lt;
      const ly = stemH * lt + 0.05;
      const perpAng = ang + (rng() < 0.5 ? Math.PI / 2 : -Math.PI / 2);
      const leafR = 0.05 + rng() * 0.03;
      b.add(0.05, 0.025, 0.04, lx + Math.cos(perpAng) * leafR * 0.6, ly, lz + Math.sin(perpAng) * leafR * 0.6, GREENS[Math.floor(rng() * GREENS.length)]);
      b.add(0.03, 0.020, 0.03, lx + Math.cos(perpAng) * leafR * 1.2, ly + 0.01, lz + Math.sin(perpAng) * leafR * 1.2, GREENS[Math.floor(rng() * GREENS.length)]);
      if (rng() < 0.5) {
        b.add(0.015, 0.025, 0.015, lx + Math.cos(perpAng) * leafR * 1.4, ly + 0.025, lz + Math.sin(perpAng) * leafR * 1.4, GREENS[Math.floor(rng() * GREENS.length)]);
      }
    }
    // Tip voxel — sometimes pale, sometimes dead/yellow for chaos
    const tipX = bx + Math.cos(ang) * lean;
    const tipZ = bz + Math.sin(ang) * lean;
    const tipCol = rng() < 0.25 ? DEAD[Math.floor(rng() * DEAD.length)] : GREENS[5 + Math.floor(rng() * 3)];
    b.add(0.035, 0.04, 0.035, tipX, stemH + 0.05, tipZ, tipCol);
  }
  // 3 broad jaggy rosette base leaves — dandelion-y wild feel
  for (let i = 0; i < 3; i++) {
    const ang = (i / 3) * Math.PI * 2 + rng() * 0.5;
    const r0 = 0.09 + rng() * 0.03;
    const lx = Math.cos(ang) * r0;
    const lz = Math.sin(ang) * r0;
    b.add(0.08, 0.025, 0.05, lx, 0.045, lz, GREENS[Math.floor(rng() * 4) + 1]);
    if (rng() < 0.6) {
      b.add(0.04, 0.020, 0.03, lx + Math.cos(ang) * 0.04, 0.05, lz + Math.sin(ang) * 0.04, GREENS[Math.floor(rng() * 4)]);
    }
  }
  // Flower crown — ALWAYS shown, and bright magenta/yellow so the weed
  // doesn't blend into the green lawn. This is the single most important
  // visual cue: tufts never have flowering parts, so a coloured pom-pom
  // at the top reads instantly as "not grass — pluck me".
  // Palette varies per weed (5 picks) but every option is a high-contrast
  // hue against the green ground.
  const FLOWER_PALETTE = [
    { core: 0xff4080, petal: 0xffd060 },  // magenta + gold
    { core: 0xffd040, petal: 0xfff8c0 },  // dandelion yellow
    { core: 0xff7050, petal: 0xffd0a0 },  // coral
    { core: 0xb060c8, petal: 0xffe0f0 },  // violet
    { core: 0xfff080, petal: 0xff80a8 },  // pale yellow + pink
  ];
  const flower = FLOWER_PALETTE[Math.floor(rng() * FLOWER_PALETTE.length)];
  // Centre bloom — bigger than the old seed burr (0.06 vs 0.035) so it
  // pokes well above the lawn.
  b.add(0.06, 0.05, 0.06, 0, 0.26, 0, flower.core);
  // 5 surrounding petal voxels in a ring around the core.
  for (let i = 0; i < 5; i++) {
    const a2 = (i / 5) * Math.PI * 2;
    const px = Math.cos(a2) * 0.05;
    const pz = Math.sin(a2) * 0.05;
    b.add(0.03, 0.03, 0.03, px, 0.265, pz, flower.petal);
  }
  // Optional top dot for an extra "flower" silhouette beat.
  if (rng() < 0.5) {
    b.add(0.025, 0.025, 0.025, 0, 0.30, 0, flower.petal);
  }
  return b.build();
}

// ── Storage piles (voxel renditions for the shelf bins) ─────────
// Each helper appends voxels to the shared builder, centered around `cx`,
// with quantity controlling the visible stack size. Discrete tiers keep
// things simple: 1-9 = small heap, 10-29 = medium, 30+ = full bin.

function makeWheatPile(b: VoxelBuilder, cx: number, n: number) {
  // Jute sacks: warm tan + shadow tone + dark base + highlight; black tie band;
  // loose grain trickling out of one corner.
  const JUTE     = [0xc09058, 0xb88450, 0xa87838, 0xcc9a64];
  const JUTE_D   = 0x8a6028;
  const TIE      = 0x2a1a08;          // black tie band
  const ROPE     = 0x5a3f18;          // rope ends
  const GRAIN    = 0xe8c878;
  const GRAIN_DK = 0xb88438;
  const big = n >= 30, med = n >= 10;
  const rng = rngFn(Math.floor(cx * 100) * 73 + n * 11 + 7);

  // Single bulging jute sack at (x,y,z). `rot=true` lays it on its side.
  const sack = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rot: boolean) => {
    const slabs = 4;
    for (let i = 0; i < slabs; i++) {
      const t = i / (slabs - 1);
      const bulge = Math.sin(t * Math.PI) * 0.22;       // bulges in the middle
      const ww = (rot ? sz : sx) * (0.82 + bulge);
      const dd = (rot ? sx : sz) * (0.82 + bulge);
      const hh = sy / slabs;
      const yy = y - sy / 2 + hh / 2 + i * hh;
      const col = JUTE[Math.floor(rng() * JUTE.length)];
      b.add(ww, hh * 1.02, dd, x, yy, z, col);
    }
    // Shadowed base where the sack settles
    b.add(sx * 0.86, sy * 0.18, sz * 0.86, x, y - sy / 2 + sy * 0.06, z, JUTE_D);
    // Cinched neck + black rope tie
    const topY = y + sy / 2;
    b.add(sx * 0.36, sy * 0.14, sz * 0.36, x, topY - sy * 0.04, z, JUTE_D);
    b.add(sx * 0.44, sy * 0.07, sz * 0.44, x, topY,           z, TIE);
    // Two stubby rope ends draping aside
    b.add(sx * 0.10, sy * 0.10, sz * 0.10, x + sx * 0.18, topY + sy * 0.03, z, ROPE);
    b.add(sx * 0.08, sy * 0.08, sz * 0.08, x + sx * 0.22, topY - sy * 0.02, z, ROPE);
  };

  // Front-center sack (always present)
  sack(cx, 0.10, 0.00, 0.22, 0.20, 0.20, false);

  // Loose grain trickling out the front-right (small heap of loose grain)
  for (let i = 0; i < 5; i++) {
    const ang = (i / 5) * Math.PI - Math.PI / 2;
    const r0 = 0.08 + rng() * 0.08;
    const gx = cx + 0.10 + Math.cos(ang) * r0 * 0.4;
    const gz = 0.08 + Math.sin(ang) * r0 * 0.4;
    const gc = rng() < 0.4 ? GRAIN_DK : GRAIN;
    b.add(0.035, 0.022, 0.035, gx, 0.012, gz, gc);
  }
  // Stray grains atop the front sack
  if (rng() < 0.7) b.add(0.024, 0.016, 0.024, cx - 0.04, 0.22, 0.04, GRAIN);
  if (rng() < 0.7) b.add(0.020, 0.014, 0.020, cx + 0.05, 0.22, -0.05, GRAIN_DK);

  if (med) {
    // Sack #2 (left, laid on its side), sack #3 (right, upright)
    sack(cx - 0.16, 0.09, -0.04, 0.18, 0.18, 0.20, true);
    sack(cx + 0.16, 0.10,  0.04, 0.18, 0.20, 0.20, false);
  }
  if (big) {
    // Tipped sack on top + a leaning side sack + sprinkle of straw
    sack(cx - 0.02, 0.28, -0.02, 0.18, 0.16, 0.18, false);
    sack(cx + 0.14, 0.27,  0.06, 0.14, 0.14, 0.16, true);
    for (let i = 0; i < 4; i++) {
      const tx = cx + (rng() - 0.5) * 0.18;
      const tz = (rng() - 0.5) * 0.18;
      b.add(0.06, 0.012, 0.014, tx, 0.38, tz, GRAIN);
    }
  }
}

function makeCornPile(b: VoxelBuilder, cx: number, n: number) {
  // Husked corn cobs: yellow kernels + lighter kernel highlights, peeled-back
  // green husk leaves, brown silk visible at one end. Cobs in mixed poses.
  const KERN    = [0xf4d258, 0xe8c850, 0xfae89a, 0xd8b840];
  const KERN_DK = 0xa88018;
  const HUSK    = [0x9cb24a, 0x84a040, 0xb0c460, 0x708830];
  const HUSK_DK = 0x506830;
  const SILK    = 0xc89858;
  const SILK_DK = 0x8a6428;
  const big = n >= 30, med = n >= 10;
  const rng = rngFn(Math.floor(cx * 100) * 89 + n * 17 + 11);

  // Single cob centered at (x,y,z). `axis='x'` lays it horizontally; `'y'` upright.
  const cob = (x: number, y: number, z: number, len: number, dia: number, axis: 'x' | 'y') => {
    const segs = 5;
    for (let i = 0; i < segs; i++) {
      const t = (i / (segs - 1)) - 0.5;             // -0.5 → +0.5 along axis
      // Cobs taper at both ends — narrower at tips
      const taper = 1 - Math.abs(t) * 0.35;
      const w = dia * taper;
      const cx_ = axis === 'x' ? x + t * len : x;
      const cy_ = axis === 'y' ? y + t * len : y;
      const cz_ = z;
      // Kernel rows alternate light/dark to show kernel pattern
      const kCol = (i % 2 === 0) ? KERN[Math.floor(rng() * 2)] : KERN[2 + Math.floor(rng() * 2)];
      const sx_ = axis === 'x' ? len / segs * 1.02 : w;
      const sy_ = axis === 'y' ? len / segs * 1.02 : w;
      const sz_ = w;
      b.add(sx_, sy_, sz_, cx_, cy_, cz_, kCol);
      // Dark kernel-row dots scattered on the cob's side
      if (rng() < 0.5) {
        b.add(0.012, 0.012, 0.012,
          axis === 'x' ? x + t * len : x + (rng() - 0.5) * w * 0.6,
          axis === 'y' ? y + t * len : y + (rng() - 0.5) * w * 0.6,
          z + w * 0.45, KERN_DK);
      }
    }
    // Peeled-back husk leaves at the base (3 short fronds)
    const baseT = -0.5;
    const baseX = axis === 'x' ? x + baseT * len : x;
    const baseY = axis === 'y' ? y + baseT * len : y;
    for (let j = 0; j < 3; j++) {
      const ang = (j / 3) * Math.PI * 2 + rng() * 0.3;
      const dx_ = Math.cos(ang) * dia * 0.55;
      const dz_ = Math.sin(ang) * dia * 0.55;
      const hcol = HUSK[Math.floor(rng() * HUSK.length)];
      b.add(0.040, 0.05, 0.040,
        baseX + (axis === 'x' ? -0.020 : dx_ * 0.6),
        baseY + (axis === 'y' ? -0.020 : 0),
        z + dz_ * 0.6, hcol);
    }
    // Husk shadow band at base
    b.add(dia * 0.95, dia * 0.45, dia * 0.95,
      baseX + (axis === 'x' ? -0.02 : 0),
      baseY + (axis === 'y' ? -0.02 : 0), z, HUSK_DK);
    // Brown silk tuft at the tip
    const tipT = 0.5;
    const tipX = axis === 'x' ? x + tipT * len + 0.02 : x;
    const tipY = axis === 'y' ? y + tipT * len + 0.02 : y;
    b.add(0.025, 0.025, 0.025, tipX, tipY, z, SILK);
    b.add(0.020, 0.018, 0.020,
      tipX + (axis === 'x' ? 0.018 : 0),
      tipY + (axis === 'y' ? 0.018 : 0),
      z + 0.012, SILK_DK);
  };

  // Front cob — upright, lightly leaning
  cob(cx, 0.16, 0.00, 0.18, 0.05, 'y');
  // A toppled cob lying in front
  cob(cx + 0.02, 0.05, 0.10, 0.18, 0.045, 'x');

  if (med) {
    // Two more cobs — one upright behind-left, one lying behind
    cob(cx - 0.10, 0.16, -0.04, 0.17, 0.045, 'y');
    cob(cx - 0.02, 0.05, -0.08, 0.18, 0.045, 'x');
  }
  if (big) {
    // Stacked layer of cobs on top + small spilled-kernel scatter at base
    cob(cx - 0.05, 0.16, 0.06, 0.16, 0.045, 'x');
    cob(cx + 0.08, 0.16, -0.06, 0.16, 0.045, 'x');
    cob(cx, 0.27, 0.02, 0.16, 0.045, 'x');
    for (let i = 0; i < 4; i++) {
      const ang = rng() * Math.PI * 2;
      const r0 = 0.12 + rng() * 0.06;
      const kx = cx + Math.cos(ang) * r0;
      const kz = Math.sin(ang) * r0;
      b.add(0.022, 0.018, 0.022, kx, 0.011, kz, rng() < 0.5 ? KERN[0] : KERN[2]);
    }
  }
}

function makeCarrotPile(b: VoxelBuilder, cx: number, n: number) {
  // Bundle of carrots tied with twine — orange roots tapering to a point,
  // bushy green tops, brown twine wrap around the middle.
  const ROOT    = [0xff8030, 0xe87028, 0xff9c4a, 0xd8601c];
  const ROOT_DK = 0xa84818;
  const TOP     = [0x4a9438, 0x60a030, 0x74c450, 0x5aa040];
  const TOP_DK  = 0x386822;
  const TWINE   = 0x8a6428;
  const TWINE_D = 0x5a3f18;
  const big = n >= 30, med = n >= 10;
  const rng = rngFn(Math.floor(cx * 100) * 53 + n * 13 + 19);

  // Single carrot: tapered orange root + leafy green top. `axis='x'` lays it.
  const carrot = (x: number, y: number, z: number, len: number, dia: number, axis: 'x' | 'y', tilt: number) => {
    const segs = 4;
    for (let i = 0; i < segs; i++) {
      const t = (i / (segs - 1));                     // 0 at top, 1 at tip
      const taper = 1 - t * 0.85;                     // tip is a point
      const w = dia * taper;
      const tx = axis === 'x' ? x + t * len : x + tilt * t * len * 0.3;
      const ty = axis === 'y' ? y - t * len : y + (axis === 'x' ? tilt * t * len * 0.2 : 0);
      const rc = ROOT[Math.floor(rng() * ROOT.length)];
      const sx_ = axis === 'x' ? len / segs * 1.02 : w;
      const sy_ = axis === 'y' ? len / segs * 1.02 : w;
      b.add(sx_, sy_, w, tx, ty, z, rc);
    }
    // Shadow band on the underside of the root
    if (axis === 'x') b.add(len * 0.7, dia * 0.4, dia * 0.7, x + len * 0.3, y - dia * 0.25, z, ROOT_DK);
    // Leafy green top — 4 fronds
    const topX = axis === 'x' ? x - len * 0.08 : x;
    const topY = y + dia * 0.5;
    for (let j = 0; j < 4; j++) {
      const ang = (j / 4) * Math.PI * 2 + rng() * 0.5;
      const fx = Math.cos(ang) * dia * 0.6;
      const fz = Math.sin(ang) * dia * 0.6;
      const fh = 0.045 + rng() * 0.025;
      const tcol = TOP[Math.floor(rng() * TOP.length)];
      b.add(0.022, fh, 0.022, topX + fx * 0.4, topY + fh * 0.5, z + fz * 0.4, tcol);
    }
    // Deep-green base where leaves meet root
    b.add(dia * 0.95, dia * 0.35, dia * 0.95, topX, topY - dia * 0.15, z, TOP_DK);
  };

  // Front bundle — 3 carrots side-by-side, lying with tops to the left
  carrot(cx - 0.04, 0.06, -0.04, 0.18, 0.045, 'x', 0.0);
  carrot(cx - 0.04, 0.06,  0.00, 0.18, 0.045, 'x', 0.0);
  carrot(cx - 0.04, 0.06,  0.04, 0.18, 0.045, 'x', 0.0);
  // Twine wrapped around the middle (two narrow bands)
  b.add(0.020, 0.05, 0.16, cx + 0.02, 0.075, 0.00, TWINE);
  b.add(0.014, 0.04, 0.14, cx + 0.02, 0.075, 0.00, TWINE_D);
  // A single loose carrot tipped slightly upright on top of the bundle
  carrot(cx + 0.06, 0.12, 0.00, 0.14, 0.040, 'x', 0.5);

  if (med) {
    // Second bundle stacked behind — 3 carrots cross-laid + twine band
    carrot(cx + 0.06, 0.13, -0.10, 0.16, 0.045, 'x', 0.0);
    carrot(cx + 0.06, 0.13, -0.06, 0.16, 0.045, 'x', 0.0);
    carrot(cx + 0.06, 0.13, -0.02, 0.16, 0.045, 'x', 0.0);
    b.add(0.018, 0.04, 0.14, cx + 0.10, 0.145, -0.06, TWINE);
    // A carrot leaning vertically with tops sticking up
    carrot(cx - 0.14, 0.18, 0.04, 0.16, 0.045, 'y', 0.0);
  }
  if (big) {
    // Tipped-over bundle on top — leaves spread out
    carrot(cx - 0.02, 0.20,  0.08, 0.15, 0.042, 'x', 0.0);
    carrot(cx - 0.02, 0.20,  0.12, 0.15, 0.042, 'x', 0.0);
    // A few stray fronds + roots scattered around the base
    for (let i = 0; i < 4; i++) {
      const ang = rng() * Math.PI * 2;
      const r0 = 0.10 + rng() * 0.08;
      const sx = cx + Math.cos(ang) * r0;
      const sz = Math.sin(ang) * r0;
      const fh = 0.03 + rng() * 0.02;
      b.add(0.018, fh, 0.018, sx, 0.012 + fh / 2, sz, rng() < 0.6 ? TOP[1] : TOP[2]);
    }
  }
}

function makeFlourPile(b: VoxelBuilder, cx: number, n: number) {
  // White linen sacks — taller + finer weave than wheat. Darker seams down the
  // side, brown stamp label on the front, and a dusting of flour leaking out.
  const LINEN    = [0xf0e8d0, 0xeae0c4, 0xfaf3df, 0xe0d4b8];
  const LINEN_D  = 0xb8a880;
  const SEAM     = 0x9a8a68;          // darker stitched seam
  const STAMP    = 0x6a4828;          // brown ink label
  const STAMP_LT = 0x8a6438;
  const TIE      = 0x3a2810;
  const FLOUR    = 0xfff8e8;
  const big = n >= 30, med = n >= 10;
  const rng = rngFn(Math.floor(cx * 100) * 41 + n * 23 + 5);

  // Linen sack — taller + narrower than jute. `rot=true` lays it sideways.
  const sack = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rot: boolean, stamp: boolean) => {
    const slabs = 5;                                    // more slabs = finer weave
    for (let i = 0; i < slabs; i++) {
      const t = i / (slabs - 1);
      // Less bulge than jute — linen sacks are stiffer
      const bulge = Math.sin(t * Math.PI) * 0.14;
      const ww = (rot ? sz : sx) * (0.88 + bulge);
      const dd = (rot ? sx : sz) * (0.88 + bulge);
      const hh = sy / slabs;
      const yy = y - sy / 2 + hh / 2 + i * hh;
      const col = LINEN[Math.floor(rng() * LINEN.length)];
      b.add(ww, hh * 1.02, dd, x, yy, z, col);
    }
    // Shadow base
    b.add(sx * 0.86, sy * 0.16, sz * 0.86, x, y - sy / 2 + sy * 0.05, z, LINEN_D);
    // Two vertical seams down the front (darker stitched lines)
    if (!rot) {
      b.add(sx * 0.04, sy * 0.92, sz * 0.04, x - sx * 0.30, y, z + sz * 0.42, SEAM);
      b.add(sx * 0.04, sy * 0.92, sz * 0.04, x + sx * 0.30, y, z + sz * 0.42, SEAM);
    } else {
      b.add(sx * 0.92, sy * 0.04, sz * 0.04, x, y - sy * 0.30, z + sz * 0.42, SEAM);
      b.add(sx * 0.92, sy * 0.04, sz * 0.04, x, y + sy * 0.30, z + sz * 0.42, SEAM);
    }
    // Stamp label on the front face — brown rectangle with two lighter marks
    if (stamp && !rot) {
      b.add(sx * 0.40, sy * 0.22, 0.012, x, y + sy * 0.05, z + sz * 0.50, STAMP);
      b.add(sx * 0.10, sy * 0.08, 0.014, x - sx * 0.10, y + sy * 0.05, z + sz * 0.51, STAMP_LT);
      b.add(sx * 0.10, sy * 0.08, 0.014, x + sx * 0.10, y + sy * 0.05, z + sz * 0.51, STAMP_LT);
    }
    // Cinched neck + finer tie (smaller than jute's tie)
    const topY = y + sy / 2;
    b.add(sx * 0.30, sy * 0.10, sz * 0.30, x, topY - sy * 0.04, z, LINEN_D);
    b.add(sx * 0.38, sy * 0.05, sz * 0.38, x, topY,           z, TIE);
    // Small white tuft poking out the top (gathered linen)
    b.add(sx * 0.16, sy * 0.06, sz * 0.16, x, topY + sy * 0.04, z, LINEN[0]);
  };

  // Front-center sack — taller and slimmer than the wheat sack
  sack(cx, 0.12, 0.00, 0.18, 0.24, 0.18, false, true);

  // Flour dust patch in front (white powder spilled on the ground)
  for (let i = 0; i < 6; i++) {
    const ang = (i / 6) * Math.PI - Math.PI / 2;
    const r0 = 0.08 + rng() * 0.08;
    const dx = cx + 0.08 + Math.cos(ang) * r0 * 0.5;
    const dz = 0.10 + Math.sin(ang) * r0 * 0.4;
    b.add(0.040, 0.008, 0.040, dx, 0.004, dz, FLOUR);
  }
  // Flour fingerprint on top of the front sack
  if (rng() < 0.7) b.add(0.030, 0.008, 0.030, cx - 0.05, 0.245, 0.03, FLOUR);

  if (med) {
    sack(cx - 0.16, 0.11, -0.04, 0.16, 0.22, 0.18, true,  false);
    sack(cx + 0.16, 0.12,  0.04, 0.16, 0.24, 0.18, false, true);
  }
  if (big) {
    // Stacked sack on top + a leaning one + more flour dust
    sack(cx - 0.02, 0.34, -0.02, 0.16, 0.20, 0.16, false, false);
    sack(cx + 0.14, 0.32,  0.06, 0.14, 0.18, 0.14, true,  false);
    for (let i = 0; i < 4; i++) {
      const tx = cx + (rng() - 0.5) * 0.18;
      const tz = (rng() - 0.5) * 0.18;
      b.add(0.030, 0.006, 0.030, tx, 0.45, tz, FLOUR);
    }
  }
}

function makeWoodPile(b: VoxelBuilder, cx: number, n: number) {
  // Stacked logs end-on showing tree rings. 4 wood tones (some lighter, some
  // charred) + bark on the perimeter. Logs cross-stacked for stability.
  const HEART   = [0xc4986a, 0xb88458, 0xd0a878];      // inner rings (light)
  const SAP     = [0x9c6c44, 0x885a30, 0xa87648];      // outer sapwood
  const BARK    = [0x6a4828, 0x4a3018, 0x583820];      // bark perimeter
  const CHAR    = 0x2a1810;                             // charred ends
  const RING_DK = 0x6a4220;                             // dark heartwood ring
  const RING_LT = 0xe0b890;                             // light cambium pith
  const big = n >= 30, med = n >= 10;
  const rng = rngFn(Math.floor(cx * 100) * 31 + n * 7 + 3);

  // Single log seen end-on (axis runs along x). Shows concentric tree rings.
  const log = (x: number, y: number, z: number, len: number, dia: number, charred: boolean) => {
    const heart = HEART[Math.floor(rng() * HEART.length)];
    const sap   = SAP[Math.floor(rng() * SAP.length)];
    const bark  = BARK[Math.floor(rng() * BARK.length)];
    // Main log mass (sapwood color)
    b.add(len, dia * 0.95, dia * 0.95, x, y, z, sap);
    // Bark strips on top/bottom + front/back of the perimeter
    b.add(len, dia * 0.12, dia * 1.02, x, y + dia * 0.46, z, bark);
    b.add(len, dia * 0.12, dia * 1.02, x, y - dia * 0.46, z, bark);
    b.add(len, dia * 1.02, dia * 0.12, x, y, z + dia * 0.46, bark);
    b.add(len, dia * 1.02, dia * 0.12, x, y, z - dia * 0.46, bark);
    // End-cap rings — concentric circles at the +x end of the log
    const endX = x + len / 2 + 0.005;
    const endCol = charred ? CHAR : sap;
    b.add(0.008, dia * 0.92, dia * 0.92, endX, y, z, endCol);              // outer face
    if (!charred) {
      b.add(0.010, dia * 0.70, dia * 0.70, endX, y, z, heart);              // mid ring
      b.add(0.012, dia * 0.40, dia * 0.40, endX, y, z, RING_DK);            // dark heart
      b.add(0.014, dia * 0.15, dia * 0.15, endX, y, z, RING_LT);            // pith
      // Two radial cracks across the end face
      b.add(0.010, dia * 0.85, 0.015, endX, y, z, RING_DK);
      b.add(0.010, 0.015, dia * 0.85, endX, y, z, RING_DK);
    }
    // Knot bump somewhere along the bark
    if (rng() < 0.5) {
      const kx = (rng() - 0.5) * len * 0.6;
      b.add(0.020, 0.020, dia * 0.10, x + kx, y + dia * 0.30, z + dia * 0.45, RING_DK);
    }
  };

  // Bottom row: 2 logs side-by-side (axis along x)
  log(cx, 0.07, -0.06, 0.26, 0.10, false);
  log(cx, 0.07,  0.06, 0.26, 0.10, rng() < 0.4);   // sometimes charred

  if (med) {
    // Cross-stacked row above — short logs running perpendicular (axis z)
    b.add(0.10, 0.10, 0.26, cx - 0.06, 0.18, 0.00, SAP[0]);
    // End-cap rings on the +z face
    b.add(0.10, 0.10, 0.012, cx - 0.06, 0.18, 0.135, SAP[1]);
    b.add(0.07, 0.07, 0.014, cx - 0.06, 0.18, 0.139, HEART[0]);
    b.add(0.04, 0.04, 0.016, cx - 0.06, 0.18, 0.142, RING_DK);
    // Bark stripes on the sides of that perpendicular log
    b.add(0.012, 0.10, 0.26, cx - 0.06 + 0.05, 0.18, 0, BARK[0]);
    b.add(0.012, 0.10, 0.26, cx - 0.06 - 0.05, 0.18, 0, BARK[1]);
    // Second perpendicular log
    b.add(0.10, 0.10, 0.26, cx + 0.06, 0.18, 0.00, SAP[2]);
    b.add(0.10, 0.10, 0.012, cx + 0.06, 0.18, 0.135, SAP[0]);
    b.add(0.07, 0.07, 0.014, cx + 0.06, 0.18, 0.139, HEART[1]);
    b.add(0.04, 0.04, 0.016, cx + 0.06, 0.18, 0.142, RING_DK);
  }
  if (big) {
    // Top row — back to lengthwise orientation, slightly smaller
    log(cx - 0.02, 0.30, -0.04, 0.24, 0.09, false);
    log(cx + 0.02, 0.30,  0.06, 0.22, 0.085, rng() < 0.5);
    // A single log tipped on top of everything (raised + slightly offset)
    log(cx + 0.04, 0.40, -0.02, 0.20, 0.08, false);
    // Wood-chip scatter at the base
    for (let i = 0; i < 5; i++) {
      const ang = rng() * Math.PI * 2;
      const r0 = 0.16 + rng() * 0.06;
      const sx = cx + Math.cos(ang) * r0;
      const sz = Math.sin(ang) * r0;
      b.add(0.030, 0.012, 0.020, sx, 0.008, sz, rng() < 0.5 ? HEART[1] : SAP[1]);
    }
  }
}

/** Voxel axe — chunky steel head with a glinting edge, wooden haft +
 *  leather grip. The head sits at the origin so the cutting edge can
 *  swing right down onto the cursor's tile when a strike fires. */
export function makeAxe(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(1213);
  // Steel palette — shadow, body, highlight, edge, rust
  const STEEL_DK = 0x3a3e46;
  const STEEL    = 0x6a6e76;
  const STEEL_HI = 0x9aa0a8;
  const EDGE     = 0xd0d8e0;
  const RUST     = 0x7a4020;
  const RUST_DK  = 0x4a2410;
  const RIVET    = 0x202428;
  const WOOD_LT = 0xb88458;
  const WOOD    = 0x9c6c44;
  const WOOD_DK = 0x6a4828;
  const WOOD_VD = 0x4a3018;
  const LEATHER = 0x5a3f18;
  const LEATH_D = 0x3a2410;
  // Head — flat tapered axe BLADE, not a chunky hammer block. The blade
  // is THIN along the swing axis (Y), WIDE along the cutting axis (Z),
  // and FLARES from a narrow cheek near the eye out to a wider crescent
  // cutting edge. Silhouette from the side reads as a fan-shaped axe-head.
  //
  // Layout along X (perpendicular to haft):
  //   x = -0.06    short poll block (back of head, counterweight)
  //   x =  0.00    eye where the haft passes through (small + dark)
  //   x =  0.04    cheek — chunky base of the blade, full Y thickness
  //   x =  0.10    mid-blade — narrows in Y, widens in Z
  //   x =  0.18    outer blade — thin slab, widest in Z (fan flare)
  //   x =  0.24    cutting edge — sharp, near-line in Y, widest Z
  //   x =  0.27    edge-tip highlight (the bright bit that catches light)
  // Poll (back of head) — small steel block opposite the blade
  b.add(0.06, 0.10, 0.10, -0.07, 0.20, 0.00, STEEL_DK);
  b.add(0.06, 0.08, 0.10, -0.07, 0.21, 0.00, STEEL);
  b.add(0.06, 0.03, 0.10, -0.07, 0.245, 0.00, STEEL_HI);
  // Eye region — short dark block straddling the haft, with iron wedges
  // poking out the top so the head reads as forge-fitted.
  b.add(0.06, 0.10, 0.10, -0.01, 0.20, 0.00, STEEL_DK);
  b.add(0.06, 0.04, 0.10, -0.01, 0.25, 0.00, 0x101010);
  b.add(0.020, 0.030, 0.020, -0.01, 0.275, 0.04, EDGE);
  b.add(0.020, 0.030, 0.020, -0.01, 0.275,-0.04, EDGE);
  // Cheek — thicker base of the blade right at the eye. Full vertical
  // depth so the head looks heavy at the haft and slims toward the edge.
  b.add(0.06, 0.18, 0.18, 0.05, 0.20, 0.00, STEEL_DK);    // under-shadow
  b.add(0.06, 0.14, 0.18, 0.05, 0.20, 0.00, STEEL);       // body
  b.add(0.06, 0.04, 0.18, 0.05, 0.255, 0.00, STEEL_HI);   // top-highlight
  b.add(0.06, 0.04, 0.18, 0.05, 0.145, 0.00, STEEL_DK);   // bottom-shadow
  // Mid-blade — narrower in Y, fans out in Z. This is where the blade
  // visibly tapers toward the cutting edge.
  b.add(0.06, 0.13, 0.26, 0.11, 0.20, 0.00, STEEL_DK);
  b.add(0.06, 0.09, 0.26, 0.11, 0.20, 0.00, STEEL);
  b.add(0.06, 0.03, 0.26, 0.11, 0.245, 0.00, STEEL_HI);
  // Outer blade — thin slab, widest in Z. Reads as the visible blade face.
  b.add(0.06, 0.08, 0.34, 0.17, 0.20, 0.00, STEEL);
  b.add(0.06, 0.03, 0.34, 0.17, 0.235, 0.00, STEEL_HI);
  b.add(0.06, 0.03, 0.34, 0.17, 0.165, 0.00, STEEL_DK);
  // Cutting edge — even thinner Y, fans slightly wider in Z than the
  // outer blade so the crescent silhouette pops.
  b.add(0.04, 0.05, 0.38, 0.22, 0.20, 0.00, STEEL_HI);
  // Sharp edge — single-voxel-thin in Y, brightest steel, slight
  // extension in Z to give the blade its curving "moustache" tips.
  b.add(0.030, 0.024, 0.42, 0.25, 0.205, 0.00, EDGE);
  b.add(0.020, 0.018, 0.04, 0.26, 0.205, 0.20, EDGE);
  b.add(0.020, 0.018, 0.04, 0.26, 0.205,-0.20, EDGE);
  // Rust spots on the cheek + edge — wear character
  b.add(0.024, 0.030, 0.024, 0.06, 0.21, 0.08, RUST);
  b.add(0.020, 0.024, 0.020, 0.04, 0.18,-0.10, RUST_DK);
  b.add(0.018, 0.018, 0.018, 0.13, 0.19, 0.14, RUST);
  // Poll-end rivets (visible from the side, anchored to the eye block)
  b.add(0.018, 0.018, 0.018, -0.05, 0.18, 0.05, RIVET);
  b.add(0.018, 0.018, 0.018, -0.05, 0.18,-0.05, RIVET);
  // Maker's stamp — small recessed square on the cheek
  b.add(0.024, 0.024, 0.014, 0.05, 0.225, 0.10, STEEL_DK);
  // Haft — wood-grain stripes (4 quadrant voxels per segment)
  const HAFT_SEG = 9;
  const grain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < HAFT_SEG; i++) {
    const t = i / (HAFT_SEG - 1);
    const yy = 0.10 + i * 0.10;
    const zz = 0.00 + t * 0.40;
    const quads: [number, number, number][] = [
      [-0.020, -0.020, grain[(i + 0) & 3]],
      [ 0.020, -0.020, grain[(i + 1) & 3]],
      [-0.020,  0.020, grain[(i + 2) & 3]],
      [ 0.020,  0.020, grain[(i + 3) & 3]],
    ];
    for (const [qx, qz, c] of quads) {
      b.add(0.026, 0.10, 0.026, qx, yy, zz + qz, c);
    }
    if (rng() < 0.18) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? 0.044 : side === 1 ? -0.044 : 0;
      const kz = side === 2 ? 0.044 : side === 3 ? -0.044 : 0;
      b.add(0.020, 0.024, 0.020, kx, yy, zz + kz, WOOD_VD);
    }
  }
  // Mid-handle grip-wrapped section
  for (let i = 4; i <= 6; i++) {
    const t = i / (HAFT_SEG - 1);
    const yy = 0.10 + i * 0.10;
    const zz = 0.00 + t * 0.40;
    b.add(0.068, 0.10, 0.068, 0.00, yy, zz, LEATHER);
    b.add(0.070, 0.014, 0.070, 0.00, yy + 0.046, zz, LEATH_D);
  }
  // Leather grip wrap at the bottom of the haft + end-cap rivet
  b.add(0.07, 0.06, 0.07, 0.00, 0.84, 0.35, LEATHER);
  b.add(0.072, 0.020, 0.072, 0.00, 0.875, 0.35, LEATH_D);
  b.add(0.07, 0.05, 0.07, 0.00, 0.92, 0.37, LEATHER);
  b.add(0.020, 0.020, 0.020, 0.00, 0.945, 0.37, RIVET);
  const mesh = b.build();
  mesh.name = 'axe-prop';
  return mesh;
}

/** Voxel pickaxe — a wooden haft topped with a horizontal iron head that
 *  tapers to two pointed ends (one chisel-flat, one spike). Used to break
 *  boulders for stone. Same haft style as the axe so the toolset reads as
 *  a coherent kit. */
export function makePickaxe(): THREE.Object3D {
  const b = new VoxelBuilder();
  const rng = rngFn(1543);
  // Steel palette — matches makeAxe
  const STEEL_DK = 0x3a3e46;
  const STEEL    = 0x6a6e76;
  const STEEL_HI = 0x9aa0a8;
  const EDGE     = 0xd0d8e0;
  const RUST     = 0x7a4020;
  const RUST_DK  = 0x4a2410;
  const RIVET    = 0x202428;
  const WOOD_LT  = 0xb88458;
  const WOOD     = 0x9c6c44;
  const WOOD_DK  = 0x6a4828;
  const WOOD_VD  = 0x4a3018;
  const LEATHER  = 0x5a3f18;
  const LEATH_D  = 0x3a2410;
  // Head — horizontal bar running along z, tapering to a point at each end.
  // Centre block (where the eye accepts the haft) is widest; the two ends
  // step down in width and converge to a single voxel tip.
  const headSteps: [number, number][] = [
    // [zCenter, halfWidthX]
    [-0.32, 0.018],
    [-0.26, 0.030],
    [-0.20, 0.044],
    [-0.14, 0.058],
    [-0.07, 0.070],
    [ 0.00, 0.080],
    [ 0.07, 0.070],
    [ 0.14, 0.058],
    [ 0.20, 0.044],
    [ 0.26, 0.030],
    [ 0.32, 0.018],
  ];
  for (const [zc, hw] of headSteps) {
    // Under-shadow + body + top-highlight per step
    b.add(hw * 2, 0.05, 0.07, 0.00, 0.215, zc, STEEL_DK);
    b.add(hw * 2, 0.07, 0.07, 0.00, 0.260, zc, STEEL);
    b.add(hw * 2, 0.04, 0.07, 0.00, 0.310, zc, STEEL_HI);
  }
  // Sharpened tips — DIFFERENTIATED: +Z side is a narrow sharp spike
  // (longer, narrower), -Z side is a broad chisel (wider in Y, blunter).
  // Reads as a proper miner's pickaxe rather than a symmetric bar.
  // Spike end (+Z):
  b.add(0.020, 0.045, 0.06, 0.00, 0.265,  0.40, STEEL_HI);
  b.add(0.014, 0.024, 0.04, 0.00, 0.265,  0.45, EDGE);
  // Chisel end (-Z): broader & flatter
  b.add(0.040, 0.08,  0.04, 0.00, 0.265, -0.38, STEEL_HI);
  b.add(0.040, 0.05,  0.025,0.00, 0.265, -0.41, EDGE);
  // Rust + wear specks along the head
  b.add(0.040, 0.024, 0.030, 0.00, 0.275,  0.14, RUST);
  b.add(0.030, 0.020, 0.030, 0.00, 0.260, -0.10, RUST_DK);
  b.add(0.024, 0.018, 0.024, 0.00, 0.290,  0.22, RUST);
  // Eye / wedges where the haft passes through
  b.add(0.10, 0.07, 0.06,  0.00, 0.260, 0.00, 0x101010);
  b.add(0.020, 0.030, 0.020, 0.04, 0.290, 0.00, EDGE);
  b.add(0.020, 0.030, 0.020,-0.04, 0.290, 0.00, EDGE);
  b.add(0.018, 0.018, 0.018, 0.06, 0.230, 0.00, RIVET);
  b.add(0.018, 0.018, 0.018,-0.06, 0.230, 0.00, RIVET);
  // Haft — same wood-grain stripe pattern as the axe
  const HAFT_SEG = 9;
  const grain = [WOOD_LT, WOOD, WOOD_DK, WOOD_VD];
  for (let i = 0; i < HAFT_SEG; i++) {
    const yy = 0.10 + i * 0.10;
    const quads: [number, number, number][] = [
      [-0.020, -0.020, grain[(i + 0) & 3]],
      [ 0.020, -0.020, grain[(i + 1) & 3]],
      [-0.020,  0.020, grain[(i + 2) & 3]],
      [ 0.020,  0.020, grain[(i + 3) & 3]],
    ];
    for (const [qx, qz, c] of quads) {
      b.add(0.026, 0.10, 0.026, qx, yy, qz, c);
    }
    if (rng() < 0.18) {
      const side = Math.floor(rng() * 4);
      const kx = side === 0 ? 0.044 : side === 1 ? -0.044 : 0;
      const kz = side === 2 ? 0.044 : side === 3 ? -0.044 : 0;
      b.add(0.020, 0.024, 0.020, kx, yy, kz, WOOD_VD);
    }
  }
  // Leather grip wrap at the bottom of the haft + end-cap rivet
  b.add(0.07, 0.06, 0.07, 0.00, 0.84, 0.00, LEATHER);
  b.add(0.072, 0.020, 0.072, 0.00, 0.875, 0.00, LEATH_D);
  b.add(0.07, 0.05, 0.07, 0.00, 0.92, 0.00, LEATHER);
  b.add(0.020, 0.020, 0.020, 0.00, 0.945, 0.00, RIVET);
  const mesh = b.build();
  mesh.name = 'pickaxe-prop';
  return mesh;
}

/**
 * Construction site placeholder — foundation slab + 4 corner posts +
 * scaffolding planks + a small wooden sign. Used to render a building that
 * isn't yet fully constructed. `progress` (0..1) scales post height so the
 * site visibly "grows" between stages.
 */
function makeConstructionSite(w: number, d: number, progress: number): THREE.Object3D {
  const b = new VoxelBuilder();
  const V = 0.16;
  const rng = rngFn(Math.floor(w * 100) * 47 + Math.floor(d * 100) * 13 + Math.floor(progress * 100) + 1);
  // Wood-tone palette for variety across beams, planks, posts
  const WOOD_LT = 0xc89058;
  const WOOD    = 0x9c6c44;
  const WOOD_MD = 0x88582c;
  const WOOD_DK = 0x6a4828;
  const WALL_PL = [WOOD_LT, WOOD, WOOD_MD];
  const POST_PL = [WOOD_DK, 0x583820, WOOD_MD];

  // Foundation slab + alternating plank-seam stripes
  b.add(w - 0.15, V * 0.6, d - 0.15, 0, V * 0.30, 0, WOOD);
  for (let i = -2; i <= 2; i++) {
    const c = (i & 1) === 0 ? WOOD_MD : WOOD_DK;
    b.add(V * 0.04, V * 0.65, d - 0.2, i * V * 0.6, V * 0.32, 0, c);
  }
  // Stone footing pads under each corner post
  const xCorner = w / 2 - V;
  const zCorner = d / 2 - V;
  const corners: [number, number][] = [
    [-xCorner, -zCorner], [ xCorner, -zCorner],
    [-xCorner,  zCorner], [ xCorner,  zCorner],
  ];
  for (const [px, pz] of corners) {
    b.add(V * 1.0, V * 0.25, V * 1.0, px, V * 0.12, pz, COL.stone);
    b.add(V * 0.6, V * 0.10, V * 0.6, px, V * 0.27, pz, 0xc0c0c8);
  }

  // Corner scaffold posts (height grows with progress)
  const postH = 0.6 + progress * 1.8;
  const layers = Math.max(2, Math.round(postH / V));
  for (const [px, pz] of corners) {
    for (let h = 0; h < layers; h++) {
      const col = POST_PL[h % POST_PL.length];
      b.add(V * 0.55, V * 0.95, V * 0.55, px, V * 0.30 + h * V, pz, col);
      // Lashing band every 3 layers (rope wrap)
      if (h % 3 === 1) {
        b.add(V * 0.65, V * 0.10, V * 0.65, px, V * 0.30 + h * V, pz, 0x4a3a20);
      }
    }
  }

  // Horizontal scaffolding beams — top + mid rings
  const topY = V * 0.30 + layers * V;
  b.add(w - V, V * 0.20, V * 0.20, 0, topY, -zCorner, WOOD);
  b.add(w - V, V * 0.20, V * 0.20, 0, topY,  zCorner, WOOD_MD);
  b.add(V * 0.20, V * 0.20, d - V, -xCorner, topY, 0, WOOD_MD);
  b.add(V * 0.20, V * 0.20, d - V,  xCorner, topY, 0, WOOD);
  const midY = V * 0.30 + (layers * V) * 0.5;
  b.add(w - V, V * 0.16, V * 0.16, 0, midY, -zCorner, WOOD_DK);
  b.add(w - V, V * 0.16, V * 0.16, 0, midY,  zCorner, WOOD_DK);
  b.add(V * 0.16, V * 0.16, d - V, -xCorner, midY, 0, WOOD_DK);
  b.add(V * 0.16, V * 0.16, d - V,  xCorner, midY, 0, WOOD_DK);
  // Diagonal cross-braces on front + back
  for (let i = 0; i < 4; i++) {
    const t = i / 3;
    b.add(V * 0.22, V * 0.22, V * 0.10, -xCorner + (2 * xCorner) * t, V * 0.30 + postH * t, -zCorner - 0.01, WOOD_DK);
    b.add(V * 0.22, V * 0.22, V * 0.10,  xCorner - (2 * xCorner) * t, V * 0.30 + postH * t,  zCorner + 0.01, WOOD_DK);
  }

  // Wall planks fill in as progress rises. Front wall has a doorway gap;
  // back / sides are solid. Plank rows alternate tone for grain variety.
  const wallFrac = Math.min(1, Math.max(0, progress));
  const wallTopY = V * 0.30 + postH * wallFrac;
  const PLANK_H = V * 0.45;
  const wallPlanks = Math.floor((wallTopY - V * 0.30) / PLANK_H);
  for (let ply = 0; ply < wallPlanks; ply++) {
    const y = V * 0.30 + PLANK_H / 2 + ply * PLANK_H;
    const col = WALL_PL[ply % WALL_PL.length];
    // Back + left + right walls
    b.add(w - V * 1.4, PLANK_H * 0.92, V * 0.10, 0, y, zCorner - V * 0.10, col);
    b.add(V * 0.10, PLANK_H * 0.92, d - V * 1.4, -xCorner + V * 0.10, y, 0, col);
    b.add(V * 0.10, PLANK_H * 0.92, d - V * 1.4,  xCorner - V * 0.10, y, 0, WALL_PL[(ply + 1) % WALL_PL.length]);
    // Front wall — two segments leaving a doorway in the middle
    const doorW = w * 0.30;
    const segW = (w - V * 1.4 - doorW) / 2;
    if (segW > 0.02) {
      b.add(segW, PLANK_H * 0.92, V * 0.10, -doorW / 2 - segW / 2, y, -zCorner + V * 0.10, col);
      b.add(segW, PLANK_H * 0.92, V * 0.10,  doorW / 2 + segW / 2, y, -zCorner + V * 0.10, col);
    }
    // Seam shadow at the bottom edge of each row
    b.add(w - V * 1.4, V * 0.04, V * 0.05, 0, y - PLANK_H * 0.45, zCorner - V * 0.06, WOOD_DK);
  }

  // Plank stack on the +x side + end-grain stripe
  const stackBaseY = V * 0.42;
  for (let i = 0; i < 4; i++) {
    const c = i % 2 === 0 ? WOOD_LT : WOOD_MD;
    b.add(V * 1.4, V * 0.12, V * 0.5, xCorner - V * 1.0, stackBaseY + i * V * 0.13, -zCorner + V * 1.2, c);
  }
  b.add(0.012, V * 0.50, V * 0.5, xCorner - V * 1.0 - V * 0.70, stackBaseY + V * 0.20, -zCorner + V * 1.2, WOOD_DK);
  // Paint can — blue body + light lid + wire handle + small drip
  const canX = xCorner - V * 0.4;
  const canZ = -zCorner + V * 0.4;
  b.add(V * 0.5, V * 0.5, V * 0.5, canX, V * 0.55, canZ, 0x6a8aa8);
  b.add(V * 0.55, V * 0.06, V * 0.55, canX, V * 0.83, canZ, 0xc8c8d0);
  b.add(V * 0.10, V * 0.18, V * 0.04, canX, V * 0.90, canZ, 0xa8a8b0);
  b.add(V * 0.15, V * 0.02, V * 0.20, canX - V * 0.30, V * 0.62, canZ - V * 0.10, 0x4a6a98);
  // Sawdust mound on the -x side
  const sdX = -xCorner + V * 0.5;
  const sdZ = -zCorner + V * 1.0;
  for (let i = 0; i < 6; i++) {
    const ang = (i / 6) * Math.PI * 2;
    const r0 = 0.10 + rng() * 0.06;
    const sx = sdX + Math.cos(ang) * r0 * 0.8;
    const sz = sdZ + Math.sin(ang) * r0 * 0.8;
    b.add(V * 0.4, V * 0.10, V * 0.4, sx, V * 0.45, sz, 0xd8b888);
  }
  b.add(V * 0.7, V * 0.18, V * 0.7, sdX, V * 0.51, sdZ, 0xe0c098);
  // Hand saw resting on the plank stack
  b.add(V * 0.8, V * 0.04, V * 0.12, xCorner - V * 1.0, stackBaseY + 4 * V * 0.13 + V * 0.05, -zCorner + V * 1.2, 0xb0b8c0);
  b.add(V * 0.20, V * 0.10, V * 0.10, xCorner - V * 1.0 - V * 0.45, stackBaseY + 4 * V * 0.13 + V * 0.05, -zCorner + V * 1.2, WOOD_DK);
  // Hammer on the +z side
  b.add(V * 0.45, V * 0.06, V * 0.06, -V * 0.4, V * 0.45, zCorner - V * 0.4, WOOD_DK);
  b.add(V * 0.12, V * 0.18, V * 0.10, -V * 0.4 + V * 0.22, V * 0.50, zCorner - V * 0.4, 0x484048);

  // Front sign post + sign board (CONSTRUCTION marks)
  b.add(V * 0.18, V * 1.4, V * 0.18, -V * 0.5, V * 0.80, -zCorner + V * 0.30, WOOD_DK);
  b.add(V * 1.6, V * 0.85, V * 0.08, 0, V * 1.45, -zCorner + V * 0.20, 0xe8c860);
  b.add(V * 0.25, V * 0.45, V * 0.04, -V * 0.45, V * 1.45, -zCorner + V * 0.16, 0xc44030);
  b.add(V * 0.25, V * 0.45, V * 0.04,  V * 0.45, V * 1.45, -zCorner + V * 0.16, 0xc44030);
  b.add(V * 0.18, V * 0.18, V * 0.04, 0,        V * 1.45, -zCorner + V * 0.16, 0x2a1810);
  // Sign-board frame trim
  b.add(V * 1.7, V * 0.08, V * 0.05, 0, V * 1.85, -zCorner + V * 0.22, WOOD_DK);
  b.add(V * 1.7, V * 0.08, V * 0.05, 0, V * 1.05, -zCorner + V * 0.22, WOOD_DK);

  // Ladder leaning on the -x side — narrow rungs + two side-rails
  const ladderRungs = Math.max(3, Math.round(postH / (V * 0.6)));
  for (let i = 0; i < ladderRungs; i++) {
    b.add(V * 0.50, V * 0.10, V * 0.10, -xCorner - V * 0.25, V * 0.40 + i * V * 0.55, -zCorner + V * 0.5, WOOD_LT);
  }
  b.add(V * 0.08, postH * 1.05, V * 0.10, -xCorner - V * 0.10, V * 0.40 + postH * 0.5, -zCorner + V * 0.5, WOOD_MD);
  b.add(V * 0.08, postH * 1.05, V * 0.10, -xCorner - V * 0.45, V * 0.40 + postH * 0.5, -zCorner + V * 0.5, WOOD_MD);

  return b.build();
}

/** The complete voxel model for a building (no construction state). The
 *  build-animation in the renderer wraps this with a rising clip plane. */
function makeBuildingForRender(type: BuildingType) {
  if (type === 'granary') return { obj: makeGranary(), sails: null as THREE.Object3D | null };
  if (type === 'mill') {
    const m = makeMill();
    return { obj: m as THREE.Object3D, sails: m.userData.sails };
  }
  if (type === 'mailbox') return { obj: makeMailbox(), sails: null as THREE.Object3D | null };
  return { obj: makeTruckDepot(), sails: null as THREE.Object3D | null };
}

// ── Diorama class ───────────────────────────────────────────────────
export class Diorama {
  private renderer: THREE.WebGLRenderer;
  private postprocessing!: Postprocessing;
  private scene = new THREE.Scene();
  private camera: THREE.OrthographicCamera;
  /** Pan target — camera always looks at this point. */
  private camTarget = new THREE.Vector3(0, 0, 0);
  private camRadius = 30;
  private camPanning = false;
  private camLastPointer = { x: 0, y: 0 };
  /** Sickle is omnidirectional now — left over fields removed. The
   *  swath geometry (3×3 sub-cells) lives entirely in Sim.cutSubCellCrop. */

  // ── Zoom-in close-up state ────────────────────────────────────────
  /** When zoom mode is active the camera dives onto a 6×6 sector and the
   *  fog tightens so the rest of the world dissolves. State here tracks
   *  the *target* values; the actual camera + fog are eased toward them
   *  every frame in {@link animateZoom}. */
  private zoom = {
    active: false,
    /** True while the camera is mid-transition (in OR out). When false,
     *  animateZoom does NOT touch camera state — user pan/wheel/R-reset
     *  controls own the camera. */
    animating: false,
    /** World-space center the camera should look at while zoomed. */
    targetX: 0, targetZ: 0,
    /** Per-session zoom multiplier override (plot mode varies it by bbox
     *  size). When undefined, falls back to ZOOM_LEVEL. */
    targetZoom: undefined as number | undefined,
    /** Saved overview pose. Kept in sync with the user's pan/zoom while
     *  in overview, so re-entering zoom always starts from the latest pose
     *  and exiting restores exactly the same view. */
    savedX: 0, savedZ: 0, savedZoom: 1.0,
  };
  // ── Tool props (the hoe / can / scythe / seeds object the player "holds")
  /** One prop per manual tool. Only the prop matching state.tool is visible,
   *  and only while the camera is in close-up zoom. */
  private toolProps: { [k: string]: THREE.Object3D } = {};
  /** Cursor's projected ground position, updated every pointer move. */
  private cursorGroundPos = new THREE.Vector3();
  /** Drag-to-till state. While active, every tile the cursor crosses is
   *  pushed onto `tiles`; on pointer up, each is committed via sim.applyTool. */
  private toolDrag = {
    active: false,
    pointerId: -1,
    tileSet: new Set<string>(),
    tiles: [] as { c: number; r: number }[],
    /** Smoothed scrape phase used by the hoe bob animation. */
    phase: 0,
  };

  private static readonly ZOOM_LEVEL = 2.6;       // camera.zoom while zoomed
  // Fog is no longer tightened in zoom mode — the camera repositioning and
  // zoom level alone are enough framing. The overview's scene fog stays as
  // a soft atmospheric falloff at distance.

  // ── Manual hold-to-progress state ────────────────────────────────
  /** Active "hold and grind" action. Field / plant / scythe commit on a
   *  3×3 area around the cursor. Releases cancel; moving onto a different
   *  tile cancels. Water is NOT a hold tool — it uses the drag/paint path. */
  private hold: {
    c: number; r: number;
    tool: 'field' | 'plant' | 'scythe';
    progress: number;
    /** Highest 0..1 threshold we've already burst on (0, .25, .5, .75). */
    lastBurst: number;
    pointerId: number;
  } | null = null;
  /** Per-tile chop progress for axe strikes. Surfaces a "swing it 4 times"
   *  feel instead of the previous one-click fell. */
  private treeStrikes = new Map<string, { count: number; lastT: number }>();
  /** Active tree-fall overlays. On the final chop, a fresh tree mesh is
   *  spawned in the scene (independent of the tile group) and animated
   *  tipping over for {@link TREE_FALL_S} seconds. The underlying tile
   *  rebuild swaps the actual tree → stump immediately, so the fall
   *  plays out as a transient layer on top of the world while the
   *  stump occupies the same footprint underneath. Synced 1:1 with the
   *  chop-wood/fell audio clip. */
  private fallingTrees: Array<{
    root: THREE.Object3D;
    /** Elapsed seconds since the fall started. */
    t: number;
    /** Total fall time, matches the chop-wood/fell audio length. */
    duration: number;
    /** Rotation axis (XZ plane, unit length). */
    axis: THREE.Vector3;
    /** Final tip angle — π/2 + tiny overshoot so the trunk visibly
     *  hits the ground at the end of the arc. */
    finalAngle: number;
  }> = [];
  /** Tree-fall duration. Matches the 7.0–7.3s chop-wood/fell audio
   *  so visual + sound resolve together. */
  private static readonly TREE_FALL_S = 7.0;
  /** Player-facing graphics settings — drives runtime toggles for
   *  seasonal weather particles, fireflies, etc. Connected from
   *  main.ts after both Diorama and Settings exist. Null until
   *  connected; all flags default to "on" (the previous always-on
   *  behaviour) so unhooked builds aren't visibly downgraded. */
  private settings: Settings | null = null;
  /** Elapsed-time of the most recent axe strike — drives the axe prop's
   *  swing animation. */
  private lastAxeStrikeT = -Infinity;
  /** World-space position of the most recent strike target — used to lock
   *  the swinging prop to that tile center for the duration of the swing,
   *  so the chop motion visibly hits the tree/rock instead of trailing the
   *  cursor when the player moves the mouse mid-swing. */
  private strikeTargetX = 0;
  private strikeTargetZ = 0;

  /** World-placed tool stands. Click one to pick up that tool; click again
   *  (or right-click anywhere) to put it back. The tool mesh on the stand
   *  is hidden while the player is "holding" it. */
  private toolStands: { toolId: ToolId; root: THREE.Object3D; toolMesh: THREE.Object3D }[] = [];
  /** Translucent green quads marking the in-flight selection AND the
   *  active plot's tiles. Rebuilt on every plotSelectChanged. */
  private selectionOverlayGroup = new THREE.Group();
  /** Pointer-down + held while in selecting mode — drives the drag-paint. */
  private selectingDragActive = false;
  /** Active plot weed meshes, keyed by `c:r:subC:subR`. */
  /** Weeds were the biggest single draw-call sink — up to 2000-5000
   *  individual Group meshes (one per weed × world's ~30×30 grass-tile
   *  density). Now a single InstancedMesh holds the entire world's
   *  weeds. The data side keeps a parallel index → weed-coord array so
   *  raycast hits map back to sim coords, plus a coord→index Map so the
   *  weedPlucked event can hide an instance in O(1). */
  private weedsInstanced: THREE.InstancedMesh | null = null;
  private weedInstanceCoords: { c: number; r: number; subC: number; subR: number; alive: boolean }[] = [];
  private weedKeyToIndex = new Map<string, number>();
  private weedHiddenMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
  private weedTmpMatrix = new THREE.Matrix4();
  private weedTmpQuat = new THREE.Quaternion();
  private weedTmpVec3 = new THREE.Vector3();
  private weedTmpScale = new THREE.Vector3();
  /** Tilled sub-cell furrows — one InstancedMesh per (tile, stage). Capacity
   *  PLOT_SUB_DIM² per layer; unused slots stamped with a zero-scale matrix.
   *  Three stages × N tiles ⇒ a 9-tile plot has 27 draw calls instead of
   *  thousands of individual merged meshes. Keyed by `${c}:${r}:${stage}`. */
  private tilledInstanced = new Map<string, THREE.InstancedMesh>();
  /** Cached shared geometry per tilled stage (indices 1..3). One BufferGeometry
   *  shared across every tile + every instance — the geometry only changes
   *  with stage, not with sub-cell coords. */
  private tilledGeoCache: (THREE.BufferGeometry | null)[] = [null, null, null, null];
  /** Per-seed mesh. Keyed by the seed object itself so multiple seeds on
   *  the same sub-cell (allowed since SEED_MAX_PER_TILE > sub-cells) each
   *  get a unique entry. The mesh itself is a sway-group wrapper whose
   *  child holds the voxel wheat — sway rotates the wrapper, child keeps
   *  the static per-seed tilt. */
  private seedMeshes = new Map<object, THREE.Object3D>();
  /** Wet patches — one InstancedMesh per (tile, stage). Capacity PLOT_SUB_DIM²
   *  per layer; uses a translucent plane geometry. Keyed by `${c}:${r}:${stage}`. */
  private wetInstanced = new Map<string, THREE.InstancedMesh>();
  /** Cached translucent plane geometry per wet stage (indices 1..3). */
  private wetGeoCache: (THREE.BufferGeometry | null)[] = [null, null, null, null];
  /** Cached translucent material per wet stage (indices 1..3). */
  private wetMatCache: (THREE.MeshBasicMaterial | null)[] = [null, null, null, null];
  /** Tiles whose per-sub-cell layers need a rebuild this frame. Flushed from
   *  update(). Collapses the auto-fill burst of 256 events/tile into one
   *  rebuild. Per-mode flag so till/water/seed can be dirtied independently. */
  private dirtyTiles = new Map<string, { till: boolean; water: boolean; seed: boolean }>();
  /** Pre-computed zero-scale matrix used to hide unused InstancedMesh slots. */
  private subHiddenMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
  /** Scratch matrix for instance writes — avoids allocating per sub-cell. */
  private subTmpMatrix = new THREE.Matrix4();
  /** Sub-cell grid overlay (faint lines showing the 8×8 guide inside each plot tile). */
  private plotGridMesh: THREE.LineSegments | null = null;
  /** Voxel soil-layer columns under each plot tile — gives the plot a
   *  visible vertical profile (topsoil → subsoil → clay → bedrock). */
  private soilColumnMeshes = new Map<string, THREE.Object3D>();
  /** Dense surface grass tufts on each plot tile (PLOT_SUB_DIM² per tile). */
  private plotSurfaceMeshes = new Map<string, THREE.Object3D>();
  /** Edge-detail meshes — overhang tufts and protruding roots/stones on
   *  the perimeter of the active plot. Keyed per perimeter tile. */
  private plotEdgeMeshes = new Map<string, THREE.Object3D>();
  /** Voxel wooden fence around the outer perimeter of each adopted plot.
   *  One Group per plot index so we can target an individual plot's fence
   *  for rebuild/teardown. Stays visible in overview AND plot mode so the
   *  player can see plot boundaries at a glance. */
  private plotFenceGroups = new Map<number, THREE.Group>();
  /** Per-tile merged seed mesh. One mesh = one draw call per tile of
   *  wheat / corn / carrot, replacing the previous per-seed individual
   *  meshes (576 draw calls on a 9-tile fully-grown plot dropped to 9).
   *  Re-built when the tile's seed signature changes (subC/subR/stage
   *  per seed). */
  private tileSeedMeshes = new Map<string, THREE.Object3D>();
  /** Last-seen signature of seeds on each tile — drives the rebuild
   *  dirty check in update(). */
  private tileSeedSig = new Map<string, number>();
  /** Plot-enter drop animation. progress 0 = overview (all tiles at y=0),
   *  progress 1 = plot mode (non-plot tiles dropped to -PLOT_DROP_Y).
   *  Drives a per-frame Y translate on every tile group. */
  private plotDropAnim = { progress: 0, target: 0, t: 0 };
  /** Pointer is held + dragging a plot tool (hoe / plant / water) across
   *  sub-cells. `lastSub` tracks the most recently-affected sub-cell so a
   *  single drag-pass only fires once per cell; leaving + re-entering is
   *  what produces the next stage. */
  private plotToolDrag = {
    active: false,
    pointerId: -1,
    tool: 'field' as 'field' | 'plant' | 'water' | 'scythe',
    lastSub: '' as string,
    /** Last sub-cell COORDS — used to infer the drag direction so the hoe
     *  can till 3 sub-cells wide perpendicular to the stroke. */
    lastCoord: null as null | { c: number; r: number; subC: number; subR: number },
  };
  /** Water-pour mechanic: while the watering can is held on a sub-cell,
   *  the wet area expands outward over time as a growing diamond. Reset
   *  every time the cursor enters a new sub-cell. */
  private plotWaterAccum = 0;
  private plotWaterRadius = 0;
  /** Single consolidated tool rack — clicked to open ToolRackPanel.
   *  Replaces the four individual hoe/water/scythe/axe stands. */
  private toolRack: THREE.Object3D | null = null;
  /** Root of the seed-bag stand mesh, so propMoved can reposition it. */
  private seedbagRoot: THREE.Object3D | null = null;
  /** In-world storage shelf — voxel piles representing the player's stored
   *  crops + flour + wood. Replaces the old HUD chips for those resources. */
  private storageDisplay: { root: THREE.Group; piles: THREE.Object3D[] } = {
    root: new THREE.Group(),
    piles: [],
  };

  private static readonly HOLD_HOE_TIME    = 1.2;  // seconds to fully till a 3×3
  private static readonly HOLD_PLANT_TIME  = 1.0;  // seconds to sow a 3×3
  private static readonly HOLD_SCYTHE_TIME = 1.0;  // seconds to reap a 3×3
  private static readonly AXE_STRIKES_TO_FELL = 4; // for trees + boulders
  private static readonly AXE_STRIKE_COOLDOWN = 0.20; // min seconds between strikes

  private worldGroup = new THREE.Group();
  private placementGroup = new THREE.Group();
  private ghostGroup = new THREE.Group();
  private placementGhost: THREE.Object3D | null = null;
  private placementGhostType: string | null = null;
  private invalidOverlay: THREE.Mesh | null = null;
  /** Materials pulsed by animatePlacementSlots — recreated by updatePlacementSlots. */
  private placementPulseMats: THREE.MeshBasicMaterial[] = [];
  private placementPulseTime = 0;
  private cloudGroup = new THREE.Group();
  private flyerGroup = new THREE.Group();
  private skyGroup = new THREE.Group();
  private windowLamps: THREE.PointLight[] = [];
  private starsMesh: THREE.Mesh | null = null;
  private sunMesh: THREE.Mesh | null = null;
  private moonMesh: THREE.Mesh | null = null;
  /** Firefly swarm — small glowing motes that drift in the forest zone
   *  after dusk. A handful carry actual THREE.PointLights so they cast
   *  real illumination on nearby tree voxels; the rest are pure visual
   *  emissive cubes (cheap). All driven from animateFireflies(). */
  private fireflyGroup = new THREE.Group();
  private fireflies: {
    mesh: THREE.Mesh;
    light?: THREE.PointLight;
    bx: number; by: number; bz: number;
    phaseX: number; phaseY: number; phaseZ: number;
    speedX: number; speedY: number; speedZ: number;
    twinklePhase: number;
    twinkleSpeed: number;
    twinkleBase: number;
  }[] = [];

  /** Seasonal weather particles — snow / leaves / petals / pollen.
   *  Continuous drift across the whole play area. Each particle owns
   *  its own velocity + tumble axis; when it falls below ground it's
   *  recycled to the top with a fresh randomised position. The pool
   *  itself is sized for the densest season (winter snow); other
   *  seasons activate a subset of the pool. */
  private seasonalFxGroup = new THREE.Group();
  private seasonalFx: {
    mesh: THREE.Mesh;
    vx: number; vy: number; vz: number;
    rotAxisX: number; rotAxisZ: number;
    rotSpeed: number;
    swayPhase: number;
    swayFreq: number;
  }[] = [];
  /** The season the particle pool is currently configured for. We
   *  re-skin the pool (color, size, material) when the season changes,
   *  but the meshes themselves are reused. */
  private seasonalFxSeason: Season | null = null;

  /** Ground overlays — physical small quads laid flat on the lot to
   *  visually represent snow blanket (winter) and fallen leaf piles
   *  (fall). Implemented as InstancedMesh so we can have hundreds of
   *  instances at almost-zero per-frame cost. The whole pile is
   *  generated once at boot and faded in/out by season via the
   *  material's opacity each frame. */
  private groundOverlayGroup = new THREE.Group();
  private snowOverlay: THREE.InstancedMesh | null = null;
  private leafOverlay: THREE.InstancedMesh | null = null;
  private particles: {
    obj: THREE.Mesh;
    vx: number; vy: number; vz: number;
    life: number; maxLife: number;
    /** True if obj.material was cloned per-instance and needs disposal
     *  on death. False if the material came from the shared mat() cache
     *  (in which case disposal would break every other user). */
    disposeMat?: boolean;
  }[] = [];
  /** Drop-in animations for newly-spawned building meshes (legacy — disabled). */
  private dropAnims: { obj: THREE.Object3D; t: number; duration: number; startY: number }[] = [];
  /** Construction reveal — animates a clipping plane up through the building.
   *  Also stores the mesh + footprint so we can shake it & emit dust at the front. */
  private buildAnims: {
    plane: THREE.Plane;
    mesh: THREE.Object3D;
    cx: number; cz: number;
    w: number; d: number;
    fromY: number; toY: number;
    t: number; duration: number;
    dustCooldown: number;
  }[] = [];
  /** Approximate max Y of each building's voxel model — used by build animation. */
  private static readonly BUILDING_HEIGHT: Record<string, number> = {
    granary: 3.5,
    mill: 5.0,
    mailbox: 0,
    'truck-depot': 0,
  };
  /** Camera shake amplitude (seconds remaining). Decays each frame. */
  private camShake = 0;

  // Per-tile group registry for rebuilds
  private tileGroups = new Map<string, THREE.Group>();
  private cropEntries = new Map<string, { obj: THREE.Object3D; stage: number }>();
  private millSails = new Map<string, THREE.Object3D>();

  private cursorMarker: THREE.Mesh;
  /** Floating × / + glyph rendered above the hovered world tile when the
   *  zones manager has a plot selected. Drives the click-to-add/remove UX. */
  private zoneEditCursorEl: HTMLDivElement | null = null;
  private zoneEditKind: 'remove' | 'add' | 'none' = 'none';
  /** Dedicated bold tile selector for zones-edit hover: a filled glow
   *  plane + an outline ring + four corner brackets, all tinted red or
   *  green. Pulses on a sine so it reads as actively pickable.
   *  Lives separate from cursorMarker which is used by hold-tool UX. */
  private zoneEditMarker: THREE.Group | null = null;
  private zoneEditMarkerFill: THREE.MeshBasicMaterial | null = null;
  private zoneEditMarkerRing: THREE.MeshBasicMaterial | null = null;
  private zoneEditMarkerCorner: THREE.MeshBasicMaterial | null = null;
  /** Yellow translucent tile-glow drawn over every tile in the plot the
   *  Zones manager has selected. Visualizes the plot in the world even
   *  before the player dives in. Rebuilt on plot/edit state changes. */
  private selectedZoneOverlay: THREE.Group | null = null;
  /** Floating DOM label with the selected plot's name, positioned over
   *  the plot's bounding-box center each frame via tileScreen(). */
  private selectedZoneNameEl: HTMLDivElement | null = null;
  private cursorLamp!: THREE.PointLight;
  private waterIndicator!: THREE.Mesh;
  private sickleIndicator!: THREE.Mesh;
  private truckMesh: THREE.Object3D;
  private truckHeadlights: { mesh: THREE.Mesh; light: THREE.PointLight }[] = [];
  private truckCargoSlot: THREE.Group = new THREE.Group();
  private truckBrakeLights: THREE.Mesh[] = [];
  /** 4 wheel meshes — spin around their local X axis based on velocity. */
  private truckWheels: THREE.Mesh[] = [];
  /** Body subgroup — pitches/rolls with acceleration for suspension feel. */
  private truckBody: THREE.Group = new THREE.Group();
  /** Local-space exhaust tip + per-wheel ground positions for particle spawns. */
  private truckExhaustTip = new THREE.Vector3();
  private truckWheelGround: THREE.Vector3[] = [];
  /** Last-frame world Z position, for velocity / acceleration estimation. */
  /** Last truck X (world). Replaces the old truckPrevZ — truck now drives
   *  along the X-axis road, so we read X-delta for speed/wheel-spin/dust. */
  private truckPrevX = 0;
  private truckPrevSpeed = 0;
  /** Smoothed body pitch (radians) — eased toward target based on accel. */
  private truckBodyPitch = 0;
  /** Suspension dip — smoothed toward a target derived from cargo weight.
   *  Negative = truck sinks. Animated in animateTruck so loading reads
   *  as a gentle settle, not an instant teleport. */
  private truckDip = 0;
  /** Cached current cargo item count so animateTruck doesn't need to
   *  re-sum every frame. Updated in updateTruckCargo. */
  private lastCargoCount = 0;
  /** Per-crate spawn timestamp so newly-loaded crates can scale-in
   *  briefly — gives the visual "thunk!" of placing a box on the bed. */
  private crateSpawnT = new WeakMap<THREE.Object3D, number>();
  /** Smoothed body roll (radians) — small lateral sway. */
  private truckBodyRoll = 0;
  /** Exhaust + tire dust spawn cooldowns. */
  private truckExhaustCooldown = 0;
  private truckTireDustCooldown = 0;
  private truckBase = new THREE.Vector3();
  /** Voxel cube stack on the depot platform — renders state.truck.platform
   *  so items the player queues while the truck is away are visible at
   *  the depot. On arrival the platform drains into the truck and these
   *  crates animate over to the bed via the existing crate spawn-in. */
  private truckPlatformSlot = new THREE.Group();
  private lastPlatformHash = '';
  /** Visible material crate at each under-construction building site.
   *  Keyed by the building anchor (tileKey). Rendered from
   *  tile.construction.stagePaid so the player sees their deposited
   *  wood / wheat / stone piling up next to the build site. Cleared on
   *  stage advance + construction complete. */
  private constructionCrates = new Map<string, THREE.Group>();
  /** Per-kind crate template — built once via makeCrate, cloned per
   *  instance for each visible crate on the truck or platform. */
  private crateTemplates = new Map<string, THREE.Mesh>();
  private getCrateTemplate(kind: CargoCrateKind): THREE.Mesh {
    let t = this.crateTemplates.get(kind);
    if (!t) {
      t = makeCrate(kind, kind.charCodeAt(0) * 13 + kind.length * 7) as THREE.Mesh;
      this.crateTemplates.set(kind, t);
    }
    return t;
  }
  private lastCargoHash = '';
  private truckDustCooldown = 0;

  // Per-frame animation registries
  private swayNodes: { node: THREE.Object3D; freq: number; amp: number; phase: number; axis: 'x' | 'z' }[] = [];
  private smokers: { obj: THREE.Object3D; vy: number; life: number; maxLife: number; baseY: number }[] = [];
  private smokeSources: { x: number; y: number; z: number; cooldown: number; kind?: 'granary' | 'town' }[] = [];
  private flyers: { obj: THREE.Object3D; vx: number; vz: number; phase: number; baseY: number; freq: number }[] = [];
  private cloudData: { obj: THREE.Object3D; vx: number }[] = [];

  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private hovered: { c: number; r: number } | null = null;
  private elapsed = 0;

  // Day/night cycle — timeOfDay is now derived from sim.getTimeOfDay() so
  // the HUD calendar widget and the world animation share one source of
  // truth. Local accumulator gone; this is kept as a per-frame cache.
  private timeOfDay = 0.35;

  // Iso-snap camera — rotation locked to 4 cardinal isos. Q/E step the
  // target by ±π/2; we tween currentAzimuth toward it each frame.
  private currentAzimuth = Math.PI / 4;
  private targetAzimuth  = Math.PI / 4;
  private static readonly ISO_POLAR  = Math.PI / 3;    // ~60° from vertical (default iso)
  private static readonly PLOT_POLAR = Math.PI / 7;    // ~26° from vertical (near top-down)
  /** Live polar — tweens between iso and plot poses. */
  private currentPolar = Math.PI / 3;
  private targetPolar  = Math.PI / 3;
  private static readonly AZIMUTH_TWEEN = 6;            // ease rate
  private sun!: THREE.DirectionalLight;
  private ambient!: THREE.AmbientLight;
  private skyColor = new THREE.Color();
  private sunBaseColor = new THREE.Color();

  private canvas: HTMLCanvasElement;
  private viewSize = 14;

  constructor(canvas: HTMLCanvasElement, private sim: Sim) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    // Cap pixelRatio at 1.0 — running the composer chain (6 passes inc.
    // UnrealBloom's 10+ internal passes) at 2× resolution on retina was
    // the dominant cost on weaker GPUs. FXAA in the chain handles edge
    // smoothing well enough at native res.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.localClippingEnabled = true;     // per-material clip planes for build animation
    // Linear → sRGB tonemapping so the post-process color grade composes
    // naturally with the bloom pass. Output gamma is applied by the
    // OutputPass at the end of the composer chain.
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.scene.background = new THREE.Color(COL.sky);
    // Atmospheric fog tinted to the sky dome's horizon hue (recolored
    // every frame by animateDayNight). Pushed well past the play area
    // so the foreground reads with full contrast — only very distant
    // edges feather into the sky band. The previous (22, 44) was hazing
    // the entire scene; (55, 105) keeps the diorama edge soft without
    // obstructing close-up gameplay.
    this.scene.fog = new THREE.Fog(COL.sky, 55, 105);

    const aspect = window.innerWidth / window.innerHeight;
    const d = this.viewSize;
    this.camera = new THREE.OrthographicCamera(-d * aspect, d * aspect, d, -d, 0.1, 200);
    // Position camera to look at plot center
    this.camera.position.set(PLOT_W * 0.7, PLOT_H * 0.8, PLOT_H * 0.7);
    this.camera.lookAt(0, 0, 0);

    // Lights — driven by the day/night cycle in animateDayNight()
    this.ambient = new THREE.AmbientLight(0xb8c8e0, 0.55);
    this.scene.add(this.ambient);
    this.sun = new THREE.DirectionalLight(0xfff2d0, 1.1);
    this.sunBaseColor.set(0xfff2d0);
    this.sun.castShadow = true;
    // Smaller shadow map + moderate PCF radius = softer penumbra at a
    // lower texture cost. 2048 is plenty for the diorama scale; 16 PCF
    // samples is the perf sweet spot — 25 was overkill (only marginally
    // smoother visually but 35% more shadow-lookup cost per lit pixel).
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.radius = 6;             // PCF blur radius
    this.sun.shadow.blurSamples = 16;       // smoother penumbra (PCF)
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 80;
    const halfW = PLOT_W / 2 + 4;
    const halfH = PLOT_H / 2 + 4;
    this.sun.shadow.camera.left = -halfW;
    this.sun.shadow.camera.right = halfW;
    this.sun.shadow.camera.top = halfH;
    this.sun.shadow.camera.bottom = -halfH;
    this.sun.shadow.bias = -0.0012;
    this.sun.shadow.normalBias = 0.05;
    this.scene.add(this.sun);
    const fill = new THREE.DirectionalLight(0x5078a0, 0.25);
    fill.position.set(-10, 6, -10);
    this.scene.add(fill);
    // Initial sky color is set by animateDayNight on the first tick.

    this.scene.add(this.worldGroup);
    this.scene.add(this.placementGroup);
    this.scene.add(this.ghostGroup);
    this.scene.add(this.selectionOverlayGroup);

    // Post-processing chain — must be built AFTER scene + camera so the
    // RenderPass binds to the right targets. Drives tilt-shift miniature
    // blur, warm color grade, vignette, bloom and FXAA.
    this.postprocessing = new Postprocessing(this.renderer, this.scene, this.camera);
    this.scene.add(this.cloudGroup);
    this.scene.add(this.flyerGroup);
    this.scene.add(this.skyGroup);
    this.spawnClouds();
    this.spawnAmbientFlyers();
    this.buildStarsAndMoon();
    this.spawnFireflies();
    this.spawnSeasonalFx();
    this.spawnGroundOverlays();

    // Hover marker
    // Cursor lamp — a soft warm point-light that follows the ground cursor
    // and only ramps up after sundown so dark scenes stay readable.
    this.cursorLamp = new THREE.PointLight(0xffd070, 0, 4.5, 1.8);
    this.cursorLamp.position.set(0, 0.6, 0);
    this.scene.add(this.cursorLamp);

    this.cursorMarker = new THREE.Mesh(
      new THREE.PlaneGeometry(0.92, 0.92),
      // depthTest:false + a high renderOrder so the hover patch draws
      // OVER grass tufts. Opacity kept low so the marker reads as a
      // gentle tint rather than a flat colored cover.
      new THREE.MeshBasicMaterial({
        color: COL.hoverMark, transparent: true, opacity: 0.09,
        depthWrite: false, depthTest: false,
      }),
    );
    this.cursorMarker.rotation.x = -Math.PI / 2;
    this.cursorMarker.position.y = 0.05;
    this.cursorMarker.visible = false;
    this.cursorMarker.renderOrder = 9990;
    this.scene.add(this.cursorMarker);

    // Floating add/remove glyph (× or +) that follows the hovered tile
    // while the Zones manager has a plot selected for editing. Hidden
    // otherwise. Positioned via tileScreen() each frame.
    this.zoneEditCursorEl = document.createElement('div');
    this.zoneEditCursorEl.className = 'ui-zone-cursor';
    this.zoneEditCursorEl.hidden = true;
    document.body.appendChild(this.zoneEditCursorEl);

    // Bold zones-edit marker — separate from cursorMarker because the
    // hold-tool flow already commandeers that one. depthTest:false +
    // a high renderOrder ensures the marker draws OVER grass tufts and
    // any other tile decor rather than being occluded by them.
    {
      const g = new THREE.Group();
      g.renderOrder = 9999;
      const matOpts = { transparent: true, depthWrite: false, depthTest: false };
      this.zoneEditMarkerFill   = new THREE.MeshBasicMaterial({ color: 0xff6040, opacity: 0.55, ...matOpts });
      this.zoneEditMarkerRing   = new THREE.MeshBasicMaterial({ color: 0xff6040, opacity: 0.95, ...matOpts });
      this.zoneEditMarkerCorner = new THREE.MeshBasicMaterial({ color: 0xff6040, opacity: 1.0,  ...matOpts });
      // Raised above the tuft layer (~0.10 tall) so the marker reads
      // clearly on grassy ground.
      const FILL_Y = 0.20;
      const RING_Y = 0.21;
      const CORN_Y = 0.23;
      const fill = new THREE.Mesh(new THREE.PlaneGeometry(1.00, 1.00), this.zoneEditMarkerFill);
      fill.rotation.x = -Math.PI / 2;
      fill.position.y = FILL_Y;
      fill.renderOrder = 9999;
      g.add(fill);
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.46, 0.50, 32), this.zoneEditMarkerRing);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = RING_Y;
      ring.renderOrder = 10000;
      g.add(ring);
      // Four corner bracket voxels, each an L of two short bars.
      const BRACKET_LEN = 0.18;
      const BRACKET_W   = 0.06;
      const BRACKET_H   = 0.05;
      const E = 0.50;
      const cornerOffsets: [number, number, number, number][] = [
        [-E, -E,  1,  1],
        [ E, -E, -1,  1],
        [-E,  E,  1, -1],
        [ E,  E, -1, -1],
      ];
      for (const [cx, cz, dx, dz] of cornerOffsets) {
        const armH = new THREE.Mesh(new THREE.BoxGeometry(BRACKET_LEN, BRACKET_H, BRACKET_W), this.zoneEditMarkerCorner);
        armH.position.set(cx + dx * BRACKET_LEN / 2, CORN_Y, cz + dz * BRACKET_W / 2);
        armH.renderOrder = 10001;
        g.add(armH);
        const armV = new THREE.Mesh(new THREE.BoxGeometry(BRACKET_W, BRACKET_H, BRACKET_LEN), this.zoneEditMarkerCorner);
        armV.position.set(cx + dx * BRACKET_W / 2, CORN_Y, cz + dz * BRACKET_LEN / 2);
        armV.renderOrder = 10001;
        g.add(armV);
      }
      g.visible = false;
      this.scene.add(g);
      this.zoneEditMarker = g;
    }
    // Selected-zone overlay container — populated lazily when a plot is
    // selected via the Zones manager. Each child is a glowing yellow
    // plane over a plot tile.
    this.selectedZoneOverlay = new THREE.Group();
    this.selectedZoneOverlay.renderOrder = 9998;
    this.scene.add(this.selectedZoneOverlay);
    // Plot name banner — pure DOM, position driven from tileScreen each
    // frame. Hidden when no plot is selected.
    this.selectedZoneNameEl = document.createElement('div');
    this.selectedZoneNameEl.className = 'ui-zone-banner';
    this.selectedZoneNameEl.hidden = true;
    document.body.appendChild(this.selectedZoneNameEl);

    // Water-pour indicator — diamond outline showing the area the watering
    // can will cover (centred on the cursor). Visible only while the
    // water tool is active in plot mode.
    {
      const waterMat = new THREE.MeshBasicMaterial({ color: 0x4a78b0, transparent: true, opacity: 0.55, depthWrite: false });
      const waterRing = new THREE.Mesh(new THREE.RingGeometry(0.10, 0.16, 4), waterMat);
      waterRing.rotation.x = -Math.PI / 2;
      waterRing.rotation.z = Math.PI / 4;   // diamond orientation
      waterRing.position.y = 0.05;
      waterRing.visible = false;
      this.scene.add(waterRing);
      this.waterIndicator = waterRing;
    }

    // Sickle-swing indicator — small arrow showing the blade's facing on
    // the ground in front of the cursor. Visible only with sickle in plot.
    {
      const arrowMat = new THREE.MeshBasicMaterial({ color: 0xe8c850, transparent: true, opacity: 0.85, depthWrite: false });
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.20, 4), arrowMat);
      arrow.rotation.x = Math.PI / 2;     // lay flat on ground, tip forward
      arrow.position.y = 0.06;
      arrow.visible = false;
      this.scene.add(arrow);
      this.sickleIndicator = arrow;
    }

    // Truck mesh: visible at the depot when truck is idle/loading.
    const tr = makeTruck();
    this.truckMesh = tr.obj;
    this.truckHeadlights = tr.headlights;
    this.truckCargoSlot = tr.cargoSlot;
    this.truckBrakeLights = tr.brakeLights;
    this.truckWheels = tr.wheels;
    this.truckBody = tr.body;
    this.truckExhaustTip.copy(tr.exhaustTip);
    this.truckWheelGround = tr.wheelGround.map(v => v.clone());
    this.scene.add(this.truckMesh);
    // Depot platform — sits next to the truck-base, hosts the cube stack
    // of items the player queues while the truck is away.
    this.scene.add(this.truckPlatformSlot);

    // Custom iso-snap controller — no rotation by mouse, Q/E for snap-rotate,
    // wheel for zoom, middle-drag for pan (clamped to plot bounds).
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointerleave', this.onPointerUp);
    this.applyCamera();

    // Build the whole plot + static lot base. The plot now spans the entire
    // visible world — every tile is editable.
    this.buildLotBase();
    this.buildPlot();
    this.refreshSmokeSources();

    // Persisted plots from save: rebuild all per-sub-cell visuals up-front.
    // Weeds always render (world-wide), the rest only render if any plot
    // is adopted.
    this.buildPlotWeeds();
    if ((this.sim.state.plots ?? []).length > 0) {
      this.buildPlotTilled();
      this.buildPlotSeeds();
      this.buildPlotWet();
      this.buildPlotSoilColumns();
      this.buildPlotSurfaces();
      this.buildPlotEdges();
    }
    // Plot fences render in both overview AND plot mode so the player can
    // see plot boundaries at a glance. Built unconditionally — if there
    // are no plots yet the call is a cheap no-op.
    this.rebuildPlotFences();
    this.refreshSelectionOverlay();

    // Tool props — one per manual tool. Visibility + position handled in
    // animateToolProp; only the prop for the active tool is shown.
    this.toolProps['field']   = makeHoe();
    this.toolProps['water']   = makeWateringCan();
    this.toolProps['plant']   = makeSeedBag();
    this.toolProps['scythe']  = makeScythe();
    // Axe + pickaxe are wrapped in a parent Group whose origin sits at
    // the GRIP end of the haft. Setting wrapper.position = cursor puts
    // the grip at the cursor; rotation around X swings the head around
    // the grip pivot. Without this wrap the pivot is at the head and
    // rotation barely moves the head visibly.
    //
    // The inner mesh is shifted DOWN by 0.92 so the haft's end-cap rivet
    // (local Y = 0.92 in makeAxe / makePickaxe) aligns with the wrapper's
    // origin. The head therefore sits at local Y ≈ -0.72 below the grip.
    //
    // Pickaxe also gets a 90° local Y rotation so its head bar (built
    // along the prop's Z axis) ends up perpendicular to the camera after
    // the wrapper's camera-facing Y rotation — otherwise the bar is
    // viewed edge-on and the head appears missing.
    {
      const axeInner = makeAxe();
      axeInner.position.y = -0.92;
      const axeWrap = new THREE.Group();
      axeWrap.add(axeInner);
      this.toolProps['axe'] = axeWrap;

      const pickInner = makePickaxe();
      pickInner.position.y = -0.92;
      pickInner.rotation.y = Math.PI / 2;
      const pickWrap = new THREE.Group();
      pickWrap.add(pickInner);
      this.toolProps['pickaxe'] = pickWrap;
    }
    for (const k in this.toolProps) {
      // Sickle is a hand tool — half-size compared to the rest. Other tools
      // keep the full-size cursor scale.
      this.toolProps[k].scale.setScalar(k === 'scythe' ? 0.45 : 1.0);
      this.toolProps[k].visible = false;
      this.scene.add(this.toolProps[k]);
    }
    this.buildToolStands();
    this.buildStorageDisplay();
    this.refreshLamps();
    this.positionTruckAtDepot();

    sim.bus.on(e => {
      switch (e.type) {
        case 'planted':
          this.spawnParticles(e.col, e.row, [0x7ad440, 0x9beb60, 0xfff080], 8, { life: 0.7, spread: 0.4, upBias: 0.6 });
          this.rebuildTile(e.col, e.row);
          // (Plot-mode seed meshes are object-keyed now — orphan cleanup
          // in update() handles their removal automatically.)
          break;
        case 'harvested':
          this.spawnParticles(e.col, e.row, [0xf4d03f, 0xfae89a, 0xc89058], 14, { life: 1.0, spread: 0.5, upBias: 1.2 });
          this.rebuildTile(e.col, e.row);
          this.refreshStorageDisplay();
          break;
        case 'fieldPlaced':
          this.spawnParticles(e.col, e.row, [0x9c6c44, 0x7a4d28, 0x5a3018], 6, { life: 0.5, spread: 0.3, upBias: 0.3 });
          this.rebuildTile(e.col, e.row);
          // If we got here via sub-cell completion in plot mode, drop the
          // tilled sub-cell InstancedMesh layers — the rebuilt tile mesh
          // covers the same footprint.
          this.clearTileTilledInstanced(e.col, e.row);
          break;
        case 'fieldDried':
          // Quick puff of dry earth as the tile reverts to grass.
          this.spawnParticles(e.col, e.row, [0xa07440, 0x8b6423, 0x6a4424], 5, { life: 0.6, spread: 0.35, upBias: 0.5 });
          this.rebuildTile(e.col, e.row);
          break;
        case 'chopped':
          this.spawnParticles(e.col, e.row, [0x9c6c44, 0x6a4828, 0xc89058], 18, { life: 1.2, spread: 0.8, upBias: 1.4 });
          this.rebuildTile(e.col, e.row);
          this.refreshStorageDisplay();
          break;
        case 'mushroomGrew':
          this.rebuildTile(e.col, e.row);
          if (e.stage >= 2) {
            this.spawnParticles(e.col, e.row, [0xf0e8c8, 0xc83838, 0x9a6a3a], 6, { life: 0.7, spread: 0.3, upBias: 0.5 });
          }
          break;
        case 'berryGrew':
          this.rebuildTile(e.col, e.row);
          if (e.stage >= 2) {
            this.spawnParticles(e.col, e.row, [0xc44048, 0xd86040, 0xa01828], 6, { life: 0.7, spread: 0.3, upBias: 0.5 });
          }
          break;
        case 'foraged':
          this.spawnParticles(e.col, e.row, e.kind === 'mushroom'
            ? [0xf0e8c8, 0xc83838, 0x9a6a3a]
            : [0xc44048, 0xd86040, 0xa01828],
            14, { life: 1.0, spread: 0.5, upBias: 1.2 });
          this.rebuildTile(e.col, e.row);
          break;
        case 'berryPlanted':
        case 'acornPlanted':
        case 'saplingMatured':
        case 'treeSaplingMatured':
          this.spawnParticles(e.col, e.row, [0x7ad440, 0x9beb60, 0xfff080], 10, { life: 0.8, spread: 0.4, upBias: 0.7 });
          this.rebuildTile(e.col, e.row);
          break;
        case 'plotTileRemoved':
          this.spawnParticles(e.col, e.row, [0xc8a060, 0xa07440, 0x6a4828], 12, { life: 0.8, spread: 0.5, upBias: 0.9 });
          this.rebuildTile(e.col, e.row);
          // Rebuild every per-sub-cell layer too so the removed tile's
          // tilled / seeds / wet / soil column / surface / edges all drop
          // out instantly — gives the edit action a live response.
          this.buildPlotTilled();
          this.buildPlotSeeds();
          this.buildPlotWet();
          this.buildPlotSoilColumns();
          this.buildPlotSurfaces();
          this.buildPlotEdges();
          // Plot perimeter changed too — the fence needs to follow.
          this.rebuildPlotFences();
          this.refreshSelectionOverlay();
          // The new edge meshes + fence groups default to visible; re-run
          // the sync so non-active-plot extras stay hidden in plot mode.
          this.syncWorldVisibility();
          break;
        case 'plotsChanged':
          // A plot was renamed, deleted, or its tile list mutated from
          // the Zones manager. Released tiles (plot delete or tile-
          // toggle-off) need their native grass-tuft tile mesh back —
          // rebuild each before the plot-level layers refresh, so
          // buildPlotSurfaces doesn't double up on stale plot tufts.
          if (e.releasedTiles) {
            for (const t of e.releasedTiles) this.rebuildTile(t.c, t.r);
          }
          this.buildPlotTilled();
          this.buildPlotSeeds();
          this.buildPlotWet();
          this.buildPlotSoilColumns();
          this.buildPlotSurfaces();
          this.buildPlotEdges();
          this.rebuildPlotFences();
          this.refreshSelectionOverlay();
          this.rebuildSelectedZoneOverlay();
          this.syncWorldVisibility();
          break;
        case 'zonesModeChanged':
          // Selection or open/close changed — refresh the highlighted-
          // tile overlay + name banner.
          this.rebuildSelectedZoneOverlay();
          break;
        case 'watered':
          this.spawnParticles(e.col, e.row, [0x5ab0e8, 0x80c8f0, 0xa8dcf8], 10, { life: 0.6, spread: 0.5, upBias: 0.4 });
          this.rebuildTile(e.col, e.row);
          // Plot-mode cleanup: drop the wet sub-cell InstancedMesh layers
          // for this tile — the rebuilt tile-level wet mesh covers them.
          this.clearTileWetInstanced(e.col, e.row);
          break;
        case 'truckUnloadedAtCity':
          // Cargo just sold at the city. The crates are about to vanish
          // on the next updateTruckCargo tick — burst a coin-tinted flash
          // above the truck bed first so the player sees "goods unloaded"
          // rather than "cargo silently disappeared". Particles spawn at
          // the truck's local world position with a stronger upward bias
          // for a "delivery" feel.
          if (this.truckMesh && e.items > 0) {
            this.spawnParticlesAtWorld(
              this.truckMesh.position.x,
              this.truckMesh.position.y + 0.6,
              this.truckMesh.position.z,
              [0xf4d03f, 0xfae89a, 0xfff8c8, 0xc89058],
              14, { life: 1.0, spread: 0.5, upBias: 1.2 },
            );
            this.camShake = Math.max(this.camShake, 0.04);
          }
          break;
        case 'buildingPlaced':
        case 'constructionAdvanced': {
          // Rebuild every footprint tile — the new build animation runs on
          // the freshly-spawned mesh.
          const sz = BUILDING_SIZE[e.bld as BuildingType] ?? { w: 1, d: 1 };
          for (let dr = 0; dr < sz.d; dr++) {
            for (let dc = 0; dc < sz.w; dc++) {
              this.rebuildTile(e.col + dc, e.row + dr);
            }
          }
          this.refreshSmokeSources();
          this.refreshLamps();
          this.updatePlacementSlots();
          // Stage advanced — empty the on-site crate (stagePaid resets).
          this.updateConstructionCrate(e.col, e.row);
          // Heavy thud: dust burst + camera shake
          this.spawnParticles(e.col, e.row, [0xc8a060, 0xa07440, 0x6a4828], 18, { life: 1.0, spread: 0.8, upBias: 1.2 });
          this.camShake = 0.18;
          break;
        }
        case 'materialsDeposited':
          // Crate just got more materials. Rebuild its visible stack.
          this.updateConstructionCrate(e.col, e.row);
          break;
        case 'constructionComplete': {
          // Don't rebuild — would cancel the rising animation. The clip
          // plane finishes its rise to Y=H on its own.
          this.refreshSmokeSources();
          this.refreshLamps();
          // Dispose the construction crate — building is done, no more
          // material staging needed.
          this.removeConstructionCrate(e.col, e.row);
          this.camShake = 0.30;
          break;
        }
        case 'stateChanged':
          this.updatePlacementSlots();
          break;
        case 'propMoved': {
          const [wx, , wz] = this.tileCenter(e.col, e.row);
          if (e.kind === 'toolrack' && this.toolRack) this.toolRack.position.set(wx, 0, wz);
          else if (e.kind === 'seedbag' && this.seedbagRoot) this.seedbagRoot.position.set(wx, 0, wz);
          else if (e.kind === 'storage') this.storageDisplay.root.position.set(wx, 0, wz);
          break;
        }
        case 'sold':
        case 'millFinished':
        case 'truckLoaded':
        case 'truckReturned':
          // Any event that mutates resource counts → refresh the world shelf
          // so the piles grow/shrink in sync with state.res.
          this.refreshStorageDisplay();
          break;
        case 'zoomEntered': {
          const [wx, , wz] = this.tileCenter(e.col, e.row);
          // saved* is already in sync via animateZoom (idle branch).
          this.zoom.targetX = wx;
          this.zoom.targetZ = wz;
          this.zoom.active = true;
          this.zoom.animating = true;
          break;
        }
        case 'zoomExited':
          this.zoom.active = false;
          this.zoom.animating = true;
          break;
        case 'plotSelectChanged':
          this.refreshSelectionOverlay();
          break;
        case 'plotEntered': {
          // Compute bbox center + size from the active plot, then drive the
          // existing zoom-cam animation toward it. Tighter zoom for smaller
          // plots; looser for bigger ones so the whole thing fits.
          const tiles = this.activePlotData()?.tiles ?? [];
          if (tiles.length > 0) {
            let minC = tiles[0].c, maxC = tiles[0].c;
            let minR = tiles[0].r, maxR = tiles[0].r;
            for (const t of tiles) {
              if (t.c < minC) minC = t.c; if (t.c > maxC) maxC = t.c;
              if (t.r < minR) minR = t.r; if (t.r > maxR) maxR = t.r;
            }
            const [cx, , cz] = this.tileCenter((minC + maxC) / 2, (minR + maxR) / 2);
            const span = Math.max(maxC - minC, maxR - minR) + 2;     // +2 padding tiles
            const targetZoom = Math.max(1.0, Math.min(4.0, 14 / span));
            this.zoom.targetX = cx;
            this.zoom.targetZ = cz;
            this.zoom.targetZoom = targetZoom;
            this.zoom.active = true;
            this.zoom.animating = true;
          }
          this.refreshSelectionOverlay();
          // syncWorldVisibility now toggles plot surfaces / edges /
          // fences / tilled / wet / seed meshes via .visible, so we
          // skip the wholesale tear-down + rebuild that used to fire
          // here. Was the 5-10s freeze on plot enter for a fully-grown
          // plot — meshes were destroyed then rebuilt from scratch.
          this.buildPlotGrid();   // cheap line overlay
          this.syncWorldVisibility();
          this.updatePlacementSlots();
          // Build any missing tile-seed meshes lazily (rate-limited).
          this.syncTileSeedVisibility();
          // Kick the "non-plot tiles sink" animation toward fully-dropped.
          this.plotDropAnim.target = 1;
          this.plotDropAnim.t = 0;
          break;
        }
        case 'plotExited':
          this.zoom.active = false;
          this.zoom.animating = true;
          this.zoom.targetZoom = undefined;
          // Lift world tiles back to ground level on the way out.
          this.plotDropAnim.target = 0;
          this.plotDropAnim.t = 0;
          this.clearPlotGrid();
          // syncWorldVisibility shows every plot's surfaces / edges /
          // fences / tilled / wet / seed meshes again — no rebuild.
          this.refreshSelectionOverlay();
          this.syncWorldVisibility();
          this.updatePlacementSlots();
          this.syncTileSeedVisibility();
          break;
        case 'subCellTilled':
          // Defer the actual mesh rebuild — flushDirtyTiles() drains the
          // per-tile dirty set in update() and rebuilds the InstancedMesh
          // layers + the tile's tufts in ONE pass. Critical for auto-fill,
          // which fires 256 events/tile in a single frame.
          this.markTileDirty(e.col, e.row, 'till');
          break;
        case 'subCellSeeded':
          this.markTileDirty(e.col, e.row, 'seed');
          break;
        case 'subCellWatered':
          this.markTileDirty(e.col, e.row, 'water');
          break;
        case 'tileAutoCompleted':
          // Sim emits this AFTER the burst of subCellTilled/Watered/Seeded
          // events that filled the tile. We're already dirty from those —
          // mark again defensively in case the sim path ever emits the
          // batch event without the per-cell ones.
          this.markTileDirty(e.col, e.row, e.mode === 'till' ? 'till' : e.mode === 'water' ? 'water' : 'seed');
          break;
        case 'plotAutoCompleted': {
          const plots = this.sim.state.plots ?? [];
          const plot = plots[e.plotIdx];
          if (plot) {
            const mode = e.mode === 'till' ? 'till' : e.mode === 'water' ? 'water' : 'seed';
            for (const t of plot.tiles) this.markTileDirty(t.c, t.r, mode);
          }
          break;
        }
        case 'subCellHarvested': {
          // Single-stalk reap — keep it small. Tile-level harvest below
          // still uses the larger default chip size.
          const palette = e.cropType === 'corn'   ? [0xfff080, 0xe8c850, 0x80a050]
                        : e.cropType === 'carrot' ? [0xff8030, 0xd8601c, 0x60a030]
                        :                            [0xe8c850, 0xfae89a, 0xc89058]; // wheat
          this.spawnParticles(e.col, e.row, palette, 5,
            { life: 0.5, spread: 0.30, upBias: 0.9, scale: 0.035 });
          this.camShake = Math.max(this.camShake, 0.03);
          break;
        }
        case 'weedSpawned':
          // A weed just regenerated — rebuild the InstancedMesh so the
          // new sub-cell tuft is rendered. Regrowth is rare (interval ~
          // 25s, cap 40 instances) so the full rebuild is fine.
          this.buildPlotWeeds();
          break;
        case 'weedPlucked': {
          // Hide the instance by stamping a zero-scale matrix and marking
          // the slot dead. Cheaper than rebuilding the whole InstancedMesh
          // on every pluck (3000+ matrices). The slot returns to life
          // only on a full buildPlotWeeds rebuild.
          const k = this.weedKey(e.col, e.row, e.subC, e.subR);
          const id = this.weedKeyToIndex.get(k);
          if (id !== undefined && this.weedsInstanced) {
            const slot = this.weedInstanceCoords[id];
            if (slot && slot.alive) {
              slot.alive = false;
              this.weedsInstanced.setMatrixAt(id, this.weedHiddenMatrix);
              this.weedsInstanced.instanceMatrix.needsUpdate = true;
            }
          }
          // Particles at the sub-cell — green flecks + tiny camera kick.
          this.spawnParticles(e.col, e.row, [0x4a7a32, 0x80a050, 0xc8c860], 8,
            { life: 0.6, spread: 0.35, upBias: 0.9 });
          this.camShake = Math.max(this.camShake, 0.06);
          break;
        }
      }
    });

    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);

    this.resize(window.innerWidth, window.innerHeight);
    this.updatePlacementSlots();
  }

  // ── Coords ────────────────────────────────────────────────────────
  private tileCenter(c: number, r: number): [number, number, number] {
    return [c - PLOT_W / 2 + 0.5, 0, r - PLOT_H / 2 + 0.5];
  }
  private tileKey(c: number, r: number) { return `${c}:${r}`; }

  /**
   * Lot earth body — now flush with the surrounding world ground, so most
   * of the chunky stratified body is hidden. We render a single thin earth
   * slab under the lot to fill the volume below the tile tops.
   * (The detailed side variants would be invisible here, so they're skipped.)
   */
  private buildLotBase(): void {
    return this._buildLotBase_detailed();
  }

  private _buildLotBase_detailed(): void {
    const b = new VoxelBuilder();
    const halfW = PLOT_W / 2;
    const halfH = PLOT_H / 2;
    const SIDE_TOP = -0.30;
    const SIDE_H   = 1.8;
    // Finer voxels + more layers so the perimeter reads at the same density
    // as the plot's interior soil columns. ALONG=16 across each tile-face,
    // matching PLOT_SUB_DIM, and ~15 vertical layers.
    const VOX_W = 1 / 16;            // ≈0.0625 — matches PLOT_SUB_DIM
    const VOX_H = 0.12;
    const layersN = Math.round(SIDE_H / VOX_H);
    const ALONG = 16;

    // Interior fill — dark earth body
    b.add(PLOT_W - 2, SIDE_H, PLOT_H - 2, 0, SIDE_TOP - SIDE_H / 2, 0, COL.earthDark);

    // Strata palettes — top → bottom, multi-shade per stratum so each voxel
    // reads as a discrete crumb of earth rather than a flat band.
    const TOPSOIL = [0x9c7338, 0xa07a3a, 0x8c6a32, 0xb08850, 0x886030];
    const MIDSOIL = [0x7a5828, 0x886030, 0x6e4818, 0x9c6848, 0x5a3818];
    const DEEPSOIL= [0x5a3818, 0x4a2810, 0x6a4828, 0x3a1808, 0x2a1808];
    const CLAY    = [0xa88058, 0x986840, 0xb89068, 0xc8a878];
    const STONE   = [0x808890, 0x707880, 0x9098a0];
    const ROOT_DK = 0x402810;

    const sides: { axis: 'N' | 'S' | 'E' | 'W'; nx: number; nz: number }[] = [
      { axis: 'N', nx: 0, nz: -1 },
      { axis: 'S', nx: 0, nz:  1 },
      { axis: 'W', nx: -1, nz: 0 },
      { axis: 'E', nx:  1, nz: 0 },
    ];
    for (const side of sides) {
      const along = (side.axis === 'N' || side.axis === 'S') ? PLOT_W : PLOT_H;
      for (let i = 0; i < along; i++) {
        let tileC: number, tileR: number;
        if (side.axis === 'N') { tileC = i; tileR = 0; }
        else if (side.axis === 'S') { tileC = i; tileR = PLOT_H - 1; }
        else if (side.axis === 'W') { tileC = 0; tileR = i; }
        else { tileC = PLOT_W - 1; tileR = i; }

        const tile = this.sim.state.plot.tiles[tileR]?.[tileC];
        const variant = pickSideVariant(tileC, tileR, side.axis, tile?.thing);
        const cx = tileC - halfW + 0.5;
        const cz = tileR - halfH + 0.5;
        const seed0 = ((tileC * 7919) ^ (tileR * 17389) ^ side.axis.charCodeAt(0)) >>> 0;
        const rng = rngFn(seed0);

        for (let ax = 0; ax < ALONG; ax++) {
          for (let ly = 0; ly < layersN; ly++) {
            const yy = SIDE_TOP - (ly + 0.5) * VOX_H;
            let vx: number, vz: number;
            if (side.axis === 'N' || side.axis === 'S') {
              vx = cx - 0.5 + (ax + 0.5) * (1 / ALONG);
              vz = cz + side.nz * 0.5;
              // Slight outward extrusion per-voxel so the face is bumpy.
              vz += side.nz * (rng() - 0.5) * 0.04;
            } else {
              vz = cz - 0.5 + (ax + 0.5) * (1 / ALONG);
              vx = cx + side.nx * 0.5;
              vx += side.nx * (rng() - 0.5) * 0.04;
            }
            // Stratum pick — top topsoil (2 layers), mid (4), deep (4),
            // clay (3), deep again toward bedrock.
            let palette: number[];
            if (ly < 2) palette = TOPSOIL;
            else if (ly < 6) palette = MIDSOIL;
            else if (ly < 10) palette = DEEPSOIL;
            else if (ly < 13) palette = CLAY;
            else palette = DEEPSOIL;
            let col = palette[Math.floor(rng() * palette.length)];
            // Occasional embedded stone speck in upper layers
            if (ly >= 1 && ly <= 6 && rng() < 0.10) col = STONE[Math.floor(rng() * STONE.length)];
            // Occasional dark root strand in top topsoil
            if (ly >= 0 && ly <= 3 && rng() < 0.06) col = ROOT_DK;
            // Drop ~5% for craggy outline
            if (rng() < 0.05) continue;
            b.add(VOX_W * 0.95, VOX_H * 0.95, VOX_W * 0.95, vx, yy, vz, col);
          }
          // Extra random protruding chunks every few cells — exposed roots
          // and rocks bulging out of the cliff face for clear 3D read.
          if (rng() < 0.18) {
            const ly = Math.floor(rng() * (layersN - 3)) + 1;
            const yy = SIDE_TOP - (ly + 0.5) * VOX_H;
            let vx: number, vz: number;
            if (side.axis === 'N' || side.axis === 'S') {
              vx = cx - 0.5 + (ax + 0.5) * (1 / ALONG);
              vz = cz + side.nz * (0.5 + 0.04);
            } else {
              vz = cz - 0.5 + (ax + 0.5) * (1 / ALONG);
              vx = cx + side.nx * (0.5 + 0.04);
            }
            const protrudeCol = rng() < 0.4 ? STONE[Math.floor(rng() * STONE.length)] : ROOT_DK;
            b.add(VOX_W * 0.6, VOX_H * 0.6, VOX_W * 0.6, vx, yy, vz, protrudeCol);
          }
        }

        applySideVariant(b, variant, side, cx, cz, SIDE_TOP, VOX_W);
      }
    }

    const mesh = b.build();
    mesh.name = 'lot-base';
    this.scene.add(mesh);

    this.buildHangingVines();
  }

  /** Long voxel vines hanging from the lot's perimeter edge — gives the
   *  floating-island silhouette texture and life. Each vine is its own
   *  mesh pivoted at the top so it swings around its anchor point;
   *  registered with the sway animator so wind drifts it visibly.
   *
   *  Density is moderate — every ~3rd tile-face along each side gets a
   *  vine cluster of 1–3 strands, with length / colour / lean varied per
   *  strand. Tip carries a small leaf cluster so the bottom reads as
   *  "vine end" not "missing chunk". */
  private vinesGroup: THREE.Group | null = null;
  private buildHangingVines() {
    if (this.vinesGroup) {
      this.scene.remove(this.vinesGroup);
      disposeTree(this.vinesGroup);
      this.vinesGroup = null;
    }
    const group = new THREE.Group();
    group.name = 'hanging-vines';
    const halfW = PLOT_W / 2;
    const halfH = PLOT_H / 2;
    // Lot top sits at y=0 in world space, side runs from y=-0.30 down to
    // about y=-2.10 (SIDE_TOP - SIDE_H from _buildLotBase_detailed).
    const ATTACH_Y = -0.32;
    const VINE_GREENS = [0x4a7a32, 0x60984a, 0x7ad440, 0x86c054, 0x3a5e22, 0x6ba840];
    const sides: { axis: 'N' | 'S' | 'E' | 'W'; nx: number; nz: number }[] = [
      { axis: 'N', nx: 0, nz: -1 },
      { axis: 'S', nx: 0, nz:  1 },
      { axis: 'W', nx: -1, nz: 0 },
      { axis: 'E', nx:  1, nz: 0 },
    ];
    for (const side of sides) {
      const along = (side.axis === 'N' || side.axis === 'S') ? PLOT_W : PLOT_H;
      for (let i = 0; i < along; i++) {
        // Skip the road strip rows on east/west sides (no vines hanging
        // from the dirt road) — looks weird otherwise.
        if ((side.axis === 'W' || side.axis === 'E') && i >= ROAD_ROW) continue;
        const rng = rngFn(i * 9907 + side.axis.charCodeAt(0) * 131);
        // ~40% of tile-faces get a vine cluster.
        if (rng() > 0.40) continue;
        let cx: number, cz: number;
        if (side.axis === 'N')      { cx = i - halfW + 0.5; cz = -halfH; }
        else if (side.axis === 'S') { cx = i - halfW + 0.5; cz =  halfH; }
        else if (side.axis === 'W') { cx = -halfW;          cz = i - halfH + 0.5; }
        else                         { cx =  halfW;          cz = i - halfH + 0.5; }

        const strands = 1 + Math.floor(rng() * 3);
        for (let s = 0; s < strands; s++) {
          // Spread strands along the tile face, slight outward nudge.
          const tileLocal = (rng() - 0.5) * 0.7;
          const sx = cx + (side.axis === 'N' || side.axis === 'S' ? tileLocal : side.nx * 0.04);
          const sz = cz + (side.axis === 'E' || side.axis === 'W' ? tileLocal : side.nz * 0.04);

          const vineB = new VoxelBuilder();
          const length = 0.7 + rng() * 1.4;        // 0.7–2.1 world units
          const SEG = 0.10;
          const segCount = Math.floor(length / SEG);
          const baseCol = VINE_GREENS[Math.floor(rng() * VINE_GREENS.length)];
          // Per-strand sway phase + small static lean so neighbouring vines
          // don't sway in perfect unison.
          const leanX = (rng() - 0.5) * 0.08;
          const leanZ = (rng() - 0.5) * 0.08;
          for (let j = 0; j < segCount; j++) {
            const yTop = -j * SEG;                 // tip dangles toward y = -length
            const t = j / Math.max(1, segCount - 1);
            // Slight taper + colour variance — base wider, tip slightly
            // pinched and a touch darker for depth.
            const w = 0.06 - t * 0.012;
            const col = rng() < 0.18
              ? VINE_GREENS[Math.floor(rng() * VINE_GREENS.length)]
              : baseCol;
            vineB.add(w, SEG * 1.02, w,
              leanX * t * t, yTop - SEG / 2, leanZ * t * t, col);
          }
          // Leaf cluster at the tip — 2-3 small green cubes for a feathery end.
          const tipY = -segCount * SEG;
          const leafCount = 2 + Math.floor(rng() * 2);
          for (let k = 0; k < leafCount; k++) {
            const lx = leanX + (rng() - 0.5) * 0.10;
            const lz = leanZ + (rng() - 0.5) * 0.10;
            const lc = VINE_GREENS[Math.floor(rng() * VINE_GREENS.length)];
            vineB.add(0.08, 0.06, 0.08, lx, tipY + 0.02 + k * 0.04, lz, lc);
          }
          // 30% chance of a tiny flower or berry on the vine for visual pop.
          if (rng() < 0.30) {
            const fy = tipY + (0.05 + rng() * Math.max(0.05, segCount * SEG - 0.10));
            const fc = rng() < 0.4 ? 0xfff080 : (rng() < 0.6 ? 0xff80a8 : 0xe04848);
            vineB.add(0.05, 0.05, 0.05, leanX * 0.3, fy, leanZ * 0.3, fc);
          }

          const vineMesh = vineB.build();
          vineMesh.position.set(sx, ATTACH_Y, sz);
          vineMesh.castShadow = false;
          vineMesh.receiveShadow = false;
          // Sway — registerSway tilts the mesh around the chosen axis.
          // Since voxels are built hanging DOWN from y=0 (the mesh pivot),
          // the tilt swings the tip while the top stays anchored — exactly
          // the right hinge for a vine.
          this.registerSway(vineMesh,
            1.1 + rng() * 0.8,                    // freq
            0.05 + rng() * 0.04,                  // amplitude radians
            (side.axis === 'N' || side.axis === 'S') ? 'x' : 'z',
          );
          (vineMesh as any).userData = {
            ...(vineMesh as any).userData,
            swayPhase: rng() * Math.PI * 2,
          };
          group.add(vineMesh);
        }
      }
    }
    this.vinesGroup = group;
    this.scene.add(group);
  }

  // ── Build whole plot ──────────────────────────────────────────────
  private buildPlot() {
    // Clear current — dispose each child's geometry/materials before
    // detaching so a re-build (or post-load) doesn't leak the world.
    while (this.worldGroup.children.length > 0) {
      const c = this.worldGroup.children[0];
      this.worldGroup.remove(c);
      disposeTree(c);
    }
    this.tileGroups.clear();
    this.cropEntries.clear();
    this.millSails.clear();
    this.invalidateHoverCache();
    const { plot } = this.sim.state;
    for (let r = 0; r < plot.height; r++) {
      for (let c = 0; c < plot.width; c++) {
        this.buildTile(c, r);
      }
    }
  }

  private buildTile(c: number, r: number) {
    const t = this.sim.state.plot.tiles[r][c];
    const [x, , z] = this.tileCenter(c, r);

    let topCol = COL.grass;
    if (t.surf === 'field') topCol = t.watered ? COL.fieldWet : COL.fieldDry;
    else if (t.surf === 'stone') topCol = COL.stone;
    else if (t.surf === 'road')  topCol = 0x7e5828;     // dirt brown — matches world extension
    else if (t.surf === 'path')  topCol = COL.path;

    const tg = new THREE.Group();
    tg.name = `tile:${c}:${r}`;
    tg.userData = { col: c, row: r, isTile: true };

    // Top surface only — the lot-base mesh handles the earth body underneath.
    if (t.surf === 'road') {
      // High-density sub-voxel road surface — 16×16 mini-cells with
      // mottled dirt tones, embedded pebbles, occasional grass tufts,
      // damp puddle patches, and packed wheel ruts running east-west.
      // Density matches the grass tuft mat so the road doesn't read as
      // flat next to the surrounding world.
      const seed = (c * 7919 + r * 17389) >>> 0;
      const rng = rngFn(seed);
      const DIRTS = [
        0x7e5828, 0x8e6a38, 0x6a4220, 0x9c7338, 0x8a5e2a,
        0x6e4818, 0xa07a40, 0x583414, 0x886030, 0x947040,
      ];
      const PEBBLE_LT = [0x9098a0, 0xa8b0b8, 0x888890];
      const PEBBLE_DK = [0x383838, 0x4a4a4a, 0x303030];
      const PUDDLE    = [0x2a3848, 0x3a4858, 0x556070];
      const TUFT      = [0x6a9a40, 0x86b048, 0x4e8a30];
      const RUT       = [0x4a3018, 0x5a3a20, 0x3a2410];
      const N = PLOT_SUB_DIM;
      const step = 1.0 / N;
      const b = new VoxelBuilder();
      // Base slab — gives the road its body
      b.add(1.0, 0.20, 1.0, 0, -0.10, 0, DIRTS[0]);
      // Wheel-rut centerlines (in tile-local Z). Truck travels east-west
      // along world Z = depot row Z + 1.5; its two wheel tracks are
      // separated by ~0.92 world units (matches makeTruck's wheel layout
      // post-yaw). For each track, project into THIS tile's local Z and
      // keep it only if it falls inside the tile's [-0.5, +0.5] band.
      const tileWorldZ = r - PLOT_H / 2 + 0.5;
      const truckPathZ = (ROAD_ROW - 1) - PLOT_H / 2 + 0.5 + 1.5;
      const WHEEL_HALF_SEP = 0.46;
      const wheelZs = [truckPathZ - WHEEL_HALF_SEP, truckPathZ + WHEEL_HALF_SEP];
      const localRuts: number[] = [];
      for (const wz of wheelZs) {
        const lz = wz - tileWorldZ;
        if (lz > -0.5 && lz < 0.5) localRuts.push(lz);
      }
      const inRoadBand = localRuts.length > 0;
      const rutW = 0.10;
      // Top sub-voxel pattern — denser, with per-voxel height variance.
      for (let sr = 0; sr < N; sr++) {
        for (let sc = 0; sc < N; sc++) {
          const sx = -0.5 + step / 2 + sc * step;
          const sz = -0.5 + step / 2 + sr * step;
          // Are we inside a rut band? Check distance to each wheel track
          // that intersects this tile.
          let inRut = false;
          if (inRoadBand) {
            for (const rz of localRuts) {
              if (Math.abs(sz - rz) < rutW) { inRut = true; break; }
            }
          }
          // Occasional damp puddle patch — small clusters of bluish dirt.
          // Driven by a low-frequency noise so puddles read as patches.
          const puddleNoise = Math.sin(sc * 0.55 + sr * 0.31 + seed * 0.013) +
                              Math.cos(sc * 0.21 - sr * 0.42 + seed * 0.007);
          const isPuddle = puddleNoise > 1.45 && rng() < 0.7;
          let col: number;
          let h: number;
          if (isPuddle) {
            col = PUDDLE[Math.floor(rng() * PUDDLE.length)];
            h = 0.015 + rng() * 0.01;
          } else if (inRut) {
            col = RUT[Math.floor(rng() * RUT.length)];
            h = 0.012 + rng() * 0.012;          // sunken below the road
          } else {
            col = DIRTS[Math.floor(rng() * DIRTS.length)];
            h = 0.028 + rng() * 0.04;           // varied surface roughness
          }
          b.add(step * 0.98, h, step * 0.98, sx, h / 2, sz, col);
          // 4% chance — embedded small pebble poking up
          if (!isPuddle && !inRut && rng() < 0.045) {
            const pc = rng() < 0.5
              ? PEBBLE_LT[Math.floor(rng() * PEBBLE_LT.length)]
              : PEBBLE_DK[Math.floor(rng() * PEBBLE_DK.length)];
            b.add(step * 0.6, 0.04, step * 0.6, sx, h + 0.02, sz, pc);
          }
          // 2% chance — scrappy grass tuft on the road shoulder
          if (!isPuddle && !inRut && Math.abs(sz) > 0.34 && rng() < 0.06) {
            b.add(step * 0.4, 0.07, step * 0.4, sx, h + 0.035, sz, TUFT[Math.floor(rng() * TUFT.length)]);
          }
        }
      }
      // Scattered chunkier pebbles — bigger than the embedded ones above
      const bigPebbles = 3 + Math.floor(rng() * 4);
      for (let i = 0; i < bigPebbles; i++) {
        const px = (rng() - 0.5) * 0.85;
        const pz = (rng() - 0.5) * 0.85;
        const pc = rng() < 0.5
          ? PEBBLE_LT[Math.floor(rng() * PEBBLE_LT.length)]
          : PEBBLE_DK[Math.floor(rng() * PEBBLE_DK.length)];
        b.add(0.06, 0.06, 0.06, px, 0.06, pz, pc);
        if (rng() < 0.4) {
          b.add(0.04, 0.03, 0.04, px + 0.04, 0.05, pz, pc);
        }
      }
      // Subtle tire-track imprints inside each rut — short transverse
      // chevrons that hint at recent passage.
      if (inRoadBand) {
        for (const ruz of localRuts) {
          for (let i = 0; i < 6; i++) {
            const tx = -0.42 + i * 0.16 + (rng() - 0.5) * 0.04;
            b.add(0.10, 0.014, 0.06, tx, 0.012, ruz + (rng() - 0.5) * 0.04, RUT[1]);
          }
        }
      }
      const m = b.build();
      m.position.set(x, 0, z);
      tg.add(m);
    } else if (t.surf === 'field') {
      // Voxel furrow surface — distinct dry vs watered look.
      const m = makeField(!!t.watered, c * 13 + r * 7);
      m.position.set(x, 0, z);
      tg.add(m);
    } else if (t.surf === 'stone') {
      const m = makeStone(c * 19 + r * 23);
      m.position.set(x, 0, z);
      tg.add(m);
    } else {
      tg.add(cube(1.0, 0.3, 1.0, x, -0.15, z, topCol));
    }

    // Props (things) — seed by tile coord for deterministic variation
    const seed = c * 1000 + r * 7 + 41;
    if (t.thing === 'tree-oak')        { const m = makeTree(seed);      m.position.set(x, 0, z); tg.add(m); this.registerSubtreeSway(m); }
    else if (t.thing === 'tree-oak-stump') {
      const m = makeStump();      m.position.set(x, 0, z); tg.add(m);
      const ms = (t.mushStage ?? 0);
      if (ms >= 1) {
        const mush = makeMushrooms(ms, seed);
        mush.position.set(x, 0, z);
        tg.add(mush);
      }
      if (t.fallen) {
        const log = makeFallenLog(seed, ms);
        log.position.set(x, 0, z);
        tg.add(log);
      }
    }
    else if (t.thing === 'bush') {
      const m = makeBush(seed, t.berryStage ?? 0);
      m.position.set(x, 0, z); tg.add(m); this.registerSubtreeSway(m);
    }
    else if (t.thing === 'bush-sapling') {
      const m = makeBushSapling(seed); m.position.set(x, 0, z); tg.add(m); this.registerSubtreeSway(m);
    }
    else if (t.thing === 'tree-oak-sapling') {
      const m = makeTreeSapling(seed); m.position.set(x, 0, z); tg.add(m); this.registerSubtreeSway(m);
    }
    else if (t.thing === 'rock-boulder') { const m = makeBoulder(seed); m.position.set(x, 0, z); tg.add(m); }
    else if (t.thing === 'tall-grass') { const m = makeTallGrass();     m.position.set(x, 0, z); tg.add(m); this.registerSubtreeSway(m); }

    if (t.decor === 'flower-daisy') { const m = makeFlower('daisy'); m.position.set(x, 0, z); tg.add(m); }
    else if (t.decor === 'flower-poppy') { const m = makeFlower('poppy'); m.position.set(x, 0, z); tg.add(m); }

    // Dense PLOT_SUB_DIM² grass tuft mat on every grass tile (matches the plot's
    // surface treatment). Tiles with a tree/bush/boulder keep the centre
    // clear so the prop's base isn't buried. Skipped under buildings.
    // SKIPPED ALSO on tiles inside the adopted plot — the plot owns its
    // own per-sub-cell surface mesh which knows how to hide tufts on
    // hoed sub-cells. Without this gate, two layers of tufts would stack
    // and the hoe's furrow would still look grassy on top.
    const inAnyPlot = (this.sim.state.plots ?? []).some(p => p.tiles.some(pt => pt.c === c && pt.r === r));
    if (t.surf === 'grass' && !t.bld && !inAnyPlot) {
      const tufts = this.makeGrassTufts(c, r);
      if (tufts) { tufts.position.set(x, 0, z); tg.add(tufts); }
    }

    // (Watered look is baked into makeField via blue puddle voxels.)

    // Crop
    if (t.crop) {
      const crop = makeCrop(t.crop.type, t.crop.stage, seed);
      crop.position.set(x, 0, z);
      tg.add(crop);
      this.cropEntries.set(this.tileKey(c, r), { obj: crop, stage: t.crop.stage });
      this.registerSubtreeSway(crop);
    }

    // Buildings — render ONCE per building, at the anchor tile, centered
    // over the multi-tile footprint.
    if (t.bld && PlotSystem.isAnchor(this.sim.state, c, r)) {
      const { w, d } = BUILDING_SIZE[t.bld];
      const offX = (w - 1) * 0.5;
      const offZ = (d - 1) * 0.5;
      const bx = x + offX;
      const bz = z + offZ;

      // Always render the FULL building. While under construction we attach a
      // clipping plane that rises from the previous stage's height to the
      // current stage's height, so the building visibly grows up out of the
      // ground over ~2 seconds per stage.
      const full = makeBuildingForRender(t.bld);
      full.obj.position.set(bx, 0, bz);
      if (t.construction) {
        const cs = t.construction;
        const H = Diorama.BUILDING_HEIGHT[t.bld] ?? 3.0;
        const fromY = ((cs.stage - 1) / cs.totalStages) * H;
        const toY   = (cs.stage / cs.totalStages) * H;
        const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), fromY);
        full.obj.traverse(o => {
          if (o instanceof THREE.Mesh) {
            const mat = (o.material as THREE.Material).clone();
            (mat as any).clippingPlanes = [plane];
            (mat as any).clipShadows = true;
            o.material = mat;
          }
        });
        this.buildAnims.push({
          plane, mesh: full.obj, cx: bx, cz: bz, w, d,
          fromY, toY, t: 0, duration: 2.5, dustCooldown: 0,
        });
        // Scaffold accompanies the building until the final stage starts.
        if (cs.stage < cs.totalStages) {
          const progress = cs.stage / cs.totalStages;
          const scaffold = makeConstructionSite(w, d, progress);
          scaffold.position.set(bx, 0, bz);
          tg.add(scaffold);
        }
      }
      tg.add(full.obj);
      // Always register sails — they rotate quietly while clipped, then
      // become visible when the build animation completes.
      if (full.sails) this.millSails.set(this.tileKey(c, r), full.sails);
      if (t.bld === 'truck-depot') this.truckBase.set(bx, 0, bz + 1.5);
    }

    this.worldGroup.add(tg);
    this.tileGroups.set(this.tileKey(c, r), tg);
    this.invalidateHoverCache();
  }

  /** Dense 16×16 grass-tuft mat on every grass tile — matches the plot's
   *  surface treatment so the world reads at the same detail level. Tufts
   *  near the centre are skipped on tiles with a tree/bush/boulder so the
   *  prop's base isn't buried under voxels. Plot-edge tiles also get a
   *  fringe of overhanging tufts on the outer side. */
  private makeGrassTufts(c: number, r: number): THREE.Object3D | null {
    const rng = rngFn(c * 137 + r * 991 + 7);
    const b = new VoxelBuilder();
    const tile = this.sim.state.plot.tiles[r]?.[c];
    // Tree trunks / boulders sit dead-centre; reserve a centre square to
    // avoid Z-fighting and to keep the prop visually grounded.
    const hasProp = !!tile?.thing && tile.thing !== 'tall-grass';
    const GRASS = [0x6fb045, 0x7ad440, 0x9bb854, 0x5a9038, 0x80a050, 0x66a83a, 0x4a7a32];
    const N = PLOT_SUB_DIM;
    const step = 1.0 / N;
    // Build a Set of tilled sub-cells on this tile so we can skip planting
    // a tuft on any sub-cell the player has hoed. Without this, tufts stay
    // on top of the tilled mesh and the dirt rows look "grassy" — exactly
    // the bug the player hit when hoeing on free (non-plot) tiles.
    const tilledHere = new Set<number>();
    for (const s of (this.sim.state.freeTilled ?? [])) {
      if (s.c === c && s.r === r && s.stage >= 1) tilledHere.add(s.subR * N + s.subC);
    }
    // Include any plot the tile belongs to — the player can adopt a tile
    // that already has tilled work, and tilling continues on plot tiles.
    for (const p of (this.sim.state.plots ?? [])) {
      if (!p.tiles.some(t => t.c === c && t.r === r)) continue;
      for (const s of p.tilled) {
        if (s.c === c && s.r === r && s.stage >= 1) tilledHere.add(s.subR * N + s.subC);
      }
    }
    let any = false;
    for (let sr = 0; sr < N; sr++) {
      for (let sc = 0; sc < N; sc++) {
        // Skip the inner 8×8 if this tile hosts a tree/bush/boulder/etc.
        if (hasProp && sc >= 4 && sc <= 11 && sr >= 4 && sr <= 11) continue;
        // Skip sub-cells the player has tilled — the soil furrow shows
        // through cleanly instead of being buried under a grass tuft.
        if (tilledHere.has(sr * N + sc)) continue;
        const x = -0.5 + step / 2 + sc * step;
        const z = -0.5 + step / 2 + sr * step;
        const col = GRASS[Math.floor(rng() * GRASS.length)];
        const h = 0.06 + rng() * 0.07;
        b.add(step * 0.85, h, step * 0.85, x, h / 2 + 0.02, z, col);
        any = true;
      }
    }
    // 0–2 wildflower accents (lighter greens + occasional petal pop) for
    // tiles without a dominant prop. Each flower's tile-relative position
    // is mapped back to a sub-cell — skip flowers whose sub-cell has been
    // tilled so freshly-hoed dirt doesn't keep a wildflower on top.
    if (!hasProp) {
      const flowers = Math.floor(rng() * 3);
      for (let i = 0; i < flowers; i++) {
        const fx = (rng() - 0.5) * 0.85;
        const fz = (rng() - 0.5) * 0.85;
        const fSc = Math.max(0, Math.min(N - 1, Math.floor((fx + 0.5) * N)));
        const fSr = Math.max(0, Math.min(N - 1, Math.floor((fz + 0.5) * N)));
        if (tilledHere.has(fSr * N + fSc)) continue;
        const fc = rng() < 0.4 ? 0xfff080 : (rng() < 0.6 ? 0xffffff : 0xff80a8);
        b.add(0.03, 0.10, 0.03, fx, 0.07, fz, 0x6a9a44);
        b.add(0.06, 0.04, 0.06, fx, 0.14, fz, fc);
        any = true;
      }
    }
    // Plot-edge fringe — only if this tile is on the outermost ring
    const onEdge = (c === 0 || c === PLOT_W - 1 || r === 0 || r === PLOT_H - 1);
    if (onEdge) {
      const sides: { dx: number; dz: number; nx: number; nz: number }[] = [];
      if (c === 0)         sides.push({ dx: -0.5, dz: 0, nx: -1, nz: 0 });
      if (c === PLOT_W - 1) sides.push({ dx:  0.5, dz: 0, nx:  1, nz: 0 });
      if (r === 0)         sides.push({ dx: 0, dz: -0.5, nx: 0, nz: -1 });
      if (r === PLOT_H - 1) sides.push({ dx: 0, dz:  0.5, nx: 0, nz:  1 });
      for (const s of sides) {
        const n = 6 + Math.floor(rng() * 4);   // denser fringe — 6–9 tufts
        for (let i = 0; i < n; i++) {
          const along = (rng() - 0.5) * 0.85;
          const sz = 0.08 + rng() * 0.05;
          const px = s.dx + (s.nx === 0 ? along : 0) + s.nx * 0.04;
          const pz = s.dz + (s.nz === 0 ? along : 0) + s.nz * 0.04;
          const py = -rng() * 0.10 + 0.04;
          const cc = rng() < 0.4 ? COL.grass : COL.tallGrass;
          b.add(sz, 0.16, sz, px, py, pz, cc);
          any = true;
        }
      }
    }
    if (!any) return null;
    return b.build();
  }

  private rebuildTile(c: number, r: number) {
    const key = this.tileKey(c, r);
    const old = this.tileGroups.get(key);
    if (old) {
      this.worldGroup.remove(old);
      disposeTree(old);
      this.tileGroups.delete(key);
      this.cropEntries.delete(key);
      this.millSails.delete(key);
      this.invalidateHoverCache();
    }
    this.buildTile(c, r);
    // New tile mesh respects plot-mode visibility rules.
    if (this.sim.state.plotInView) this.syncWorldVisibility();
  }

  // ── Placement valid-slot overlay ──────────────────────────────────
  /** Dispose every material + geometry under placementGroup, then clear it.
   *  Necessary because we create fresh per-slot materials each rebuild —
   *  without this they'd leak across placement entries. */
  private clearPlacementGroup() {
    this.placementGroup.traverse(o => {
      if (o instanceof THREE.Mesh) {
        const g = o.geometry;
        if (g) g.dispose();
        const m = o.material as THREE.Material | THREE.Material[];
        if (Array.isArray(m)) m.forEach(x => x.dispose());
        else if (m) m.dispose();
      }
    });
    this.placementGroup.clear();
    this.placementPulseMats.length = 0;
  }
  private updatePlacementSlots() {
    this.clearPlacementGroup();
    // Tear down ghost when leaving placement mode OR entering plot view —
    // both states mean "no building is being placed in the overview".
    if (!this.sim.state.placing || this.sim.state.plotInView) {
      this.disposeGhost();
      return;
    }
    const type = this.sim.state.placing.type;
    const { w, d } = BUILDING_SIZE[type];
    const SLOT_GREEN = 0x80ff60;
    PlotSystem.forEachTile(this.sim.state, (_t, c, r) => {
      if (!PlotSystem.canBuildAt(this.sim.state, c, r, type)) return;
      // Mark every tile of the building's footprint, not just the anchor —
      // gives a clear footprint preview for multi-tile buildings (granary,
      // mill). The slot quad sits at y=0.35 — well above the 16-voxel tuft
      // layer (~0.16) so the grass detail never buries it.
      for (let dr = 0; dr < d; dr++) {
        for (let dc = 0; dc < w; dc++) {
          if (!PlotSystem.in(this.sim.state, c + dc, r + dr)) continue;
          const [tx, , tz] = this.tileCenter(c + dc, r + dr);
          // Floor quad — pulses between 0.35 and 0.65 opacity at 2 Hz.
          const floorMat = new THREE.MeshBasicMaterial({
            color: SLOT_GREEN, transparent: true, opacity: 0.5,
            depthWrite: false, side: THREE.DoubleSide,
          });
          const m = new THREE.Mesh(new THREE.PlaneGeometry(0.94, 0.94), floorMat);
          m.rotation.x = -Math.PI / 2;
          m.position.set(tx, 0.35, tz);
          m.userData = { isSlot: true, c, r };
          this.placementGroup.add(m);
          this.placementPulseMats.push(floorMat);

          // Corner pillars — 4 tall slim boxes at each tile corner. These
          // read from any iso angle, even when the floor quad is edge-on
          // to the camera (matches the selection overlay treatment).
          const corners: [number, number][] = [
            [-0.45, -0.45], [+0.45, -0.45], [-0.45, +0.45], [+0.45, +0.45],
          ];
          for (const [cdx, cdz] of corners) {
            const postMat = new THREE.MeshBasicMaterial({
              color: SLOT_GREEN, transparent: true, opacity: 0.85,
              depthWrite: false,
            });
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.55, 0.06), postMat);
            post.position.set(tx + cdx, 0.30, tz + cdz);
            this.placementGroup.add(post);
            this.placementPulseMats.push(postMat);
          }
        }
      }
    });
    // (Re)build ghost on entry to placement mode
    if (this.placementGhostType !== type) this.spawnGhost(type);
  }
  /** Pulse the valid-slot materials between 0.35 and 0.65 (floor) / 0.60 and
   *  1.0 (pillars) at 2 Hz. Hooked into the per-frame update so the slot
   *  footprint visibly breathes even when the cursor is still. */
  private animatePlacementSlots(dt: number) {
    if (this.placementPulseMats.length === 0) return;
    this.placementPulseTime += dt;
    // 2 Hz pulse — sine completes one full cycle every 0.5s.
    const t = 0.5 + 0.5 * Math.sin(this.placementPulseTime * Math.PI * 4);
    // Multiplier maps base opacity 0.5 → [0.35, 0.65].
    const mul = (0.35 + 0.30 * t) / 0.5;
    for (const m of this.placementPulseMats) {
      const ud = (m as any).userData;
      if (ud.baseOpacity === undefined) ud.baseOpacity = m.opacity;
      m.opacity = Math.min(1, ud.baseOpacity * mul);
    }
  }

  /** Build a semi-transparent ghost of the chosen building. Uses the same
   *  voxel factory the real building does (makeBuildingForRender) so the
   *  player sees the EXACT structure that's about to land — sails, smoke
   *  vents, signage and all. Every material gets a transparent clone pass. */
  private spawnGhost(type: BuildingType) {
    this.disposeGhost();
    const full = makeBuildingForRender(type);
    const obj = full.obj;
    // Replace materials with transparent clones so the player can see the
    // world through the ghost.
    obj.traverse(o => {
      if (o instanceof THREE.Mesh) {
        const cloneMat = (mat: THREE.Material) => {
          const c = mat.clone();
          c.transparent = true;
          (c as any).opacity = 0.55;
          c.depthWrite = false;
          return c;
        };
        const src = o.material as THREE.Material | THREE.Material[];
        o.material = Array.isArray(src) ? src.map(cloneMat) : cloneMat(src);
        o.castShadow = false;
        o.receiveShadow = false;
      }
    });
    obj.visible = false;
    this.ghostGroup.add(obj);
    this.placementGhost = obj;
    this.placementGhostType = type;

    // Invalid-position overlay — a red "no" rectangle, raised above the
    // tuft layer (y=0.36) and bright (0xff6040) so it can't be missed.
    const { w, d } = BUILDING_SIZE[type];
    const overlay = new THREE.Mesh(
      new THREE.PlaneGeometry(w - 0.05, d - 0.05),
      new THREE.MeshBasicMaterial({
        color: 0xff6040, transparent: true, opacity: 0.55,
        depthWrite: false, side: THREE.DoubleSide,
      }),
    );
    overlay.rotation.x = -Math.PI / 2;
    overlay.visible = false;
    this.ghostGroup.add(overlay);
    this.invalidOverlay = overlay;
  }

  private disposeGhost() {
    if (this.placementGhost) {
      this.placementGhost.traverse(o => {
        if (o instanceof THREE.Mesh) {
          const m = o.material as THREE.Material | THREE.Material[];
          if (Array.isArray(m)) m.forEach(x => x.dispose());
          else m.dispose();
        }
      });
      this.placementGhost.parent?.remove(this.placementGhost);
    }
    if (this.invalidOverlay) {
      (this.invalidOverlay.material as THREE.Material).dispose();
      this.invalidOverlay.parent?.remove(this.invalidOverlay);
    }
    this.placementGhost = null;
    this.placementGhostType = null;
    this.invalidOverlay = null;
  }

  /** Sync ghost / overlay position + visibility with the hovered tile. */
  private syncGhostFromHover() {
    if (!this.sim.state.placing || !this.placementGhost) return;
    const type = this.sim.state.placing.type;
    const { w, d } = BUILDING_SIZE[type];
    if (!this.hovered) {
      this.placementGhost.visible = false;
      if (this.invalidOverlay) this.invalidOverlay.visible = false;
      return;
    }
    const { c, r } = this.hovered;
    const [x, , z] = this.tileCenter(c, r);
    const bx = x + (w - 1) * 0.5;
    const bz = z + (d - 1) * 0.5;
    const canBuild = PlotSystem.canBuildAt(this.sim.state, c, r, type);
    this.placementGhost.position.set(bx, 0, bz);
    this.placementGhost.visible = canBuild;
    if (this.invalidOverlay) {
      // Raised to y=0.36 — sits above the tallest grass tuft (~0.16) so the
      // red "no-build" patch never gets buried by the voxel ground detail.
      this.invalidOverlay.position.set(bx, 0.36, bz);
      this.invalidOverlay.visible = !canBuild;
    }
  }

  // ── Truck animation ──────────────────────────────────────────────
  private positionTruckAtDepot() {
    this.truckMesh.position.copy(this.truckBase);
    // Parked nose-east so the truck is ready to drive along the road.
    // Local forward = -Z; rotating by -π/2 around Y points forward toward +X.
    this.truckMesh.rotation.y = -Math.PI / 2;
  }
  private animateTruck(dt: number) {
    // In plot view the truck is out of frame entirely — skip all animation
    // so it doesn't bounce back to visible after syncWorldVisibility hides it.
    if (this.sim.state.plotInView) {
      this.truckMesh.visible = false;
      return;
    }
    const t = this.sim.state.truck;
    this.updateTruckCargo();
    this.updateTruckPlatformCargo();
    this.animateTruckCargo(dt);

    // Headlight intensity: bright at night, faint by day (regardless of state)
    const above = Math.sin(this.timeOfDay * Math.PI * 2 - Math.PI / 2);
    const nightFactor = Math.max(0, Math.min(1, -above + 0.2));
    const isMoving = t.status === 'departed' || t.status === 'returning';
    for (const h of this.truckHeadlights) {
      h.light.intensity = nightFactor * (isMoving ? 1.8 : 0.4);
      const m = h.mesh.material as THREE.MeshBasicMaterial;
      m.color.setHex(h.light.intensity > 0.15 ? 0xfff080 : 0x807040);
    }

    if (!isMoving) {
      this.truckMesh.visible = true;
      this.truckMesh.position.copy(this.truckBase);
      this.truckMesh.position.y += Math.sin(this.elapsed * 6) * 0.005;
      // Rear-only suspension dip — the cab stays level, the body pitches
      // so the cargo bed sinks under load. The truck model has the cab
      // at local -Z and the bed at local +Z. With three.js's XYZ Euler,
      // positive rotation.x rotates +Y toward +Z, so the BED (+Z) sinks
      // while the cab (-Z) stays up. At full capacity (~30 items) the
      // rear sags ~0.15 rad ≈ 8.5°. Smoothed so loading reads as a
      // gentle settle.
      const cargoLoadFraction = Math.min(1, this.lastCargoCount / 30);
      const pitchTarget = +0.15 * cargoLoadFraction;
      // Parked nose-east, ready to drive along the road.
      this.truckMesh.rotation.y = -Math.PI / 2;
      for (const bl of this.truckBrakeLights) (bl.material as THREE.MeshBasicMaterial).color.setHex(0x602010);
      this.truckBodyPitch += (pitchTarget - this.truckBodyPitch) * Math.min(1, dt * 4);
      this.truckBodyRoll  += (0 - this.truckBodyRoll)  * Math.min(1, dt * 6);
      this.truckBody.rotation.x = this.truckBodyPitch;
      this.truckBody.rotation.z = this.truckBodyRoll;
      // Slow idle wheel rotation while parked — engine on, slight wobble
      for (const w of this.truckWheels) w.rotation.x = 0;
      this.truckPrevX = this.truckMesh.position.x;
      this.truckPrevSpeed = 0;
      return;
    }

    // Drive ALONG the road (east-west, +X). The road strip runs the full
    // width of the lot; the truck rolls eastward off-screen and returns
    // from the same direction. Z stays pinned to the road row.
    const total = TRUCK_TRAVEL_TIME;
    const raw = 1 - Math.max(0, Math.min(1, t.timer / total));
    const startX = this.truckBase.x;
    const endX = PLOT_W / 2 + 4;                          // off-screen east
    const roadZ = this.truckBase.z;
    const bounce = Math.sin(raw * Math.PI * 16) * 0.03 * (1 - Math.abs(raw - 0.5) * 2);
    // Slight S-wobble across the road's width — keeps the path feeling
    // organic without leaving the dirt surface.
    const lateral = Math.sin(raw * Math.PI * 2.3) * 0.10;

    const VISIBLE_WINDOW = 0.30;     // fraction of trip spent driving each way
    let onScreen = false;
    if (t.status === 'departed') {
      if (raw <= VISIBLE_WINDOW) {
        const driveF = raw / VISIBLE_WINDOW;
        const eased = driveF * driveF * (3 - 2 * driveF);
        this.truckMesh.visible = true;
        onScreen = true;
        this.truckMesh.position.set(startX + (endX - startX) * eased, bounce, roadZ + lateral);
        // Forward = +X → base yaw is -π/2 (rotates local -Z onto +X).
        const curveDeriv = Math.cos(raw * Math.PI * 2.3) * 0.18;
        this.truckMesh.rotation.y = -Math.PI / 2 - curveDeriv;
      } else {
        this.truckMesh.visible = false;
      }
    } else {
      const startAt = 1 - VISIBLE_WINDOW;
      if (raw >= startAt) {
        const driveF = (raw - startAt) / VISIBLE_WINDOW;
        const eased = driveF * driveF * (3 - 2 * driveF);
        this.truckMesh.visible = true;
        onScreen = true;
        this.truckMesh.position.set(endX + (startX - endX) * eased, bounce, roadZ - lateral);
        // Returning: forward = -X → base yaw is +π/2.
        const curveDeriv = Math.cos(raw * Math.PI * 2.3) * 0.18;
        this.truckMesh.rotation.y = +Math.PI / 2 + curveDeriv;
      } else {
        this.truckMesh.visible = false;
      }
    }

    // ── Velocity + acceleration (used for wheels, dust, body pitch) ──
    const curX = this.truckMesh.position.x;
    let speedWorld = onScreen ? Math.abs(curX - this.truckPrevX) / Math.max(dt, 1e-3) : 0;
    speedWorld = Math.min(speedWorld, 8);     // clamp on visibility transitions
    const accel = (speedWorld - this.truckPrevSpeed) / Math.max(dt, 1e-3);
    this.truckPrevX = curX;
    this.truckPrevSpeed = speedWorld;

    if (!onScreen) {
      // Off-screen — settle body, stop wheels.
      this.truckBodyPitch += (0 - this.truckBodyPitch) * Math.min(1, dt * 4);
      this.truckBodyRoll  += (0 - this.truckBodyRoll)  * Math.min(1, dt * 4);
      this.truckBody.rotation.x = this.truckBodyPitch;
      this.truckBody.rotation.z = this.truckBodyRoll;
      return;
    }

    // ── Wheel spin ──
    // Truck moves +X (departed → east) or -X (returning → west). After the
    // base yaw rotation, local forward is still -Z, so rolling forward = the
    // same negative rotation around X regardless of which way we're driving.
    const TIRE_RADIUS = 0.18;
    const dirSign = t.status === 'departed' ? +1 : -1;
    const wheelOmega = (speedWorld / TIRE_RADIUS) * dirSign;
    for (const w of this.truckWheels) {
      w.rotation.x -= wheelOmega * dt;
    }

    // ── Body pitch from acceleration (nose-up when accelerating, nose-down
    // when braking). Clamped + lerped for a smooth suspension feel. ──
    const targetPitch = Math.max(-0.10, Math.min(0.10, -accel * 0.012));
    this.truckBodyPitch += (targetPitch - this.truckBodyPitch) * Math.min(1, dt * 5);
    // Slight roll based on lateral curve derivative + road noise
    const targetRoll = Math.cos(raw * Math.PI * 2.3) * 0.05
                       + Math.sin(this.elapsed * 9) * 0.02 * Math.min(1, speedWorld * 0.5);
    this.truckBodyRoll += (targetRoll - this.truckBodyRoll) * Math.min(1, dt * 4);
    this.truckBody.rotation.x = this.truckBodyPitch;
    this.truckBody.rotation.z = this.truckBodyRoll;
    // Subtle high-frequency vertical bounce overlay — driving over road texture
    this.truckBody.position.y = Math.sin(this.elapsed * 24) * 0.012 * Math.min(1, speedWorld * 0.5);

    // Brake lights light up during deceleration (accel < -2).
    const decel = accel < -1.5;
    for (const bl of this.truckBrakeLights) {
      (bl.material as THREE.MeshBasicMaterial).color.setHex(decel ? 0xff3018 : 0x602010);
    }

    // ── Exhaust smoke — small gray puffs rising from the tailpipe ──
    this.truckExhaustCooldown -= dt;
    if (this.truckExhaustCooldown <= 0 && speedWorld > 0.2) {
      this.truckExhaustCooldown = 0.08 + Math.random() * 0.06;
      const localTip = this.truckExhaustTip.clone();
      const worldTip = localTip.applyMatrix4(this.truckMesh.matrixWorld);
      const puff = new THREE.Mesh(BOX, mat(Math.random() < 0.4 ? 0xa0a0a0 : 0x808080));
      const sz = 0.10 + Math.random() * 0.05;
      puff.scale.set(sz, sz, sz);
      puff.position.set(worldTip.x + (Math.random() - 0.5) * 0.05, worldTip.y, worldTip.z + (Math.random() - 0.5) * 0.05);
      puff.castShadow = false;
      const mm = (puff.material as THREE.MeshLambertMaterial).clone();
      mm.transparent = true; mm.opacity = 0.55;
      puff.material = mm;
      this.flyerGroup.add(puff);
      this.particles.push({
        obj: puff,
        vx: (Math.random() - 0.5) * 0.15,
        vy: 0.20 + Math.random() * 0.10,         // smoke rises
        vz: (Math.random() - 0.5) * 0.15,
        life: 1.0,
        maxLife: 1.0,
        disposeMat: true,
      });
    }

    // ── Per-wheel tire dust — kicked up at each ground contact ──
    this.truckTireDustCooldown -= dt;
    const dustRate = decel ? 0.025 : (speedWorld > 0.5 ? 0.06 : 999);
    if (this.truckTireDustCooldown <= 0) {
      this.truckTireDustCooldown = dustRate + Math.random() * 0.02;
      // Pick a random wheel — rear wheels kick more often (drive wheels)
      const wheelIdx = Math.random() < 0.7 ? (2 + (Math.random() < 0.5 ? 0 : 1)) : (Math.random() < 0.5 ? 0 : 1);
      const localPos = this.truckWheelGround[wheelIdx].clone();
      const worldPos = localPos.applyMatrix4(this.truckMesh.matrixWorld);
      const dustCol = Math.random() < 0.4 ? 0xc8a878 : (Math.random() < 0.6 ? 0xa08458 : 0x9c7848);
      const m2 = new THREE.Mesh(BOX, mat(dustCol));
      const sz = 0.10 + Math.random() * 0.08;
      m2.scale.set(sz, sz * 0.7, sz);
      m2.position.set(worldPos.x + (Math.random() - 0.5) * 0.15, 0.08, worldPos.z + (Math.random() - 0.5) * 0.15);
      m2.castShadow = false;
      const mClone = (m2.material as THREE.MeshLambertMaterial).clone();
      mClone.transparent = true; mClone.opacity = 0.7;
      m2.material = mClone;
      this.flyerGroup.add(m2);
      // Rear wheels kick dust BACKWARD relative to motion; front wheels
      // mostly just settle. Truck now travels along ±X, so the rearward
      // kick goes along -motionSign on the X axis (Z just gets a little
      // jitter so the puff fans out across the road).
      const isRear = wheelIdx >= 2;
      const motionSign = t.status === 'departed' ? +1 : -1;
      this.particles.push({
        obj: m2,
        vx: isRear ? -motionSign * (0.6 + Math.random() * 0.4) : (Math.random() - 0.5) * 0.2,
        vy: 0.10 + Math.random() * 0.12,
        vz: (Math.random() - 0.5) * 0.4,
        life: 0.55,
        maxLife: 0.55,
        disposeMat: true,
      });
    }

  }

  /** Render voxel crates on the truck bed reflecting current cargo.
   *  Crates pack densely so the bed visibly fills as the truck loads up.
   *  New crates (count increased since last render) scale-in briefly so
   *  the player sees them "thunk" onto the bed when items arrive. */
  private updateTruckCargo() {
    const c = this.sim.state.truck.cargo;
    const hash = `${c.wheat}|${c.corn}|${c.carrot}|${c.flour}|${c.cornFlour ?? 0}|${c.wood}|${c.mushroom ?? 0}|${c.berry ?? 0}|${c.acorn ?? 0}`;
    // Always recompute the cached cargo count so suspension dip tracks
    // even when the visual hash hasn't changed.
    this.lastCargoCount =
      c.wheat + c.corn + c.carrot + c.flour + (c.cornFlour ?? 0) +
      c.wood + (c.mushroom ?? 0) + (c.berry ?? 0) + (c.acorn ?? 0);
    if (hash === this.lastCargoHash) return;
    const prevVisibleCount = this.truckCargoSlot.children.length;
    this.lastCargoHash = hash;
    while (this.truckCargoSlot.children.length > 0) {
      this.truckCargoSlot.remove(this.truckCargoSlot.children[0]);
    }
    // Flatten cargo into a per-item list, grouped by kind. Order matters
    // for stacking: same-kind crates land together so a half-loaded
    // truck reads as "row of wheat" rather than scattered colors.
    const kinds: { kind: CargoCrateKind; count: number }[] = [
      { kind: 'wheat',     count: c.wheat },
      { kind: 'corn',      count: c.corn },
      { kind: 'carrot',    count: c.carrot },
      { kind: 'flour',     count: c.flour },
      { kind: 'cornFlour', count: c.cornFlour ?? 0 },
      { kind: 'wood',      count: c.wood },
      { kind: 'mushroom',  count: c.mushroom ?? 0 },
      { kind: 'berry',     count: c.berry ?? 0 },
      { kind: 'acorn',     count: c.acorn ?? 0 },
    ];
    // Grid: 5 cols × 5 rows × N layers stacks the cargo bed densely.
    // Each crate is wider/taller than before (room for the visible
    // produce poking out the top), and the layer height matches.
    const COLS = 5;
    const ROWS = 5;
    const STEP_X = 0.22;
    const STEP_Z = 0.24;
    const LAYER_H = 0.24;
    const MAX_VISIBLE = COLS * ROWS * 2;
    const items: CargoCrateKind[] = [];
    for (const k of kinds) {
      for (let i = 0; i < k.count && items.length < MAX_VISIBLE; i++) {
        items.push(k.kind);
      }
    }
    for (let idx = 0; idx < items.length; idx++) {
      const layer = Math.floor(idx / (COLS * ROWS));
      const inLayer = idx % (COLS * ROWS);
      const row = Math.floor(inLayer / COLS);
      const col = inLayer % COLS;
      // Stagger every other layer slightly so the stack reads "piled"
      // not "printed grid".
      const offsetX = layer % 2 === 1 ? STEP_X * 0.5 : 0;
      const offsetZ = layer % 2 === 1 ? STEP_Z * 0.5 : 0;
      const m = this.getCrateTemplate(items[idx]).clone() as THREE.Mesh;
      m.position.set(
        (col - (COLS - 1) / 2) * STEP_X + offsetX,
        layer * LAYER_H,
        (row - (ROWS - 1) / 2) * STEP_Z + offsetZ - 0.05,
      );
      // Per-crate Y rotation jitter — same kind, different placement
      // angle so adjacent crates aren't perfect mirrors.
      m.rotation.y = ((idx * 9173) % 100) / 100 * 0.40 - 0.20;
      m.castShadow = true;
      // New crates stay invisible for the fly-time delay then scale-in.
      if (idx >= prevVisibleCount) {
        const stagger = (idx - prevVisibleCount) * 0.05;
        m.scale.setScalar(0.001);
        this.crateSpawnT.set(m, this.elapsed + 0.50 + stagger);
      }
      this.truckCargoSlot.add(m);
    }
  }

  /** Render the depot-platform cube stack reflecting state.truck.platform.
   *  Layout mirrors the truck's cargo grid but smaller (4×3 per layer)
   *  and positioned next to the parked truck. New cubes scale-in with
   *  the same fly-time delay as truck-bed crates so the box appears
   *  WHEN the flying icon from the HUD lands, not at click time. */
  private updateTruckPlatformCargo() {
    const p = this.sim.state.truck.platform;
    const hash = p
      ? `${p.wheat}|${p.corn}|${p.carrot}|${p.flour}|${p.cornFlour ?? 0}|${p.wood}|${p.mushroom ?? 0}|${p.berry ?? 0}|${p.acorn ?? 0}`
      : '';
    if (hash === this.lastPlatformHash) return;
    const prevVisibleCount = this.truckPlatformSlot.children.length;
    this.lastPlatformHash = hash;
    while (this.truckPlatformSlot.children.length > 0) {
      this.truckPlatformSlot.remove(this.truckPlatformSlot.children[0]);
    }
    if (!p) return;
    // Place the platform next to where the truck parks. Offset to the
    // side (+Z) of the truck so cubes don't overlap the bed and so the
    // player can see them while the truck is on the road.
    this.truckPlatformSlot.position.set(
      this.truckBase.x,
      0.10,
      this.truckBase.z + 1.4,
    );
    const kinds: { kind: CargoCrateKind; count: number }[] = [
      { kind: 'wheat',     count: p.wheat },
      { kind: 'corn',      count: p.corn },
      { kind: 'carrot',    count: p.carrot },
      { kind: 'flour',     count: p.flour },
      { kind: 'cornFlour', count: p.cornFlour ?? 0 },
      { kind: 'wood',      count: p.wood },
      { kind: 'mushroom',  count: p.mushroom ?? 0 },
      { kind: 'berry',     count: p.berry ?? 0 },
      { kind: 'acorn',     count: p.acorn ?? 0 },
    ];
    const COLS = 4;
    const ROWS = 3;
    const STEP_X = 0.24;
    const STEP_Z = 0.24;
    const LAYER_H = 0.24;
    const MAX_VISIBLE = COLS * ROWS * 3;
    const items: CargoCrateKind[] = [];
    for (const k of kinds) {
      for (let i = 0; i < k.count && items.length < MAX_VISIBLE; i++) items.push(k.kind);
    }
    for (let idx = 0; idx < items.length; idx++) {
      const layer = Math.floor(idx / (COLS * ROWS));
      const inLayer = idx % (COLS * ROWS);
      const row = Math.floor(inLayer / COLS);
      const col = inLayer % COLS;
      const offsetX = layer % 2 === 1 ? STEP_X * 0.5 : 0;
      const offsetZ = layer % 2 === 1 ? STEP_Z * 0.5 : 0;
      const m = this.getCrateTemplate(items[idx]).clone() as THREE.Mesh;
      m.position.set(
        (col - (COLS - 1) / 2) * STEP_X + offsetX,
        layer * LAYER_H,
        (row - (ROWS - 1) / 2) * STEP_Z + offsetZ,
      );
      m.rotation.y = ((idx * 7919) % 100) / 100 * 0.40 - 0.20;
      m.castShadow = true;
      if (idx >= prevVisibleCount) {
        const stagger = (idx - prevVisibleCount) * 0.05;
        m.scale.setScalar(0.001);
        this.crateSpawnT.set(m, this.elapsed + 0.50 + stagger);
      }
      this.truckPlatformSlot.add(m);
    }
  }

  /** Rebuild the on-site material crate for the construction at
   *  (c, r). Reads tile.construction.stagePaid and stacks one mini-crate
   *  per material (wheat sack, wood logs, stone chunks). The crate sits
   *  one tile-step south-east of the building footprint anchor so it's
   *  always visible from the iso camera. Auto-removed when the building
   *  isn't under construction or has no deposits. */
  private updateConstructionCrate(c: number, r: number) {
    const tile = this.sim.state.plot.tiles[r]?.[c];
    if (!tile || !tile.construction || !tile.bld) {
      this.removeConstructionCrate(c, r);
      return;
    }
    const paid = tile.construction.stagePaid ?? { money: 0, wood: 0, wheat: 0, stone: 0 };
    const total = paid.wood + paid.wheat + paid.stone;        // money has no crate
    if (total === 0) {
      this.removeConstructionCrate(c, r);
      return;
    }
    const key = this.tileKey(c, r);
    let group = this.constructionCrates.get(key);
    if (!group) {
      group = new THREE.Group();
      this.scene.add(group);
      this.constructionCrates.set(key, group);
    }
    while (group.children.length > 0) group.remove(group.children[0]);
    // Position next to the SE corner of the footprint.
    const sz = BUILDING_SIZE[tile.bld] ?? { w: 1, d: 1 };
    const [tx, , tz] = this.tileCenter(c + sz.w - 1, r + sz.d - 1);
    group.position.set(tx + 0.4, 0.10, tz + 0.6);
    // One crate per N units (compact) — 3 col × 2 row × 2 layer = 12 cap.
    const items: CargoCrateKind[] = [];
    for (let i = 0; i < paid.wood  && items.length < 12; i++) items.push('wood');
    for (let i = 0; i < paid.wheat && items.length < 12; i++) items.push('wheat');
    for (let i = 0; i < paid.stone && items.length < 12; i++) items.push('stone');
    const COLS = 3;
    const ROWS = 2;
    const STEP_X = 0.22;
    const STEP_Z = 0.22;
    const LAYER_H = 0.24;
    for (let idx = 0; idx < items.length; idx++) {
      const layer = Math.floor(idx / (COLS * ROWS));
      const inLayer = idx % (COLS * ROWS);
      const row = Math.floor(inLayer / COLS);
      const col = inLayer % COLS;
      const offsetX = layer % 2 === 1 ? STEP_X * 0.5 : 0;
      const offsetZ = layer % 2 === 1 ? STEP_Z * 0.5 : 0;
      const m = this.getCrateTemplate(items[idx]).clone() as THREE.Mesh;
      m.position.set(
        (col - (COLS - 1) / 2) * STEP_X + offsetX,
        layer * LAYER_H,
        (row - (ROWS - 1) / 2) * STEP_Z + offsetZ,
      );
      m.rotation.y = ((idx * 5413) % 100) / 100 * 0.40 - 0.20;
      m.castShadow = true;
      // Reuse the fly-time delayed scale-in so new crates pop when the
      // flying icon lands. Existing crates render immediately.
      // (constructionCrates rebuilds clear all children; the first
      // build after a deposit gets the delay; subsequent rebuilds
      // within the same deposit also delay — slightly redundant but
      // harmless.)
      const stagger = idx * 0.05;
      m.scale.setScalar(0.001);
      this.crateSpawnT.set(m, this.elapsed + 0.50 + stagger);
      group.add(m);
    }
  }

  private removeConstructionCrate(c: number, r: number) {
    const key = this.tileKey(c, r);
    const group = this.constructionCrates.get(key);
    if (!group) return;
    this.scene.remove(group);
    this.constructionCrates.delete(key);
  }

  /** Per-frame scale-in animation for crates the player just loaded.
   *  Covers BOTH the truck-bed cargo slot AND the depot-platform slot.
   *  Each crate's start timestamp is already offset by the flying-icon
   *  flight duration so the scale-in syncs with icon landing. Eases
   *  scale 0 → 1 over ~220ms with a slight overshoot for "thunk". */
  private animateTruckCargo(_dt: number) {
    const SPAWN_DUR = 0.22;
    const animate = (m: THREE.Object3D) => {
      const tStart = this.crateSpawnT.get(m);
      if (tStart == null) return;
      // Still in the pre-landing delay — keep the crate invisible.
      if (this.elapsed < tStart) {
        m.scale.setScalar(0.001);
        return;
      }
      const t = (this.elapsed - tStart) / SPAWN_DUR;
      if (t >= 1) {
        m.scale.setScalar(1);
        this.crateSpawnT.delete(m);
        return;
      }
      // Overshoot ease — pops above 1 then settles, giving a tactile feel.
      const e = t * t * (3 - 2 * t);
      const overshoot = Math.sin(t * Math.PI) * 0.15;
      m.scale.setScalar(e + overshoot);
    };
    for (const m of this.truckCargoSlot.children) animate(m);
    for (const m of this.truckPlatformSlot.children) animate(m);
    for (const group of this.constructionCrates.values()) {
      for (const m of group.children) animate(m);
    }
  }

  // ── Sway / smoke / flyers ─────────────────────────────────────────
  private registerSway(node: THREE.Object3D, freq: number, amp: number, axis: 'x' | 'z' = 'z') {
    this.swayNodes.push({ node, freq, amp, axis, phase: Math.random() * Math.PI * 2 });
  }
  private registerSubtreeSway(root: THREE.Object3D) {
    root.traverse(o => {
      const f = (o.userData as any)?.swayFreq;
      if (typeof f === 'number') {
        const amp = (o.userData as any).swayAmp ?? 0.05;
        const axis = (o.userData as any).swayAxis ?? 'z';
        this.registerSway(o, f, amp, axis);
      }
    });
  }
  private isInScene(o: THREE.Object3D): boolean {
    let n: THREE.Object3D | null = o;
    while (n) { if (n === this.scene) return true; n = n.parent; }
    return false;
  }
  private animateSway(_dt: number) {
    if (this.swayNodes.length === 0) return;
    const t = this.elapsed;
    // In-place compaction — animates live nodes and drops dead ones in
    // a single pass, no per-frame array allocation. Previous code only
    // filtered when dead > 16 so up to 16 stale nodes burned isInScene
    // checks every frame; with plot rebuilds dropping hundreds of seed
    // sway entries that added up to real CPU.
    let writeIdx = 0;
    for (let readIdx = 0; readIdx < this.swayNodes.length; readIdx++) {
      const s = this.swayNodes[readIdx];
      if (!this.isInScene(s.node)) continue;          // dead — skip
      s.node.rotation[s.axis] = Math.sin(t * s.freq + s.phase) * s.amp;
      if (writeIdx !== readIdx) this.swayNodes[writeIdx] = s;
      writeIdx++;
    }
    if (writeIdx < this.swayNodes.length) this.swayNodes.length = writeIdx;
  }

  // ── Cursor-push: nearby wheat leans away from the mouse ───────────
  /** Radius in world units within which a seed responds to the cursor.
   *  Sub-cells are 0.125 world units, so 0.35 covers ~3 sub-cells around
   *  the cursor — enough to brush a small swath, narrow enough that the
   *  rest of the plot keeps its own idle sway. */
  private static readonly CURSOR_PUSH_RADIUS = 0.35;
  /** Maximum lean (radians) when the cursor is right on top of a stalk.
   *  ~0.5 rad ≈ 28°. The full body of the wrapper rotates, so this reads
   *  as a noticeable "brush aside" without flattening the stalk. */
  private static readonly CURSOR_PUSH_MAX = 0.5;
  /** Per-second LERP rate toward the target push angle. Higher = snappier
   *  reaction in / out; lower = laggier, more grass-like. */
  private static readonly CURSOR_PUSH_LERP = 12.0;

  /** Cursor-driven wheat sway. Only runs in plot mode. Iterates seed meshes
   *  and bends each one's wrapper around Z away from the cursor on the XZ
   *  plane. Coexists with the per-stalk idle sway (which owns rotation.x). */
  private animateCursorPush(dt: number) {
    if (!this.sim.state.plotInView) {
      // Out of plot mode — relax any leftover push so the next plot entry
      // starts neutral. Cheap: only touches wrappers that still have non-zero z.
      for (const mesh of this.seedMeshes.values()) {
        if (mesh.rotation.z !== 0) {
          const ud: any = (mesh as any).userData ?? ((mesh as any).userData = {});
          ud.cursorPush = 0;
          mesh.rotation.z = 0;
        }
      }
      return;
    }
    if (this.seedMeshes.size === 0) return;

    const cx = this.cursorGroundPos.x;
    const cz = this.cursorGroundPos.z;
    const R = Diorama.CURSOR_PUSH_RADIUS;
    const R2 = R * R;
    const MAX = Diorama.CURSOR_PUSH_MAX;
    // Frame-rate-independent LERP. Clamped so a single huge dt (e.g. a tab
    // resume) doesn't overshoot.
    const k = Math.min(1, dt * Diorama.CURSOR_PUSH_LERP);

    for (const mesh of this.seedMeshes.values()) {
      const dx = mesh.position.x - cx;
      const dz = mesh.position.z - cz;
      // Fast AABB pre-filter: cheap integer-ish compare avoids the sqrt for
      // the overwhelming majority of seeds (most aren't near the cursor).
      let target = 0;
      if (dx >= -R && dx <= R && dz >= -R && dz <= R) {
        const d2 = dx * dx + dz * dz;
        if (d2 < R2 && d2 > 1e-8) {
          const d = Math.sqrt(d2);
          const falloff = 1 - d / R;
          // Smoothstep for a softer near-cursor curve — feels more "wave"
          // and less "step".
          const smooth = falloff * falloff * (3 - 2 * falloff);
          // Sign of the lean: stalk leans in +X direction if it's on the +X
          // side of the cursor, etc. Project onto rotation.z which tilts
          // the stalk around the world Z axis (i.e. the stalk's top moves
          // along world X). Use the X component of (mesh - cursor) as the
          // dominant push direction; weight by horizontal-ness so a
          // stalk directly N/S of the cursor barely tilts.
          const dirX = dx / d;
          target = dirX * smooth * MAX;
        }
      }
      const ud: any = (mesh as any).userData ?? ((mesh as any).userData = {});
      const prev = typeof ud.cursorPush === 'number' ? ud.cursorPush : 0;
      const next = prev + (target - prev) * k;
      ud.cursorPush = next;
      mesh.rotation.z = next;
    }
  }

  private registerSmokeSource(x: number, y: number, z: number) {
    this.smokeSources.push({ x, y, z, cooldown: Math.random() });
  }
  private refreshSmokeSources() {
    // Keep "town" smoke sources (built once in buildWorldBeyond); replace granaries.
    this.smokeSources = this.smokeSources.filter(s => s.kind === 'town');
    for (const s of this.smokers) s.obj.parent?.remove(s.obj);
    this.smokers = [];
    for (const g of PlotSystem.buildingsOfType(this.sim.state, 'granary')) {
      if (!ConstructionSystem.isOperational(g.tile)) continue;
      const { w, d } = BUILDING_SIZE.granary;
      const [x, , z] = this.tileCenter(g.c, g.r);
      this.smokeSources.push({ x: x + (w - 1) * 0.5, y: 2.0, z: z + (d - 1) * 0.5, cooldown: Math.random(), kind: 'granary' });
    }
  }
  private animateSmoke(dt: number) {
    for (const src of this.smokeSources) {
      src.cooldown -= dt;
      if (src.cooldown <= 0) {
        // Slower spawn rate + thinner puffs so the chimney looks like a homely
        // wisp rather than a factory plume.
        src.cooldown = 1.4 + Math.random() * 0.7;
        const size = 0.10 + Math.random() * 0.06;
        const puff = cube(size, size, size, src.x + (Math.random() - 0.5) * 0.04, src.y, src.z + (Math.random() - 0.5) * 0.04, COL.smokePuff);
        const m = (puff.material as THREE.MeshLambertMaterial).clone();
        m.transparent = true; m.opacity = 0.45;
        puff.material = m;
        puff.castShadow = false;
        this.flyerGroup.add(puff);
        this.smokers.push({ obj: puff, vy: 0.40 + Math.random() * 0.15, life: 2.0, maxLife: 2.0, baseY: src.y });
      }
    }
    const alive: typeof this.smokers = [];
    for (const s of this.smokers) {
      s.life -= dt;
      if (s.life <= 0) {
        // Each puff has its own cloned material — dispose so GPU memory
        // doesn't accumulate over long sessions.
        if (s.obj instanceof THREE.Mesh) {
          const m = s.obj.material as THREE.Material;
          m.dispose();
        }
        s.obj.parent?.remove(s.obj);
        continue;
      }
      s.obj.position.y += s.vy * dt;
      s.obj.position.x += Math.sin((this.elapsed + s.maxLife - s.life) * 1.5) * 0.06 * dt;
      const f = s.life / s.maxLife;
      const scale = 1 + (1 - f) * 0.6;
      s.obj.scale.set(scale, scale, scale);
      const m = s.obj instanceof THREE.Mesh ? s.obj.material as THREE.MeshLambertMaterial : null;
      if (m) m.opacity = 0.45 * f;
      alive.push(s);
    }
    this.smokers = alive;
  }

  private spawnAmbientFlyers() {
    // More colors so the swarm reads as a meadow's worth of species rather
    // than a half-dozen pet butterflies. Palette mixes pastel + jewel tones.
    const colors = [
      0xffb6e0, 0xfff080, 0xa8d8ff, 0xff8a5a, 0xffc8e8, 0xb0e0d0,
      0xe8a0ff, 0xfffac0, 0x80c0ff, 0xff6090, 0xc8f080, 0xffa040,
    ];
    // 22 butterflies scattered across the lot AND past its edges, so the
    // distant terrain reads alive too. Smaller wing geometry (0.18 vs the
    // old 0.32) keeps them readable as butterflies, not paper airplanes.
    for (let i = 0; i < 22; i++) {
      const fly = new THREE.Group();
      const c = colors[i % colors.length];
      const wL = cube(0.18, 0.025, 0.12, -0.055, 0, 0, c);
      const wR = cube(0.18, 0.025, 0.12,  0.055, 0, 0, c);
      const body = cube(0.035, 0.035, 0.06, 0, 0.012, 0, 0x222222);
      fly.add(wL); fly.add(wR); fly.add(body);
      // Slight per-butterfly size jitter so the swarm doesn't read as clones.
      const sizeMul = 0.7 + Math.random() * 0.7;
      fly.scale.setScalar(sizeMul);
      // Spawn wider — 1.3× the lot in each axis so some butterflies drift
      // across the distant terrain edge of the view.
      fly.position.set(
        (Math.random() - 0.5) * PLOT_W * 1.3,
        1.5 + Math.random() * 3.5,
        (Math.random() - 0.5) * PLOT_H * 1.3,
      );
      this.flyerGroup.add(fly);
      this.flyers.push({
        obj: fly,
        vx: (Math.random() - 0.5) * 1.4,
        vz: (Math.random() - 0.5) * 1.4,
        phase: Math.random() * Math.PI * 2,
        baseY: fly.position.y,
        freq: 6 + Math.random() * 6,
      });
      // Faster wing-flap on smaller butterflies feels insect-correct.
      this.registerSway(wL, 36, 0.85, 'z');
      this.registerSway(wR, 36, 0.85, 'z');
    }
  }
  private animateFlyers(dt: number) {
    // Roam range matches the wider spawn — butterflies drift onto the
    // distant terrain ring too.
    const range = PLOT_W * 0.85;
    for (const f of this.flyers) {
      f.obj.position.x += f.vx * dt;
      f.obj.position.z += f.vz * dt;
      f.obj.position.y = f.baseY + Math.sin(this.elapsed * f.freq * 0.3 + f.phase) * 0.18;
      if (Math.abs(f.obj.position.x) > range) f.vx *= -1;
      if (Math.abs(f.obj.position.z) > range) f.vz *= -1;
      f.obj.rotation.y = Math.atan2(f.vx, f.vz);
    }
  }

  // ── Window lamps (PointLights at granary/mill windows) ───────────
  private refreshLamps() {
    for (const l of this.windowLamps) this.scene.remove(l);
    this.windowLamps = [];
    for (const g of PlotSystem.buildingsOfType(this.sim.state, 'granary')) {
      if (!ConstructionSystem.isOperational(g.tile)) continue;
      const { w, d } = BUILDING_SIZE.granary;
      const [x, , z] = this.tileCenter(g.c, g.r);
      const bx = x + (w - 1) * 0.5;
      const bz = z + (d - 1) * 0.5;
      const lamp = new THREE.PointLight(0xffc070, 0, 5, 1.8);
      lamp.position.set(bx, 1.4, bz + 0.7);
      this.scene.add(lamp);
      this.windowLamps.push(lamp);
    }
    for (const m of PlotSystem.buildingsOfType(this.sim.state, 'mill')) {
      if (!ConstructionSystem.isOperational(m.tile)) continue;
      const { w, d } = BUILDING_SIZE.mill;
      const [x, , z] = this.tileCenter(m.c, m.r);
      const bx = x + (w - 1) * 0.5;
      const bz = z + (d - 1) * 0.5;
      const a = new THREE.PointLight(0xffc070, 0, 5, 1.8);
      a.position.set(bx, 1.2, bz + 0.6);
      this.scene.add(a);
      this.windowLamps.push(a);
      const b = new THREE.PointLight(0xffc070, 0, 6, 1.8);
      b.position.set(bx + 0.6, 1.9, bz + 0.6);
      this.scene.add(b);
      this.windowLamps.push(b);
    }
  }

  // ── Stars + moon ─────────────────────────────────────────────────
  /** Large inverted-sphere sky dome with a vertical gradient shader.
   *  Replaces the flat solid-color background and gives the horizon a
   *  warm haze that the world fades INTO via fog — so the play area never
   *  reads as a hard edge cut against a flat blue wall. Colors updated by
   *  animateDayNight to drive the day/night cycle. */
  private skyDomeMat: THREE.ShaderMaterial | null = null;
  private skyDome: THREE.Mesh | null = null;
  private buildSkyDome() {
    const geom = new THREE.SphereGeometry(120, 32, 16);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        topColor:    { value: new THREE.Color(0x4a78b8) },
        midColor:    { value: new THREE.Color(0x9bc5e8) },
        bottomColor: { value: new THREE.Color(0xf4d4a8) },
        offset:      { value: 0.0 },
        exponent:    { value: 0.85 },
      },
      vertexShader: /* glsl */`
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */`
        uniform vec3 topColor;
        uniform vec3 midColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPos;
        void main() {
          // Normalize on the radius so the gradient depends on view-angle
          // height, not world position. 0 at horizon, 1 at zenith.
          float h = normalize(vWorldPos + vec3(0.0, offset, 0.0)).y;
          float t = max(h, 0.0);
          t = pow(t, exponent);
          // Three-stop gradient: horizon (warm) → mid (sky blue) → zenith (deep)
          vec3 col = mix(bottomColor, midColor, smoothstep(0.0, 0.45, t));
          col = mix(col, topColor, smoothstep(0.40, 1.0, t));
          // Below-horizon (rare with orthographic but cleaner) fades to bottom.
          col = mix(bottomColor, col, smoothstep(-0.10, 0.05, h));
          gl_FragColor = vec4(col, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    const dome = new THREE.Mesh(geom, mat);
    dome.frustumCulled = false;
    dome.renderOrder = -1000;            // draw before everything else
    this.skyDome = dome;
    this.skyDomeMat = mat;
    this.skyGroup.add(dome);
  }

  private buildStarsAndMoon() {
    this.buildSkyDome();
    const sb = new VoxelBuilder();
    const rng = rngFn(31337);
    const N = 120;
    for (let i = 0; i < N; i++) {
      const theta = rng() * Math.PI * 2;
      const r = 28 + rng() * 8;
      const yFrac = 0.2 + rng() * 0.7;
      const x = Math.cos(theta) * r * (1 - yFrac);
      const z = Math.sin(theta) * r * (1 - yFrac);
      const y = 12 + yFrac * 22;
      const sz = 0.08 + rng() * 0.14;
      const c = rng() < 0.15 ? 0xa8c4ff : (rng() < 0.10 ? 0xfff0c0 : 0xffffff);
      sb.add(sz, sz, sz, x, y, z, c);
    }
    const stars = sb.build();
    stars.material = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0, depthWrite: false });
    stars.castShadow = false;
    stars.receiveShadow = false;
    this.starsMesh = stars;
    this.skyGroup.add(stars);

    // Voxel moon — chunky disc
    const mb = new VoxelBuilder();
    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d > 2.2) continue;
          const c = d < 1.6 ? 0xf8f0d8 : 0xc8c0a8;
          mb.add(0.18, 0.18, 0.18, dx * 0.18, dy * 0.18, dz * 0.18, c);
        }
      }
    }
    const moon = mb.build();
    moon.material = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0 });
    moon.castShadow = false;
    this.moonMesh = moon;
    this.skyGroup.add(moon);

    // Voxel sun — bigger, brighter, with a soft halo voxel ring. Color
    // gets recolored per-frame from the palette (warm at horizon, near-
    // white at noon). This is what the god-ray pass actually samples as
    // the bright pixel — without it, rays had no source to stream from.
    const sb2 = new VoxelBuilder();
    for (let dx = -3; dx <= 3; dx++) {
      for (let dy = -3; dy <= 3; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d > 3.0) continue;
          // Core is brighter than halo so the sun reads as a hot disc with
          // a soft falloff (the godrays sampler kicks in around the rim).
          const c = d < 2.0 ? 0xfff4d0 : 0xffd070;
          sb2.add(0.22, 0.22, 0.22, dx * 0.22, dy * 0.22, dz * 0.22, c);
        }
      }
    }
    const sun = sb2.build();
    sun.material = new THREE.MeshBasicMaterial({
      vertexColors: true, transparent: true, opacity: 1.0, depthWrite: false, fog: false,
    });
    sun.castShadow = false;
    sun.renderOrder = -500;
    this.sunMesh = sun;
    this.skyGroup.add(sun);
  }

  // ── Particle bursts ─────────────────────────────────────────────
  /**
   * Spawn voxel-chip particles. `opts.scale` controls the per-chip size in
   * world units — defaults to a tile-level chip (~0.08–0.18). Sub-cell-scale
   * events (sickle reap, sub-cell water) should pass scale ≈ 0.03 so the
   * burst reads at the 16-voxel level instead of dwarfing the prop.
   */
  /** Spawn particles at an explicit world-space position (x, y, z).
   *  Use for events that aren't anchored to a tile — truck delivery,
   *  prop pickups, etc. */
  private spawnParticlesAtWorld(
    x: number, y: number, z: number, palette: number[], count: number,
    opts: { life: number; spread: number; upBias: number; scale?: number },
  ) {
    const base = opts.scale ?? 0.05;
    for (let i = 0; i < count; i++) {
      const col = palette[Math.floor(Math.random() * palette.length)];
      const sz = base * (0.66 + Math.random() * 0.5);
      const m = new THREE.Mesh(BOX, mat(col));
      m.scale.set(sz, sz, sz);
      m.position.set(x + (Math.random() - 0.5) * 0.3, y, z + (Math.random() - 0.5) * 0.3);
      m.castShadow = false;
      m.receiveShadow = false;
      this.flyerGroup.add(m);
      const ang = Math.random() * Math.PI * 2;
      const speed = 0.6 + Math.random() * 1.4;
      this.particles.push({
        obj: m,
        vx: Math.cos(ang) * speed * opts.spread,
        vz: Math.sin(ang) * speed * opts.spread,
        vy: 1.0 * opts.upBias + Math.random() * 0.6,
        life: opts.life,
        maxLife: opts.life,
      });
    }
  }
  private spawnParticles(
    c: number, r: number, palette: number[], count: number,
    opts: { life: number; spread: number; upBias: number; scale?: number },
  ) {
    const [x, , z] = this.tileCenter(c, r);
    const base = opts.scale ?? 0.05;
    for (let i = 0; i < count; i++) {
      const col = palette[Math.floor(Math.random() * palette.length)];
      // Two-thirds of `base` up to a full `base` so the burst has variance.
      const sz = base * (0.66 + Math.random() * 0.5);
      const m = new THREE.Mesh(BOX, mat(col));
      m.scale.set(sz, sz, sz);
      m.position.set(x + (Math.random() - 0.5) * 0.3, 0.3, z + (Math.random() - 0.5) * 0.3);
      m.castShadow = false;
      m.receiveShadow = false;
      this.flyerGroup.add(m);
      const ang = Math.random() * Math.PI * 2;
      const speed = 0.6 + Math.random() * 1.4;
      this.particles.push({
        obj: m,
        vx: Math.cos(ang) * speed * opts.spread,
        vz: Math.sin(ang) * speed * opts.spread,
        vy: 1.0 * opts.upBias + Math.random() * 0.6,
        life: opts.life,
        maxLife: opts.life,
      });
    }
  }
  private animateParticles(dt: number) {
    const alive: typeof this.particles = [];
    const gravity = 4.2;
    for (const p of this.particles) {
      p.life -= dt;
      if (p.life <= 0) {
        // Dispose CLONED materials on the way out — truck exhaust /
        // tire dust / chimney smoke each get their own material clone
        // (so they can fade independently). Particles with shared mat()
        // materials are tagged disposeMat:false so we don't break the
        // global material cache.
        if (p.disposeMat && p.obj instanceof THREE.Mesh) {
          const m = p.obj.material as THREE.Material | THREE.Material[];
          if (Array.isArray(m)) m.forEach(mm => mm.dispose());
          else m.dispose();
        }
        p.obj.parent?.remove(p.obj);
        continue;
      }
      p.vy -= gravity * dt;
      p.obj.position.x += p.vx * dt;
      p.obj.position.y += p.vy * dt;
      p.obj.position.z += p.vz * dt;
      if (p.obj.position.y < 0.05) {
        p.obj.position.y = 0.05;
        p.vy = 0;
        p.vx *= 0.6;
        p.vz *= 0.6;
      }
      p.obj.rotation.x += dt * 4;
      p.obj.rotation.z += dt * 3;
      const f = p.life / p.maxLife;
      if (f < 0.4) p.obj.scale.setScalar(0.10 * (f / 0.4));
      alive.push(p);
    }
    this.particles = alive;
  }

  private spawnClouds() {
    // Multi-layered cloud field — wispy high-cirrus + heavy mid-altitude
    // anvils + a couple of low-drifters. Each cloud is its own voxel
    // sculpt (makeCloud) so no two read the same. Heights staggered so
    // the sky has front-back depth instead of one flat plane.
    const rng = rngFn(2027);
    // Cloud altitudes are well above the play area — trees + buildings
    // top out around y=5-6, so anything lower than ~y=14 invades the
    // scene and reads as fog clutter, not sky. Lifted every layer.
    const layers = [
      // Heavy anvils — biggest, highest altitude, the dominant shapes.
      { count: 5, yMin: 26, yMax: 32, scaleMin: 1.8, scaleMax: 2.6,
        speedMin: 0.18, speedMax: 0.32, rRange: 26 },
      // Mid-fluff — smaller, slightly faster, mid-sky.
      { count: 7, yMin: 20, yMax: 26, scaleMin: 1.0, scaleMax: 1.6,
        speedMin: 0.28, speedMax: 0.55, rRange: 28 },
      // High wisps — small, light, faster drift higher up.
      { count: 6, yMin: 16, yMax: 21, scaleMin: 0.55, scaleMax: 0.95,
        speedMin: 0.45, speedMax: 0.75, rRange: 30 },
    ];
    let seed = 91;
    for (const L of layers) {
      for (let i = 0; i < L.count; i++) {
        const cloud = makeCloud(seed++, L.scaleMin + rng() * (L.scaleMax - L.scaleMin));
        // Distribute across the wider area (well past the lot edges) so
        // clouds drift in from off-screen rather than popping mid-frame.
        cloud.position.set(
          (rng() - 0.5) * L.rRange * 2,
          L.yMin + rng() * (L.yMax - L.yMin),
          (rng() - 0.5) * L.rRange * 2,
        );
        cloud.rotation.y = rng() * Math.PI * 2;
        this.cloudGroup.add(cloud);
        this.cloudData.push({
          obj: cloud,
          vx: (L.speedMin + rng() * (L.speedMax - L.speedMin)) * (rng() < 0.5 ? -1 : 1),
        });
      }
    }
  }

  // ── Input ─────────────────────────────────────────────────────────
  private onPointerMove = (ev: PointerEvent) => {
    if (this.camPanning) {
      this.onPanMove(ev);
      return;
    }
    const r = this.canvas.getBoundingClientRect();
    this.pointer.x = ((ev.clientX - r.left) / r.width) * 2 - 1;
    this.pointer.y = -(((ev.clientY - r.top) / r.height) * 2 - 1);
    this.updateHover();
    this.updateCursorGround();
    // Plot drag-select paint
    if (this.selectingDragActive && this.hovered && this.sim.state.selecting) {
      this.sim.addToSelection(this.hovered.c, this.hovered.r);
    }
    // Plot tool sub-cell drag-paint (hoe / plant / water). Only fire when
    // the cursor enters a NEW sub-cell — preserves the "one pass = one
    // stage" feel for multi-pass tools (hoe / water).
    if (this.plotToolDrag.active) {
      const s = this.subCellAt(this.cursorGroundPos.x, this.cursorGroundPos.z);
      if (s) {
        const k = `${s.c}:${s.r}:${s.subC}:${s.subR}`;
        if (k !== this.plotToolDrag.lastSub) {
          this.plotToolDrag.lastSub = k;
          // Cursor entered a new sub-cell → reset the water dwell so the
          // pour-spread starts fresh from the new spot.
          this.plotWaterAccum = 0;
          this.plotWaterRadius = 0;
          this.applyPlotTool(s.c, s.r, s.subC, s.subR);
        }
      } else {
        this.plotToolDrag.lastSub = '';
      }
    }
    // Water drag — paint water onto each new tile the cursor enters while
    // LMB is held. Same drag state we used to use for the old hoe sweep.
    if (this.toolDrag.active && this.hovered && this.sim.state.tool === 'water') {
      this.applyWaterTile(this.hovered.c, this.hovered.r);
    }
    // Cursor moved off the tile being held — cancel the in-flight action.
    // The player has to re-click the original tile to resume.
    if (this.hold && this.hovered && (this.hovered.c !== this.hold.c || this.hovered.r !== this.hold.r)) {
      this.cancelHold();
    }
  };
  private onPointerDown = (ev: PointerEvent) => {
    if (ev.button === 2) {
      // Right-click: cancel any in-flight move / selection, drop the held
      // tool, and cancel any placement — one quick "abort" gesture. NOT
      // an exit-plot shortcut anymore so the player can right-click freely
      // while working a plot (e.g. to drop the held hoe).
      if (this.sim.state.moving) {
        this.sim.cancelMoving();
        return;
      }
      if (this.sim.state.selecting) {
        this.sim.cancelPlotSelect();
        return;
      }
      if (this.sim.state.tool !== 'hands') {
        this.sim.setTool('hands');
      }
      this.sim.cancelPlacement();
      return;
    }
    if (ev.button === 1) {
      // Middle-drag normally pans the camera, EXCEPT when the sickle is
      // Sickle is omnidirectional now — middle-drag always pans the
      // camera regardless of equipped tool.
      this.onPanStart(ev);
      return;
    }
    // While placing a prop, the next tile click is what matters — fall
    // through to the regular clickTile path so confirmMoveProp runs.
    if (this.sim.state.moving) {
      this.updateHover();
      if (this.hovered) this.sim.clickTile(this.hovered.c, this.hovered.r);
      return;
    }
    // Tool-stand pickup. The seed bag is the only stand left; clicking it
    // both equips the plant tool AND opens the seeds chooser so the player
    // can pick what to plant.
    const standHit = this.pickToolStand();
    if (standHit) {
      const next = this.sim.state.tool === standHit ? 'hands' : standHit;
      this.sim.setTool(next);
      this.sim.cancelPlacement();
      if (next === 'plant') {
        this.sim.bus.emit({ type: 'inspect', kind: 'seeds', col: -1, row: -1 });
      }
      return;
    }
    // Toolrack — opens the tool-chooser panel.
    if (this.pickToolRack()) {
      this.sim.bus.emit({ type: 'inspect', kind: 'toolrack', col: -1, row: -1 });
      this.sim.cancelPlacement();
      return;
    }
    // Click the storage shelf → open the storage panel (full breakdown +
    // platform send / order). Pass -1/-1 so StoragePanel doesn't think
    // the click was on a granary tile (no demolish button).
    if (this.pickStorageDisplay()) {
      this.sim.bus.emit({ type: 'inspect', kind: 'storage', col: -1, row: -1 });
      return;
    }
    this.updateHover();
    if (!this.hovered) return;
    const c = this.hovered.c, r = this.hovered.r;
    const tile = this.sim.state.plot.tiles[r]?.[c];
    const tool = this.sim.state.tool;

    // Zones manager interception — clicks in the world route through
    // the manager instead of the world tool. Three sub-modes:
    //   editing an existing plot → toggle this tile in/out of it
    //   in-flight new-plot selection → paint tile into selection
    //   browsing → click on a plot tile dives in (with the drop anim)
    if (this.sim.state.zonesMode) {
      const editIdx = this.sim.state.zonesEditPlotIdx;
      if (editIdx != null) {
        // Only accept clicks that match the live hover state — clicks on
        // disallowed tiles (already in another plot, road, etc.) silently
        // no-op so the player isn't punished for missing the green/red.
        if (this.zoneEditKind === 'remove' || this.zoneEditKind === 'add') {
          this.sim.togglePlotTile(editIdx, c, r);
        }
        return;
      }
      if (this.sim.state.selecting) {
        this.selectingDragActive = true;
        try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
        this.sim.addToSelection(c, r);
        return;
      }
      // No plot selected, not painting — bare click does nothing. Diving
      // in happens from the sidebar's Enter button now.
      return;
    }

    // Plot drag-select mode — paint tiles into the selection while LMB
    // is held; the confirm panel updates live.
    if (this.sim.state.selecting) {
      this.selectingDragActive = true;
      try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
      this.sim.addToSelection(c, r);
      return;
    }

    // Empty hands → yank weeds anywhere. Quick raycast against the weed
    // meshes; if hit, pluck it. Works in BOTH plot view and overview now
    // that farming is uniform across both modes.
    if (this.sim.state.tool === 'hands') {
      const w = this.pickWeed();
      if (w) {
        this.sim.pluckWeed(w.c, w.r, w.subC, w.subR);
        return;
      }
    }

    // Farming tools all do sub-cell drag-paint — same UX whether the
    // player is inside an adopted plot or in overview on free tiles. Each
    // sub-cell crossed by the drag commits one Sim helper call. The
    // tile-coords routing in Sim.subContainer picks the right destination
    // (plot arrays vs. state.freeTilled/Seeds/Wet).
    //
    // EXCEPTION: plant tool + a "special" seed (berry / acorn) plants a
    // bush-sapling or tree-oak-sapling at TILE level — fall through to
    // sim.clickTile → applyTool which routes to TreeSystem.plant*.
    const isSpecialPlant = tool === 'plant' && !!this.sim.state.selectedSpecialSeed;
    if (!isSpecialPlant &&
        (tool === 'field' || tool === 'plant' || tool === 'water' || tool === 'scythe')) {
      this.plotToolDrag.active = true;
      this.plotToolDrag.pointerId = ev.pointerId;
      this.plotToolDrag.tool = tool;
      this.plotToolDrag.lastSub = '';
      this.plotToolDrag.lastCoord = null;
      try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
      const s = this.subCellAt(this.cursorGroundPos.x, this.cursorGroundPos.z);
      if (s) {
        this.plotToolDrag.lastSub = `${s.c}:${s.r}:${s.subC}:${s.subR}`;
        this.applyPlotTool(s.c, s.r, s.subC, s.subR);
      }
      return;
    }

    // Plant tool + berry/acorn picked: plant at tile-level on the clicked
    // tile.
    if (isSpecialPlant) {
      this.sim.clickTile(c, r);
      return;
    }
    // Multi-strike axe / pickaxe. Axe handles trees / stumps / bushes;
    // pickaxe handles boulders (drops stone). The actual chop runs in
    // TreeSystem.chop which checks state.tool for boulder gating.
    if (tool === 'axe' || tool === 'pickaxe') {
      // Defer to the regular Sim.clickTile path if any "modal" world
      // mode is active — placing a building, relocating a prop, or
      // editing the active plot. Otherwise strikeTree happily counts a
      // strike on a tile the player meant to act on differently.
      if (this.sim.state.placing || this.sim.state.moving || this.sim.state.editingPlot) {
        this.sim.clickTile(c, r);
        return;
      }
      if (tile?.thing) { this.strikeTree(c, r); return; }
      this.sim.bus.emit({ type: 'hint', text:
        tool === 'pickaxe' ? 'Use the pickaxe on boulders.' :
                              'Use the axe on a tree, bush, or stump.' });
      return;
    }
    // Everything else (hands, buildings, etc.) — direct dispatch.
    this.sim.clickTile(c, r);
  };
  private onPointerUp = (ev: PointerEvent) => {
    if (this.toolDrag.active && ev.pointerId === this.toolDrag.pointerId) {
      this.endToolDrag(ev);
    }
    if (this.hold && ev.pointerId === this.hold.pointerId) {
      this.cancelHold();
    }
    if (this.selectingDragActive) {
      this.selectingDragActive = false;
      try {
        if (this.canvas.hasPointerCapture(ev.pointerId)) {
          this.canvas.releasePointerCapture(ev.pointerId);
        }
      } catch { /* ignore */ }
    }
    if (this.plotToolDrag.active && ev.pointerId === this.plotToolDrag.pointerId) {
      this.plotToolDrag.active = false;
      try {
        if (this.canvas.hasPointerCapture(ev.pointerId)) {
          this.canvas.releasePointerCapture(ev.pointerId);
        }
      } catch { /* ignore */ }
    }
    this.onPanEnd(ev);
  };
  /** Free-fly camera held-keys. WASD / arrow keys pan the target across the
   *  ground plane; T/G tilt the polar angle (look-down ↔ look-from-side).
   *  Per-frame integration happens in animateCamera so motion is smooth. */
  private heldKeys = new Set<string>();
  private onKeyDown = (ev: KeyboardEvent) => {
    // Track held keys (lowercased) for per-frame pan/tilt. Skip if a text
    // input has focus so HUD typing isn't hijacked.
    const target = ev.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    const lk = ev.key.toLowerCase();
    if (['w','a','s','d','t','g','arrowup','arrowdown','arrowleft','arrowright','pageup','pagedown'].includes(lk)) {
      this.heldKeys.add(lk);
    }
    if (ev.key === 'Escape') {
      if (this.sim.state.moving)        { this.sim.cancelMoving();   return; }
      if (this.sim.state.placingBerry)  { this.sim.cancelBerryPlanting(); return; }
      if (this.sim.state.editingPlot)   { this.sim.togglePlotEdit();      return; }
      if (this.sim.state.selecting)     { this.sim.cancelPlotSelect(); return; }
      if (this.sim.state.plotInView)    { this.sim.exitPlot();       return; }
      if (this.sim.state.zoom?.active)  { this.sim.exitZoom();       return; }
      this.sim.cancelPlacement();
    }
    else if (ev.key === 'q' || ev.key === 'Q') this.targetAzimuth -= Math.PI / 2;
    else if (ev.key === 'e' || ev.key === 'E') this.targetAzimuth += Math.PI / 2;
    else if ((ev.key === 'r' || ev.key === 'R') && !this.sim.state.zoom?.active) {
      // Reset view → snap to default NE iso, centered on the lot, polar +
      // zoom back to factory. Disabled in zoom mode; ESC first.
      this.targetAzimuth = Math.PI / 4;
      this.targetPolar = Math.PI / 3;
      this.camTarget.set(0, 0, 0);
      this.camera.zoom = 1.0;
      this.camera.updateProjectionMatrix();
    }
    else if (ev.key === 'x' || ev.key === 'X') {
      if (this.hovered) this.sim.demolish(this.hovered.c, this.hovered.r);
    }
  };
  private onKeyUp = (ev: KeyboardEvent) => {
    this.heldKeys.delete(ev.key.toLowerCase());
  };

  /** Recompute camera world position from azimuth, polar, radius, target.
   *  Adds a small random offset while `camShake` > 0 for placement weight. */
  private applyCamera() {
    const sinP = Math.sin(this.currentPolar);
    const cosP = Math.cos(this.currentPolar);
    const r = this.camRadius;
    // Settings can disable camera shake entirely — motion-sensitive
    // players opt out, AND it's the simplest way to test whether a
    // bug repro is shake-correlated.
    const shakeGate = !this.settings || this.settings.cameraShake();
    const shake = (shakeGate && this.camShake > 0) ? Math.min(0.4, this.camShake) : 0;
    const sx = shake > 0 ? (Math.random() - 0.5) * shake * 0.8 : 0;
    const sy = shake > 0 ? (Math.random() - 0.5) * shake * 0.4 : 0;
    const sz = shake > 0 ? (Math.random() - 0.5) * shake * 0.8 : 0;
    this.camera.position.set(
      this.camTarget.x + sinP * Math.cos(this.currentAzimuth) * r + sx,
      this.camTarget.y + cosP * r + sy,
      this.camTarget.z + sinP * Math.sin(this.currentAzimuth) * r + sz,
    );
    this.camera.lookAt(this.camTarget);
  }

  /** Ease the camera azimuth + polar toward their targets. Also processes
   *  held-key free-fly inputs: WASD / arrows pan the target across the ground
   *  plane (rotated to match the camera's yaw); T / G tilt the polar angle. */
  private animateCamera(dt: number) {
    const azDiff = this.targetAzimuth - this.currentAzimuth;
    if (Math.abs(azDiff) > 0.0005) {
      this.currentAzimuth += azDiff * Math.min(1, dt * Diorama.AZIMUTH_TWEEN);
    }
    const polDiff = this.targetPolar - this.currentPolar;
    if (Math.abs(polDiff) > 0.0005) {
      this.currentPolar += polDiff * Math.min(1, dt * Diorama.AZIMUTH_TWEEN);
    }

    // ── Held-key free-fly pan + tilt ──────────────────────────────────
    // Skip entirely while a plot is in view — WASD panning would yank the
    // camera out of the focused dive and confuse the player.
    if (this.heldKeys.size > 0 && !this.sim.state.plotInView) {
      // Pan vector in screen-relative axes — uses the camera's current
      // azimuth so "forward" always means "away from camera".
      let dxLocal = 0, dzLocal = 0;
      // W/up = camera moves "up" on screen (toward the back of the iso view).
      // S/down = "down" on screen. A/D follow screen-left / screen-right.
      if (this.heldKeys.has('w') || this.heldKeys.has('arrowup'))    dzLocal += 1;
      if (this.heldKeys.has('s') || this.heldKeys.has('arrowdown'))  dzLocal -= 1;
      if (this.heldKeys.has('a') || this.heldKeys.has('arrowleft'))  dxLocal += 1;
      if (this.heldKeys.has('d') || this.heldKeys.has('arrowright')) dxLocal -= 1;
      if (dxLocal || dzLocal) {
        // Keyboard input cancels the in-flight zoom animation too.
        this.zoom.animating = false;
        const len = Math.hypot(dxLocal, dzLocal);
        dxLocal /= len; dzLocal /= len;
        // Pan speed scales inversely with zoom so high-zoom doesn't fly
        // across the world. Base = 18 world units/sec at zoom 1.
        const speed = 18 / Math.max(0.5, this.camera.zoom);
        const cos = Math.cos(this.currentAzimuth);
        const sin = Math.sin(this.currentAzimuth);
        // Camera "forward" on the ground plane is (-cos, -sin) by the
        // applyCamera math; "right" is (-sin, cos).
        const fwdX = -cos, fwdZ = -sin;
        const rightX = -sin, rightZ = cos;
        const dx = (rightX * dxLocal + fwdX * dzLocal) * speed * dt;
        const dz = (rightZ * dxLocal + fwdZ * dzLocal) * speed * dt;
        this.camTarget.x += dx;
        this.camTarget.z += dz;
        // Clamp to plot bounds + generous margin — same as drag-pan so
        // the camera never strays into the void.
        const halfW = PLOT_W / 2 + CAM_CLAMP_MARGIN;
        const halfH = PLOT_H / 2 + CAM_CLAMP_MARGIN;
        this.camTarget.x = Math.max(-halfW, Math.min(halfW, this.camTarget.x));
        this.camTarget.z = Math.max(-halfH, Math.min(halfH, this.camTarget.z));
      }
      // Tilt: T = look-down (smaller polar), G = look-from-side (larger).
      // Clamped to a safe range so we never flip past vertical or below
      // the ground.
      const tiltSpeed = 1.2;
      if (this.heldKeys.has('t') || this.heldKeys.has('pageup')) {
        this.targetPolar = Math.max(0.10, this.targetPolar - tiltSpeed * dt);
      }
      if (this.heldKeys.has('g') || this.heldKeys.has('pagedown')) {
        this.targetPolar = Math.min(1.40, this.targetPolar + tiltSpeed * dt);
      }
    }

    if (this.camShake > 0) this.camShake = Math.max(0, this.camShake - dt);
    this.applyCamera();
  }

  /** Ease camera target, zoom, and fog toward whichever pose the player is
   *  in (overview vs. close-up zoom). Idles when not animating so the user's
   *  pan / wheel / R-reset controls own the camera between transitions. */
  private animateZoom(dt: number) {
    if (!this.zoom.animating) {
      // Idle — when in overview, snapshot the user's live camera as the
      // pose we'll restore the next time they exit a zoom. When in zoom
      // mode, just leave everything alone.
      if (!this.zoom.active) {
        this.zoom.savedX = this.camTarget.x;
        this.zoom.savedZ = this.camTarget.z;
        this.zoom.savedZoom = this.camera.zoom;
      }
      return;
    }
    const desiredX    = this.zoom.active ? this.zoom.targetX     : this.zoom.savedX;
    const desiredZ    = this.zoom.active ? this.zoom.targetZ     : this.zoom.savedZ;
    const desiredZoom = this.zoom.active
      ? (this.zoom.targetZoom ?? Diorama.ZOOM_LEVEL)
      : this.zoom.savedZoom;
    const ease = Math.min(1, dt * 5.5);
    this.camTarget.x += (desiredX - this.camTarget.x) * ease;
    this.camTarget.z += (desiredZ - this.camTarget.z) * ease;
    this.camera.zoom += (desiredZoom - this.camera.zoom) * ease;
    this.camera.updateProjectionMatrix();
    this.applyCamera();
    const settled =
      Math.abs(desiredX - this.camTarget.x) < 0.005 &&
      Math.abs(desiredZ - this.camTarget.z) < 0.005 &&
      Math.abs(desiredZoom - this.camera.zoom) < 0.005;
    if (settled) {
      this.camTarget.x = desiredX;
      this.camTarget.z = desiredZ;
      this.camera.zoom = desiredZoom;
      this.camera.updateProjectionMatrix();
      this.applyCamera();
      this.zoom.animating = false;
    }
  }

  /**
   * Construction reveal — raises the clip plane in visible "courses". Each
   * course pops in over the first 30% of its window, then holds. While the
   * animation runs the mesh rattles horizontally and dust trickles down
   * from the rising front.
   */
  private animateBuildAnims(dt: number) {
    if (this.buildAnims.length === 0) return;
    const COURSES = 6;        // visible build "rows" per stage transition
    for (const a of this.buildAnims) {
      a.t += dt;
      const p = Math.min(1, a.t / a.duration);
      // Stepped ease: each course rises with a quick cubic ease-out, then holds
      const stepSize = 1 / COURSES;
      const stepIdx = Math.min(COURSES - 1, Math.floor(p / stepSize));
      const subT = (p - stepIdx * stepSize) / stepSize;
      const easedSub = subT < 0.35 ? 1 - Math.pow(1 - subT / 0.35, 3) : 1;
      const eased = Math.min(1, (stepIdx + easedSub) * stepSize);
      a.plane.constant = a.fromY + (a.toY - a.fromY) * eased;

      // Horizontal rattle — intensity tapers off as we finish
      const intensity = (1 - p) * 0.05;
      if (intensity > 0.002) {
        a.mesh.position.x = a.cx + (Math.random() - 0.5) * intensity;
        a.mesh.position.z = a.cz + (Math.random() - 0.5) * intensity;
      } else {
        a.mesh.position.x = a.cx;
        a.mesh.position.z = a.cz;
      }

      // Dust trickle at the rising front + tiny camera shake on each course step
      a.dustCooldown -= dt;
      if (a.dustCooldown <= 0 && a.t < a.duration - 0.1) {
        a.dustCooldown = 0.05 + Math.random() * 0.05;
        const frontY = a.plane.constant;
        const dx = (Math.random() - 0.5) * a.w * 0.85;
        const dz = (Math.random() - 0.5) * a.d * 0.85;
        const m = new THREE.Mesh(BOX, mat(Math.random() < 0.4 ? 0xc8a060 : 0xa07840));
        const sz = 0.06 + Math.random() * 0.06;
        m.scale.set(sz, sz, sz);
        m.position.set(a.cx + dx, frontY + 0.06, a.cz + dz);
        m.castShadow = false;
        this.flyerGroup.add(m);
        this.particles.push({
          obj: m,
          vx: (Math.random() - 0.5) * 0.5,
          vy: -0.2 - Math.random() * 0.3,    // trickle DOWN from the build front
          vz: (Math.random() - 0.5) * 0.5,
          life: 0.7,
          maxLife: 0.7,
        });
      }
      // Small camera shake right when a new course locks in
      const courseEdge = Math.abs(subT - 0.35);
      if (courseEdge < 0.02 && this.camShake < 0.05) this.camShake = 0.06;
    }
    // Reset transforms + drop finished entries
    this.buildAnims = this.buildAnims.filter(a => {
      if (a.t >= a.duration) {
        a.mesh.position.x = a.cx;
        a.mesh.position.z = a.cz;
        return false;
      }
      return true;
    });
  }
  /** Legacy no-op drop animation handler (kept so update() compiles). */
  private animateDrops(_dt: number) { /* drops disabled */ }
  private isInSceneObj(o: THREE.Object3D): boolean {
    let n: THREE.Object3D | null = o;
    while (n) { if (n === this.scene) return true; n = n.parent; }
    return false;
  }

  private onWheel = (ev: WheelEvent) => {
    ev.preventDefault();
    // Any wheel input cancels the in-flight zoom animation so the player
    // can scroll the moment they enter / exit plot mode instead of waiting
    // for the dive-in tween to finish.
    this.zoom.animating = false;
    const speed = ev.shiftKey ? 3 : 1;
    const factor = Math.pow(0.95, (ev.deltaY * speed) / 100);
    // Plot mode allows much deeper zoom now so the player can inspect
    // individual sub-cells. World mode keeps its own ceiling for overview
    // legibility. Both share a single floor so wide-overview wheel-out
    // never goes past framing the whole island.
    const maxZoom = this.sim.state.plotInView ? 22.0 : 28.0;
    this.camera.zoom = Math.max(0.4, Math.min(maxZoom, this.camera.zoom * factor));
    this.camera.updateProjectionMatrix();
  };

  private onPanStart(ev: PointerEvent) {
    // Any pan input cancels the in-flight zoom animation.
    this.zoom.animating = false;
    this.camPanning = true;
    this.camLastPointer = { x: ev.clientX, y: ev.clientY };
    // Set cursor first — pointer-capture may throw on some event sources.
    this.canvas.style.cursor = 'grabbing';
    try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
  }

  // Reusable scratch vectors so we don't allocate every pan-frame.
  private _panRight = new THREE.Vector3();
  private _panUp = new THREE.Vector3();
  private _panBack = new THREE.Vector3();

  private onPanMove(ev: PointerEvent) {
    if (!this.camPanning) return;
    const dx = ev.clientX - this.camLastPointer.x;
    const dy = ev.clientY - this.camLastPointer.y;
    this.camLastPointer = { x: ev.clientX, y: ev.clientY };
    const px = (2 * this.viewSize) / this.canvas.clientHeight / this.camera.zoom;

    // Pull the camera's right and up vectors straight from its world matrix,
    // then flatten to the ground plane so pan stays planar.
    this.camera.matrixWorld.extractBasis(this._panRight, this._panUp, this._panBack);
    this._panRight.y = 0; this._panRight.normalize();
    this._panUp.y = 0;    this._panUp.normalize();

    // "Grab the world" pan — the world follows the cursor, the camera moves
    // the opposite way. Drag DOWN to make the camera rise; drag LEFT to make
    // the camera slide right. Same mental model as Google Maps / Photoshop.
    //   drag right (+dx) → camera target moves -right → camera moves LEFT
    //   drag down  (+dy) → camera target moves +up    → camera moves UP on screen
    const dxScreen = -dx;
    const dyScreen =  dy;

    this.camTarget.x += (this._panRight.x * dxScreen + this._panUp.x * dyScreen) * px;
    this.camTarget.z += (this._panRight.z * dxScreen + this._panUp.z * dyScreen) * px;

    // Clamp to plot bounds + generous margin — wide enough that the player
    // can dolly outside the plot to look back at it from any angle.
    const halfW = PLOT_W / 2 + CAM_CLAMP_MARGIN;
    const halfH = PLOT_H / 2 + CAM_CLAMP_MARGIN;
    if (this.camTarget.x < -halfW) this.camTarget.x = -halfW;
    else if (this.camTarget.x > halfW) this.camTarget.x = halfW;
    if (this.camTarget.z < -halfH) this.camTarget.z = -halfH;
    else if (this.camTarget.z > halfH) this.camTarget.z = halfH;
    this.applyCamera();
  }

  private onPanEnd = (ev: PointerEvent) => {
    if (this.camPanning) {
      this.camPanning = false;
      this.canvas.style.cursor = '';
      if (this.canvas.hasPointerCapture(ev.pointerId)) {
        this.canvas.releasePointerCapture(ev.pointerId);
      }
    }
  };

  // ── Tool drag (hoe line-till) ─────────────────────────────────────
  /** Project the pointer onto the ground plane (y=0) so the tool prop can
   *  sit exactly under the cursor, even when the cursor is between tiles. */
  private _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private updateCursorGround() {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    this.raycaster.ray.intersectPlane(this._groundPlane, this.cursorGroundPos);
    if (this.cursorLamp) {
      const above = Math.sin(this.timeOfDay * Math.PI * 2 - Math.PI / 2);
      const nightFactor = Math.max(0, Math.min(1, -above + 0.15));
      // Dampen the lamp at close-up zoom — the camera is in plot mode
      // and the lamp would otherwise wash out the work area.
      // viewSize starts at 14 (overview) and drops as the player zooms
      // in (plot mode lands around 6-8). Scale intensity by viewSize / 14.
      const zoomFactor = Math.min(1, this.viewSize / 14);
      this.cursorLamp.intensity = nightFactor * 1.8 * zoomFactor;
      this.cursorLamp.position.set(this.cursorGroundPos.x, 0.7, this.cursorGroundPos.z);
    }
    // Water-pour + sickle indicators — only show when the right tool is
    // active in plot mode, follow the cursor on the ground plane.
    const inPlot = this.sim.state.plotInView;
    const tool = this.sim.state.tool;
    if (this.waterIndicator) {
      this.waterIndicator.visible = !!(inPlot && tool === 'water');
      if (this.waterIndicator.visible) {
        this.waterIndicator.position.set(this.cursorGroundPos.x, 0.05, this.cursorGroundPos.z);
        // Pulse the ring scale slightly so it reads as "active"
        const pulse = 1 + Math.sin(this.elapsed * 4) * 0.10;
        this.waterIndicator.scale.set(pulse, pulse, pulse);
      }
    }
    if (this.sickleIndicator) {
      this.sickleIndicator.visible = !!(inPlot && tool === 'scythe');
      if (this.sickleIndicator.visible) {
        // Omnidirectional sickle — sit the indicator dead-on the cursor,
        // flat against the ground. Player swings cover a 3×3 sub-cell
        // swath around the click, no rotation needed.
        this.sickleIndicator.position.set(this.cursorGroundPos.x, 0.06, this.cursorGroundPos.z);
        this.sickleIndicator.rotation.set(Math.PI / 2, 0, 0);
      }
    }
  }

  // ── Water drag (paint mode) ───────────────────────────────────────
  /** Start the water-drag: each tile the cursor enters while LMB is held
   *  gets watered immediately. */
  private startWaterDrag(ev: PointerEvent, c: number, r: number) {
    this.toolDrag.active = true;
    this.toolDrag.pointerId = ev.pointerId;
    this.toolDrag.tileSet.clear();
    this.toolDrag.tiles = [];
    this.toolDrag.phase = 0;
    this.applyWaterTile(c, r);
    try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
  }
  /** Idempotent water-on-tile: dedupes via tileSet so a stationary cursor
   *  doesn't keep retriggering. */
  private applyWaterTile(c: number, r: number) {
    const key = `${c}:${r}`;
    if (this.toolDrag.tileSet.has(key)) return;
    this.toolDrag.tileSet.add(key);
    this.spawnParticles(c, r, [0x5ab0e8, 0x80c8f0, 0xa8dcf8], 6, { life: 0.55, spread: 0.20, upBias: 0.1 });
    this.sim.applyTool(c, r);
  }
  private endToolDrag(ev: PointerEvent) {
    if (!this.toolDrag.active) return;
    this.toolDrag.active = false;
    try {
      if (this.canvas.hasPointerCapture(ev.pointerId)) {
        this.canvas.releasePointerCapture(ev.pointerId);
      }
    } catch { /* ignore */ }
    this.toolDrag.tiles = [];
    this.toolDrag.tileSet.clear();
  }

  // ── Hold-to-progress (3×3 hoe / plant / scythe) ───────────────────
  private startHold(ev: PointerEvent, c: number, r: number, tool: 'field' | 'plant' | 'scythe') {
    this.hold = { c, r, tool, progress: 0, lastBurst: -1, pointerId: ev.pointerId };
    try { this.canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
    this.spawnHoldBurst(c, r, tool, /*big=*/false);
  }
  private cancelHold() {
    if (!this.hold) return;
    try {
      if (this.canvas.hasPointerCapture(this.hold.pointerId)) {
        this.canvas.releasePointerCapture(this.hold.pointerId);
      }
    } catch { /* ignore */ }
    this.hold = null;
    this.cursorMarker.scale.set(1, 1, 1);
    (this.cursorMarker.material as THREE.MeshBasicMaterial).opacity = 0.45;
  }
  /** Per-frame progress + visual feedback while the player holds on a tile.
   *  All three hold tools commit to the 3×3 around the cursor when full. */
  private animateHold(dt: number) {
    if (!this.hold) return;
    const h = this.hold;
    const duration =
      h.tool === 'field'  ? Diorama.HOLD_HOE_TIME    :
      h.tool === 'plant'  ? Diorama.HOLD_PLANT_TIME  :
                            Diorama.HOLD_SCYTHE_TIME;
    h.progress = Math.min(1, h.progress + dt / duration);
    // Continuous low-rate spray
    if (Math.random() < 0.55) this.spawnHoldBurst(h.c, h.r, h.tool, false);
    // Stage thresholds — each quarter triggers a bigger burst + faint shake
    const thresholds = [0.25, 0.5, 0.75];
    for (const t of thresholds) {
      if (h.lastBurst < t && h.progress >= t) {
        this.spawnHoldBurst(h.c, h.r, h.tool, true);
        this.camShake = Math.max(this.camShake, 0.04);
        h.lastBurst = t;
      }
    }
    // Hover marker reads as the 3×3 area indicator: scaled to 3 tiles,
    // tint deepens as completion nears.
    const mat = this.cursorMarker.material as THREE.MeshBasicMaterial;
    mat.opacity = 0.30 + h.progress * 0.45;
    const pulse = Math.sin(this.elapsed * 14) * 0.06;
    this.cursorMarker.scale.set(3 + pulse, 1, 3 + pulse);
    // Commit on full progress — fan out to the 3×3 around the cursor.
    if (h.progress >= 1) {
      const cc = h.c, rr = h.r, tool = h.tool;
      this.camShake = Math.max(this.camShake, 0.10);
      this.cancelHold();
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          this.spawnHoldBurst(cc + dc, rr + dr, tool, /*big=*/true);
          this.sim.applyTool(cc + dc, rr + dr);
        }
      }
    }
  }
  private spawnHoldBurst(c: number, r: number, tool: 'field' | 'plant' | 'scythe', big: boolean) {
    let colors: number[];
    switch (tool) {
      case 'field':   colors = [0x9c6c44, 0x7a4d28, 0xc89058]; break;
      case 'plant':   colors = [0xc09058, 0x9a6c38, 0xfff080]; break;
      case 'scythe':  colors = [0xe8c850, 0xc8a040, 0xfff080, 0x80a050]; break;
    }
    this.spawnParticles(c, r, colors, big ? 9 : 3,
      { life: big ? 0.55 : 0.35, spread: 0.32, upBias: 0.5 });
  }

  // ── Multi-strike axe ──────────────────────────────────────────────
  /** Each click on a chop-able thing is one swing. Trees + boulders take
   *  AXE_STRIKES_TO_FELL hits; bushes / stumps / tall grass fall in one.
   *  Boulders are pickaxe-only; trees/stumps/bushes/saplings are axe-only.
   *  Mismatched tool → one hint, no strike count, no wasted clicks. */
  private strikeTree(c: number, r: number) {
    const tile = this.sim.state.plot.tiles[r]?.[c];
    if (!tile?.thing) return;
    const tool = this.sim.state.tool;
    // Tool-thing match check: boulders need pickaxe, everything else
    // (trees, stumps, bushes, saplings, tall-grass) takes the axe.
    const isBoulder = tile.thing === 'rock-boulder';
    if (isBoulder && tool !== 'pickaxe') {
      this.sim.bus.emit({ type: 'hint', text: 'Boulders need the PICKAXE.' });
      return;
    }
    if (!isBoulder && tool === 'pickaxe') {
      this.sim.bus.emit({ type: 'hint', text: 'Pickaxe is for boulders — use the AXE here.' });
      return;
    }
    const key = `${c}:${r}`;
    const entry = this.treeStrikes.get(key) ?? { count: 0, lastT: -Infinity };
    if (this.elapsed - entry.lastT < Diorama.AXE_STRIKE_COOLDOWN) return;
    entry.count++;
    entry.lastT = this.elapsed;
    this.treeStrikes.set(key, entry);
    this.lastAxeStrikeT = this.elapsed;       // drives the axe prop swing
    // Lock the prop to the struck tile's world position for the duration
    // of the swing animation. Without this the prop trails the cursor and
    // the chop misses the actual target if the player nudges their mouse.
    const [tx, , tz] = this.tileCenter(c, r);
    this.strikeTargetX = tx;
    this.strikeTargetZ = tz;

    // Hit feedback: thing-specific chip particles + camera kick.
    let colors: number[];
    if (tile.thing === 'tree-oak' || tile.thing === 'tree-oak-stump') colors = [0x9c6c44, 0x6a4828, 0xc89058];
    else if (tile.thing === 'rock-boulder') colors = [0x808080, 0x606060, 0xa0a0a0];
    else colors = [0x80a050, 0x60803a, 0xc8d878];
    this.spawnParticles(c, r, colors, 10, { life: 0.7, spread: 0.55, upBias: 1.0 });
    this.camShake = Math.max(this.camShake, 0.12);

    // Trees + boulders need multiple strikes; everything else falls now.
    const hardThing = tile.thing === 'tree-oak' || tile.thing === 'rock-boulder';
    const need = hardThing ? Diorama.AXE_STRIKES_TO_FELL : 1;
    if (entry.count < need) {
      // Intermediate strike — emit a per-swing impact event so Sounds
      // can play the chop-wood / pickaxe cue at the tile's position.
      // No hint toast here (the strike progress is communicated by
      // particles + cam shake + axe prop swing — a hint event would
      // route through ui/hint and mask the impact sound).
      const kind = tile.thing === 'rock-boulder' ? 'rock' : 'tree';
      this.sim.bus.emit({ type: 'strikeImpact', col: c, row: r, kind });
      return;
    }
    this.treeStrikes.delete(key);
    // Final swing — if this was a full oak (not a stump / bush / boulder),
    // spawn a falling-tree overlay BEFORE delegating to the sim. The sim
    // converts the tile from 'tree-oak' → 'tree-oak-stump' synchronously,
    // which triggers a rebuild that removes the visible tree from the
    // tile group. The overlay is independent of that group, so it plays
    // out a 7s tipping animation on top of the now-rendered stump.
    const wasFullTree = tile.thing === 'tree-oak';
    if (wasFullTree) this.startTreeFall(c, r);
    // Final swing — let the sim run the actual chop (energy + state).
    this.sim.clickTile(c, r);
  }

  /** Spawn a fresh oak mesh as a falling overlay and queue it for the
   *  animation loop. The mesh is positioned at the tile center with
   *  its local origin at the trunk base, so rotating around an XZ-
   *  plane axis tips the trunk over without the base drifting.
   *
   *  Fall direction is hashed from the tile coords — deterministic so
   *  the same tree always falls the same way (replay-safe) and a row
   *  of trees doesn't all tip uniformly. */
  private startTreeFall(c: number, r: number) {
    const seed = c * 1000 + r * 7 + 41;       // matches the rebuildTile seed
    const root = makeTree(seed);
    const [x, , z] = this.tileCenter(c, r);
    root.position.set(x, 0, z);
    // Fall direction: deterministic per-tile angle in [0, 2π).
    const ang = ((c * 73 + r * 31) % 360) * Math.PI / 180;
    const dx = Math.cos(ang);
    const dz = Math.sin(ang);
    // Rotation axis perpendicular to the fall direction, lying in the
    // ground plane: +Y × d = (dz, 0, -dx). Rotating around this axis
    // by +π/2 tips the trunk top from (0, h, 0) to (h·dx, 0, h·dz).
    const axis = new THREE.Vector3(dz, 0, -dx).normalize();
    this.scene.add(root);
    this.fallingTrees.push({
      root, t: 0,
      duration: Diorama.TREE_FALL_S,
      axis,
      finalAngle: Math.PI / 2 + 0.08,         // tiny overshoot for "thud"
    });
  }

  /** Per-frame fall animation. Curve is ease-in cubic-ish — the trunk
   *  creaks barely-moving for the first ~70% of time, then accelerates
   *  through the back half, hitting the ground as the audio impact
   *  cracks. Pendulum-physics would be more accurate but the 7-second
   *  budget is too slow for a real gravity fall; the eased curve
   *  matches the cinematic shape of the audio clip instead.
   *
   *  After the duration elapses, the trunk is fully laid out at
   *  ground level. We hold it for ~300ms (the audio's "settle" tail)
   *  then dispose. The stump rendered underneath provides the
   *  permanent visual; no fallen-log mesh is left behind unless the
   *  underlying tile has its `fallen` flag set. */
  private animateFallingTrees(dt: number) {
    if (this.fallingTrees.length === 0) return;
    for (let i = this.fallingTrees.length - 1; i >= 0; i--) {
      const f = this.fallingTrees[i];
      f.t += dt;
      const u = Math.min(1, f.t / f.duration);
      // Ease-in: slow creak then accelerating tip. Power ~3 mirrors the
      // angular-acceleration curve of a tipping rigid rod close enough
      // to read as natural at this duration.
      const eased = Math.pow(u, 3.0);
      const angle = f.finalAngle * eased;
      f.root.setRotationFromAxisAngle(f.axis, angle);
      // Hold on the ground for a beat after the visual fall completes
      // so the trunk doesn't snap out of existence mid-audio-tail.
      if (f.t >= f.duration + 0.35) {
        this.scene.remove(f.root);
        f.root.traverse(o => {
          if (o instanceof THREE.Mesh) {
            o.geometry?.dispose();
            const m = o.material as THREE.Material | THREE.Material[];
            if (Array.isArray(m)) m.forEach(x => x.dispose());
            else if (m) m.dispose();
          }
        });
        this.fallingTrees.splice(i, 1);
      }
    }
  }

  // ── Tool stands (physical world racks) ───────────────────────────
  /** Tool selection lives entirely in the HUD now (PlotToolbar / Dock).
   *  No physical world stands; this is intentionally empty. */
  private buildToolStands() {
    return;
  }

  /** Did the cursor hit the toolrack? */
  private pickToolRack(): boolean {
    if (!this.toolRack) return false;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObject(this.toolRack, true);
    return hits.length > 0;
  }

  /** Raycast against tool-stand meshes; return the matched tool id or null. */
  private pickToolStand(): ToolId | null {
    if (this.toolStands.length === 0) return null;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const roots = this.toolStands.map(s => s.root);
    const hits = this.raycaster.intersectObjects(roots, true);
    if (hits.length === 0) return null;
    let n: THREE.Object3D | null = hits[0].object;
    while (n) {
      const id = n.userData?.toolStandId as ToolId | undefined;
      if (id) return id;
      n = n.parent;
    }
    return null;
  }

  // ── Storage shelf (replaces HUD basket/flour/wood chips) ─────────
  /** Build the wooden market-stall shelf with 5 bins under a thatched
   *  roof. Piles are added per refresh; position comes from state.props. */
  private buildStorageDisplay() {
    const layout = this.sim.state.props!;
    const shelfCol = layout.storage.c;
    const shelfRow = layout.storage.r;

    const root = this.storageDisplay.root;
    const b = new VoxelBuilder();

    // Counter top + front lip
    b.add(1.70, 0.08, 0.50, 0, 0.10, 0, COL.wood);
    b.add(1.70, 0.04, 0.04, 0, 0.16,  0.25, COL.woodDk);   // front edge
    // Counter underbody — slats below the top
    for (let i = -3; i <= 3; i++) {
      b.add(0.20, 0.10, 0.04, i * 0.24, 0.06, 0.22, i % 2 === 0 ? COL.wood : COL.woodDk);
    }

    // Bin dividers
    for (let i = -2; i <= 2; i++) {
      b.add(0.05, 0.22, 0.46, i * 0.30, 0.21, 0, COL.woodDk);
    }
    // Back wall (planks) — slightly varied for texture
    for (let i = 0; i < 6; i++) {
      const x = -0.80 + i * 0.32;
      b.add(0.30, 0.42, 0.04, x, 0.32, -0.23, i % 2 === 0 ? 0xa07440 : 0x8b6423);
    }

    // Two thick corner posts that support the roof
    for (const px of [-0.85, 0.85]) {
      for (let i = 0; i < 7; i++) {
        b.add(0.08, 0.10, 0.08, px, 0.12 + i * 0.10, -0.22,
              i % 2 === 0 ? COL.wood : COL.woodDk);
      }
    }
    // Roof ridge beam
    b.add(1.90, 0.06, 0.08, 0, 0.85, -0.22, COL.woodDk);
    // Slanted thatched roof — overlapping rows of warm-tinted planks
    const THATCH_A = 0xc8a55c, THATCH_B = 0xa78240, THATCH_C = 0x8c6630;
    for (let lay = 0; lay < 4; lay++) {
      const w = 1.90 - lay * 0.05;
      const y = 0.86 + lay * 0.07;
      const z = -0.22 + (lay + 1) * 0.16;
      const colors = [THATCH_A, THATCH_B, THATCH_C];
      const tint = colors[lay % colors.length];
      b.add(w, 0.06, 0.18, 0, y, z, tint);
    }
    // Roof front overhang (drips a little past the counter)
    b.add(1.95, 0.04, 0.06, 0, 1.05,  0.45, THATCH_C);

    // Tiny hanging "STORAGE" sign — small board on chains under the overhang
    b.add(0.06, 0.14, 0.02,  0.50, 1.00, 0.40, COL.woodDk);   // hang strap L
    b.add(0.06, 0.14, 0.02,  0.66, 1.00, 0.40, COL.woodDk);   // hang strap R
    b.add(0.30, 0.18, 0.04,  0.58, 0.90, 0.40, COL.wood);     // sign board
    b.add(0.10, 0.04, 0.02,  0.58, 0.92, 0.42, COL.woodDk);   // sign engraving stroke

    // Side fences for visual stop (just decor)
    b.add(0.04, 0.14, 0.06, -0.88, 0.10,  0.22, COL.woodDk);
    b.add(0.04, 0.14, 0.06,  0.88, 0.10,  0.22, COL.woodDk);

    root.add(b.build());
    root.userData.isStorageDisplay = true;
    root.traverse(o => { o.userData.isStorageDisplay = true; });

    const [wx, , wz] = this.tileCenter(shelfCol, shelfRow);
    root.position.set(wx, 0, wz);
    // Sized to fit in one tile so it stays in the props line without
    // overlapping the seed-bag stand next door.
    root.scale.setScalar(0.55);
    this.scene.add(root);
    this.refreshStorageDisplay();
  }

  /** Tear down + rebuild the per-bin piles. Cheap; runs on resource events. */
  private refreshStorageDisplay() {
    if (!this.storageDisplay) return;
    for (const m of this.storageDisplay.piles) this.storageDisplay.root.remove(m);
    this.storageDisplay.piles = [];
    const r = this.sim.state.res;
    const bins: { count: number; x: number; build: (b: VoxelBuilder, x: number, n: number) => void }[] = [
      { count: r.basket, x: -0.60, build: makeWheatPile },
      { count: r.corn,   x: -0.30, build: makeCornPile },
      { count: r.carrot, x:  0.00, build: makeCarrotPile },
      { count: r.flour,  x:  0.30, build: makeFlourPile },
      { count: r.wood,   x:  0.60, build: makeWoodPile },
    ];
    for (const bin of bins) {
      if (bin.count <= 0) continue;
      const vb = new VoxelBuilder();
      bin.build(vb, bin.x, bin.count);
      const mesh = vb.build();
      mesh.userData.isStorageDisplay = true;
      mesh.traverse(o => { o.userData.isStorageDisplay = true; });
      this.storageDisplay.root.add(mesh);
      this.storageDisplay.piles.push(mesh);
    }
  }

  /** Tiles owned by visible plots. In plot mode, only the active plot;
   *  in overview, all adopted plots. Used by every per-sub-cell builder. */
  private allPlotTiles(): { c: number; r: number }[] {
    const out: { c: number; r: number }[] = [];
    for (const p of this.visiblePlots()) for (const t of p.tiles) out.push(t);
    return out;
  }
  /** The set of plots whose per-sub-cell work should currently be rendered. */
  private visiblePlots() {
    if (this.sim.state.plotInView) {
      const ap = this.activePlotData();
      return ap ? [ap] : [];
    }
    return this.sim.state.plots ?? [];
  }
  /** The currently-active plot's data, or null if none. */
  private activePlotData() {
    const i = this.sim.state.activePlotIdx;
    const plots = this.sim.state.plots ?? [];
    return i != null && i >= 0 && i < plots.length ? plots[i] : null;
  }
  /** Non-plot tile groups sink + shrink into nothing as plot mode opens
   *  so the world reads as falling away beneath the lifted plot. Each
   *  tile's start time is staggered by its distance from the active
   *  plot's center — tiles closest to the plot drop first, the far
   *  edges last, producing a ripple-outward effect. Active plot tiles
   *  stay at y=0 / scale=1 throughout. */
  private animatePlotTileDrop(dt: number) {
    const a = this.plotDropAnim;
    // Snappier timeline. Snap-to-target when at rest AND make sure the
    // non-active tiles are fully hidden at progress=1 so they can't peek.
    if (a.progress === a.target) {
      const hide = a.progress >= 1;
      for (const group of this.tileGroups.values()) {
        const isActive = group.userData.isActivePlotTile === true;
        if (isActive) continue;
        if (group.visible === hide) group.visible = !hide;
      }
      return;
    }
    // Calmer drop — all tiles fall together over a short window, no
    // per-tile stagger (the rippling cascade read as "hectic" per QA),
    // smaller depth, no shrink-to-zero. Just a quick uniform sink so
    // the focus shifts to the active plot.
    const TOTAL_DURATION = 0.45;
    const PLOT_DROP_Y = -8.0;
    const STAGGER_PER_RING = 0.0;     // tiles drop together
    const TILE_FALL_TIME = TOTAL_DURATION;
    a.t += dt;
    const elapsed = Math.min(TOTAL_DURATION, a.t);
    const globalFrac = elapsed / TOTAL_DURATION;
    a.progress = a.target === 1 ? globalFrac : 1 - globalFrac;
    if (elapsed >= TOTAL_DURATION) {
      a.progress = a.target;
      a.t = 0;
    }

    // Pick a center to ripple out from. Use the active plot's bounding-
    // box center when known; otherwise centre of the lot.
    const ap = this.activePlotData();
    let cx = 0, cr = 0;
    if (ap && ap.tiles.length > 0) {
      let minC = ap.tiles[0].c, maxC = minC;
      let minR = ap.tiles[0].r, maxR = minR;
      for (const t of ap.tiles) {
        if (t.c < minC) minC = t.c; if (t.c > maxC) maxC = t.c;
        if (t.r < minR) minR = t.r; if (t.r > maxR) maxR = t.r;
      }
      cx = (minC + maxC) / 2;
      cr = (minR + maxR) / 2;
    }

    for (const group of this.tileGroups.values()) {
      const isActive = group.userData.isActivePlotTile === true;
      if (isActive) {
        if (group.position.y !== 0) group.position.y = 0;
        if (group.scale.x !== 1) group.scale.setScalar(1);
        continue;
      }
      const tc = group.userData.col as number | undefined;
      const tr = group.userData.row as number | undefined;
      let dist = 0;
      if (typeof tc === 'number' && typeof tr === 'number') {
        dist = Math.abs(tc - cx) + Math.abs(tr - cr);
      }
      const delay = dist * STAGGER_PER_RING;
      // Per-tile progress = clamp((elapsed - delay) / TILE_FALL_TIME, 0, 1)
      // Driven by the global timeline so reverse just plays in negative time.
      let p: number;
      if (a.target === 1) {
        p = Math.max(0, Math.min(1, (elapsed - delay) / TILE_FALL_TIME));
      } else {
        // On exit, latest-dropped tile rises first.
        const reverseElapsed = elapsed;
        const reverseStart = TOTAL_DURATION - TILE_FALL_TIME - delay;
        p = 1 - Math.max(0, Math.min(1, (reverseElapsed - reverseStart) / TILE_FALL_TIME));
        p = Math.max(0, Math.min(1, p));
      }
      // Ease-out cubic so the drop is fast at the start and gentle at
      // the end — feels intentional, not chaotic.
      const eased = a.target === 1
        ? 1 - Math.pow(1 - p, 3)
        : 1 - Math.pow(1 - p, 3);
      group.position.y = PLOT_DROP_Y * eased;
      // No shrink — tiles stay full size, just sink. Less visual noise.
      if (group.scale.x !== 1) group.scale.setScalar(1);
      if (!group.visible) group.visible = true;
    }
  }

  /** Hide everything except the active plot's tiles when plot mode is on.
   *  Other adopted plots stay hidden too — focused work view. */
  private syncWorldVisibility() {
    const ap = this.activePlotData();
    const hideOutside = !!this.sim.state.plotInView;
    const inPlot = new Set(ap && hideOutside ? ap.tiles.map(t => `${t.c}:${t.r}`) : []);
    // Tile groups stay VISIBLE in plot mode — the drop animation in
    // animatePlotTileDrop() translates non-plot tiles down so the player
    // can see them sink away. Hiding them would skip the cool effect.
    for (const [key, group] of this.tileGroups) {
      group.visible = true;
      // Snapshot the membership for the drop anim — checked each frame.
      group.userData.isInPlotMode = hideOutside;
      group.userData.isActivePlotTile = inPlot.has(key);
    }
    // World props are hidden too — the inline PlotToolbar HUD takes over
    // tool selection while in plot mode.
    for (const s of this.toolStands) s.root.visible = !hideOutside;
    if (this.toolRack) this.toolRack.visible = !hideOutside;
    this.storageDisplay.root.visible = !hideOutside;
    // Lot base slab + truck stay out of the picture too.
    const baseMesh = this.scene.getObjectByName('lot-base');
    if (baseMesh) baseMesh.visible = !hideOutside;
    this.truckMesh.visible = !hideOutside;
    // Clouds float over the overview — hide them in plot mode so they
    // don't drift into the close-up frame.
    this.cloudGroup.visible = !hideOutside;
    // Hanging vines drape off the floating-island perimeter — they sit
    // outside the lot tile grid so syncWorldVisibility wouldn't catch
    // them via tileGroups, but they'd float free in the plot close-up.
    if (this.vinesGroup) this.vinesGroup.visible = !hideOutside;
    // Tile hover indicator: the player doesn't need a per-tile selector
    // in plot mode (sub-cell precision is what matters).
    this.cursorMarker.visible = !hideOutside;
    // Per-tile plot edge markers (the grass-tipped corner poles). In plot
    // mode we only want the active plot's perimeter to read; other plots'
    // poles would float in the empty background.
    for (const [key, mesh] of this.plotEdgeMeshes) {
      mesh.visible = !hideOutside || inPlot.has(key);
    }
    // Plot fences are addressable by plotIdx — keep only the active plot's
    // fence on screen in plot mode. Other plots' fences would draw as
    // free-floating rectangles on blank ground (see screenshot bug).
    const activeIdx = this.sim.state.activePlotIdx ?? -1;
    for (const [pi, group] of this.plotFenceGroups) {
      group.visible = !hideOutside || pi === activeIdx;
    }
    // Per-tile sub-cell layers — show only for tiles in the active plot
    // when in plot mode, otherwise show all. Replaces the expensive
    // tear-down + rebuild that used to run on plot enter/exit (the
    // 5-10s freeze the player saw on fully-grown plots).
    const showTile = (key: string) => !hideOutside || inPlot.has(key);
    for (const [k, m] of this.plotSurfaceMeshes) m.visible = showTile(k);
    for (const [k, m] of this.soilColumnMeshes) m.visible = showTile(k);
    for (const [k, m] of this.tileSeedMeshes)   m.visible = showTile(k);
    // tilledInstanced and wetInstanced are keyed `${c}:${r}:${stage}` —
    // strip the stage suffix to match a tile.
    for (const [k, m] of this.tilledInstanced) {
      const dot = k.lastIndexOf(':');
      const tileKey = dot >= 0 ? k.substring(0, dot) : k;
      m.visible = showTile(tileKey);
    }
    for (const [k, m] of this.wetInstanced) {
      const dot = k.lastIndexOf(':');
      const tileKey = dot >= 0 ? k.substring(0, dot) : k;
      m.visible = showTile(tileKey);
    }
    // World-wide weed InstancedMesh — hidden entirely in plot mode.
    if (this.weedsInstanced) this.weedsInstanced.visible = !hideOutside;
  }

  /** Did the cursor hit the storage shelf? */
  private pickStorageDisplay(): boolean {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObject(this.storageDisplay.root, true);
    return hits.length > 0;
  }

  // ── Weed minigame (Phase 2) ───────────────────────────────────────
  private weedKey(c: number, r: number, subC: number, subR: number): string {
    return `${c}:${r}:${subC}:${subR}`;
  }
  /** PLOT_SUB_DIM sub-cells per tile → world position of the sub-cell
   *  centre. With PLOT_SUB_DIM = 16, step is 0.0625. */
  private subCellWorld(c: number, r: number, subC: number, subR: number): [number, number, number] {
    const [tx, , tz] = this.tileCenter(c, r);
    const step = 1 / PLOT_SUB_DIM;
    const dx = -0.5 + step / 2 + subC * step;
    const dz = -0.5 + step / 2 + subR * step;
    return [tx + dx, 0, tz + dz];
  }
  /** Spawn voxel weed meshes. Hidden entirely while plot mode is on —
   *  the close-up view should be a clean tile workbench, the surrounding
   *  forest's weed thicket would just clutter the frame. In overview we
   *  render every world-wide weed. */
  private buildPlotWeeds() {
    this.clearPlotWeeds();
    if (this.sim.state.plotInView) return;
    const allWeeds = this.sim.state.weeds ?? [];
    const weeds = allWeeds;
    if (weeds.length === 0) return;
    // One InstancedMesh for the whole field. Geometry is taken from
    // makeWeed() (all weeds use the same fixed-seed voxel sculpt anyway,
    // so a single shared geometry covers them with per-instance
    // translation + rotation + scale providing the variation). The hit-
    // box geometry from the old per-weed Group is dropped — the voxel
    // weed mesh is roughly the same size and serves as the click target.
    const template = makeWeed() as THREE.Mesh;
    const geom = template.geometry;
    const mat = template.material as THREE.Material;
    const instanced = new THREE.InstancedMesh(geom, mat, weeds.length);
    instanced.castShadow = false;
    instanced.receiveShadow = false;
    // Pre-allocated matrix scratch — avoids 3000+ new Matrix4 allocations.
    const m = this.weedTmpMatrix;
    const q = this.weedTmpQuat;
    const t = this.weedTmpVec3;
    const s = this.weedTmpScale;
    const stepDelta = 1 / PLOT_SUB_DIM;
    for (let i = 0; i < weeds.length; i++) {
      const w = weeds[i];
      const rng = rngFn((w.c * 911) ^ (w.r * 313) ^ (w.subC * 23) ^ (w.subR * 41));
      const scale = 1.0 + rng() * 0.30;
      const yRot = rng() * Math.PI * 2;
      const [wx, , wz] = this.subCellWorld(w.c, w.r, w.subC, w.subR);
      t.set(
        wx + (rng() - 0.5) * stepDelta * 0.5,
        0.04,
        wz + (rng() - 0.5) * stepDelta * 0.5,
      );
      q.setFromAxisAngle(THREE_Y_AXIS, yRot);
      s.set(scale, scale, scale);
      m.compose(t, q, s);
      instanced.setMatrixAt(i, m);
      const key = this.weedKey(w.c, w.r, w.subC, w.subR);
      this.weedInstanceCoords.push({ c: w.c, r: w.r, subC: w.subC, subR: w.subR, alive: true });
      this.weedKeyToIndex.set(key, i);
    }
    instanced.instanceMatrix.needsUpdate = true;
    this.weedsInstanced = instanced;
    // Put weeds on layer 1 so the default raycaster (used for tile
    // hover + click picking) ignores them. They're too tall and dense
    // to leave on the default layer — every click would hit a weed
    // first and pull focus from the tile beneath. pickWeed() temporarily
    // enables layer 1 on the raycaster to find them explicitly.
    instanced.layers.set(1);
    this.scene.add(instanced);
  }
  private clearPlotWeeds() {
    if (this.weedsInstanced) {
      this.scene.remove(this.weedsInstanced);
      this.weedsInstanced.dispose();
      this.weedsInstanced = null;
    }
    this.weedInstanceCoords.length = 0;
    this.weedKeyToIndex.clear();
  }
  /** World ground position (x, z) → sub-cell coordinates. Returns null only
   *  for points outside the world grid; tiles outside any adopted plot are
   *  fully usable for sub-cell drag-paint (farming works everywhere).
   *
   *  In PLOT VIEW the picker is still constrained to the active plot's
   *  tiles, because the camera is in focused-work mode and only those
   *  tiles are visible (other tiles are hidden by syncWorldVisibility).
   *  In overview, any reachable tile is valid. */
  private subCellAt(wx: number, wz: number): { c: number; r: number; subC: number; subR: number } | null {
    const c = Math.floor(wx + PLOT_W / 2);
    const r = Math.floor(wz + PLOT_H / 2);
    if (c < 0 || r < 0 || c >= PLOT_W || r >= PLOT_H) return null;
    if (this.sim.state.plotInView) {
      const ap = this.activePlotData();
      if (!ap || !ap.tiles.some(t => t.c === c && t.r === r)) return null;
    }
    // Tile-local coords in [-0.5, 0.5]
    const dx = wx - (c - PLOT_W / 2 + 0.5);
    const dz = wz - (r - PLOT_H / 2 + 0.5);
    const subC = Math.max(0, Math.min(PLOT_SUB_DIM - 1, Math.floor((dx + 0.5) * PLOT_SUB_DIM)));
    const subR = Math.max(0, Math.min(PLOT_SUB_DIM - 1, Math.floor((dz + 0.5) * PLOT_SUB_DIM)));
    return { c, r, subC, subR };
  }

  /** Shared furrow geometry per stage. Built once on first use, reused
   *  by every tile's InstancedMesh. Stage 1 scratch / 2 trench / 3 furrow.
   *  Voxels are sized for a 16×16 sub-grid (≈ 0.0625 per sub-cell). */
  private getTilledGeo(stage: number): THREE.BufferGeometry {
    const idx = Math.max(1, Math.min(3, stage));
    const cached = this.tilledGeoCache[idx];
    if (cached) return cached;
    const b = new VoxelBuilder();
    if (idx === 1) {
      b.add(0.055, 0.02, 0.055, 0, 0.01, 0, 0xa07440);
      b.add(0.015, 0.01, 0.055, 0, 0.025, 0, 0x8b6423);
    } else if (idx === 2) {
      b.add(0.0575, 0.03, 0.0575, 0, 0.015, 0, 0x8b6423);
      b.add(0.015, 0.03, 0.0575, 0, 0.040, 0.011, 0x6a4220);
      b.add(0.015, 0.03, 0.0575, 0, 0.040, -0.011, 0xa07440);
    } else {
      b.add(0.0575, 0.04, 0.0575, 0, 0.02, 0,    0x7a4d28);
      b.add(0.0525, 0.02, 0.015, 0, 0.05, 0,    0xa07440);
      b.add(0.0525, 0.02, 0.015, 0, 0.05, 0.020, 0x6a4220);
    }
    const mesh = b.build();
    const geo = mesh.geometry;
    this.tilledGeoCache[idx] = geo;
    return geo;
  }
  /** Rebuild ONE tile's tilled InstancedMesh layers from current sim state.
   *  Called by flushDirtyTiles(); collapses 256 per-sub-cell events into a
   *  single pass that writes up to 256 instance matrices per stage layer. */
  private rebuildTileTilled(c: number, r: number) {
    // Collect every tilled sub-cell on this tile, bucketed by stage.
    const byStage: { c: number; r: number; subC: number; subR: number; stage: number }[][] = [[], [], [], []];
    const collect = (arr: { c: number; r: number; subC: number; subR: number; stage: number }[]) => {
      for (const s of arr) {
        if (s.c !== c || s.r !== r) continue;
        const st = Math.max(1, Math.min(3, s.stage));
        byStage[st].push(s);
      }
    };
    // Pick the right source: visible plot data first, then free tiles
    // (only in overview — matches the old buildPlotTilled gating).
    let consumed = false;
    for (const p of this.visiblePlots()) {
      if (p.tiles.some(t => t.c === c && t.r === r)) {
        collect(p.tilled);
        consumed = true;
        break;
      }
    }
    if (!consumed && !this.sim.state.plotInView) {
      collect(this.sim.state.freeTilled ?? []);
    }
    for (let stage = 1; stage <= 3; stage++) {
      const list = byStage[stage];
      const key = `${c}:${r}:${stage}`;
      let im = this.tilledInstanced.get(key);
      if (list.length === 0) {
        if (im) {
          this.scene.remove(im);
          im.dispose();
          this.tilledInstanced.delete(key);
        }
        continue;
      }
      if (!im) {
        // Capacity = PLOT_SUB_DIM² so the worst case (every sub-cell at
        // this stage) fits without re-allocation.
        im = new THREE.InstancedMesh(this.getTilledGeo(stage), VX_MAT, PLOT_SUB_DIM * PLOT_SUB_DIM);
        im.castShadow = false;
        im.receiveShadow = true;
        this.scene.add(im);
        this.tilledInstanced.set(key, im);
      }
      const cap = PLOT_SUB_DIM * PLOT_SUB_DIM;
      for (let i = 0; i < list.length && i < cap; i++) {
        const s = list[i];
        const [wx, , wz] = this.subCellWorld(s.c, s.r, s.subC, s.subR);
        this.subTmpMatrix.makeTranslation(wx, 0.01, wz);
        im.setMatrixAt(i, this.subTmpMatrix);
      }
      // Hide the remaining slots — zero scale = invisible, no draw cost
      // beyond the instance matrix upload.
      for (let i = list.length; i < cap; i++) {
        im.setMatrixAt(i, this.subHiddenMatrix);
      }
      im.count = cap;
      im.instanceMatrix.needsUpdate = true;
    }
  }
  private buildPlotTilled() {
    this.clearPlotTilled();
    // Mark every visible tile dirty; flushDirtyTiles() drains in update().
    // We rebuild eagerly here so the visuals appear THIS frame (callers
    // expect synchronous results on plot enter/exit + tile removal).
    const tiles = new Set<string>();
    for (const p of this.visiblePlots()) {
      for (const s of p.tilled) tiles.add(`${s.c}:${s.r}`);
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeTilled ?? [])) tiles.add(`${s.c}:${s.r}`);
    }
    for (const tk of tiles) {
      const [tc, tr] = tk.split(':').map(Number);
      this.rebuildTileTilled(tc, tr);
    }
  }
  private clearPlotTilled() {
    for (const im of this.tilledInstanced.values()) {
      this.scene.remove(im);
      im.dispose();
    }
    this.tilledInstanced.clear();
  }
  /** Drop the per-tile tilled InstancedMesh layers (all 3 stages) for one
   *  tile. Used when the tile-level field mesh takes over. */
  private clearTileTilledInstanced(c: number, r: number) {
    for (let stage = 1; stage <= 3; stage++) {
      const key = `${c}:${r}:${stage}`;
      const im = this.tilledInstanced.get(key);
      if (im) {
        this.scene.remove(im);
        im.dispose();
        this.tilledInstanced.delete(key);
      }
    }
  }

  /** Build ONE merged seed mesh per tile, containing every seed on that
   *  tile (wheat / corn / carrot, any stage). Cuts a 64-seed tile from
   *  64 draw calls down to 1 + drops 63 sway nodes per tile. Per-stalk
   *  tilt is dropped (uniform sway phase per tile); jitter in position
   *  + height keeps the visual variation.
   *  Called from flushDirtyTiles when a 'seed' dirty flag is set, and
   *  from the per-frame signature-change detector. */
  private rebuildTileSeedMesh(c: number, r: number) {
    const key = this.tileKey(c, r);
    const old = this.tileSeedMeshes.get(key);
    if (old) {
      this.scene.remove(old);
      disposeTree(old);
      this.removeSwayNode(old);
      this.tileSeedMeshes.delete(key);
    }
    // Gather seeds on this tile from the right source — active plot in
    // plot view, or free + all-plots in overview.
    const seeds: { c: number; r: number; subC: number; subR: number;
                   type: string; stage: number;
                   ox?: number; oz?: number; growMul?: number }[] = [];
    for (const p of this.visiblePlots()) {
      for (const s of p.seeds) if (s.c === c && s.r === r) seeds.push(s);
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeSeeds ?? [])) {
        if (s.c === c && s.r === r) seeds.push(s);
      }
    }
    if (seeds.length === 0) return;

    const step = 1 / PLOT_SUB_DIM;
    const b = new VoxelBuilder();
    for (const seed of seeds) {
      const rng = rngFn(
        (seed.c * 7349) ^ (seed.r * 3491) ^ (seed.subC * 31) ^ (seed.subR * 17)
        ^ Math.round((seed.ox ?? 0) * 1000) ^ Math.round((seed.oz ?? 0) * 1000),
      );
      const heightMul = 0.85 + rng() * 0.30;
      // Sub-cell center relative to tile center, plus the per-seed ox/oz
      // visual jitter. Voxel offsets baked into the merged geometry.
      const subCenterX = -0.5 + step / 2 + seed.subC * step;
      const subCenterZ = -0.5 + step / 2 + seed.subR * step;
      const ox = (seed.ox ?? 0) * step;
      const oz = (seed.oz ?? 0) * step;
      b.setOffset(subCenterX + ox, 0.01, subCenterZ + oz);
      if (seed.type === 'wheat') {
        buildWheatModel(b, seed.stage, rng, heightMul);
      } else if (seed.stage <= 0) {
        b.add(0.06, 0.03, 0.06, 0, 0.03, 0, 0x8b6423);
        b.add(0.03, 0.02, 0.03, 0, 0.05, 0, 0xfff080);
      } else {
        const stalkW = 0.034 + rng() * 0.014;
        const baseH = 0.08 + seed.stage * 0.08;
        const stalkH = baseH * heightMul;
        const stalkY = stalkH / 2 + 0.02;
        const stalkCol = seed.type === 'corn' ? 0x80a050 : 0x60a030;
        b.add(stalkW, stalkH, stalkW, -0.02, stalkY, -0.02, stalkCol);
        b.add(stalkW, stalkH, stalkW,  0.02, stalkY,  0.02, stalkCol);
        if (seed.stage >= 3) {
          const headCol = seed.type === 'corn' ? 0xfff080 : 0xff8030;
          b.add(0.06, 0.04, 0.06, 0, stalkH + 0.05, 0, headCol);
        }
      }
    }
    b.setOffset(0, 0, 0);
    const mesh = b.build();
    // Swap to the sway material — vertex shader displaces only vertices
    // above the root threshold so the dirt at the tile edges stays put
    // while the stalks lean. One shared material, no extra GC pressure.
    mesh.material = VX_MAT_SWAY;
    const wrapper = new THREE.Group();
    wrapper.add(mesh);
    const [tx, , tz] = this.tileCenter(c, r);
    wrapper.position.set(tx, 0, tz);
    (wrapper as any).userData = { isTileSeedMesh: true };
    this.scene.add(wrapper);
    this.tileSeedMeshes.set(key, wrapper);
  }

  /** Cheap integer signature of the seed configuration on a tile —
   *  changes when seeds are added, removed, or change stage. */
  private computeTileSeedSig(c: number, r: number): number {
    let sig = 0;
    for (const p of this.visiblePlots()) {
      for (const s of p.seeds) {
        if (s.c === c && s.r === r) sig = ((sig * 31) | 0) + (s.subC * 17 + s.subR * 137 + s.stage * 4099);
      }
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeSeeds ?? [])) {
        if (s.c === c && s.r === r) sig = ((sig * 31) | 0) + (s.subC * 17 + s.subR * 137 + s.stage * 4099);
      }
    }
    return sig;
  }

  /** Per-frame check — walk visible tiles, compare seed signatures,
   *  mark dirty tiles whose seed state changed (stage transition,
   *  harvest, new sowing). Replaces the per-seed orphan-cleanup loop
   *  that did 576 individual mesh updates on a 9-tile plot. */
  private syncTileSeedMeshes() {
    const tilesToCheck = new Set<string>();
    for (const p of this.visiblePlots()) {
      for (const s of p.seeds) tilesToCheck.add(`${s.c}:${s.r}`);
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeSeeds ?? [])) tilesToCheck.add(`${s.c}:${s.r}`);
    }
    // Tiles that lost ALL their seeds — sig drops to 0; still need a
    // rebuild to dispose the empty merged mesh.
    for (const k of this.tileSeedMeshes.keys()) tilesToCheck.add(k);

    for (const k of tilesToCheck) {
      const [c, r] = k.split(':').map(Number);
      const sig = this.computeTileSeedSig(c, r);
      if (this.tileSeedSig.get(k) === sig) continue;
      this.tileSeedSig.set(k, sig);
      this.markTileDirty(c, r, 'seed');
    }
  }

  /** Per-sub-cell crop voxel. Visual scales with the crop's stage:
   *   0 = seed buried, just a yellow speck on the furrow
   *   1 = sprout, tiny green stalk
   *   2 = growing, taller stalks
   *   3 = nearly ripe, full stalks + heads
   *   4 = mature, golden ripe heads (or red carrot tops, orange corn) */
  private addSeedMesh(seed: {
    c: number; r: number; subC: number; subR: number;
    type: string; stage: number; ox?: number; oz?: number; growMul?: number;
  }) {
    const old = this.seedMeshes.get(seed);
    if (old) {
      this.scene.remove(old);
      disposeTree(old);
      this.seedMeshes.delete(seed);
      this.removeSwayNode(old);
    }
    const type = seed.type;
    const stage = seed.stage;
    // Deterministic per-seed jitter — height, tilt, palette tweak — so
    // neighbouring stalks read as individuals, not clones. Mix the seed's
    // ox/oz into the rng seed so two seeds on the same sub-cell still
    // differ visually.
    const rng = rngFn(
      (seed.c * 7349) ^ (seed.r * 3491) ^ (seed.subC * 31) ^ (seed.subR * 17)
      ^ Math.round((seed.ox ?? 0) * 1000) ^ Math.round((seed.oz ?? 0) * 1000)
    );
    const heightMul = 0.85 + rng() * 0.30;
    const tilt = (rng() - 0.5) * 0.32;
    const tilt2 = (rng() - 0.5) * 0.20;
    const stalkW = 0.034 + rng() * 0.014;
    const b = new VoxelBuilder();
    if (type === 'wheat') {
      buildWheatModel(b, stage, rng, heightMul);
    } else if (stage <= 0) {
      b.add(0.06, 0.03, 0.06, 0, 0.03, 0, 0x8b6423);
      b.add(0.03, 0.02, 0.03, 0, 0.05, 0, 0xfff080);
    } else {
      const baseH = 0.08 + stage * 0.08;
      const stalkH = baseH * heightMul;
      const stalkY = stalkH / 2 + 0.02;
      const stalkCol = type === 'corn' ? 0x80a050 : 0x60a030;
      b.add(stalkW, stalkH, stalkW, -0.02, stalkY, -0.02, stalkCol);
      b.add(stalkW, stalkH, stalkW,  0.02, stalkY,  0.02, stalkCol);
      if (stage >= 3) {
        const headCol = type === 'corn' ? 0xfff080 : 0xff8030;
        b.add(0.06, 0.04, 0.06, 0, stalkH + 0.05, 0, headCol);
      }
    }
    const inner = b.build();
    // Inner mesh holds the per-seed static tilt.
    inner.rotation.z = tilt;
    inner.rotation.x = tilt2;

    // Parent group does the sway. We rotate the WRAPPER on a separate axis
    // (x) so it composes with the inner's z-axis tilt without clobbering it.
    // Wheat sway: gentle ±3.5° at ~1.8 Hz with per-seed phase offset.
    const wrapper = new THREE.Group();
    wrapper.add(inner);
    const [wx, , wz] = this.subCellWorld(seed.c, seed.r, seed.subC, seed.subR);
    const step = 1 / PLOT_SUB_DIM;
    const ox = (seed.ox ?? 0) * step;
    const oz = (seed.oz ?? 0) * step;
    wrapper.position.set(wx + ox, 0.01, wz + oz);
    if (stage >= 1) {
      // Only sway once the plant has visible foliage (stage 0 = buried seed).
      // Sway amplitude scales with stage so seedlings barely move and mature
      // wheat catches wind more.
      const swayAmp = 0.025 + stage * 0.018;
      const swayFreq = 1.6 + rng() * 0.8;
      this.registerSway(wrapper, swayFreq, swayAmp, 'x');
      (wrapper as any).userData = { ...(wrapper as any).userData, swayPhase: rng() * Math.PI * 2 };
    }
    (wrapper as any).userData = { ...(wrapper as any).userData, cropStage: stage };
    this.scene.add(wrapper);
    this.seedMeshes.set(seed, wrapper);
  }
  /** Remove a sway-node entry whose target matches the given object. */
  private removeSwayNode(target: THREE.Object3D) {
    for (let i = this.swayNodes.length - 1; i >= 0; i--) {
      if (this.swayNodes[i].node === target) this.swayNodes.splice(i, 1);
    }
  }
  private buildPlotSeeds() {
    // Visibility sync only — DON'T clear/rebuild existing per-tile
    // merged meshes. Rebuilding 9 fully-grown tiles in one frame was
    // the 5-10s freeze on plot enter/exit. The per-frame
    // syncTileSeedMeshes() catches any tile whose data drifted and
    // queues a rate-limited rebuild.
    this.syncTileSeedVisibility();
  }
  /** Hide tile-seed meshes whose tile isn't in the currently-visible
   *  source set; show the rest. Called on plot enter/exit instead of
   *  the wholesale clearPlotSeeds + rebuild that froze the frame. */
  private syncTileSeedVisibility() {
    const visibleTiles = new Set<string>();
    for (const p of this.visiblePlots()) {
      for (const s of p.seeds) visibleTiles.add(`${s.c}:${s.r}`);
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeSeeds ?? [])) visibleTiles.add(`${s.c}:${s.r}`);
    }
    for (const [k, mesh] of this.tileSeedMeshes) {
      const v = visibleTiles.has(k);
      if (mesh.visible !== v) mesh.visible = v;
    }
    // Tiles that just became visible but don't yet have a mesh → mark
    // dirty so the rate-limited flush builds them on subsequent frames.
    for (const k of visibleTiles) {
      if (!this.tileSeedMeshes.has(k)) {
        const [c, r] = k.split(':').map(Number);
        this.tileSeedSig.delete(k);
        this.markTileDirty(c, r, 'seed');
      }
    }
  }
  private clearPlotSeeds() {
    // Legacy per-seed meshes (unused after the per-tile merge but kept
    // for old-state cleanup paths).
    for (const m of this.seedMeshes.values()) {
      this.scene.remove(m);
      disposeTree(m);
      this.removeSwayNode(m);
    }
    this.seedMeshes.clear();
    // Per-tile merged seed meshes — the active path.
    for (const m of this.tileSeedMeshes.values()) {
      this.scene.remove(m);
      disposeTree(m);
      this.removeSwayNode(m);
    }
    this.tileSeedMeshes.clear();
    this.tileSeedSig.clear();
  }

  /** Shared translucent-plane geometry per wet stage. Plane size encodes
   *  the stage's puddle width — instead of varying per-instance scale, we
   *  bake size into the geometry so the 3 stage layers stay visually
   *  distinct. */
  private getWetGeo(stage: number): THREE.BufferGeometry {
    const idx = Math.max(1, Math.min(3, stage));
    const cached = this.wetGeoCache[idx];
    if (cached) return cached;
    const sizes = [0, 0.0425, 0.0525, 0.060];
    // PlaneGeometry is XY by default — pre-rotate it to lie flat on XZ so
    // we don't need a rotation matrix in every instance.
    const geo = new THREE.PlaneGeometry(sizes[idx], sizes[idx]);
    geo.rotateX(-Math.PI / 2);
    this.wetGeoCache[idx] = geo;
    return geo;
  }
  private getWetMat(stage: number): THREE.MeshBasicMaterial {
    const idx = Math.max(1, Math.min(3, stage));
    const cached = this.wetMatCache[idx];
    if (cached) return cached;
    const colors = [0, 0x6b8aa8, 0x4a78b0, 0x2e5fa8];
    const opacities = [0, 0.25, 0.40, 0.65];
    const mat = new THREE.MeshBasicMaterial({
      color: colors[idx], transparent: true, opacity: opacities[idx], depthWrite: false,
    });
    this.wetMatCache[idx] = mat;
    return mat;
  }
  /** Rebuild ONE tile's wet InstancedMesh layers from current sim state. */
  private rebuildTileWet(c: number, r: number) {
    const byStage: { c: number; r: number; subC: number; subR: number; stage: number }[][] = [[], [], [], []];
    const collect = (arr: { c: number; r: number; subC: number; subR: number; stage: number }[]) => {
      for (const s of arr) {
        if (s.c !== c || s.r !== r) continue;
        const st = Math.max(1, Math.min(3, s.stage));
        byStage[st].push(s);
      }
    };
    let consumed = false;
    for (const p of this.visiblePlots()) {
      if (p.tiles.some(t => t.c === c && t.r === r)) {
        collect(p.wet);
        consumed = true;
        break;
      }
    }
    if (!consumed && !this.sim.state.plotInView) {
      collect(this.sim.state.freeWet ?? []);
    }
    for (let stage = 1; stage <= 3; stage++) {
      const list = byStage[stage];
      const key = `${c}:${r}:${stage}`;
      let im = this.wetInstanced.get(key);
      if (list.length === 0) {
        if (im) {
          this.scene.remove(im);
          im.dispose();
          this.wetInstanced.delete(key);
        }
        continue;
      }
      if (!im) {
        im = new THREE.InstancedMesh(this.getWetGeo(stage), this.getWetMat(stage), PLOT_SUB_DIM * PLOT_SUB_DIM);
        im.castShadow = false;
        im.receiveShadow = false;
        this.scene.add(im);
        this.wetInstanced.set(key, im);
      }
      const cap = PLOT_SUB_DIM * PLOT_SUB_DIM;
      for (let i = 0; i < list.length && i < cap; i++) {
        const s = list[i];
        const [wx, , wz] = this.subCellWorld(s.c, s.r, s.subC, s.subR);
        this.subTmpMatrix.makeTranslation(wx, 0.06, wz);
        im.setMatrixAt(i, this.subTmpMatrix);
      }
      for (let i = list.length; i < cap; i++) {
        im.setMatrixAt(i, this.subHiddenMatrix);
      }
      im.count = cap;
      im.instanceMatrix.needsUpdate = true;
    }
  }
  private buildPlotWet() {
    this.clearPlotWet();
    const tiles = new Set<string>();
    for (const p of this.visiblePlots()) {
      for (const s of p.wet) tiles.add(`${s.c}:${s.r}`);
    }
    if (!this.sim.state.plotInView) {
      for (const s of (this.sim.state.freeWet ?? [])) tiles.add(`${s.c}:${s.r}`);
    }
    for (const tk of tiles) {
      const [tc, tr] = tk.split(':').map(Number);
      this.rebuildTileWet(tc, tr);
    }
  }
  private clearPlotWet() {
    for (const im of this.wetInstanced.values()) {
      this.scene.remove(im);
      im.dispose();
    }
    this.wetInstanced.clear();
  }
  /** Drop the per-tile wet InstancedMesh layers (all 3 stages) for one tile. */
  private clearTileWetInstanced(c: number, r: number) {
    for (let stage = 1; stage <= 3; stage++) {
      const key = `${c}:${r}:${stage}`;
      const im = this.wetInstanced.get(key);
      if (im) {
        this.scene.remove(im);
        im.dispose();
        this.wetInstanced.delete(key);
      }
    }
  }

  /** Mark a tile's sub-cell layer dirty for the given mode. Drained in
   *  update() via flushDirtyTiles(). Lets us collapse 256 per-sub-cell
   *  events from a single plot-auto-fill into one rebuild + one particle
   *  burst per affected tile. */
  private markTileDirty(c: number, r: number, mode: 'till' | 'water' | 'seed') {
    const key = `${c}:${r}`;
    let entry = this.dirtyTiles.get(key);
    if (!entry) {
      entry = { till: false, water: false, seed: false };
      this.dirtyTiles.set(key, entry);
    }
    entry[mode] = true;
  }
  /** Drain dirtyTiles. For each affected tile we rebuild that tile's
   *  InstancedMesh layers (till/wet) or refresh seed meshes (seed mode),
   *  and emit ONE celebratory particle burst per kind — regardless of
   *  how many per-sub-cell events accumulated this frame. */
  /** Per-frame budget for tile rebuilds. Each per-tile seed mesh merge
   *  is ~3k voxel BoxGeometries → ~50 ms wall time at peak; processing
   *  all 9 of a fully-grown plot in one frame is the harvest-burst /
   *  plot-enter freeze. Capping per frame turns the burst into a few
   *  cheap frames instead of one freeze. */
  private static MAX_TILE_FLUSH_PER_FRAME = 2;
  private flushDirtyTiles() {
    if (this.dirtyTiles.size === 0) return;
    let processed = 0;
    for (const [key, flags] of this.dirtyTiles) {
      if (processed >= Diorama.MAX_TILE_FLUSH_PER_FRAME) break;
      processed++;
      const [c, r] = key.split(':').map(Number);
      if (flags.till) {
        this.rebuildTileTilled(c, r);
        // Regen grass tufts — makeGrassTufts (called via rebuildTile)
        // skips tilled sub-cells, so re-running it AFTER tilling clears
        // the tuft over any newly-furrowed cell. Plot tiles use the
        // plot surface mesh instead; for those, rebuildPlotSurface is
        // the right call. Done here (batched per-frame) instead of in
        // the event handler so auto-fill bursts only trigger ONE
        // rebuild per tile per frame.
        const inAnyPlot = (this.sim.state.plots ?? []).some(p =>
          p.tiles.some(t => t.c === c && t.r === r));
        if (inAnyPlot) this.rebuildPlotSurface(c, r);
        else           this.rebuildTile(c, r);
        // One tile-scale burst rather than the per-sub-cell particles
        // the original code spawned for every subCellTilled event.
        this.spawnParticles(c, r, [0x9c6c44, 0x7a4d28, 0xc89058], 4,
          { life: 0.35, spread: 0.30, upBias: 0.4, scale: 0.05 });
      }
      if (flags.water) {
        this.rebuildTileWet(c, r);
        this.spawnParticles(c, r, [0x5ab0e8, 0x80c8f0, 0xa8dcf8], 4,
          { life: 0.45, spread: 0.30, upBias: 0.3, scale: 0.04 });
      }
      if (flags.seed) {
        // One merged mesh per tile for all of its seeds — wheat/corn/carrot
        // all baked into a single BufferGeometry. Cuts draw calls from
        // ~64/tile (per-seed) down to 1/tile.
        this.rebuildTileSeedMesh(c, r);
        // No particle burst — the seed-dirty signal now fires on every
        // stage transition (auto-growth), not just on manual placement.
        // Spawning particles here would emit per-growth bursts across
        // every tile and read as visual noise.
      }
      this.dirtyTiles.delete(key);
    }
  }

  /** Faint sub-cell grid overlay on plot tiles. Coarse 8×8 line guide
   *  drawn over the 16×16 grid; opacity kept low so it doesn't fight with
   *  the surface tufts and crops. */
  private buildPlotGrid() {
    this.clearPlotGrid();
    const ap = this.activePlotData();
    if (!ap) return;
    const pts: number[] = [];
    const step = 1 / PLOT_SUB_DIM;
    for (const t of ap.tiles) {
      const [tx, , tz] = this.tileCenter(t.c, t.r);
      // (PLOT_SUB_DIM + 1) vertical + horizontal lines per tile, but skip
      // interior ones at odd indices so the overlay reads as a slightly
      // coarser visual guide — readable through the surface tufts and crops.
      for (let i = 0; i <= PLOT_SUB_DIM; i++) {
        if (i % 2 !== 0 && i !== 0 && i !== PLOT_SUB_DIM) continue;
        const x = tx - 0.5 + i * step;
        pts.push(x, 0.02, tz - 0.5, x, 0.02, tz + 0.5);
      }
      for (let i = 0; i <= PLOT_SUB_DIM; i++) {
        if (i % 2 !== 0 && i !== 0 && i !== PLOT_SUB_DIM) continue;
        const z = tz - 0.5 + i * step;
        pts.push(tx - 0.5, 0.02, z, tx + 0.5, 0.02, z);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color: 0xfff080, transparent: true, opacity: 0.20, depthWrite: false });
    const mesh = new THREE.LineSegments(geo, mat);
    this.scene.add(mesh);
    this.plotGridMesh = mesh;
  }
  private clearPlotGrid() {
    if (this.plotGridMesh) {
      this.scene.remove(this.plotGridMesh);
      this.plotGridMesh.geometry.dispose();
      (this.plotGridMesh.material as THREE.Material).dispose();
      this.plotGridMesh = null;
    }
  }

  /** 3D soil layer columns under each plot tile. 6 layers × 10×10 sub-
   *  voxels each = 600 little voxels per tile, plus embedded stones and
   *  organic matter. Lots of natural texture, and the per-cell variation
   *  is the hook future soil-quality mechanics will drive. */
  private buildPlotSoilColumns() {
    this.clearPlotSoilColumns();
    const tiles = this.allPlotTiles();
    if (tiles.length === 0) return;
    const plot = { tiles };
    // Six soil layers, top to bottom. Each is 0.10 thick.
    const TOPSOIL_FRESH = [0x8c6a32, 0xa07a3a, 0x7a5828, 0x9c7338, 0x6e4818, 0xb08850];
    const TOPSOIL_DEEP  = [0x7a5828, 0x6a4818, 0x886030, 0x5a3818, 0x6e4825];
    const SUBSOIL_LIGHT = [0x8a5838, 0x7a4828, 0x9c6848, 0x6a3818, 0x885030];
    const SUBSOIL_DARK  = [0x5a3818, 0x4a2810, 0x6a4828, 0x3a1808, 0x5a3020];
    const CLAY          = [0xa88058, 0xb89068, 0x986840, 0xc8a878, 0xb08858, 0x9c7848];
    const BEDROCK       = [0x707070, 0x606060, 0x808080, 0x505050, 0x787878, 0x686868];
    const STONE         = [0x808890, 0x707880, 0x9098a0];
    const ROOT          = 0x402810;
    const ORG_MATTER    = 0x3a3018;
    // 8 layers × 16×16 = 2048 sub-voxels per tile — soil column reads as
    // dense crumbly earth instead of flat slabs.
    const layers: { y: number; palette: number[]; stones: number; roots: number; organic: number }[] = [
      { y: -0.3375, palette: TOPSOIL_FRESH, stones: 10, roots: 10, organic: 14 },
      { y: -0.4125, palette: TOPSOIL_FRESH, stones:  8, roots:  6, organic:  8 },
      { y: -0.4875, palette: TOPSOIL_DEEP,  stones:  8, roots:  4, organic:  4 },
      { y: -0.5625, palette: SUBSOIL_LIGHT, stones:  9, roots:  2, organic:  0 },
      { y: -0.6375, palette: SUBSOIL_LIGHT, stones:  8, roots:  1, organic:  0 },
      { y: -0.7125, palette: SUBSOIL_DARK,  stones:  6, roots:  0, organic:  0 },
      { y: -0.7875, palette: CLAY,          stones:  4, roots:  0, organic:  0 },
      { y: -0.8625, palette: BEDROCK,       stones:  0, roots:  0, organic:  0 },
    ];
    const N = 16;                              // 16×16 sub-voxels per layer — matches PLOT_SUB_DIM
    const step = 1.0 / N;                       // ≈ 0.0625
    const VW = step * 0.95;                     // sub-voxel footprint with hairline gap
    const VH = 0.075;                           // matches layer thickness above
    for (const t of plot.tiles) {
      const b = new VoxelBuilder();
      const rng = rngFn(t.c * 137 + t.r * 991 + 7);
      for (const layer of layers) {
        // Dense sub-voxel grid — each one a discrete colored crumb.
        for (let sr = 0; sr < N; sr++) {
          for (let sc = 0; sc < N; sc++) {
            const cx = -0.5 + step / 2 + sc * step;
            const cz = -0.5 + step / 2 + sr * step;
            const col = layer.palette[Math.floor(rng() * layer.palette.length)];
            const yJ = (rng() - 0.5) * 0.010;
            b.add(VW, VH, VW, cx, layer.y + yJ, cz, col);
          }
        }
        // Embedded stones in upper layers — visible pebbles
        for (let i = 0; i < layer.stones; i++) {
          const ox = (rng() - 0.5) * 0.85;
          const oz = (rng() - 0.5) * 0.85;
          const sz = 0.045 + rng() * 0.05;
          const sCol = STONE[Math.floor(rng() * STONE.length)];
          b.add(sz, sz * 0.7, sz, ox, layer.y + 0.025, oz, sCol);
        }
        // Roots — short vertical dark voxels
        for (let i = 0; i < layer.roots; i++) {
          const ox = (rng() - 0.5) * 0.85;
          const oz = (rng() - 0.5) * 0.85;
          b.add(0.025, VH, 0.025, ox, layer.y, oz, ROOT);
        }
        // Organic matter — tiny dark flecks scattered as humus
        for (let i = 0; i < layer.organic; i++) {
          const ox = (rng() - 0.5) * 0.90;
          const oz = (rng() - 0.5) * 0.90;
          b.add(0.022, 0.022, 0.022, ox, layer.y + 0.035, oz, ORG_MATTER);
        }
      }
      const mesh = b.build();
      const [wx, , wz] = this.tileCenter(t.c, t.r);
      mesh.position.set(wx, 0, wz);
      this.scene.add(mesh);
      this.soilColumnMeshes.set(`${t.c}:${t.r}`, mesh);
    }
  }
  private clearPlotSoilColumns() {
    for (const m of this.soilColumnMeshes.values()) {
      this.scene.remove(m);
      disposeTree(m);
    }
    this.soilColumnMeshes.clear();
  }

  /** Dense surface tufts on each plot tile — PLOT_SUB_DIM² small grass
   *  voxels, each with palette + height variation. Sub-cells that have
   *  been hoed (stage ≥ 1) hide their tufts so the furrow shows through. */
  private buildPlotSurfaces() {
    this.clearPlotSurfaces();
    const tiles = this.allPlotTiles();
    if (tiles.length === 0) return;
    // Aggregate tilled sub-cells across ALL plots + free tiles so each
    // tile knows which sub-cells to skip (those are tilled — soil shows
    // instead of tuft).
    const allTilled: { c: number; r: number; subC: number; subR: number; stage: number }[] = [];
    for (const p of this.sim.state.plots ?? []) for (const s of p.tilled) allTilled.push(s);
    for (const s of (this.sim.state.freeTilled ?? [])) allTilled.push(s);
    const plot = { tiles, tilled: allTilled };
    const GRASS = [0x6fb045, 0x7ad440, 0x9bb854, 0x5a9038, 0x80a050, 0x66a83a];
    const N = PLOT_SUB_DIM;
    const step = 1.0 / N;
    for (const t of plot.tiles) {
      const b = new VoxelBuilder();
      const rng = rngFn(t.c * 919 + t.r * 281 + 13);
      // 1:1 mapping — each surface tuft IS a sub-cell. Tilled (stage ≥ 1)
      // sub-cells skip the tuft so the furrow shows through cleanly.
      for (let sr = 0; sr < N; sr++) {
        for (let sc = 0; sc < N; sc++) {
          const tilled = plot.tilled.some(s =>
            s.c === t.c && s.r === t.r && s.subC === sc && s.subR === sr && s.stage >= 1);
          if (tilled) continue;
          const x = -0.5 + step / 2 + sc * step;
          const z = -0.5 + step / 2 + sr * step;
          const col = GRASS[Math.floor(rng() * GRASS.length)];
          const h = 0.06 + rng() * 0.06;
          b.add(step * 0.85, h, step * 0.85, x, h / 2 + 0.02, z, col);
        }
      }
      // Occasional surface accents — a small wildflower
      const flowers = Math.floor(rng() * 3);
      for (let i = 0; i < flowers; i++) {
        const fx = (rng() - 0.5) * 0.85;
        const fz = (rng() - 0.5) * 0.85;
        const fc = rng() < 0.4 ? 0xfff080 : (rng() < 0.6 ? 0xffffff : 0xff80a8);
        b.add(0.03, 0.10, 0.03, fx, 0.07, fz, 0x6a9a44);
        b.add(0.06, 0.04, 0.06, fx, 0.14, fz, fc);
      }
      const mesh = b.build();
      const [wx, , wz] = this.tileCenter(t.c, t.r);
      mesh.position.set(wx, 0, wz);
      this.scene.add(mesh);
      this.plotSurfaceMeshes.set(`${t.c}:${t.r}`, mesh);
    }
  }
  private clearPlotSurfaces() {
    for (const m of this.plotSurfaceMeshes.values()) {
      this.scene.remove(m);
      disposeTree(m);
    }
    this.plotSurfaceMeshes.clear();
  }

  /** Break up the flat green/brown side walls with overhang tufts and
   *  protruding roots, stones, and clumps. Built once on plotEntered;
   *  static for the session. Only perimeter tiles (edges that face a
   *  non-plot neighbor) get detail — interior tiles share a hidden seam. */
  private buildPlotEdges() {
    this.clearPlotEdges();
    const tiles = this.allPlotTiles();
    if (tiles.length === 0) return;
    const plot = { tiles };
    const tileSet = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    const GRASS_TOP    = [0x6fb045, 0x7ad440, 0x5a9038, 0x80a050, 0x66a83a];
    const GRASS_SHADOW = [0x4a7028, 0x3a5820, 0x547032];
    const TOPSOIL      = [0x8c6a32, 0xa07a3a, 0x7a5828, 0x9c7338];
    const DEEPSOIL     = [0x6a4818, 0x5a3818, 0x4a2810, 0x886030];
    const ROOT_DARK    = [0x402810, 0x352208, 0x4a2c10];
    const STONE        = [0x808890, 0x707880, 0x9098a0];

    type Edge = { axis: 'N' | 'S' | 'W' | 'E'; sx: number; sz: number };
    for (const t of plot.tiles) {
      const exposed: Edge[] = [];
      if (!tileSet.has(`${t.c}:${t.r - 1}`)) exposed.push({ axis: 'N', sx:  0, sz: -1 });
      if (!tileSet.has(`${t.c}:${t.r + 1}`)) exposed.push({ axis: 'S', sx:  0, sz:  1 });
      if (!tileSet.has(`${t.c - 1}:${t.r}`)) exposed.push({ axis: 'W', sx: -1, sz:  0 });
      if (!tileSet.has(`${t.c + 1}:${t.r}`)) exposed.push({ axis: 'E', sx:  1, sz:  0 });
      if (exposed.length === 0) continue;

      const b = new VoxelBuilder();
      const rng = rngFn(t.c * 1187 + t.r * 313 + 71);

      for (const edge of exposed) {
        // Tangent vector lies along the edge; we step samples along it.
        const tangIsX = edge.axis === 'N' || edge.axis === 'S';
        const samples = 22 + Math.floor(rng() * 6);   // 22–27 per edge
        for (let i = 0; i < samples; i++) {
          const t01 = (i + rng() * 0.8) / samples;     // jittered
          const along = -0.5 + t01;                     // -0.5..0.5
          // Position the base voxel hovering just past the slab edge.
          // sx/sz on N/S is 0/±1, on E/W is ±1/0. Multiplied by 0.5 puts
          // us right at the slab face; the rng() * 0.06 adds outward poke.
          const out = 0.50 + rng() * 0.06;
          const px = tangIsX ? along + (rng() - 0.5) * 0.03 : edge.sx * out;
          const pz = tangIsX ? edge.sz * out : along + (rng() - 0.5) * 0.03;

          // Roll a feature type — biased toward grass overhang up top, roots
          // below the grass band, occasional stone or dirt-clod accent.
          const roll = rng();
          if (roll < 0.50) {
            // Overhang grass tuft — sits just at the top edge, dangles down
            // a bit so the green band is no longer a clean horizontal line.
            const col = (rng() < 0.30 ? GRASS_SHADOW : GRASS_TOP)
                        [Math.floor(rng() * (rng() < 0.30 ? GRASS_SHADOW.length : GRASS_TOP.length))];
            const w = 0.07 + rng() * 0.05;
            const h = 0.09 + rng() * 0.12;
            const y = 0.05 + rng() * 0.10 - h * 0.3;   // some droop below the top
            b.add(w, h, w, px, y, pz, col);
            // ~25% spawn a tiny crown voxel above for irregular silhouette
            if (rng() < 0.25) {
              b.add(w * 0.6, h * 0.5, w * 0.6, px + (rng() - 0.5) * 0.02,
                    y + h * 0.6, pz + (rng() - 0.5) * 0.02, col);
            }
          } else if (roll < 0.82) {
            // Exposed root — vertical brown stick poking out of the dirt
            // wall at varied depth. Spans across one or two soil layers.
            const col = ROOT_DARK[Math.floor(rng() * ROOT_DARK.length)];
            const w = 0.035 + rng() * 0.025;
            const h = 0.10 + rng() * 0.30;
            const y = -0.10 - rng() * 0.55;
            b.add(w, h, w, px, y, pz, col);
            // A horizontal nub branching out — gives the root a knee
            if (rng() < 0.40) {
              const nubLen = 0.05 + rng() * 0.04;
              const nubX = tangIsX ? px + (rng() - 0.5) * 0.06 : px + edge.sx * nubLen * 0.5;
              const nubZ = tangIsX ? pz + edge.sz * nubLen * 0.5 : pz + (rng() - 0.5) * 0.06;
              b.add(tangIsX ? 0.04 : nubLen, 0.03, tangIsX ? nubLen : 0.04,
                    nubX, y + (rng() - 0.5) * 0.04, nubZ, col);
            }
          } else if (roll < 0.94) {
            // Embedded stone breaking the dirt wall — mid-to-deep depth.
            const col = STONE[Math.floor(rng() * STONE.length)];
            const w = 0.06 + rng() * 0.05;
            const h = 0.05 + rng() * 0.04;
            const y = -0.25 - rng() * 0.45;
            b.add(w, h, w * 0.9, px, y, pz, col);
          } else {
            // Loose dirt clod just below the grass band — softens the seam
            // between grass and topsoil.
            const palette = rng() < 0.5 ? TOPSOIL : DEEPSOIL;
            const col = palette[Math.floor(rng() * palette.length)];
            const w = 0.07 + rng() * 0.05;
            const h = 0.07 + rng() * 0.05;
            const y = -0.02 - rng() * 0.18;
            b.add(w, h, w, px, y, pz, col);
          }
        }

        // 2-3 dramatic "tall grass" voxels per edge — head-and-shoulders
        // taller than the tuft band so the silhouette is jagged from a distance.
        const tallCount = 2 + Math.floor(rng() * 2);
        for (let i = 0; i < tallCount; i++) {
          const along = -0.45 + rng() * 0.90;
          const out = 0.50 + rng() * 0.04;
          const px = tangIsX ? along : edge.sx * out;
          const pz = tangIsX ? edge.sz * out : along;
          const col = GRASS_TOP[Math.floor(rng() * GRASS_TOP.length)];
          const h = 0.18 + rng() * 0.10;
          b.add(0.05, h, 0.05, px, 0.08 + h * 0.4, pz, col);
        }
      }

      const mesh = b.build();
      const [wx, , wz] = this.tileCenter(t.c, t.r);
      mesh.position.set(wx, 0, wz);
      this.scene.add(mesh);
      this.plotEdgeMeshes.set(`${t.c}:${t.r}`, mesh);
    }
  }
  private clearPlotEdges() {
    for (const m of this.plotEdgeMeshes.values()) {
      this.scene.remove(m);
      disposeTree(m);
    }
    this.plotEdgeMeshes.clear();
  }

  /** Voxel fence around the OUTER perimeter of every adopted plot. For
   *  each tile we test its 4 cardinal neighbours against the same plot's
   *  tile set — any side whose neighbour is NOT in the same plot gets a
   *  fence segment (so interior tile-to-tile boundaries stay open, even
   *  when the neighbour belongs to a DIFFERENT plot). Built once per plot
   *  into a Group anchored at the world origin; each rail/post is added in
   *  world coords because a single plot can span many tiles.
   *
   *  Visible in BOTH overview and plot mode — the whole point of the
   *  fence is to mark plot boundaries from a distance. */
  private buildPlotFences() {
    this.clearPlotFences();
    const plots = this.sim.state.plots ?? [];
    if (plots.length === 0) return;
    // Warm wood for the rails + a slightly brighter top voxel that reads
    // as "sun-lit" so the fence has a voxel highlight rather than a flat
    // monotone strip.
    const WOOD       = 0x7a4f24;
    const WOOD_LIT   = 0x9a6a36;
    const POST_DARK  = 0x5a3a18;
    // Fence geometry constants. Height stays around 0.15 — taller than
    // the 0.06–0.10 grass tufts but short enough not to occlude seeds /
    // crops growing behind it. Thickness 0.04 keeps the silhouette thin.
    const FENCE_H      = 0.15;
    const RAIL_THICK   = 0.04;
    const RAIL_INSET   = 0.02;   // nudge the rail just inside the tile edge
    const POST_SIZE    = 0.07;   // chunky little corner post
    const POST_H       = 0.18;

    for (let pi = 0; pi < plots.length; pi++) {
      const plot = plots[pi];
      if (!plot.tiles || plot.tiles.length === 0) continue;
      const tileSet = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
      const b = new VoxelBuilder();
      // Track which posts we've already placed so corners shared by two
      // exposed edges don't get a double post (we key by the tile corner
      // in world coords, rounded).
      const postsAdded = new Set<string>();
      const addPost = (wx: number, wz: number) => {
        const key = `${wx.toFixed(3)}:${wz.toFixed(3)}`;
        if (postsAdded.has(key)) return;
        postsAdded.add(key);
        b.add(POST_SIZE, POST_H,       POST_SIZE, wx, POST_H / 2,       wz, POST_DARK);
        b.add(POST_SIZE * 0.9, 0.04,   POST_SIZE * 0.9, wx, POST_H + 0.02, wz, WOOD_LIT);
      };

      for (const t of plot.tiles) {
        const [tx, , tz] = this.tileCenter(t.c, t.r);
        // For each cardinal direction, an edge is "exposed" if the
        // neighbour tile is NOT in THIS plot (covers both empty space
        // and other plots — we want a visible boundary in both cases).
        const exposed = {
          N: !tileSet.has(`${t.c}:${t.r - 1}`),  // -z face
          S: !tileSet.has(`${t.c}:${t.r + 1}`),  // +z face
          W: !tileSet.has(`${t.c - 1}:${t.r}`),  // -x face
          E: !tileSet.has(`${t.c + 1}:${t.r}`),  // +x face
        };

        // Helper: lay a fence run along one tile edge. The run is the
        // full ~0.92 along the tile face, made of a thicker lower rail
        // and a thinner brighter top rail to read as "lit". Two short
        // posts at either end of the run anchor the voxel feel.
        const runLen = 0.92;   // a hair under tile width so corners breathe
        const railY  = FENCE_H * 0.45;
        const litY   = FENCE_H * 0.85;
        // North face: line z = tz - 0.5 + inset, runs along x.
        if (exposed.N) {
          const fz = tz - 0.5 + RAIL_INSET + RAIL_THICK / 2;
          b.add(runLen, FENCE_H * 0.55, RAIL_THICK, tx, railY, fz, WOOD);
          b.add(runLen * 0.95, FENCE_H * 0.18, RAIL_THICK * 0.9, tx, litY, fz, WOOD_LIT);
          addPost(tx - 0.5 + POST_SIZE / 2 + RAIL_INSET, fz);
          addPost(tx + 0.5 - POST_SIZE / 2 - RAIL_INSET, fz);
        }
        if (exposed.S) {
          const fz = tz + 0.5 - RAIL_INSET - RAIL_THICK / 2;
          b.add(runLen, FENCE_H * 0.55, RAIL_THICK, tx, railY, fz, WOOD);
          b.add(runLen * 0.95, FENCE_H * 0.18, RAIL_THICK * 0.9, tx, litY, fz, WOOD_LIT);
          addPost(tx - 0.5 + POST_SIZE / 2 + RAIL_INSET, fz);
          addPost(tx + 0.5 - POST_SIZE / 2 - RAIL_INSET, fz);
        }
        if (exposed.W) {
          const fx = tx - 0.5 + RAIL_INSET + RAIL_THICK / 2;
          b.add(RAIL_THICK, FENCE_H * 0.55, runLen, fx, railY, tz, WOOD);
          b.add(RAIL_THICK * 0.9, FENCE_H * 0.18, runLen * 0.95, fx, litY, tz, WOOD_LIT);
          addPost(fx, tz - 0.5 + POST_SIZE / 2 + RAIL_INSET);
          addPost(fx, tz + 0.5 - POST_SIZE / 2 - RAIL_INSET);
        }
        if (exposed.E) {
          const fx = tx + 0.5 - RAIL_INSET - RAIL_THICK / 2;
          b.add(RAIL_THICK, FENCE_H * 0.55, runLen, fx, railY, tz, WOOD);
          b.add(RAIL_THICK * 0.9, FENCE_H * 0.18, runLen * 0.95, fx, litY, tz, WOOD_LIT);
          addPost(fx, tz - 0.5 + POST_SIZE / 2 + RAIL_INSET);
          addPost(fx, tz + 0.5 - POST_SIZE / 2 - RAIL_INSET);
        }
      }

      // If no exposed edges were found (single-tile plot somehow with all
      // neighbours in-plot — shouldn't happen, but guard anyway), the
      // builder will be empty and produce an empty geometry. That's fine.
      const mesh = b.build();
      const group = new THREE.Group();
      group.add(mesh);
      this.scene.add(group);
      this.plotFenceGroups.set(pi, group);
    }
  }
  private clearPlotFences() {
    for (const g of this.plotFenceGroups.values()) {
      this.scene.remove(g);
      disposeTree(g);
    }
    this.plotFenceGroups.clear();
  }
  /** Tear down + rebuild all plot fences — call whenever plot membership
   *  may have changed (adopted, tile removed, plot deleted). */
  private rebuildPlotFences() {
    this.buildPlotFences();
  }

  /** Rebuild the surface for one tile only — called when a sub-cell on
   *  that tile gets tilled so the now-tilled patch isn't covered with
   *  grass tufts. Cheap (one mesh rebuild). */
  private rebuildPlotSurface(c: number, r: number) {
    const key = `${c}:${r}`;
    const old = this.plotSurfaceMeshes.get(key);
    if (old) { this.scene.remove(old); disposeTree(old); this.plotSurfaceMeshes.delete(key); }
    const allTilled: { c: number; r: number; subC: number; subR: number; stage: number }[] = [];
    for (const p of this.sim.state.plots ?? []) for (const s of p.tilled) allTilled.push(s);
    for (const s of (this.sim.state.freeTilled ?? [])) allTilled.push(s);
    const plot = { tilled: allTilled };
    // Tile must belong to some plot OR have free-tile sub-cell work — both
    // get the per-sub-cell tuft mesh so tilled rows show as bare soil.
    const inAnyPlot = (this.sim.state.plots ?? []).some(p => p.tiles.some(t => t.c === c && t.r === r));
    const hasFreeWork = allTilled.some(s => s.c === c && s.r === r);
    if (!inAnyPlot && !hasFreeWork) return;
    const GRASS = [0x6fb045, 0x7ad440, 0x9bb854, 0x5a9038, 0x80a050, 0x66a83a];
    const N = PLOT_SUB_DIM;
    const step = 1.0 / N;
    const b = new VoxelBuilder();
    const rng = rngFn(c * 919 + r * 281 + 13);
    for (let sr = 0; sr < N; sr++) {
      for (let sc = 0; sc < N; sc++) {
        const tilled = plot.tilled.some(s =>
          s.c === c && s.r === r && s.subC === sc && s.subR === sr && s.stage >= 1);
        if (tilled) continue;
        const x = -0.5 + step / 2 + sc * step;
        const z = -0.5 + step / 2 + sr * step;
        const col = GRASS[Math.floor(rng() * GRASS.length)];
        const h = 0.06 + rng() * 0.06;
        b.add(step * 0.85, h, step * 0.85, x, h / 2 + 0.02, z, col);
      }
    }
    const mesh = b.build();
    const [wx, , wz] = this.tileCenter(c, r);
    mesh.position.set(wx, 0, wz);
    this.scene.add(mesh);
    this.plotSurfaceMeshes.set(key, mesh);
  }

  /** Map (c, r, subC + dsc, subR + dsr) → (newC, newR, newSubC, newSubR),
   *  carrying overflows across tile boundaries. Returns null if the new
   *  tile is outside the plot grid. */
  private subOffset(c: number, r: number, subC: number, subR: number, dsc: number, dsr: number) {
    let ns = subC + dsc, nr = subR + dsr, nc = c, ntr = r;
    if (ns < 0) { nc -= 1; ns += PLOT_SUB_DIM; }
    if (ns >= PLOT_SUB_DIM) { nc += 1; ns -= PLOT_SUB_DIM; }
    if (nr < 0) { ntr -= 1; nr += PLOT_SUB_DIM; }
    if (nr >= PLOT_SUB_DIM) { ntr += 1; nr -= PLOT_SUB_DIM; }
    if (nc < 0 || nc >= PLOT_W || ntr < 0 || ntr >= PLOT_H) return null;
    return { c: nc, r: ntr, subC: ns, subR: nr };
  }

  /** Per-tool sub-cell action dispatcher (called by the plot drag-paint).
   *  The hoe is special: it tills 5 sub-cells wide perpendicular to the
   *  drag direction, mimicking a real rake/hoe stroke. (5-wide on the
   *  16-grid covers the same physical width the old 3-wide did on the 8-grid.) */
  private applyPlotTool(c: number, r: number, subC: number, subR: number) {
    const tool = this.plotToolDrag.tool;
    if (tool === 'field') {
      // Infer drag direction from the previous sub-cell coord.
      const last = this.plotToolDrag.lastCoord;
      let offsets: [number, number][] = [[0, 0]];
      if (last) {
        const dx = (c - last.c) * PLOT_SUB_DIM + (subC - last.subC);
        const dy = (r - last.r) * PLOT_SUB_DIM + (subR - last.subR);
        if (Math.abs(dx) >= Math.abs(dy)) {
          // Drag along X → till 5 wide perpendicular (in Y)
          offsets = [[0, -2], [0, -1], [0, 0], [0, 1], [0, 2]];
        } else {
          offsets = [[-2, 0], [-1, 0], [0, 0], [1, 0], [2, 0]];
        }
      } else {
        // No drag history yet — still 5 wide horizontal as a sensible default
        offsets = [[0, -2], [0, -1], [0, 0], [0, 1], [0, 2]];
      }
      for (const [dsc, dsr] of offsets) {
        const target = this.subOffset(c, r, subC, subR, dsc, dsr);
        if (!target) continue;
        this.sim.tillSubCell(target.c, target.r, target.subC, target.subR);
      }
      this.plotToolDrag.lastCoord = { c, r, subC, subR };
      return;
    }
    // Other tools — single sub-cell action.
    switch (tool) {
      case 'plant':  this.sim.placeSeedSub(c, r, subC, subR); break;
      case 'water':  this.sim.waterSub(c, r, subC, subR); break;
      case 'scythe': this.sim.cutSubCellCrop(c, r, subC, subR); break;
    }
    this.plotToolDrag.lastCoord = { c, r, subC, subR };
  }

  /** Raycast for the topmost weed instance under the cursor. With
   *  InstancedMesh the hit comes back with `instanceId` which maps via
   *  weedInstanceCoords back to the sim coords. Plucked (alive=false)
   *  instances are skipped so they can't be re-clicked before the next
   *  buildPlotWeeds rebuild. */
  private pickWeed(): { c: number; r: number; subC: number; subR: number } | null {
    const im = this.weedsInstanced;
    if (!im) return null;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    // Weeds live on layer 1 (so they don't intercept tile picks). Opt
    // in here so this raycast actually sees them, then revert so other
    // callers using `this.raycaster` aren't affected.
    this.raycaster.layers.enable(1);
    const hits = this.raycaster.intersectObject(im, false);
    this.raycaster.layers.disable(1);
    for (const h of hits) {
      const id = h.instanceId;
      if (id == null) continue;
      const e = this.weedInstanceCoords[id];
      if (!e || !e.alive) continue;
      return { c: e.c, r: e.r, subC: e.subC, subR: e.subR };
    }
    return null;
  }

  // ── Plot selection overlay ────────────────────────────────────────
  /** Rebuild the green-tinted quads showing the in-flight selection AND
   *  the active plot's tiles. Cheap; runs on plot* events. */
  /** Materials that should pulse — refreshed per-frame in animateSelectionOverlay. */
  private selectionPulseMats: THREE.MeshBasicMaterial[] = [];
  private selectionPulseTime = 0;
  private refreshSelectionOverlay() {
    const group = this.selectionOverlayGroup;
    while (group.children.length > 0) group.remove(group.children[0]);
    this.selectionPulseMats.length = 0;
    // In plot mode the overlay only adds visual noise on top of the worked
    // ground — hide everything until the player exits back to overview.
    if (this.sim.state.plotInView) return;
    const sel  = this.sim.state.selecting?.tiles ?? [];
    const plot = this.allPlotTiles();
    const selecting = !!this.sim.state.selecting;

    // The saved-but-not-currently-selected plot only shows its gold ring
    // DURING a new selection — gives the player a visual reminder that
    // adopting overlaps will replace existing work. Otherwise the plot is
    // "deselected" (just the wheats/furrows rendered, no overlay).
    const passes: { tiles: { c: number; r: number }[]; color: number; corner: number; opacity: number; pulse: boolean }[] = [];
    if (selecting) passes.push({ tiles: plot, color: 0xffd060, corner: 0xffe080, opacity: 0.30, pulse: false });
    passes.push({ tiles: sel,  color: 0x7ad440, corner: 0xb0ff60, opacity: 0.42, pulse: true  });

    for (const { tiles, color, corner, opacity, pulse } of passes) {
      const tileSet = new Set(tiles.map(t => `${t.c}:${t.r}`));
      for (const t of tiles) {
        const [wx, , wz] = this.tileCenter(t.c, t.r);

        // 1. Hovering floor quad at y=0.22 — sits just above the tallest
        //    grass tuft so it never gets buried by them.
        const floorMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false });
        const floor = new THREE.Mesh(new THREE.PlaneGeometry(0.94, 0.94), floorMat);
        floor.rotation.x = -Math.PI / 2;
        floor.position.set(wx, 0.22, wz);
        group.add(floor);
        if (pulse) this.selectionPulseMats.push(floorMat);

        // 2. Corner pillars — tall thin voxels at each of the 4 tile
        //    corners. These read from any iso angle, even when the floor
        //    quad is edge-on to the camera.
        const corners: [number, number][] = [[-0.45, -0.45], [+0.45, -0.45], [-0.45, +0.45], [+0.45, +0.45]];
        for (const [dx, dz] of corners) {
          const postMat = new THREE.MeshBasicMaterial({ color: corner, transparent: true, opacity: 0.85, depthWrite: false });
          const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.55, 0.06), postMat);
          post.position.set(wx + dx, 0.30, wz + dz);
          group.add(post);
          if (pulse) this.selectionPulseMats.push(postMat);
        }

        // 3. Edge ribbons — short vertical strips along each tile edge,
        //    only drawn on edges that face a non-selected neighbour so
        //    interior seams stay clean.
        const edgeMat = new THREE.MeshBasicMaterial({ color: corner, transparent: true, opacity: 0.55, depthWrite: false });
        // (axis: 'x' for ±x edges, 'z' for ±z edges; sx/sz = sign)
        const edges: { sx?: number; sz?: number; neighbour: string }[] = [
          { sx: -1, neighbour: `${t.c - 1}:${t.r}` },
          { sx: +1, neighbour: `${t.c + 1}:${t.r}` },
          { sz: -1, neighbour: `${t.c}:${t.r - 1}` },
          { sz: +1, neighbour: `${t.c}:${t.r + 1}` },
        ];
        for (const e of edges) {
          if (tileSet.has(e.neighbour)) continue;
          const isXEdge = e.sx !== undefined;
          const edge = new THREE.Mesh(
            isXEdge ? new THREE.BoxGeometry(0.04, 0.30, 0.94) : new THREE.BoxGeometry(0.94, 0.30, 0.04),
            edgeMat,
          );
          edge.position.set(
            wx + (e.sx ?? 0) * 0.47,
            0.18,
            wz + (e.sz ?? 0) * 0.47,
          );
          group.add(edge);
        }
        if (pulse) this.selectionPulseMats.push(edgeMat);
      }
    }
  }

  /** While the watering can is held on a sub-cell, expand the wet area
   *  outward as a growing diamond — pour-and-spread mechanic so the
   *  player can saturate a patch by holding instead of dragging. */
  private animateWaterSpread(dt: number) {
    if (!this.plotToolDrag.active || this.plotToolDrag.tool !== 'water') return;
    const sub = this.subCellAt(this.cursorGroundPos.x, this.cursorGroundPos.z);
    if (!sub) return;
    this.plotWaterAccum += dt;
    // Every ~0.18s the puddle grows by one sub-cell, capped at radius 6.
    // (Radius-6 on the 16-grid covers the same physical area the old
    //  radius-3 cap did on the 8-grid.)
    if (this.plotWaterAccum < 0.18) return;
    this.plotWaterAccum = 0;
    this.plotWaterRadius = Math.min(6, this.plotWaterRadius + 1);
    const R = this.plotWaterRadius;
    for (let dr = -R; dr <= R; dr++) {
      for (let dc = -R; dc <= R; dc++) {
        if (Math.abs(dc) + Math.abs(dr) > R) continue;     // diamond shape
        const t = this.subOffset(sub.c, sub.r, sub.subC, sub.subR, dc, dr);
        if (t) this.sim.waterSub(t.c, t.r, t.subC, t.subR);
      }
    }
  }

  /** Per-frame pulse animation for active-selection overlay materials. */
  private animateSelectionOverlay(dt: number) {
    if (this.selectionPulseMats.length === 0) return;
    this.selectionPulseTime += dt;
    // Triangle wave between 0.55× and 1.45× of the material's stored opacity.
    const factor = 0.55 + 0.90 * (0.5 + 0.5 * Math.sin(this.selectionPulseTime * 4.5));
    for (const m of this.selectionPulseMats) {
      // We multiply rather than overwrite so each material keeps its own base.
      // Base opacity was applied at create-time; encode the base in userData.
      const ud = (m as any).userData;
      if (ud.baseOpacity === undefined) ud.baseOpacity = m.opacity;
      m.opacity = ud.baseOpacity * factor;
    }
  }

  /** Per-frame: show only the prop matching the active tool, park it at the
   *  cursor's ground projection, and animate per-tool. Animation source is
   *  the current `hold` (for hoe/water) and the recent axe strike timestamp
   *  (for the axe swing). */
  private animateToolProp(_dt: number) {
    const tool = this.sim.state.tool;
    const activeKey =
      (tool === 'field' || tool === 'plant' || tool === 'water' ||
       tool === 'scythe' || tool === 'axe' || tool === 'pickaxe') ? tool : null;
    for (const k in this.toolProps) {
      this.toolProps[k].visible = (k === activeKey);
    }
    // Sync each stand: hide the tool mesh while it's "in the player's hand".
    for (const s of this.toolStands) {
      s.toolMesh.visible = s.toolId !== activeKey;
    }
    if (!activeKey) return;
    const prop = this.toolProps[activeKey];

    const isHolding =
      !!this.hold &&
      this.hold.tool === activeKey &&
      (activeKey === 'field' || activeKey === 'plant' || activeKey === 'scythe');
    const isWatering =
      this.toolDrag.active && activeKey === 'water';
    const sinceStrike = this.elapsed - this.lastAxeStrikeT;
    // Slightly longer swing window so the three-phase animation (wind-up,
    // strike, recoil) has time to read at 60 fps.
    const SWING_DURATION = 0.42;
    const isSwinging = (activeKey === 'axe' || activeKey === 'pickaxe') && sinceStrike < SWING_DURATION;

    // Axe + pickaxe have their pivot at the GRIP (wrapper origin), so
    // we drive position directly — no back-solve. Other tools keep the
    // legacy "origin = head" pivot and track the cursor at near-ground.
    if (activeKey !== 'axe' && activeKey !== 'pickaxe') {
      prop.position.set(this.cursorGroundPos.x, 0.02, this.cursorGroundPos.z);
    }
    // Most tools just face the camera so the head reads. The sickle in
    // plot mode uses the player-controlled yaw instead — middle-drag
    // rotates `sickleYaw` and the blade swings to that heading.
    // Sickle no longer rotates with player input — always faces the
    // camera like every other prop.
    prop.rotation.y = this.currentAzimuth + Math.PI;
    // Reset transient rotations the swing/idle phases set so an idle
    // axe doesn't keep a leftover roll from a prior swing.
    prop.rotation.z = 0;

    if (isHolding) {
      const phase = this.elapsed * 14;
      const bob = Math.abs(Math.sin(phase));
      if (activeKey === 'field') {
        prop.position.y = 0.02 + bob * 0.10;
        prop.rotation.x = -0.10 - Math.sin(phase * 2) * 0.25;
      } else if (activeKey === 'plant') {
        prop.position.y = 0.30 + Math.sin(phase * 1.4) * 0.04;
        prop.rotation.x = Math.sin(phase * 1.2) * 0.12;
        prop.rotation.z = Math.cos(phase * 0.9) * 0.18;
      } else {
        // scythe — wide sweep
        prop.position.y = 0.06 + bob * 0.03;
        prop.rotation.x = -0.05 - bob * 0.15;
        prop.rotation.y += Math.sin(phase) * 0.25;
      }
    } else if (isWatering) {
      const phase = this.elapsed * 14;
      // Tipped forward to pour while the player drags
      prop.position.y = 0.25;
      prop.rotation.x = -0.60 + Math.sin(phase) * 0.06;
    } else if (isSwinging && (activeKey === 'axe' || activeKey === 'pickaxe')) {
      // Chop swing with the GRIP as pivot. The prop's wrapper has its
      // origin at the haft's end-cap (where the player's hand grips).
      // Setting prop.position = (target, gripY, target) anchors the
      // hand at the strike tile and lets rotation.x naturally swing
      // the head around the hand. No back-solve — direct drive.
      //
      // Rotation convention (for the head at local (0, -0.72, 0)):
      //   xRot = -2.2  → head up & forward of grip (rest)
      //   xRot = +2.7  → head up & behind grip (wind-up, over shoulder)
      //   xRot = -0.6  → head forward & DOWN, hits target base
      const t = sinceStrike / SWING_DURATION;
      let xRot: number;
      let gripY: number;
      if (t < 0.40) {
        // Wind-up — grip rises slightly, head swings up & back over the
        // shoulder. Rotation goes from rest (-2.2) backward to +2.7
        // (passing through ±π — the head sweeps over the top).
        const wt = t / 0.40;
        const e = wt * (2 - wt);
        gripY = 0.0 + e * 0.55;                  // raise the hand
        xRot = -2.2 - e * 2.40;                  // -2.2 → -4.60 (≡ +1.68)
      } else if (t < 0.62) {
        // Strike — grip lifts a touch more, rotation accelerates from
        // wind-up through to the chop angle so the head whips DOWN.
        const st = (t - 0.40) / 0.22;
        const e = st * st;
        gripY = 0.55 + e * 0.40;                 // 0.55 → 0.95 (peak grip)
        xRot = -4.60 + e * 4.00;                 // -4.60 → -0.60 (chop)
      } else if (t < 0.74) {
        // Stick — head paused at the impact pose. The "chunk" frame.
        gripY = 0.95;
        xRot = -0.60;
      } else {
        // Recoil — grip lowers, rotation eases back to rest. X/Z
        // simultaneously blend from strike target back to cursor so
        // the prop is at the cursor when isSwinging flips off.
        const rt = (t - 0.74) / 0.26;
        const e = rt * (2 - rt);
        gripY = 0.95 - e * 0.95;                 // back to ground
        xRot = -0.60 - e * 1.60;                 // -0.60 → -2.20
      }
      // Position: XZ stays at the strike target through wind-up + strike
      // + stick; recoil blends back toward the cursor for a smooth handoff.
      let posX = this.strikeTargetX;
      let posZ = this.strikeTargetZ;
      if (t >= 0.74) {
        const rt = (t - 0.74) / 0.26;
        const e = rt * (2 - rt);
        posX = this.strikeTargetX * (1 - e) + this.cursorGroundPos.x * e;
        posZ = this.strikeTargetZ * (1 - e) + this.cursorGroundPos.z * e;
      }
      prop.position.set(posX, gripY, posZ);
      prop.rotation.x = xRot;
    } else {
      const idle = Math.sin(this.elapsed * 2.5);
      switch (activeKey) {
        case 'field':
          prop.position.y = 0.10 + idle * 0.03;
          prop.rotation.x = -0.05 + idle * 0.05;
          break;
        case 'water':
          prop.position.y = 0.22 + idle * 0.03;
          prop.rotation.x = -0.20 + idle * 0.04;     // slight forward tilt at rest
          break;
        case 'plant':
          prop.position.y = 0.30 + idle * 0.03;
          prop.rotation.x = 0;
          prop.rotation.z = idle * 0.06;
          break;
        case 'scythe':
          prop.position.y = 0.06 + idle * 0.02;
          prop.rotation.x = -0.05;
          prop.rotation.z = 0;
          break;
        case 'axe':
        case 'pickaxe': {
          // Rest pose with the GRIP anchored AT the mouse cursor on the
          // ground. The wrapper's origin sits at the grip (player's hand
          // end of the haft), so setting position = cursor puts that end
          // exactly where the player is pointing. Rotation tilts the
          // prop diagonally so the haft extends UP and FORWARD from the
          // grip, head sticking up to read as a held tool.
          prop.position.set(this.cursorGroundPos.x, 0, this.cursorGroundPos.z);
          prop.rotation.x = -2.2 + idle * 0.05;
          break;
        }
      }
    }
  }

  /** Cached tile-group references for the hover raycast — avoids the
   *  per-frame worldGroup.traverse() (which walks every descendant of
   *  ~900 tile groups and was a measurable hot path). Invalidated by
   *  buildTile / rebuildTile / buildPlot — anywhere tiles are added or
   *  removed from worldGroup. */
  private hoverCandidatesCache: THREE.Object3D[] | null = null;
  private invalidateHoverCache() { this.hoverCandidatesCache = null; }
  private updateHover() {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    if (!this.hoverCandidatesCache) {
      this.hoverCandidatesCache = [];
      // tileGroups Map already holds exactly the candidate set — no need
      // to traverse the whole worldGroup subtree to discover them.
      for (const tg of this.tileGroups.values()) this.hoverCandidatesCache.push(tg);
    }
    const hits = this.raycaster.intersectObjects(this.hoverCandidatesCache, true);
    if (hits.length > 0) {
      let n: THREE.Object3D | null = hits[0].object;
      while (n && !n.userData?.isTile) n = n.parent;
      if (n) {
        const ud = n.userData;
        this.hovered = { c: ud.col, r: ud.row };
        const [wx, , wz] = this.tileCenter(ud.col, ud.row);
        this.cursorMarker.position.set(wx, 0.04, wz);
        // Hide the per-tile selector in plot mode (sub-cell precision is
        // what matters there) AND while a building ghost is up.
        this.cursorMarker.visible = !this.sim.state.placing && !this.sim.state.plotInView;
        this.updateZoneEditCursor();
        this.syncGhostFromHover();
        return;
      }
    }
    this.hovered = null;
    this.cursorMarker.visible = false;
    this.updateZoneEditCursor();
    this.syncGhostFromHover();
  }

  /** Rebuild the yellow tile-glow group for the currently-selected
   *  plot (zonesEditPlotIdx). Called whenever plot membership or the
   *  selection itself changes. Clears the group when nothing is selected. */
  private rebuildSelectedZoneOverlay() {
    const overlay = this.selectedZoneOverlay;
    if (!overlay) return;
    // Tear down existing children (dispose materials cloned per tile).
    while (overlay.children.length > 0) {
      const child = overlay.children[0];
      overlay.remove(child);
      child.traverse(obj => {
        const mesh = obj as THREE.Mesh;
        if ((mesh as any).geometry?.dispose) (mesh as any).geometry.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach(m => m.dispose?.());
        else if (mat && (mat as any).dispose) (mat as any).dispose();
      });
    }
    const s = this.sim.state;
    const idx = s.zonesEditPlotIdx;
    if (!s.zonesMode || idx == null) return;
    const plot = s.plots[idx];
    if (!plot || plot.tiles.length === 0) return;

    // Palette — warm gold gradient. The outline darkens slightly so it
    // reads against the fill rather than blending into it.
    const FILL_OUTER = 0xfff0a8;
    const FILL_MID   = 0xffd860;
    const FILL_INNER = 0xffb830;
    const RIM        = 0xb87010;
    const STAR_CORE  = 0xfff7d0;
    const STAR_RAY   = 0xffe070;

    // 1) Per-tile radial glow — three stacked translucent planes give a
    //    soft falloff without needing a custom shader. Each tile then gets
    //    a small voxel "medallion" in the center so the surface reads as
    //    decorative, not as a flat shape.
    const fillOuter = new THREE.MeshBasicMaterial({ color: FILL_OUTER, transparent: true, opacity: 0.18, depthWrite: false, depthTest: false });
    const fillMid   = new THREE.MeshBasicMaterial({ color: FILL_MID,   transparent: true, opacity: 0.22, depthWrite: false, depthTest: false });
    const fillInner = new THREE.MeshBasicMaterial({ color: FILL_INNER, transparent: true, opacity: 0.30, depthWrite: false, depthTest: false });

    const tileSet = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));

    for (const t of plot.tiles) {
      const [wx, , wz] = this.tileCenter(t.c, t.r);
      const group = new THREE.Group();
      group.position.set(wx, 0, wz);
      group.renderOrder = 9998;

      // Soft glow stack
      const planeOuter = new THREE.Mesh(new THREE.PlaneGeometry(0.96, 0.96), fillOuter);
      planeOuter.rotation.x = -Math.PI / 2;
      planeOuter.position.y = 0.18;
      planeOuter.renderOrder = 9998;
      group.add(planeOuter);
      const planeMid = new THREE.Mesh(new THREE.PlaneGeometry(0.68, 0.68), fillMid);
      planeMid.rotation.x = -Math.PI / 2;
      planeMid.position.y = 0.185;
      planeMid.renderOrder = 9998;
      group.add(planeMid);
      const planeInner = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.34), fillInner);
      planeInner.rotation.x = -Math.PI / 2;
      planeInner.position.y = 0.19;
      planeInner.renderOrder = 9998;
      group.add(planeInner);

      // Voxel star medallion — bright core + 4 cardinal rays.
      const mb = new VoxelBuilder();
      mb.add(0.10, 0.05, 0.10, 0, 0.22, 0, STAR_CORE);
      // Rays — short bars in the 4 cardinal directions.
      mb.add(0.06, 0.04, 0.18, 0,  0.215, 0,  STAR_RAY);
      mb.add(0.18, 0.04, 0.06, 0,  0.215, 0,  STAR_RAY);
      // Diagonal accents — small cubes at the 45° offsets.
      mb.add(0.05, 0.04, 0.05,  0.14, 0.22,  0.14, STAR_RAY);
      mb.add(0.05, 0.04, 0.05, -0.14, 0.22,  0.14, STAR_RAY);
      mb.add(0.05, 0.04, 0.05,  0.14, 0.22, -0.14, STAR_RAY);
      mb.add(0.05, 0.04, 0.05, -0.14, 0.22, -0.14, STAR_RAY);
      const star = mb.build();
      star.traverse(o => { (o as THREE.Mesh).renderOrder = 9999; });
      group.add(star);

      overlay.add(group);
    }

    // 2) Outer perimeter rim — thin golden voxel run along every tile
    //    edge that faces outside the plot. Internal tile-to-tile edges
    //    stay open so the plot reads as one continuous shape.
    const rim = new VoxelBuilder();
    const E = 0.50;
    const RIM_Y = 0.20;
    const RIM_T = 0.05;          // thickness of the rim bar
    const RIM_H = 0.06;          // height
    const RIM_RUN = 1.0;         // along the tile edge
    for (const t of plot.tiles) {
      const [tx, , tz] = this.tileCenter(t.c, t.r);
      // N (-z), S (+z), W (-x), E (+x) — exposed if neighbour not in plot.
      if (!tileSet.has(`${t.c}:${t.r - 1}`)) {
        rim.add(RIM_RUN, RIM_H, RIM_T, tx,            RIM_Y, tz - E + RIM_T / 2, RIM);
      }
      if (!tileSet.has(`${t.c}:${t.r + 1}`)) {
        rim.add(RIM_RUN, RIM_H, RIM_T, tx,            RIM_Y, tz + E - RIM_T / 2, RIM);
      }
      if (!tileSet.has(`${t.c - 1}:${t.r}`)) {
        rim.add(RIM_T, RIM_H, RIM_RUN, tx - E + RIM_T / 2, RIM_Y, tz,            RIM);
      }
      if (!tileSet.has(`${t.c + 1}:${t.r}`)) {
        rim.add(RIM_T, RIM_H, RIM_RUN, tx + E - RIM_T / 2, RIM_Y, tz,            RIM);
      }
    }
    const rimMesh = rim.build();
    rimMesh.traverse(o => { (o as THREE.Mesh).renderOrder = 9999; });
    overlay.add(rimMesh);
  }

  /** Position the selected-zone name banner over its plot's bounding-
   *  box center, projected to screen. Called every frame while a plot
   *  is selected so the banner tracks camera pans / dives. */
  private updateSelectedZoneBanner() {
    const el = this.selectedZoneNameEl;
    if (!el) return;
    const s = this.sim.state;
    const idx = s.zonesEditPlotIdx;
    if (!s.zonesMode || idx == null) {
      if (!el.hidden) el.hidden = true;
      return;
    }
    const plot = s.plots[idx];
    if (!plot || plot.tiles.length === 0) {
      if (!el.hidden) el.hidden = true;
      return;
    }
    let minC = plot.tiles[0].c, maxC = minC;
    let minR = plot.tiles[0].r, maxR = minR;
    for (const t of plot.tiles) {
      if (t.c < minC) minC = t.c; if (t.c > maxC) maxC = t.c;
      if (t.r < minR) minR = t.r; if (t.r > maxR) maxR = t.r;
    }
    const cx = (minC + maxC) / 2;
    const cr = (minR + maxR) / 2;
    // tileScreen requires integer tile coords; use the nearest tile that
    // exists in the plot so the projection always succeeds.
    const anchor = plot.tiles.reduce((best, t) => {
      const d = Math.abs(t.c - cx) + Math.abs(t.r - cr);
      const db = Math.abs(best.c - cx) + Math.abs(best.r - cr);
      return d < db ? t : best;
    }, plot.tiles[0]);
    const pos = this.tileScreen(anchor.c, anchor.r);
    if (!pos) {
      if (!el.hidden) el.hidden = true;
      return;
    }
    const name = (plot as any).name ?? `Plot ${idx + 1}`;
    if (el.textContent !== name) el.textContent = name;
    el.hidden = false;
    el.style.left = `${pos.x}px`;
    el.style.top  = `${pos.y - 48}px`;
  }

  /** Drive the zone-edit hover state: when the Zones manager has a plot
   *  selected, tint the cursor marker red over member tiles (click to
   *  remove) or green over adjacent-and-free tiles (click to add). Off
   *  otherwise. Also drives the floating ×/+ glyph element. */
  private updateZoneEditCursor() {
    const s = this.sim.state;
    const editIdx = s.zonesEditPlotIdx;
    const editing = !!s.zonesMode && editIdx != null;
    const el = this.zoneEditCursorEl;
    const marker = this.zoneEditMarker;
    if (!editing || !this.hovered) {
      this.zoneEditKind = 'none';
      if (marker) marker.visible = false;
      if (el) {
        el.hidden = true;
        el.classList.remove('ui-zone-cursor--remove', 'ui-zone-cursor--add');
      }
      return;
    }
    const { c, r } = this.hovered;
    const plot = s.plots[editIdx!];
    if (!plot) return;
    const inThisPlot = plot.tiles.some(t => t.c === c && t.r === r);
    let kind: 'remove' | 'add' | 'none' = 'none';
    if (inThisPlot) {
      kind = 'remove';
    } else {
      const adj = plot.tiles.some(t =>
        (Math.abs(t.c - c) === 1 && t.r === r) ||
        (Math.abs(t.r - r) === 1 && t.c === c));
      const ownedElsewhere = s.plots.some((p, pi) =>
        pi !== editIdx && p.tiles.some(t => t.c === c && t.r === r));
      if (adj && !ownedElsewhere) {
        const tile = PlotSystem.tile(s, c, r);
        if (tile && !tile.bld && tile.surf !== 'road') kind = 'add';
      }
    }
    this.zoneEditKind = kind;
    if (marker) {
      if (kind === 'none') {
        marker.visible = false;
      } else {
        const [wx, , wz] = this.tileCenter(c, r);
        marker.position.set(wx, 0, wz);
        marker.visible = true;
        const hex = kind === 'remove' ? 0xff5030 : 0x4adc4a;
        const hexBright = kind === 'remove' ? 0xff8060 : 0x9fff80;
        this.zoneEditMarkerFill?.color.setHex(hex);
        this.zoneEditMarkerRing?.color.setHex(hexBright);
        this.zoneEditMarkerCorner?.color.setHex(hexBright);
      }
    }
    if (el) {
      if (kind === 'none') {
        el.hidden = true;
      } else {
        const screen = this.tileScreen(c, r);
        if (!screen) {
          el.hidden = true;
        } else {
          el.hidden = false;
          el.textContent = kind === 'remove' ? '×' : '+';
          el.classList.toggle('ui-zone-cursor--remove', kind === 'remove');
          el.classList.toggle('ui-zone-cursor--add',    kind === 'add');
          el.style.left = `${screen.x}px`;
          el.style.top  = `${screen.y - 24}px`;
        }
      }
    }
  }


  // ── Tile→screen (FlyToHud) ────────────────────────────────────────
  /** Live perf snapshot for the HUD monitor — renderer GPU resource
   *  counts + per-frame render stats + scene-graph object count. The
   *  monitor watches these for leaks: if `geometries` keeps climbing
   *  during play, something is rebuilding without disposing. */
  perfInfo(): {
    geometries: number; textures: number; programs: number;
    drawCalls: number; triangles: number; sceneObjects: number;
    tileGroups: number; tilledLayers: number; wetLayers: number;
    seedMeshes: number; weedInstances: number;
  } {
    const info = this.renderer.info;
    let sceneObjects = 0;
    this.scene.traverse(() => { sceneObjects++; });
    return {
      geometries:   info.memory.geometries,
      textures:     info.memory.textures,
      programs:     info.programs?.length ?? 0,
      drawCalls:    info.render.calls,
      triangles:    info.render.triangles,
      sceneObjects,
      tileGroups:   this.tileGroups.size,
      tilledLayers: this.tilledInstanced.size,
      wetLayers:    this.wetInstanced?.size ?? 0,
      seedMeshes:   this.seedMeshes.size,
      weedInstances: this.weedKeyToIndex?.size ?? 0,
    };
  }

  tileScreen(c: number, r: number): { x: number; y: number } | null {
    if (!PlotSystem.in(this.sim.state, c, r)) return null;
    const [x, y, z] = this.tileCenter(c, r);
    const v = new THREE.Vector3(x, y, z).project(this.camera);
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (v.x * 0.5 + 0.5) * rect.width + rect.left,
      y: (-v.y * 0.5 + 0.5) * rect.height + rect.top,
    };
  }

  /** Truck's current world position projected to screen coords — used by
   *  FlyToHud to animate items flying TO the truck on load and FROM the
   *  truck on return (bought seeds). Returns null when no depot has been
   *  built yet (truckBase still at origin). */
  truckScreen(): { x: number; y: number } | null {
    if (!this.truckMesh) return null;
    const v = new THREE.Vector3(0, 0.4, 0)
      .add(this.truckMesh.position)
      .project(this.camera);
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (v.x * 0.5 + 0.5) * rect.width + rect.left,
      y: (-v.y * 0.5 + 0.5) * rect.height + rect.top,
    };
  }

  /** Depot platform's screen position — where queued cubes land when the
   *  truck is away. FlyToHud uses this so loading-while-away animations
   *  travel to the stationary pallet, not after the truck on its way to
   *  the city. */
  platformScreen(): { x: number; y: number } | null {
    // truckPlatformSlot.position is set in updateTruckPlatformCargo to
    // (truckBase.x, 0.10, truckBase.z + 1.4). Project that + a slight
    // y-lift so icons land at cube height, not at ground level.
    const v = new THREE.Vector3(this.truckBase.x, 0.40, this.truckBase.z + 1.4)
      .project(this.camera);
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (v.x * 0.5 + 0.5) * rect.width + rect.left,
      y: (-v.y * 0.5 + 0.5) * rect.height + rect.top,
    };
  }

  // ── Spatial-audio accessors ─────────────────────────────────────
  /** Listener state for the audio system. Returns the camera's current
   *  world focus point (camTarget) and zoom factor. The audio bank uses
   *  this each frame to compute per-emitter distance attenuation, pan,
   *  and lowpass — a chopped tree 20 units off-camera plays muffled
   *  and quiet; the same tree at close-up zoom plays full and present. */
  getListenerState(): { x: number; z: number; zoom: number } {
    return {
      x: this.camTarget.x,
      z: this.camTarget.z,
      zoom: this.camera.zoom,
    };
  }

  /** Player-facing graphics settings store. Per-frame-checked flags
   *  (seasonal weather + fireflies + cameraShake) just read the
   *  current value each tick. Stateful flags (renderScale, shadows,
   *  bloom, tilt-shift, vignette) need a re-apply — done here on
   *  initial connect and again on every settings change. */
  connectSettings(settings: Settings): void {
    this.settings = settings;
    settings.subscribe(() => this.applyGraphicsSettings());
    this.applyGraphicsSettings();
  }

  /** Push settings flags into the renderer + postprocessing. Called
   *  on connect and on every settings change. Cheap enough to run
   *  unconditionally — toggles are read in constant time and only
   *  flip GPU state when the value actually differs.
   *
   *  Per-frame gated flags (cameraShake, seasonalFx, fireflies) live
   *  inside their respective animateXxx methods so re-applying them
   *  here would be redundant. */
  private applyGraphicsSettings(): void {
    if (!this.settings) return;
    const s = this.settings;
    // Render scale — affects every pass downstream of WebGLRenderer.
    // Need to re-run resize() so the composer's targets pick up the
    // new pixel ratio (resize internally calls postprocessing.setSize
    // which re-reads renderer.getPixelRatio()).
    const desiredRs = s.renderScale();
    if (Math.abs(this.renderer.getPixelRatio() - desiredRs) > 1e-3) {
      this.renderer.setPixelRatio(desiredRs);
      this.resize(window.innerWidth, window.innerHeight);
    }
    // Shadows — toggle the directional light's castShadow rather than
    // the renderer's shadowMap.enabled. Cheaper at toggle-time (no
    // shader recompile) and shadows simply stop being submitted.
    this.sun.castShadow = s.shadows();
    // Postprocessing toggles.
    this.postprocessing.setBloomEnabled(s.bloom());
    this.postprocessing.setTiltShiftEnabled(s.tiltShift());
    this.postprocessing.setVignetteEnabled(s.vignette());
    // Vignette uses the per-plot-mode amount internally — re-trigger
    // setPlotInView so the right value lands after a toggle.
    this.postprocessing.setPlotInView(!!this.sim.state.plotInView);
  }

  /** Truck world position + movement state for the positional motor
   *  emitter. Returns null when the truck hasn't been spawned yet
   *  (no depot built). `isMoving` is true only while driving onto or
   *  off the lot — the motor emitter fades to silence otherwise. */
  getTruckState(): { x: number; z: number; isMoving: boolean } | null {
    if (!this.truckMesh) return null;
    const st = this.sim.state.truck.status;
    return {
      x: this.truckMesh.position.x,
      z: this.truckMesh.position.z,
      isMoving: st === 'departed' || st === 'returning',
    };
  }

  // ── Day/night cycle ───────────────────────────────────────────────
  /**
   * Continuous palette keyframes around the daily cycle. `t` ∈ [0,1)
   * wraps at midnight. Every visual that follows the clock (sun light,
   * ambient, sky dome's three gradient stops, sun-mesh tint) is sampled
   * from this single source of truth via lerping between adjacent
   * keyframes — so there are no discrete "modes", just a smooth ramp
   * matching the in-game hour.
   *
   * Indices used by samplePalette:
   *   0  sunCol      directional-light color
   *   1  sunInt      directional-light intensity
   *   2  ambCol      ambient color
   *   3  ambInt      ambient intensity
   *   4  domeTop     sky-dome zenith
   *   5  domeMid     sky-dome mid band
   *   6  domeBtm     sky-dome horizon
   *   7  dayFactor   0..1 daylight strength (drives bloom/warmth/rays)
   */
  private static readonly DAY_PALETTE: {
    t: number;
    sunCol: number; sunInt: number;
    ambCol: number; ambInt: number;
    domeTop: number; domeMid: number; domeBtm: number;
    dayFactor: number;
  }[] = [
    // Deep night — moonlit cool, low contrast.
    { t: 0.00, sunCol: 0x6f80b8, sunInt: 0.25, ambCol: 0x4a5874, ambInt: 0.32,
      domeTop: 0x0c142c, domeMid: 0x182446, domeBtm: 0x303c5e, dayFactor: 0.00 },
    // Late night, hint of indigo rising in the east.
    { t: 0.18, sunCol: 0x8870b8, sunInt: 0.28, ambCol: 0x6470a0, ambInt: 0.38,
      domeTop: 0x182240, domeMid: 0x2c2a5a, domeBtm: 0x5a4a78, dayFactor: 0.06 },
    // First light — pink/purple horizon, sun just rising.
    { t: 0.22, sunCol: 0xff8870, sunInt: 0.50, ambCol: 0x9080b0, ambInt: 0.50,
      domeTop: 0x342c60, domeMid: 0x8c6890, domeBtm: 0xff9870, dayFactor: 0.18 },
    // Sunrise — full orange/gold flood, sun a few degrees above horizon.
    { t: 0.27, sunCol: 0xffb068, sunInt: 0.85, ambCol: 0xb8b0c8, ambInt: 0.65,
      domeTop: 0x4a64a0, domeMid: 0xc8aa90, domeBtm: 0xffba80, dayFactor: 0.45 },
    // Morning — clear warm, blue sky establishing.
    { t: 0.34, sunCol: 0xfff0c8, sunInt: 1.05, ambCol: 0xbccae0, ambInt: 0.78,
      domeTop: 0x4f80c0, domeMid: 0xa8c8e8, domeBtm: 0xe6dcc0, dayFactor: 0.80 },
    // Noon — bright, near-white sun, deep blue zenith.
    { t: 0.50, sunCol: 0xfff8e0, sunInt: 1.25, ambCol: 0xc0d0e8, ambInt: 0.90,
      domeTop: 0x3e74c0, domeMid: 0x9bc5e8, domeBtm: 0xd6e8f4, dayFactor: 1.00 },
    // Mid-afternoon — slight warm lean, slight haze.
    { t: 0.62, sunCol: 0xfff0c0, sunInt: 1.10, ambCol: 0xc4c8da, ambInt: 0.82,
      domeTop: 0x4d80c4, domeMid: 0xb0caec, domeBtm: 0xe8dac0, dayFactor: 0.85 },
    // Golden hour — long shadows, warm everywhere.
    { t: 0.72, sunCol: 0xffa860, sunInt: 0.85, ambCol: 0xc090a8, ambInt: 0.68,
      domeTop: 0x5e7ab8, domeMid: 0xe6b298, domeBtm: 0xffaa70, dayFactor: 0.55 },
    // Sunset — deep red/orange horizon, sun on the way down.
    { t: 0.77, sunCol: 0xff7448, sunInt: 0.55, ambCol: 0x9c7898, ambInt: 0.55,
      domeTop: 0x4a4c88, domeMid: 0xb47090, domeBtm: 0xff7050, dayFactor: 0.28 },
    // Dusk — purple sky deepening, sun just below.
    { t: 0.82, sunCol: 0x9870b0, sunInt: 0.36, ambCol: 0x6c6098, ambInt: 0.45,
      domeTop: 0x2e2c5c, domeMid: 0x6a4884, domeBtm: 0x8e547a, dayFactor: 0.10 },
    // Late dusk — fading to night blue.
    { t: 0.88, sunCol: 0x7468a8, sunInt: 0.28, ambCol: 0x584e7c, ambInt: 0.36,
      domeTop: 0x141c34, domeMid: 0x2a2c54, domeBtm: 0x4a3e6c, dayFactor: 0.04 },
    // Approaching midnight — same as 0.00, used for wrap-around.
    { t: 1.00, sunCol: 0x6f80b8, sunInt: 0.25, ambCol: 0x4a5874, ambInt: 0.32,
      domeTop: 0x0c142c, domeMid: 0x182446, domeBtm: 0x303c5e, dayFactor: 0.00 },
  ];

  /** Pre-allocated palette output + temp Color instances. samplePalette()
   *  is called every frame; the old impl created 7 new THREE.Color objects
   *  per call (~420/s) which produced measurable GC churn. These are
   *  mutated in place each tick and returned by reference. Callers must
   *  treat the result as read-only. */
  private paletteOut = {
    sunCol:    new THREE.Color(),
    sunInt:    0,
    ambCol:    new THREE.Color(),
    ambInt:    0,
    domeTop:   new THREE.Color(),
    domeMid:   new THREE.Color(),
    domeBtm:   new THREE.Color(),
    dayFactor: 0,
  };
  private paletteTmpB = new THREE.Color();

  /**
   * Per-season RGB multipliers applied on top of the time-of-day
   * palette. Each season tints the sun, ambient light, sky dome
   * gradient, and fog so the world reads visibly different even at
   * the same hour. Multipliers ride above the day palette — so we
   * keep the smooth sunrise/sunset transitions but the WHOLE day
   * is colored by the season.
   *
   *   spring  fresh / cool greens, soft pastel light
   *   summer  vivid blue zenith, warm/hot sun, hazy gold horizon
   *   fall    amber gold sun, dusty muted sky, copper fog
   *   winter  cold pale blue, low contrast, washed-out sun
   *
   * Multipliers stay close to 1.0 so the time-of-day palette still
   * drives the broad strokes — anything stronger than ~1.15 starts
   * to feel cartoonish at noon.
   */
  private static readonly SEASON_TINT: Record<Season, {
    sunMul:  [number, number, number];
    ambMul:  [number, number, number];
    domeMul: [number, number, number];
    fogMul:  [number, number, number];
    /** Foliage / grass shader tint — multiplied against green-dominant
     *  vertex colors in VX_MAT.onBeforeCompile. Spring/summer near 1.0
     *  (vibrant green), fall pushes red way up + green down (orange/red
     *  leaves), winter desaturates + pales (frosted / dormant). */
    foliageTint: [number, number, number];
  }> = {
    spring: {
      sunMul:  [1.00, 1.02, 0.96],
      ambMul:  [0.98, 1.04, 0.98],
      domeMul: [0.96, 1.00, 1.02],
      fogMul:  [0.98, 1.02, 1.00],
      foliageTint: [1.00, 1.10, 0.90],   // fresh vivid green
    },
    summer: {
      sunMul:  [1.06, 1.02, 0.92],
      ambMul:  [1.04, 1.02, 0.96],
      domeMul: [0.94, 1.00, 1.08],
      fogMul:  [1.04, 1.02, 0.92],
      foliageTint: [0.95, 1.05, 0.80],   // lush, slightly yellow-green
    },
    fall: {
      sunMul:  [1.08, 0.96, 0.80],
      ambMul:  [1.06, 0.98, 0.86],
      domeMul: [1.02, 0.96, 0.88],
      fogMul:  [1.10, 0.98, 0.82],
      foliageTint: [1.55, 0.70, 0.40],   // amber / orange / rusty
    },
    winter: {
      sunMul:  [0.90, 0.94, 1.04],
      ambMul:  [0.92, 0.96, 1.08],
      domeMul: [0.96, 0.99, 1.06],
      fogMul:  [0.94, 0.98, 1.06],
      foliageTint: [1.10, 1.05, 1.20],   // pale frosted, slight blue
    },
  };
  /** Smoothly-blended seasonal tint for the current calendar day.
   *  Interpolates between two adjacent seasons across a 7-day window
   *  spanning the month boundary, so the world doesn't snap from
   *  "winter" to "spring" overnight on March 1. Updated every frame
   *  by sampleSeasonTint(). */
  private seasonTintOut = {
    sunMul:  [1, 1, 1] as [number, number, number],
    ambMul:  [1, 1, 1] as [number, number, number],
    domeMul: [1, 1, 1] as [number, number, number],
    fogMul:  [1, 1, 1] as [number, number, number],
    /** Pushed into the VX_MAT shader's uFoliageTint uniform every frame. */
    foliageTint: [1, 1, 1] as [number, number, number],
  };
  /** Reusable vector for projecting the sun's world position to screen
   *  space each frame (godrays focal point). Replaces a per-frame .clone(). */
  private sunProjTmp = new THREE.Vector3();

  /** Sample the day palette by time-of-day t (wraps at 1.0). Linear
   *  interpolation between the two surrounding keyframes; smoothstep
   *  applied so neighbouring keyframes blend with an eased curve rather
   *  than a sharp linear ramp, which reads more natural to the eye.
   *  Writes into and returns this.paletteOut (no per-frame allocation). */
  private samplePalette(t: number) {
    const P = Diorama.DAY_PALETTE;
    t = ((t % 1) + 1) % 1;
    // Find surrounding keyframes (palette is sorted ascending by t).
    let i = 0;
    while (i < P.length - 1 && P[i + 1].t <= t) i++;
    const a = P[i];
    const b = P[Math.min(i + 1, P.length - 1)];
    const span = b.t - a.t;
    const raw = span > 1e-6 ? (t - a.t) / span : 0;
    // Smoothstep on the lerp factor — softer fades around the keyframe
    // boundaries (no kinks in dawn / dusk).
    const m = raw * raw * (3 - 2 * raw);
    const out = this.paletteOut;
    const tmp = this.paletteTmpB;
    const lerpInto = (dst: THREE.Color, ax: number, bx: number) => {
      dst.setHex(ax);
      tmp.setHex(bx);
      dst.lerp(tmp, m);
    };
    lerpInto(out.sunCol,  a.sunCol,  b.sunCol);
    lerpInto(out.ambCol,  a.ambCol,  b.ambCol);
    lerpInto(out.domeTop, a.domeTop, b.domeTop);
    lerpInto(out.domeMid, a.domeMid, b.domeMid);
    lerpInto(out.domeBtm, a.domeBtm, b.domeBtm);
    out.sunInt    = a.sunInt    + (b.sunInt    - a.sunInt)    * m;
    out.ambInt    = a.ambInt    + (b.ambInt    - a.ambInt)    * m;
    out.dayFactor = a.dayFactor + (b.dayFactor - a.dayFactor) * m;
    return out;
  }

  /**
   * Compute the season tint for the CURRENT calendar day, smoothly
   * blending into the next season across the last/first few days of
   * each month so the transition isn't a jarring midnight snap.
   *
   * Writes into and returns this.seasonTintOut.
   */
  private sampleSeasonTint() {
    const d = this.sim.getDate();
    const cur = Diorama.SEASON_TINT[d.season];
    // Days-since-season-start vs days-til-season-end. We blend with
    // the next season over the last ~10 days of the current season.
    // Each season has 3 months ≈ 90 days, so 10 days is a soft tail.
    // Compute the absolute day-of-year for the first day of each
    // adjacent season once and cache later. For now, compute inline.
    // 1) Walk back through months to find first month of current season.
    let firstMonthIdx = d.monthIdx;
    while (MONTH_SEASON[MONTHS[(firstMonthIdx + 12 - 1) % 12]] === d.season) {
      firstMonthIdx = (firstMonthIdx + 12 - 1) % 12;
    }
    // 2) Walk forward to find first month of NEXT season.
    let nextSeasonMonthIdx = (firstMonthIdx + 1) % 12;
    while (MONTH_SEASON[MONTHS[nextSeasonMonthIdx]] === d.season) {
      nextSeasonMonthIdx = (nextSeasonMonthIdx + 1) % 12;
    }
    // 3) Days remaining in the current season from "today" forward.
    let daysLeft = 0;
    let walk = d.monthIdx;
    daysLeft += MONTH_DAYS[MONTHS[walk]] - d.dayOfMonth + 1;
    walk = (walk + 1) % 12;
    while (walk !== nextSeasonMonthIdx) {
      daysLeft += MONTH_DAYS[MONTHS[walk]];
      walk = (walk + 1) % 12;
    }
    const FADE_WINDOW = 10;
    const nextSeason = MONTH_SEASON[MONTHS[nextSeasonMonthIdx]];
    const blend = daysLeft >= FADE_WINDOW ? 0
      : 1 - Math.max(0, daysLeft - 1) / FADE_WINDOW;
    const nxt = Diorama.SEASON_TINT[nextSeason];
    const out = this.seasonTintOut;
    const mix = (ai: number, bi: number) => ai + (bi - ai) * blend;
    out.sunMul[0]  = mix(cur.sunMul[0],  nxt.sunMul[0]);
    out.sunMul[1]  = mix(cur.sunMul[1],  nxt.sunMul[1]);
    out.sunMul[2]  = mix(cur.sunMul[2],  nxt.sunMul[2]);
    out.ambMul[0]  = mix(cur.ambMul[0],  nxt.ambMul[0]);
    out.ambMul[1]  = mix(cur.ambMul[1],  nxt.ambMul[1]);
    out.ambMul[2]  = mix(cur.ambMul[2],  nxt.ambMul[2]);
    out.domeMul[0] = mix(cur.domeMul[0], nxt.domeMul[0]);
    out.domeMul[1] = mix(cur.domeMul[1], nxt.domeMul[1]);
    out.domeMul[2] = mix(cur.domeMul[2], nxt.domeMul[2]);
    out.fogMul[0]  = mix(cur.fogMul[0],  nxt.fogMul[0]);
    out.fogMul[1]  = mix(cur.fogMul[1],  nxt.fogMul[1]);
    out.fogMul[2]  = mix(cur.fogMul[2],  nxt.fogMul[2]);
    out.foliageTint[0] = mix(cur.foliageTint[0], nxt.foliageTint[0]);
    out.foliageTint[1] = mix(cur.foliageTint[1], nxt.foliageTint[1]);
    out.foliageTint[2] = mix(cur.foliageTint[2], nxt.foliageTint[2]);
    return out;
  }

  /** Multiply a THREE.Color in place by an RGB triple. */
  private tintColor(c: THREE.Color, mul: [number, number, number]) {
    c.r = Math.max(0, Math.min(1, c.r * mul[0]));
    c.g = Math.max(0, Math.min(1, c.g * mul[1]));
    c.b = Math.max(0, Math.min(1, c.b * mul[2]));
  }

  /** Per-season config for the falling-weather particles. `count` is
   *  the number of particles active at peak density for the season;
   *  winter has the most (snow), summer the fewest (a few pollen
   *  motes). When the season changes, the pool is reskinned (color +
   *  size) rather than reallocated. */
  private static readonly SEASON_FX: Record<Season, {
    count: number;
    colors: number[];
    size: number;
    /** Vertical fall speed range in units/sec (positive number = downward). */
    fall:    [number, number];
    /** Horizontal drift amplitude (sin-sway around vertical axis). */
    drift:   number;
    /** Tumble rotation rate range in rad/sec. */
    rot:     [number, number];
    /** Pull toward 0 for low gravity (snow), 1 for normal (leaves). */
    weight:  number;
    /** Opacity floor; particles can fade independently below this. */
    opacity: number;
  }> = {
    spring: {
      // Cherry-blossom petals — pink/white drifting slowly.
      count: 55,
      colors: [0xffd0e0, 0xffb8d4, 0xfff0f4, 0xffc4d4],
      size: 0.07,
      fall: [0.6, 1.1],
      drift: 0.6,
      rot: [0.8, 1.8],
      weight: 0.45,
      opacity: 0.92,
    },
    summer: {
      // Pollen / dandelion fluff — a few small bright motes.
      count: 22,
      colors: [0xfff8a8, 0xfff080, 0xfff4c0, 0xffffff],
      size: 0.04,
      fall: [0.3, 0.7],
      drift: 0.9,
      rot: [0.2, 0.6],
      weight: 0.15,
      opacity: 0.75,
    },
    fall: {
      // Leaves — orange/red/yellow tumbling.
      count: 60,
      colors: [0xd47840, 0xc04830, 0xe8a040, 0xa05028, 0xe8c060, 0x80401c],
      size: 0.10,
      fall: [1.2, 2.0],
      drift: 0.7,
      rot: [1.5, 3.5],
      weight: 0.75,
      opacity: 0.95,
    },
    winter: {
      // Snowflakes — white, lots of them, slow.
      count: 180,
      colors: [0xffffff, 0xeef4ff, 0xdde8fa],
      size: 0.06,
      fall: [0.5, 1.2],
      drift: 0.55,
      rot: [0.1, 0.4],
      weight: 0.30,
      opacity: 0.95,
    },
  };

  /** Build the seasonal particle pool — single allocation, sized for the
   *  densest season (winter). Each particle is a small flat quad that
   *  faces the camera (sort of — they're plane meshes, the tumble gives
   *  enough variation). Geometry + materials are reskinned in
   *  `reskinSeasonalFx` when the season changes. */
  private spawnSeasonalFx() {
    this.scene.add(this.seasonalFxGroup);
    // Pool size = max across all seasons. Quads are cheap.
    let max = 0;
    for (const s of SEASONS) max = Math.max(max, Diorama.SEASON_FX[s].count);
    // Tiny square plane. Each particle has its own material so colors /
    // opacities vary per particle.
    const geom = new THREE.PlaneGeometry(1, 1);
    for (let i = 0; i < max; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
        toneMapped: false,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.frustumCulled = false;
      mesh.visible = false;
      this.seasonalFxGroup.add(mesh);
      this.seasonalFx.push({
        mesh,
        vx: 0, vy: 0, vz: 0,
        rotAxisX: 0, rotAxisZ: 0, rotSpeed: 0,
        swayPhase: Math.random() * Math.PI * 2,
        swayFreq: 0.5 + Math.random() * 1.5,
      });
    }
  }

  /** Reskin the pool for a new season — set count, color, size on each
   *  pooled particle. Called when the season actually changes. Particles
   *  beyond the new season's count are turned invisible. */
  private reskinSeasonalFx(season: Season) {
    const cfg = Diorama.SEASON_FX[season];
    const halfW = PLOT_W / 2 + 2;
    const halfH = PLOT_H / 2 + 2;
    for (let i = 0; i < this.seasonalFx.length; i++) {
      const p = this.seasonalFx[i];
      const active = i < cfg.count;
      p.mesh.visible = active;
      if (!active) continue;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.color.setHex(cfg.colors[i % cfg.colors.length]);
      mat.opacity = cfg.opacity * (0.7 + Math.random() * 0.3);
      const sz = cfg.size * (0.7 + Math.random() * 0.6);
      p.mesh.scale.setScalar(sz);
      // Randomize initial position above the lot. Stagger vertical so
      // the first frame doesn't have everything at the same height.
      p.mesh.position.set(
        (Math.random() * 2 - 1) * halfW,
        Math.random() * 8 + 2,
        (Math.random() * 2 - 1) * halfH,
      );
      p.mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI,
      );
      const fallSpeed = cfg.fall[0] + Math.random() * (cfg.fall[1] - cfg.fall[0]);
      p.vy = -fallSpeed;
      p.vx = (Math.random() - 0.5) * cfg.drift;
      p.vz = (Math.random() - 0.5) * cfg.drift;
      p.rotAxisX = Math.random() - 0.5;
      p.rotAxisZ = Math.random() - 0.5;
      p.rotSpeed = (cfg.rot[0] + Math.random() * (cfg.rot[1] - cfg.rot[0]))
        * (Math.random() < 0.5 ? -1 : 1);
      p.swayPhase = Math.random() * Math.PI * 2;
      p.swayFreq = 0.5 + Math.random() * 1.5;
    }
    this.seasonalFxSeason = season;
  }

  /** Drive the seasonal particles each frame. Reskins the pool when
   *  the season changes; otherwise it's just integrate-position +
   *  tumble + recycle on ground hit. */
  private animateSeasonalFx(dt: number) {
    if (this.seasonalFx.length === 0) return;
    // Settings toggle — disabling weather particles hides the whole
    // group and short-circuits the per-particle update so the cost
    // drops to a single visibility flag write per frame.
    if (this.settings && !this.settings.seasonalFx()) {
      this.seasonalFxGroup.visible = false;
      return;
    }
    // Plot mode is a focused close-up — weather particles raining down
    // through the zoomed tiles just add visual noise.
    if (this.sim.state.plotInView) {
      this.seasonalFxGroup.visible = false;
      return;
    }
    this.seasonalFxGroup.visible = true;
    const season = this.sim.getSeason();
    if (season !== this.seasonalFxSeason) this.reskinSeasonalFx(season);
    const cfg = Diorama.SEASON_FX[season];
    const halfW = PLOT_W / 2 + 2;
    const halfH = PLOT_H / 2 + 2;
    const t = this.elapsed;
    for (let i = 0; i < cfg.count; i++) {
      const p = this.seasonalFx[i];
      if (!p.mesh.visible) continue;
      // Sway: gentle horizontal sin oscillation on top of the base drift.
      const sway = Math.sin(t * p.swayFreq + p.swayPhase) * cfg.drift * 0.5;
      p.mesh.position.x += (p.vx + sway * 0.3) * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += (p.vz + sway * 0.3) * dt;
      // Tumble — rotate around a random axis.
      p.mesh.rotation.x += p.rotAxisX * p.rotSpeed * dt;
      p.mesh.rotation.y += p.rotSpeed * dt;
      p.mesh.rotation.z += p.rotAxisZ * p.rotSpeed * dt;
      // Recycle when below ground (y < 0) or off-area horizontally.
      if (p.mesh.position.y < -0.5 ||
          Math.abs(p.mesh.position.x) > halfW + 1 ||
          Math.abs(p.mesh.position.z) > halfH + 1) {
        p.mesh.position.set(
          (Math.random() * 2 - 1) * halfW,
          8 + Math.random() * 4,
          (Math.random() * 2 - 1) * halfH,
        );
        const fallSpeed = cfg.fall[0] + Math.random() * (cfg.fall[1] - cfg.fall[0]);
        p.vy = -fallSpeed;
        p.vx = (Math.random() - 0.5) * cfg.drift;
        p.vz = (Math.random() - 0.5) * cfg.drift;
      }
    }
  }

  /** Build the snow-blanket and fall-leaf-pile ground overlays. Each is
   *  a single InstancedMesh of small voxel cubes scattered across the
   *  lot. They sit flush on the ground (slight y) and live in the scene
   *  from boot — we just fade their material opacity in/out by season.
   *
   *  Voxel-style instead of soft splats so the overlay matches the rest
   *  of the diorama's chunky pixel-cube aesthetic. */
  private spawnGroundOverlays() {
    this.scene.add(this.groundOverlayGroup);
    const halfW = PLOT_W / 2;
    const halfH = PLOT_H / 2;
    const SNOW_COUNT = 900;
    const LEAF_COUNT = 480;
    // Unit cube — gets scaled per-instance to a flat, thin slab so it
    // reads as a layer of snow / leaves on top of the ground rather
    // than chunky toy blocks.
    const cubeGeom = new THREE.BoxGeometry(1, 1, 1);

    // ── Snow blanket — flat white voxel slabs scattered densely.
    // toneMapped:false keeps them bright white through the
    // postprocessing tonemap so they read as fresh snow.
    const snowMat = new THREE.MeshLambertMaterial({
      color: 0xf4f8ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.snowOverlay = new THREE.InstancedMesh(cubeGeom, snowMat, SNOW_COUNT);
    this.snowOverlay.frustumCulled = false;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const eul = new THREE.Euler();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    // Voxel dimensions: snow is a thin slab, so y is tiny vs x/z.
    // Mild per-instance variation (size + height) so the blanket reads
    // as drifty / uneven instead of a paint-rolled flat.
    for (let i = 0; i < SNOW_COUNT; i++) {
      pos.set(
        (Math.random() * 2 - 1) * halfW,
        0.05 + Math.random() * 0.06,         // 0.05..0.11 — just above ground
        (Math.random() * 2 - 1) * halfH,
      );
      // Random yaw so slabs aren't all axis-aligned (still voxel-y).
      eul.set(0, Math.random() * Math.PI * 2, 0);
      q.setFromEuler(eul);
      // Snow slab footprint: 0.15..0.32 in x/z, 0.05..0.12 thick.
      const sx = 0.15 + Math.random() * 0.17;
      const sz = 0.15 + Math.random() * 0.17;
      const sy = 0.05 + Math.random() * 0.07;
      scl.set(sx, sy, sz);
      m.compose(pos, q, scl);
      this.snowOverlay.setMatrixAt(i, m);
    }
    this.snowOverlay.instanceMatrix.needsUpdate = true;
    this.groundOverlayGroup.add(this.snowOverlay);

    // ── Fallen leaves — voxel slabs with per-instance autumn colors.
    const leafMat = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.leafOverlay = new THREE.InstancedMesh(cubeGeom, leafMat, LEAF_COUNT);
    this.leafOverlay.frustumCulled = false;
    const LEAF_COLORS = [
      new THREE.Color(0xd47840), new THREE.Color(0xc04830),
      new THREE.Color(0xe8a040), new THREE.Color(0xa05028),
      new THREE.Color(0xe8c060), new THREE.Color(0x80401c),
    ];
    for (let i = 0; i < LEAF_COUNT; i++) {
      pos.set(
        (Math.random() * 2 - 1) * halfW,
        0.04 + Math.random() * 0.05,
        (Math.random() * 2 - 1) * halfH,
      );
      eul.set(0, Math.random() * Math.PI * 2, 0);
      q.setFromEuler(eul);
      // Leaves are smaller + thinner than snow slabs.
      const sx = 0.10 + Math.random() * 0.10;
      const sz = 0.10 + Math.random() * 0.10;
      const sy = 0.03 + Math.random() * 0.04;
      scl.set(sx, sy, sz);
      m.compose(pos, q, scl);
      this.leafOverlay.setMatrixAt(i, m);
      this.leafOverlay.setColorAt(i, LEAF_COLORS[i % LEAF_COLORS.length]);
    }
    this.leafOverlay.instanceMatrix.needsUpdate = true;
    if (this.leafOverlay.instanceColor) this.leafOverlay.instanceColor.needsUpdate = true;
    this.groundOverlayGroup.add(this.leafOverlay);
  }

  /** Update the ground-overlay opacities each frame based on season.
   *  Snow fades in across winter (peak mid-winter), leaves fade in
   *  across fall (peak late-fall). Both fade out at season boundaries
   *  rather than snapping. */
  private animateGroundOverlays(_dt: number) {
    if (!this.snowOverlay && !this.leafOverlay) return;
    // In plot mode the camera zooms into a single tile cluster which
    // gets its own dedicated ground rendering — the world-scale
    // overlays would just clutter the close-up view, so hide the
    // whole group while we're in plot view.
    if (this.sim.state.plotInView) {
      this.groundOverlayGroup.visible = false;
      return;
    }
    this.groundOverlayGroup.visible = true;
    const d = this.sim.getDate();
    // Snow opacity — full during winter (Dec/Jan/Feb), fading in over
    // the last ~14 days of November and fading out over the first
    // ~14 days of March.
    let snowOpacity = 0;
    if (d.season === 'winter') {
      snowOpacity = 0.85;
    } else if (d.month === 'november') {
      // Ramps up in the last fortnight of November.
      const t = Math.max(0, (d.dayOfMonth - 16) / 14);
      snowOpacity = Math.min(0.85, t * 0.85);
    } else if (d.month === 'march') {
      // Ramps down in the first fortnight of March.
      const t = Math.max(0, 1 - d.dayOfMonth / 14);
      snowOpacity = t * 0.85;
    }
    // Leaf opacity — peaks late fall (October/November), fades in
    // across late September and fades out as winter starts.
    let leafOpacity = 0;
    if (d.month === 'september') {
      const t = Math.max(0, (d.dayOfMonth - 10) / 20);
      leafOpacity = Math.min(0.75, t * 0.75);
    } else if (d.month === 'october' || d.month === 'november') {
      leafOpacity = 0.75;
    } else if (d.month === 'december') {
      // Buried under snow within the first 2 weeks.
      const t = Math.max(0, 1 - d.dayOfMonth / 14);
      leafOpacity = t * 0.75;
    }
    if (this.snowOverlay) {
      const mat = this.snowOverlay.material as THREE.MeshBasicMaterial;
      mat.opacity = snowOpacity;
      this.snowOverlay.visible = snowOpacity > 0.01;
    }
    if (this.leafOverlay) {
      const mat = this.leafOverlay.material as THREE.MeshBasicMaterial;
      mat.opacity = leafOpacity;
      this.leafOverlay.visible = leafOpacity > 0.01;
    }
  }

  /** Populate the forest with a swarm of fireflies. Each is a small
   *  emissive yellow-green cube anchored to a random forest-zone tile.
   *  A few carry actual PointLights so the dim forest understory gets
   *  warm bouncing light. Always visible (the forest is ambiently buggy)
   *  but each firefly twinkles, and the WHOLE swarm brightens at night. */
  private spawnFireflies() {
    this.scene.add(this.fireflyGroup);
    // Spawn anywhere in the forest zone (rows north of the clearing),
    // regardless of what's on the tile. This way the swarm survives the
    // player chopping the forest down.
    const forestTiles: { c: number; r: number }[] = [];
    for (let r = 0; r < ROAD_ROW - 4; r++) {
      for (let c = 0; c < PLOT_W; c++) {
        forestTiles.push({ c, r });
      }
    }
    if (forestTiles.length === 0) return;
    const COUNT = 60;
    const LIGHT_BUDGET = 6;
    const halfW = PLOT_W / 2;
    const halfH = PLOT_H / 2;
    // Bigger geometry so the glow reads against the world at typical
    // camera distance. 0.14 is roughly 1/7th of a tile.
    const geom = new THREE.BoxGeometry(0.14, 0.14, 0.14);
    for (let i = 0; i < COUNT; i++) {
      const tile = forestTiles[Math.floor(Math.random() * forestTiles.length)];
      const bx = tile.c - halfW + 0.5 + (Math.random() - 0.5) * 0.8;
      const bz = tile.r - halfH + 0.5 + (Math.random() - 0.5) * 0.8;
      const by = 0.5 + Math.random() * 1.8;
      const mat = new THREE.MeshBasicMaterial({
        // Warm yellow-green, the classic firefly glow. toneMapped:false
        // keeps it bright after the postprocessing tonemap, so it pops
        // against dark tree foliage even in daylight.
        color: 0xfff070,
        transparent: true,
        opacity: 0.95,
        toneMapped: false,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(bx, by, bz);
      mesh.frustumCulled = false;
      this.fireflyGroup.add(mesh);
      let light: THREE.PointLight | undefined;
      if (i < LIGHT_BUDGET) {
        light = new THREE.PointLight(0xfff090, 0, 3.8, 2.0);
        mesh.add(light);
      }
      this.fireflies.push({
        mesh, light,
        bx, by, bz,
        phaseX: Math.random() * Math.PI * 2,
        phaseY: Math.random() * Math.PI * 2,
        phaseZ: Math.random() * Math.PI * 2,
        speedX: 0.4 + Math.random() * 0.6,
        speedY: 0.25 + Math.random() * 0.45,
        speedZ: 0.4 + Math.random() * 0.6,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: 1.8 + Math.random() * 3.2,
        twinkleBase: 0.65 + Math.random() * 0.35,
      });
    }
  }

  /** Drift + twinkle the firefly swarm. Night-only: the whole group is
   *  hidden during the day and ramps in at dusk so the visible sparkle
   *  reads as "the forest is alive after dark." */
  private animateFireflies(dt: number) {
    void dt;
    if (this.fireflies.length === 0) return;
    // Settings toggle — hide the firefly group entirely on low-end
    // hardware. Skips both the visibility-curve math and the per-
    // firefly update loop below.
    if (this.settings && !this.settings.fireflies()) {
      this.fireflyGroup.visible = false;
      return;
    }
    // Plot mode is a focused close-up — fireflies just add clutter.
    if (this.sim.state.plotInView) {
      this.fireflyGroup.visible = false;
      return;
    }
    const t = this.elapsed;
    // 0 at noon, ramps up through dusk to 1 at midnight.
    const above = Math.sin(this.timeOfDay * Math.PI * 2 - Math.PI / 2);
    const nightFactor = Math.max(0, Math.min(1, -above * 1.3 + 0.15));
    const visible = nightFactor > 0.04;
    this.fireflyGroup.visible = visible;
    if (!visible) {
      for (const f of this.fireflies) if (f.light) f.light.intensity = 0;
      return;
    }
    for (const f of this.fireflies) {
      f.mesh.position.x = f.bx + Math.sin(t * f.speedX + f.phaseX) * 0.45;
      f.mesh.position.y = f.by + Math.sin(t * f.speedY + f.phaseY) * 0.30;
      f.mesh.position.z = f.bz + Math.sin(t * f.speedZ + f.phaseZ) * 0.45;
      const tw = (Math.sin(t * f.twinkleSpeed + f.twinklePhase) * 0.5 + 0.5);
      const intensity = (0.3 + tw * 0.7) * f.twinkleBase * nightFactor;
      const mat = f.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.5 + intensity * 0.5;
      const s = 0.75 + intensity * 0.7;
      f.mesh.scale.setScalar(s);
      if (f.light) f.light.intensity = intensity * 1.6;
    }
  }

  private animateDayNight(_dt: number) {
    this.timeOfDay = this.sim.getTimeOfDay();
    const t = this.timeOfDay;
    // Sun rotation — full 360° around the lot per in-game day. `above` is
    // the sun's height in [-1, +1] (sin curve), so it dips smoothly below
    // the horizon at night and the path traces a true arc, not a clamp.
    const sunAngle = t * Math.PI * 2;
    const above = Math.sin(sunAngle - Math.PI / 2);   // -1 midnight, +1 noon
    const dist = 35;
    this.sun.position.set(
      Math.cos(sunAngle) * dist * 0.6,
      // Allow the light to actually go below the world (down to y≈-22) so
      // the arc reads as a real rotation. Shadows fade gracefully via the
      // intensity ramp from the palette — no need to clamp height.
      above * 30 + 8,
      Math.sin(sunAngle) * dist * 0.4 + 14,
    );

    const P = this.samplePalette(t);

    // Season tint — multiplies on top of the time-of-day palette so the
    // SAME hour reads visibly different in spring vs winter. Blends
    // smoothly across the last 10 days of each season so transitions
    // aren't a midnight jolt.
    const ST = this.sampleSeasonTint();
    this.tintColor(P.sunCol,  ST.sunMul);
    this.tintColor(P.ambCol,  ST.ambMul);
    this.tintColor(P.domeTop, ST.domeMul);
    this.tintColor(P.domeMid, ST.domeMul);
    this.tintColor(P.domeBtm, ST.domeMul);
    // Push the foliage tint into VX_MAT's shader uniform — affects
    // every green-dominant voxel in the world (tree leaves, bushes,
    // grass tufts) without touching geometry. Trunks / dirt / stone
    // skip the tint because they're not green-dominant.
    const ud = (VX_MAT as any).userData;
    if (ud?.seasonTintUniform) {
      ud.seasonTintUniform.value.set(ST.foliageTint[0], ST.foliageTint[1], ST.foliageTint[2]);
    }

    // Directional light + ambient — both fully driven by the palette so
    // the transitions are continuous all the way around the cycle.
    this.sun.color.copy(P.sunCol);
    this.sun.intensity = P.sunInt;
    this.ambient.color.copy(P.ambCol);
    this.ambient.intensity = P.ambInt;

    // Background fallback (mostly overwritten by the dome but kept for
    // edge cases like the dome failing to compile).
    this.skyColor.copy(P.domeMid);
    (this.scene.background as THREE.Color).copy(this.skyColor);

    // Sky dome — three gradient stops driven by the palette every frame.
    if (this.skyDomeMat) {
      (this.skyDomeMat.uniforms.bottomColor.value as THREE.Color).copy(P.domeBtm);
      (this.skyDomeMat.uniforms.midColor.value    as THREE.Color).copy(P.domeMid);
      (this.skyDomeMat.uniforms.topColor.value    as THREE.Color).copy(P.domeTop);
    }
    // Fog hugs the dome's horizon stop so the lot's edge dissolves into
    // whatever the sky's bottom band currently is — smoothly tinted with
    // the sun's color all day long.
    if (this.scene.fog) {
      const fog = this.scene.fog as THREE.Fog;
      const fogCol = fog.color;
      fogCol.copy(P.domeBtm);
      // Fog gets an extra seasonal nudge ON TOP of the dome tint —
      // copper-amber in fall, cold-pale in winter, hazy-warm in summer.
      // ST.fogMul is a small delta around 1.0 so this is subtle.
      const ST = this.seasonTintOut;
      fogCol.r = Math.max(0, Math.min(1, fogCol.r * ST.fogMul[0] / ST.domeMul[0]));
      fogCol.g = Math.max(0, Math.min(1, fogCol.g * ST.fogMul[1] / ST.domeMul[1]));
      fogCol.b = Math.max(0, Math.min(1, fogCol.b * ST.fogMul[2] / ST.domeMul[2]));
      // Fog density scales with season: winter pulls the far plane in
      // for a heavy cold mist, summer pushes it out for crisp clear
      // air. Spring + fall sit at the baseline. The fog NEAR also
      // shifts so the haze ramp stays smooth.
      const season = this.sim.getSeason();
      const fogFarMul =
        season === 'winter' ? 0.65 :
        season === 'summer' ? 1.25 :
        1.00;
      fog.near = 55 * fogFarMul;
      fog.far  = 105 * fogFarMul;
    }

    // ── Sun mesh — orbits with the directional light + brightens with it.
    if (this.sunMesh) {
      // Place far enough away to read as "in the sky", but still inside the
      // sky-dome radius (120). Match the light's direction.
      const sunVisDist = 60;
      this.sunMesh.position.set(
        Math.cos(sunAngle) * sunVisDist * 0.6,
        above * sunVisDist * 0.45 + 6,
        Math.sin(sunAngle) * sunVisDist * 0.4 + 18,
      );
      // Sun tints with the palette so it reads pink at sunrise, gold at
      // noon, blood-orange at sunset. Multiply by a brightness factor that
      // ramps with sun height so the disc dims as it sinks below horizon.
      const m = this.sunMesh.material as THREE.MeshBasicMaterial;
      m.color.copy(P.sunCol);
      // Fade the visible disc out below the horizon (it would otherwise
      // sit awkwardly behind the lot base). Smoothstep instead of a hard
      // clamp so the sun eases in/out around sunrise/sunset instead of
      // flickering as `above` oscillates near zero.
      const fadeRaw = Math.max(0, Math.min(1, (above + 0.15) / 0.45));
      m.opacity = fadeRaw * fadeRaw * (3 - 2 * fadeRaw);     // smoothstep
      this.sunMesh.visible = m.opacity > 0.02;
    }

    // ── Stars: smooth fade as the night palette kicks in.
    if (this.starsMesh) {
      const sm = this.starsMesh.material as THREE.MeshBasicMaterial;
      // Visible when day factor low. Mapped through smoothstep so they
      // don't pop on/off at the boundary.
      const k = 1.0 - Math.max(0, Math.min(1, P.dayFactor * 2.2));
      sm.opacity = k * k * (3 - 2 * k);
      this.starsMesh.rotation.y = this.elapsed * 0.012;
    }

    // ── Moon: orbits opposite the sun, visible at night.
    if (this.moonMesh) {
      const moonAngle = sunAngle + Math.PI;
      const moonAbove = Math.sin(moonAngle - Math.PI / 2);
      this.moonMesh.position.set(
        Math.cos(moonAngle) * dist * 0.5,
        Math.max(4, moonAbove * 26 + 8),
        Math.sin(moonAngle) * dist * 0.3 + 14,
      );
      const mm = this.moonMesh.material as THREE.MeshBasicMaterial;
      mm.opacity = Math.max(0, Math.min(1, moonAbove * 1.5));
    }

    // Window lamps ramp on as the day factor dies.
    const lampIntensity = Math.max(0, Math.min(1.6, (1 - P.dayFactor) * 1.6));
    for (const l of this.windowLamps) l.intensity = lampIntensity;

    // ── Post-processing — bloom, warmth, godrays all driven by the same
    // smooth dayFactor + actual sun screen position.
    if (this.postprocessing) {
      this.postprocessing.setDayFactor(P.dayFactor);
      this.sunProjTmp.copy(this.sun.position).project(this.camera);
      const sx = this.sunProjTmp.x * 0.5 + 0.5;
      const sy = this.sunProjTmp.y * 0.5 + 0.5;
      // Rays follow the same dayFactor curve — strong at noon, soft at
      // dawn/dusk, off at night.
      const rayStrength = P.dayFactor * 0.6;
      this.postprocessing.setSunScreen(sx, sy, rayStrength);
    }
  }

  // ── Per-frame ─────────────────────────────────────────────────────
  update(dt: number) {
    // Hard pause — the Settings modal flips sim.paused, and the whole
    // world freezes here: no sim tick, no elapsed accumulator, no
    // animateXxx, no camera ease. Otherwise trees keep swaying / the
    // truck keeps driving / fireflies keep twinkling while the modal
    // is open, which reads as "the game didn't pause" even though
    // sim state itself was frozen.
    if (this.sim.paused) return;
    this.elapsed += dt;
    this.sim.tick(dt);
    this.animateZoom(dt);
    this.animateCamera(dt);
    // Refresh the hover target every frame so the gold tile outline
    // follows the cursor's WORLD position when the camera moves (pan,
    // wheel zoom, plot dive) even without a pointer-move event. Without
    // this, the highlight sticks to whatever tile was under the pointer
    // last time it moved on screen.
    this.updateHover();
    this.animateDayNight(dt);
    this.animateSway(dt);
    this.animateFireflies(dt);
    this.animateSeasonalFx(dt);
    // Drive the merged-seed-mesh wind sway via the shared shader uniform.
    (VX_MAT_SWAY as any).userData.swayTimeUniform.value = this.elapsed;
    this.animateGroundOverlays(dt);
    this.animateCursorPush(dt);
    this.animateSmoke(dt);
    this.animateFlyers(dt);
    this.animateParticles(dt);
    this.animateFallingTrees(dt);
    this.animateBuildAnims(dt);
    this.animateTruck(dt);
    this.animateToolProp(dt);
    this.animateHold(dt);
    this.animateSelectionOverlay(dt);
    this.animatePlacementSlots(dt);
    this.animateWaterSpread(dt);
    this.animatePlotTileDrop(dt);
    this.updateSelectedZoneBanner();

    // Drain per-frame dirty-tile set — collapses bursts of subCellTilled /
    // Watered / Seeded events (256/tile during auto-fill) into one rebuild
    // + one particle burst per affected tile.
    this.flushDirtyTiles();

    // Crop stage sync (tile-level legacy crops, mostly for non-plot land)
    PlotSystem.forEachTile(this.sim.state, (tile, c, r) => {
      const key = this.tileKey(c, r);
      const entry = this.cropEntries.get(key);
      if (tile.crop && entry && entry.stage !== tile.crop.stage) {
        const parent = entry.obj.parent;
        if (parent) {
          parent.remove(entry.obj);
          // Free the old crop's voxel geometry — without this, every
          // stage transition leaks the previous mesh's buffers.
          disposeTree(entry.obj);
          this.removeSwayNode(entry.obj);
          const next = makeCrop(tile.crop.type, tile.crop.stage, c * 1000 + r * 7 + 41);
          const [x, , z] = this.tileCenter(c, r);
          next.position.set(x, 0, z);
          parent.add(next);
          this.cropEntries.set(key, { obj: next, stage: tile.crop.stage });
          this.registerSubtreeSway(next);
        }
      }
    });

    // Per-tile merged seed meshes — replaces the per-seed mesh path.
    // syncTileSeedMeshes() compares each visible tile's seed signature
    // against the cached one and marks dirty tiles for rebuild. The
    // rebuild itself is batched via flushDirtyTiles. Dropped: the old
    // per-seed orphan loop that did O(N) work for N seeds every frame.
    this.syncTileSeedMeshes();

    // Mill sails — only spin once the mill is fully operational.
    PlotSystem.forEachTile(this.sim.state, (tile, c, r) => {
      if (tile.bld !== 'mill') return;
      if (!ConstructionSystem.isOperational(tile)) return;
      const sails = this.millSails.get(this.tileKey(c, r));
      if (!sails) return;
      const busy = MillSystem.isBusy(tile);
      const speed = busy ? 2.6 : 0.7;
      sails.rotation.z -= speed * dt;
    });

    // Drift clouds. Wrap from the far edge back to the near edge with a
    // brief fade so the teleport isn't visible at zoom. Each cloud has
    // an opacity ramp keyed to |x| / CLOUD_WRAP: fully opaque in the
    // middle 70% of the range, fading toward 0 in the outer 30% so the
    // jump from +30 → -30 happens while the cloud is invisible.
    const CLOUD_WRAP = 30;
    for (const cd of this.cloudData) {
      cd.obj.position.x += cd.vx * dt;
      if (cd.obj.position.x >  CLOUD_WRAP) cd.obj.position.x = -CLOUD_WRAP;
      if (cd.obj.position.x < -CLOUD_WRAP) cd.obj.position.x =  CLOUD_WRAP;
      const t01 = Math.abs(cd.obj.position.x) / CLOUD_WRAP;
      // Opacity = 1 until t01 = 0.70, then ramps to 0 by t01 = 1.0.
      const fade = Math.max(0, Math.min(1, (1.0 - t01) / 0.30));
      // Clouds are merged voxel meshes (single material). Set opacity
      // on the inner mesh's material; mesh added is the only child of
      // the wrapper group (per makeCloud).
      const inner = cd.obj.children[0] as THREE.Mesh | undefined;
      if (inner) {
        const m = inner.material as THREE.MeshBasicMaterial;
        if (!m.transparent) m.transparent = true;
        m.opacity = fade;
      }
    }

    // Sync post-processing strength to the current plot-view state — the
    // tilt-shift damps in plot view so the work area isn't blurred.
    this.postprocessing.setPlotInView(!!this.sim.state.plotInView);
    this.postprocessing.render(dt);
  }

  resize(w: number, h: number) {
    const aspect = w / h;
    const d = this.viewSize;
    this.camera.left = -d * aspect;
    this.camera.right = d * aspect;
    this.camera.top = d;
    this.camera.bottom = -d;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.postprocessing.setSize(w, h);
  }

  dispose() {
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointerleave', this.onPointerUp);
    this.canvas.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.renderer.dispose();
  }
}

